import * as i0 from '@angular/core';
import { InjectionToken, inject, ElementRef, input, Directive, Component, output, ChangeDetectionStrategy, Renderer2, HostListener, Input, forwardRef, viewChild, signal, ContentChildren, ChangeDetectorRef, Injector, ViewContainerRef, model, computed, effect, contentChildren, ComponentFactoryResolver, ApplicationRef, Injectable, viewChildren, contentChild, TemplateRef, ViewChild } from '@angular/core';
import { cva } from 'class-variance-authority';
import * as i1 from '@angular/forms';
import { FormsModule, NG_VALUE_ACCESSOR, NG_VALIDATORS, FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import * as i1$1 from '@angular/common';
import { DatePipe, CommonModule, NgTemplateOutlet, NgClass, AsyncPipe } from '@angular/common';
import { tap, Subject, BehaviorSubject, takeUntil } from 'rxjs';
import { debounceTime, tap as tap$1, finalize } from 'rxjs/operators';
import { trigger, state, transition, style as style$1, animate } from '@angular/animations';
import * as i1$2 from '@angular/router';
import { RouterModule, RouterLink, RouterLinkActive } from '@angular/router';

const THEME_TOKEN = new InjectionToken('theme');

function provideTheme(theme) {
    return [{ provide: THEME_TOKEN, useValue: theme }];
}

function style(elementRef, twClasses) {
    twClasses.split(' ').forEach(twClass => elementRef.nativeElement.classList.add(twClass));
}
function updateElementStyles(elementRef, twClasses) {
    elementRef.nativeElement.className = twClasses;
}

const buttonBaseStyles = [
    'inline-flex',
    'items-center',
    'justify-center',
    'gap-2',
    'px-6',
    'py-3',
    'data-[square=true]:!p-3',
    'data-[square=true]:aspect-square',
    'data-[square=true]:gap-0',
    'data-[square=true]:w-[46px]',
    'data-[size=sm]:!px-3',
    'data-[size=sm]:!py-1.5',
    'select-none',
    'font-semibold',
    'disabled:cursor-not-allowed',
    'transition-colors',
    'enabled:cursor-pointer',
    'relative',
    'box-border',
    'text-sm',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const buttonStylesSAG = cva(buttonBaseStyles, {
    variants: {
        color: {
            primary: [
                'text-white',
                'border',
                'border-[#007993]',
                'rounded-sm',
                'bg-[#007993]',
                'enabled:hover:bg-[#005159]',
                'enabled:hover:border-[#005159]',
                'enabled:active:bg-[#196269]',
                'dark:bg-[#00CCCC]',
                'dark:text-[#000028]',
                'dark:border-[#00CCCC]',
                'dark:hover:bg-[#00FFB9]',
                'dark:hover:border-[#00FFB9]',
                'dark:active:bg-[#00E5AA]',
                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:border-[#333353]',
                'disabled:bg-[#E5E5E9]',
                'disabled:border-[#E5E5E9]',
                'disabled:text-[#9999A9]'
            ],
            secondary: [
                'text-[#007993]',
                'border',
                'border-[#007993]',
                'bg-transparent',
                'rounded-sm',
                'enabled:hover:bg-[#D1FFF2]',
                'enabled:hover:border-[#005159]',
                'enabled:hover:text-[#005159]',
                'enabled:active:bg-transparent',
                'enabled:active:text-[#005159]',
                'enabled:active:border-[#005159]',
                'disabled:border-[#9999A9]',
                'disabled:text-[#9999A9]',
                'dark:border-[#00CCCC]',
                'dark:text-[#00CCCC]',
                'dark:enabled:hover:text-[#00FFB9]',
                'dark:enabled:hover:border-[#00FFB9]',
                'dark:enabled:hover:bg-[#001F39]',
                'dark:enabled:active:text-[#00FFB9]',
                'dark:enabled:active:border-[#00FFB9]',
                'dark:enabled:active:bg-transparent',
                'dark:disabled:text-[#737389]',
                'dark:disabled:border-[#737389]'
            ],
            tertiary: [
                'text-[#007993]',
                'border',
                'border-transparent',
                'bg-transparent',
                'enabled:hover:text-[#005159]',
                'enabled:hover:bg-[#D1FFF2]',
                'enabled:active:text-[#005159]',
                'enabled:active:bg-transparent',
                'disabled:bg-transparent',
                'disabled:text-[#9999A9]',
                'dark:text-[#00CCCC]',
                'dark:enabled:hover:text-[#00FFB9]',
                'dark:enabled:hover:bg-[#001F39]',
                'dark:enabled:active:bg-transparent',
                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-transparent'
            ],
            danger: [
                'text-white',
                'border',
                'border-[#D72339]',
                'rounded-sm',
                'bg-[#D72339]',
                'enabled:hover:bg-[#c81c31]',
                'enabled:hover:border-[#c81c31]',
                'enabled:active:bg-[#b7192c]',
                'dark:bg-[#FF2640]',
                'dark:text-[#000028]',
                'dark:border-[#FF2640]',
                'dark:hover:bg-[#d51e34]',
                'dark:hover:border-[#d51e34]',
                'dark:active:bg-[#b41a2d]',
                'dark:disabled:text-[#737389]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:border-[#333353]',
                'disabled:bg-[#E5E5E9]',
                'disabled:border-[#E5E5E9]',
                'disabled:text-[#9999A9]'
            ]
        }
    },
    defaultVariants: {
        color: 'primary'
    }
});
const buttonStylesSHS = cva(buttonBaseStyles, {
    variants: {
        color: {
            primary: [
                'text-white',
                'rounded-full',
                'bg-[#cf4b00]',
                'enabled:hover:bg-[#ba4400]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:text-[#737389]',
                'disabled:text-[#9999A9]',
                'disabled:bg-[#E5E5E9]'
            ],
            secondary: [
                'text-[#1A1A1A]',
                'rounded-full',
                'bg-white',
                'border',
                'border-[#b3b3b3]',
                'enabled:hover:bg-[#f2f2f2]',
                '[&>*]:enabled:text-[#cf4b00]',
                'disabled:text-[#9999A9]',
                'disabled:border-[#9999A9]',
                'dark:bg-[#1a1a1a]',
                'dark:border-[#4d4d4d]',
                'dark:text-[#e6e6e6]',
                '[&>*]:dark:enabled:text-[#e6e6e6]',
                'dark:hover:bg-[#313131]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:text-[#737389]'
            ],
            tertiary: [
                'text-[#1A1A1A]',
                'rounded-full',
                'bg-transparent',
                '[&>*]:enabled:text-[#cf4b00]',
                'enabled:hover:bg-[#e6e6e6]',
                'disabled:text-[#9999A9]',
                'dark:text-[#e6e6e6]',
                '[&>*]:dark:enabled:text-[#e6e6e6]',
                'dark:hover:bg-[#313131]',
                'dark:disabled:text-[#737389]'
            ],
            danger: [
                'text-white',
                'rounded-full',
                'bg-[#cf4b00]',
                'enabled:hover:bg-[#ba4400]',
                'dark:disabled:bg-[#333353]',
                'dark:disabled:text-[#737389]',
                'disabled:text-[#9999A9]',
                'disabled:bg-[#E5E5E9]'
            ]
        }
    },
    defaultVariants: {
        color: 'primary'
    }
});

function componentStylesByTheme(theme, sag, shs) {
    return theme === 'shs' ? shs : sag;
}

class ButtonDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    color = input();
    ngOnChanges() {
        this.applyStyling();
    }
    ngOnInit() {
        this.applyStyling();
    }
    applyStyling() {
        const buttonStyles = componentStylesByTheme(this.theme, buttonStylesSAG, buttonStylesSHS);
        updateElementStyles(this.elementRef, buttonStyles({ color: this.color() }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: ButtonDirective, isStandalone: true, selector: "button[sag-ui-button]", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null } }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[sag-ui-button]',
                    standalone: true
                }]
        }] });

const spinnerStylesSAG = cva(['stroke-[#007993]', 'dark:stroke-[#00CCCC]']);
const spinnerStylesSHS = cva(['stroke-[#cf4b00]']);

class SpinnerComponent {
    theme = inject(THEME_TOKEN);
    /**
     * The size of the spinner.
     * @default 'md'
     * @type {'sm' | 'md' | 'lg'}
     * @optional
     */
    size = input('md');
    ariaLabel = input('Loading Content');
    circleSizes = {
        sm: {
            cx: 8,
            cy: 8,
            r: 7
        },
        md: {
            cx: 10,
            cy: 10,
            r: 9
        },
        lg: {
            cx: 20,
            cy: 20,
            r: 18
        }
    };
    get classes() {
        return `sag-ui-spinner-${this.size()}`;
    }
    get circleClasses() {
        const spinnerStyles = componentStylesByTheme(this.theme, spinnerStylesSAG, spinnerStylesSHS);
        return spinnerStyles();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: SpinnerComponent, isStandalone: true, selector: "sag-ui-spinner", inputs: { size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<svg\n  [class]=\"classes\"\n  tabindex=\"0\"\n  [attr.aria-label]=\"ariaLabel()\"\n  >\n  <circle\n    [attr.cx]=\"circleSizes[size()].cx\"\n    [attr.cy]=\"circleSizes[size()].cy\"\n    [attr.r]=\"circleSizes[size()].r\"\n    [class]=\"circleClasses\"\n  ></circle>\n</svg>\n", styles: [".sag-ui-spinner-sm{width:16px;height:16px;x:0px;y:0px;viewbox:0 0 16px 16px}.sag-ui-spinner-sm circle{fill:transparent;stroke-width:1.6px;stroke-linecap:round;stroke-dasharray:50.24px;transform-origin:50% 50% 0;animation:spinner_16px 2s linear infinite}.sag-ui-spinner-md{width:20px;height:20px;x:0px;y:0px;viewbox:0 0 20px 20px}.sag-ui-spinner-md circle{fill:transparent;stroke-width:2px;stroke-linecap:round;stroke-dasharray:62.8px;transform-origin:50% 50% 0;animation:spinner_20px 2s linear infinite}.sag-ui-spinner-lg{width:40px;height:40px;x:0px;y:0px;viewbox:0 0 40px 40px}.sag-ui-spinner-lg circle{fill:transparent;stroke-width:4px;stroke-linecap:round;stroke-dasharray:125.6px;transform-origin:50% 50% 0;animation:spinner_40px 2s linear infinite}@keyframes spinner_16px{0%{transform:rotate(0);stroke-dashoffset:10.56px}50%{transform:rotate(720deg);stroke-dashoffset:50.24px}to{transform:rotate(1080deg);stroke-dashoffset:10.56px}}@keyframes spinner_20px{0%{transform:rotate(0);stroke-dashoffset:13.2px}50%{transform:rotate(720deg);stroke-dashoffset:62.8px}to{transform:rotate(1080deg);stroke-dashoffset:13.2px}}@keyframes spinner_40px{0%{transform:rotate(0);stroke-dashoffset:26.4px}50%{transform:rotate(720deg);stroke-dashoffset:125.6px}to{transform:rotate(1080deg);stroke-dashoffset:26.4px}}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-spinner', standalone: true, template: "<svg\n  [class]=\"classes\"\n  tabindex=\"0\"\n  [attr.aria-label]=\"ariaLabel()\"\n  >\n  <circle\n    [attr.cx]=\"circleSizes[size()].cx\"\n    [attr.cy]=\"circleSizes[size()].cy\"\n    [attr.r]=\"circleSizes[size()].r\"\n    [class]=\"circleClasses\"\n  ></circle>\n</svg>\n", styles: [".sag-ui-spinner-sm{width:16px;height:16px;x:0px;y:0px;viewbox:0 0 16px 16px}.sag-ui-spinner-sm circle{fill:transparent;stroke-width:1.6px;stroke-linecap:round;stroke-dasharray:50.24px;transform-origin:50% 50% 0;animation:spinner_16px 2s linear infinite}.sag-ui-spinner-md{width:20px;height:20px;x:0px;y:0px;viewbox:0 0 20px 20px}.sag-ui-spinner-md circle{fill:transparent;stroke-width:2px;stroke-linecap:round;stroke-dasharray:62.8px;transform-origin:50% 50% 0;animation:spinner_20px 2s linear infinite}.sag-ui-spinner-lg{width:40px;height:40px;x:0px;y:0px;viewbox:0 0 40px 40px}.sag-ui-spinner-lg circle{fill:transparent;stroke-width:4px;stroke-linecap:round;stroke-dasharray:125.6px;transform-origin:50% 50% 0;animation:spinner_40px 2s linear infinite}@keyframes spinner_16px{0%{transform:rotate(0);stroke-dashoffset:10.56px}50%{transform:rotate(720deg);stroke-dashoffset:50.24px}to{transform:rotate(1080deg);stroke-dashoffset:10.56px}}@keyframes spinner_20px{0%{transform:rotate(0);stroke-dashoffset:13.2px}50%{transform:rotate(720deg);stroke-dashoffset:62.8px}to{transform:rotate(1080deg);stroke-dashoffset:13.2px}}@keyframes spinner_40px{0%{transform:rotate(0);stroke-dashoffset:26.4px}50%{transform:rotate(720deg);stroke-dashoffset:125.6px}to{transform:rotate(1080deg);stroke-dashoffset:26.4px}}\n"] }]
        }] });

class ButtonComponent {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    color = input('primary');
    size = input('md');
    iconPosition = input('start');
    disabled = input(false);
    loading = input(false);
    square = input(null);
    type = input('button');
    style = input('');
    class = input('');
    icon = input('');
    text = input(undefined);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // eslint-disable-next-line @angular-eslint/no-output-native
    blur = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    focus = output();
    handleBlur(event) {
        event.stopPropagation();
        this.blur.emit(event);
    }
    handleFocus(event) {
        event.stopPropagation();
        this.focus.emit(event);
    }
    get iconClass() {
        return `text-sm aspect-square icon-${this.icon()} ${this.loading() ? 'opacity-0' : ''}`;
    }
    get styles() {
        const buttonStyles = componentStylesByTheme(this.theme, buttonStylesSAG, buttonStylesSHS);
        return buttonStyles({ color: this.color() }) + (this.class() ? ` ${this.class()}` : '');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ButtonComponent, isStandalone: true, selector: "sag-ui-button", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, iconPosition: { classPropertyName: "iconPosition", publicName: "iconPosition", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, square: { classPropertyName: "square", publicName: "square", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, style: { classPropertyName: "style", publicName: "style", isSignal: true, isRequired: false, transformFunction: null }, class: { classPropertyName: "class", publicName: "class", isSignal: true, isRequired: false, transformFunction: null }, icon: { classPropertyName: "icon", publicName: "icon", isSignal: true, isRequired: false, transformFunction: null }, text: { classPropertyName: "text", publicName: "text", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { blur: "blur", focus: "focus" }, ngImport: i0, template: ` <button
        [disabled]="disabled() || loading()"
        [attr.data-square]="square()"
        [attr.data-size]="size()"
        [attr.aria-labelledby]="ariaLabelledby()"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-describedby]="ariaDescribedby()"
        [type]="type()"
        [style]="style()"
        [class]="styles"
        (blur)="handleBlur($event)"
        (focus)="handleFocus($event)"
    >
        @if (iconPosition() === 'start' && icon()) {
            <span [class]="iconClass"></span>
        }
        <span [class]="loading() ? 'opacity-0' : ''">
            @if (text()) {
                <span>{{ text() }}</span>
            } @else {
                <ng-content></ng-content>
            }
        </span>
        @if (iconPosition() === 'end' && icon()) {
            <span [class]="iconClass"></span>
        }
        @if (loading()) {
            <sag-ui-spinner
                size="sm"
                class="absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center"
            ></sag-ui-spinner>
        }
    </button>`, isInline: true, dependencies: [{ kind: "component", type: SpinnerComponent, selector: "sag-ui-spinner", inputs: ["size", "ariaLabel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-button',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    standalone: true,
                    template: ` <button
        [disabled]="disabled() || loading()"
        [attr.data-square]="square()"
        [attr.data-size]="size()"
        [attr.aria-labelledby]="ariaLabelledby()"
        [attr.aria-label]="ariaLabel()"
        [attr.aria-describedby]="ariaDescribedby()"
        [type]="type()"
        [style]="style()"
        [class]="styles"
        (blur)="handleBlur($event)"
        (focus)="handleFocus($event)"
    >
        @if (iconPosition() === 'start' && icon()) {
            <span [class]="iconClass"></span>
        }
        <span [class]="loading() ? 'opacity-0' : ''">
            @if (text()) {
                <span>{{ text() }}</span>
            } @else {
                <ng-content></ng-content>
            }
        </span>
        @if (iconPosition() === 'end' && icon()) {
            <span [class]="iconClass"></span>
        }
        @if (loading()) {
            <sag-ui-spinner
                size="sm"
                class="absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center"
            ></sag-ui-spinner>
        }
    </button>`,
                    imports: [SpinnerComponent]
                }]
        }] });

const inputBaseStyles$1 = [
    'peer',
    'relative',
    'w-full',
    'select-none',
    'focus:ring-0',
    'form-input',
    'rounded-sm',
    'py-2',
    'px-4',
    'text-base', // TODO: Check with Tom (18px in Figma seem to large)
    'data-[float-label=true]:pb-0',
    'data-[float-label=true]:pt-4',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const inputStylesSAG$1 = cva([
    ...inputBaseStyles$1,
    'text-[#000028]',
    'bg-[#EBF7F8]',
    'border-[#4C4C68]',
    'placeholder:text-[#4C4C68]',
    'hover:bg-[#D1FFF2]',
    'hover:border-[#005159]',
    'hover:placeholder:text-[#000028]',
    'focus:bg-[#D1FFF2]',
    'focus:border-[#4C4C68]',
    'focus:placeholder:text-[#4C4C68]',
    'active:bg-[#D1FFF2]',
    'active:border-[#005159]',
    'invalid:bg-[#FCCCD7]',
    'invalid:border-[#D72339]',
    'invalid:placeholder:text-[#000028]',
    'invalid:hover:bg-[#FCCCD7]',
    'invalid:hover:border-[#D72339]',
    'invalid:hover:placeholder:text-[#000028]',
    'invalid:active:bg-[#FCCCD7]',
    'invalid:active:border-[#D72339]',
    'invalid:active:placeholder:text-[#000028]',
    'invalid:focus:bg-[#FCCCD7]',
    'invalid:focus:border-[#D72339]',
    'invalid:focus:placeholder:text-[#000028]',
    'data-[invalid=true]:bg-[#FCCCD7]',
    'data-[invalid=true]:border-[#D72339]',
    'data-[invalid=true]:placeholder:text-[#000028]',
    'data-[invalid=true]:hover:bg-[#FCCCD7]',
    'data-[invalid=true]:hover:border-[#D72339]',
    'data-[invalid=true]:hover:placeholder:text-[#000028]',
    'data-[invalid=true]:active:bg-[#FCCCD7]',
    'data-[invalid=true]:active:border-[#D72339]',
    'data-[invalid=true]:active:placeholder:text-[#000028]',
    'data-[invalid=true]:focus:bg-[#FCCCD7]',
    'data-[invalid=true]:focus:border-[#D72339]',
    'data-[invalid=true]:focus:placeholder:text-[#000028]',
    'disabled:bg-[#EBF7F8]',
    'disabled:border-[#CCCCD4]',
    'disabled:placeholder:text-[#4C4C68]',
    'disabled:hover:bg-[#EBF7F8]',
    'disabled:hover:border-[#CCCCD4]',
    'disabled:hover:placeholder:text-[#4C4C68]',
    'disabled:focus:bg-[#EBF7F8]',
    'disabled:focus:border-[#CCCCD4]',
    'disabled:focus:placeholder:text-[#4C4C68]',
    'disabled:text-[#4C4C68]',
    'read-only:bg-white',
    'read-only:border-[#CCCCD4]',
    'read-only:placeholder:text-[#4C4C68]',
    'read-only:hover:bg-white',
    'read-only:hover:border-[#CCCCD4]',
    'read-only:hover:placeholder:text-[#4C4C68]',
    'read-only:focus:bg-white',
    'read-only:focus:border-[#CCCCD4]',
    'read-only:focus:placeholder:text-[#4C4C68]',
    'dark:text-white',
    'dark:bg-[#00183B]',
    'dark:border-[#B3B3BE]',
    'dark:placeholder:text-[#B3B3BE]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:placeholder:text-white',
    'dark:focus:bg-[#001F39]',
    'dark:focus:border-[#00FFB9]',
    'dark:focus:placeholder:text-[#B3B3BE]',
    'dark:active:bg-[#001F39]',
    'dark:active:border-[#00FFB9]',
    'dark:invalid:bg-[#331131]',
    'dark:invalid:border-[#FF2640]',
    'dark:invalid:placeholder:text-[#FFFFFF]',
    'dark:invalid:hover:bg-[#331131]',
    'dark:invalid:hover:border-[#FF2640]',
    'dark:invalid:hover:placeholder:text-[#FFFFFF]',
    'dark:invalid:active:bg-[#331131]',
    'dark:invalid:active:border-[#FF2640]',
    'dark:invalid:active:placeholder:text-[#FFFFFF]',
    'dark:invalid:focus:bg-[#331131]',
    'dark:invalid:focus:border-[#FF2640]',
    'dark:invalid:focus:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:bg-[#331131]',
    'dark:data-[invalid=true]:border-[#FF2640]',
    'dark:data-[invalid=true]:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:hover:bg-[#331131]',
    'dark:data-[invalid=true]:hover:border-[#FF2640]',
    'dark:data-[invalid=true]:hover:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:active:bg-[#331131]',
    'dark:data-[invalid=true]:active:border-[#FF2640]',
    'dark:data-[invalid=true]:active:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:focus:bg-[#331131]',
    'dark:data-[invalid=true]:focus:border-[#FF2640]',
    'dark:data-[invalid=true]:focus:placeholder:text-[#FFFFFF]',
    'dark:disabled:bg-[#262648]',
    'dark:disabled:border-[#4C4C68]',
    'dark:disabled:placeholder:text-[#737389]',
    'dark:disabled:hover:bg-[#262648]',
    'dark:disabled:hover:border-[#4C4C68]',
    'dark:disabled:hover:placeholder:text-[#737389]',
    'dark:disabled:focus:bg-[#262648]',
    'dark:disabled:focus:border-[#4C4C68]',
    'dark:disabled:focus:placeholder:text-[#737389]',
    'dark:disabled:text-[#737389]',
    'dark:read-only:bg-[#000028]',
    'dark:read-only:border-[#333353]',
    'dark:read-only:placeholder:text-[#B3B3BE]',
    'dark:read-only:hover:bg-[#000028]',
    'dark:read-only:hover:border-[#333353]',
    'dark:read-only:hover:placeholder:text-[#B3B3BE]',
    'dark:read-only:focus:bg-[#000028]',
    'dark:read-only:focus:border-[#333353]',
    'dark:read-only:focus:placeholder:text-[#B3B3BE]'
]);
const inputStylesSHS$1 = cva([
    ...inputBaseStyles$1,
    'text-[#1a1a1a]',
    'bg-[#ededed]',
    'border-[#666666]',
    'placeholder:text-[#666666]',
    'hover:bg-[#ededed]',
    'hover:border-[#666666]',
    'hover:placeholder:text-[#666666]',
    'focus:bg-[#ededed]',
    'focus:border-[#666666]',
    'focus:placeholder:text-[#666666]',
    'active:bg-[#ededed]',
    'active:border-[#005159]',
    'invalid:bg-[#ededed]',
    'invalid:border-[#e7001e]',
    'invalid:placeholder:text-[#666666]',
    'invalid:hover:bg-[#ededed]',
    'invalid:hover:border-[#e7001e]',
    'invalid:hover:placeholder:text-[#666666]',
    'invalid:active:bg-[#ededed]',
    'invalid:active:border-[#e7001e]',
    'invalid:active:placeholder:text-[#666666]',
    'invalid:focus:bg-[#ededed]',
    'invalid:focus:border-[#e7001e]',
    'invalid:focus:placeholder:text-[#666666]',
    'data-[invalid=true]:bg-[#ededed]',
    'data-[invalid=true]:border-[#e7001e]',
    'data-[invalid=true]:placeholder:text-[#666666]',
    'data-[invalid=true]:hover:bg-[#ededed]',
    'data-[invalid=true]:hover:border-[#e7001e]',
    'data-[invalid=true]:hover:placeholder:text-[#666666]',
    'data-[invalid=true]:active:bg-[#ededed]',
    'data-[invalid=true]:active:border-[#e7001e]',
    'data-[invalid=true]:active:placeholder:text-[#666666]',
    'data-[invalid=true]:focus:bg-[#ededed]',
    'data-[invalid=true]:focus:border-[#e7001e]',
    'data-[invalid=true]:focus:placeholder:text-[#666666]',
    'disabled:bg-[#f8f8f8]',
    'disabled:border-[#c2c2c2]',
    'disabled:placeholder:text-[#4C4C68]',
    'disabled:hover:bg-[#f8f8f8]',
    'disabled:hover:border-[#c2c2c2]',
    'disabled:hover:placeholder:text-[#4C4C68]',
    'disabled:focus:bg-[#f8f8f8]',
    'disabled:focus:border-[#c2c2c2]',
    'disabled:focus:placeholder:text-[#4C4C68]',
    'disabled:text-[#4C4C68]',
    'read-only:bg-[#ededed]',
    'read-only:border-[#666666]',
    'read-only:placeholder:text-[#666666]',
    'read-only:hover:bg-[#ededed]',
    'read-only:hover:border-[#666666]',
    'read-only:hover:placeholder:text-[#666666]',
    'read-only:focus:bg-[#ededed]',
    'read-only:focus:border-[#666666]',
    'read-only:focus:placeholder:text-[#666666]',
    'dark:text-[#e6e6e6]',
    'dark:bg-[#1a1a1a]',
    'dark:border-[#999999]',
    'dark:placeholder:text-[#999999]',
    'dark:hover:bg-[#1a1a1a]',
    'dark:hover:border-[#999999]',
    'dark:hover:placeholder:text-[#999999]',
    'dark:focus:bg-[#1a1a1a]',
    'dark:focus:border-[#999999]',
    'dark:focus:placeholder:text-[#999999]',
    'dark:active:bg-[#1a1a1a]',
    'dark:active:border-[#999999]',
    'dark:invalid:bg-[#1a1a1a]',
    'dark:invalid:border-[#e7001e]',
    'dark:invalid:placeholder:text-[#999999]',
    'dark:invalid:hover:bg-[#1a1a1a]',
    'dark:invalid:hover:border-[#e7001e]',
    'dark:invalid:hover:placeholder:text-[#999999]',
    'dark:invalid:active:bg-[#1a1a1a]',
    'dark:invalid:active:border-[#e7001e]',
    'dark:invalid:active:placeholder:text-[#999999]',
    'dark:invalid:focus:bg-[#1a1a1a]',
    'dark:invalid:focus:border-[#e7001e]',
    'dark:invalid:focus:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:border-[#e7001e]',
    'dark:data-[invalid=true]:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:hover:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:hover:border-[#e7001e]',
    'dark:data-[invalid=true]:hover:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:active:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:active:border-[#e7001e]',
    'dark:data-[invalid=true]:active:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:focus:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:focus:border-[#e7001e]',
    'dark:data-[invalid=true]:focus:placeholder:text-[#999999]',
    'dark:disabled:bg-[#0a0a0a]',
    'dark:disabled:border-[#3d3d3d]',
    'dark:disabled:placeholder:text-[#999999]',
    'dark:disabled:hover:bg-[#0a0a0a]',
    'dark:disabled:hover:border-[#3d3d3d]',
    'dark:disabled:hover:placeholder:text-[#999999]',
    'dark:disabled:focus:bg-[#0a0a0a]',
    'dark:disabled:focus:border-[#3d3d3d]',
    'dark:disabled:focus:placeholder:text-[#999999]',
    'dark:disabled:text-[#999999]',
    'dark:read-only:bg-[#1a1a1a]',
    'dark:read-only:border-[#999999]',
    'dark:read-only:placeholder:text-[#999999]',
    'dark:read-only:hover:bg-[#1a1a1a]',
    'dark:read-only:hover:border-[#999999]',
    'dark:read-only:hover:placeholder:text-[#999999]',
    'dark:read-only:focus:bg-[#1a1a1a]',
    'dark:read-only:focus:border-[#999999]',
    'dark:read-only:focus:placeholder:text-[#999999]'
]);

class InputDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, inputStylesSAG$1, inputStylesSHS$1);
        updateElementStyles(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: InputDirective, isStandalone: true, selector: "input[sag-ui-input],textarea[sag-ui-input]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-input],textarea[sag-ui-input]',
                    standalone: true
                }]
        }] });

const inputLabelBaseStyles = [
    'select-none',
    'text-sm',
    'mb-1',
    'flex',
    'transition-all',
    'data-[float=true]:absolute',
    'data-[float=true]:text-xs',
    'data-[float=true]:left-4',
    'data-[float=true]:top-1',
    'data-[float=false]:absolute',
    'data-[float=false]:text-base',
    'data-[float=false]:left-4',
    'data-[float=false]:top-[10px]'
];
const inputLabelStylesSAG = cva([
    ...inputLabelBaseStyles,
    'text-[#66667E]',
    'peer-hover:text-[#000028]',
    'peer-focus:text-[#005159]',
    'peer-active:text-[#005159]',
    'peer-invalid:text-[#B81E31]',
    'peer-invalid:peer-hover:text-[#B81E31]',
    'peer-invalid:peer-focus:text-[#B81E31]',
    'peer-data-[invalid=true]:text-[#B81E31]',
    'peer-data-[invalid=true]:peer-hover:text-[#B81E31]',
    'peer-data-[invalid=true]:peer-focus:text-[#B81E31]',
    'peer-disabled:text-[#9999A9]',
    'peer-disabled:peer-hover:text-[#9999A9]',
    'peer-disabled:peer-focus:text-[#9999A9]',
    'peer-read-only:text-[#66667E]',
    'peer-read-only:peer-hover:text-[#66667E]',
    'peer-read-only:peer-focus:text-[#66667E]',
    'dark:text-[#9999A9]',
    'dark:peer-hover:text-[#FFFFFF]',
    'dark:peer-focus:text-[#00FFB9]',
    'dark:peer-active:text-[#00FFB9]',
    'dark:peer-invalid:text-[#FF7687]',
    'dark:peer-invalid:peer-hover:text-[#FF7687]',
    'dark:peer-invalid:peer-focus:text-[#FF7687]',
    'dark:peer-data-[invalid=true]:text-[#FF7687]',
    'dark:peer-data-[invalid=true]:peer-hover:text-[#FF7687]',
    'dark:peer-data-[invalid=true]:peer-focus:text-[#FF7687]',
    'dark:peer-disabled:text-[#737389]',
    'dark:peer-disabled:peer-hover:text-[#737389]',
    'dark:peer-disabled:peer-focus:text-[#737389]',
    'dark:peer-read-only:text-[#9999A9]',
    'dark:peer-read-only:peer-hover:text-[#FFFFFF]',
    'dark:peer-read-only:peer-focus:text-[#FFFFFF]'
]);
const inputLabelStylesSHS = cva([
    ...inputLabelBaseStyles,
    'text-[#666666]',
    'peer-invalid:text-[#e7001e]',
    'peer-data-[invalid=true]:text-[#e7001e]',
    'peer-disabled:text-[#666666]',
    'dark:text-[#999999]',
    'dark:peer-invalid:text-[#e7001e]',
    'dark:peer-data-[invalid=true]:text-[#e7001e]',
    'dark:peer-disabled:text-[#999999]'
]);

class InputLabelDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputLabelStyles = componentStylesByTheme(this.theme, inputLabelStylesSAG, inputLabelStylesSHS);
        style(this.elementRef, inputLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: InputLabelDirective, isStandalone: true, selector: "label[sag-ui-input-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[sag-ui-input-label]',
                    standalone: true
                }]
        }] });

const inputErrorStylesSAG = cva(['dark:text-[#FF7687]', 'text-[#B81E31]', 'text-xs', 'select-none', 'mt-1']);
const inputErrorStylesSHS = cva(['dark:text-[#e7001e]', 'text-[#e7001e]', 'text-xs', 'select-none', 'mt-1']);

class InputErrorDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputErrorStyles = componentStylesByTheme(this.theme, inputErrorStylesSAG, inputErrorStylesSHS);
        style(this.elementRef, inputErrorStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputErrorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: InputErrorDirective, isStandalone: true, selector: "span[sag-ui-input-error]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputErrorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'span[sag-ui-input-error]',
                    standalone: true
                }]
        }] });

const tooltipBaseStyles = ['absolute', 'block', 'py-2', 'px-4', 'text-xs', 'z-50', 'box-border', 'font-bold'];
const tooltipStylesSAG = cva([
    ...tooltipBaseStyles,
    'rounded-md',
    'bg-[#000028]',
    'text-white',
    'dark:bg-[#262648]',
    'dark:text-[#ffffff]'
]);
const tooltipStylesSHS = cva([
    ...tooltipBaseStyles,
    'shadow-md',
    'bg-white',
    'text-black',
    'dark:bg-[#1a1a1a]',
    'dark:text-white'
]);

class TooltipDirective {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    renderer = inject(Renderer2);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    tooltip = '';
    tooltipPosition = input('top');
    showDelay = input(300);
    hideDelay = input(100);
    tooltipElement = null;
    isVisible = false;
    tooltipId = `tooltip-${Math.random().toString(36).substring(2, 5)}`;
    showTimer = null;
    hideTimer = null;
    constructor() {
        this.makeHostFocusable();
    }
    onFocus() {
        this.scheduleTooltipShow();
    }
    onFocusLeave() {
        this.scheduleTooltipHide();
    }
    onMouseEnter() {
        this.scheduleTooltipShow();
    }
    onMouseMove() {
        if (this.isVisible) {
            this.positionTooltip();
        }
    }
    onMouseLeave() {
        this.scheduleTooltipHide();
    }
    ngOnDestroy() {
        this.clearTimers();
        this.removeTooltip();
    }
    scheduleTooltipShow() {
        // Clear any existing timers to prevent conflicts
        this.clearTimers();
        // Only schedule if there's content and tooltip isn't already visible
        if (this.tooltip && !this.isVisible) {
            this.showTimer = setTimeout(() => {
                this.createTooltip();
            }, this.showDelay());
        }
    }
    scheduleTooltipHide() {
        // Clear any existing timers to prevent conflicts
        this.clearTimers();
        // Schedule hiding if tooltip is visible
        if (this.isVisible) {
            this.hideTimer = setTimeout(() => {
                this.removeTooltip();
            }, this.hideDelay());
        }
    }
    clearTimers() {
        // Clear both timers to prevent any pending actions
        if (this.showTimer) {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }
        if (this.hideTimer) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }
    createTooltip() {
        if (!this.isVisible && this.tooltip) {
            this.tooltipElement = this.renderer.createElement('div');
            const tooltipStyles = componentStylesByTheme(this.theme, tooltipStylesSAG, tooltipStylesSHS);
            if (!this.tooltipElement)
                return;
            this.tooltipElement.setAttribute('class', tooltipStyles());
            this.tooltipElement.setAttribute('id', this.tooltipId);
            this.tooltipElement.setAttribute('role', 'tooltip');
            this.tooltipElement.setAttribute('aria-live', 'polite');
            this.renderer.addClass(this.tooltipElement, 'tooltip');
            this.renderer.setAttribute(this.el.nativeElement, 'aria-describedby', this.tooltipId);
            this.renderer.appendChild(this.tooltipElement, this.renderer.createText(this.tooltip));
            this.renderer.appendChild(document.body, this.tooltipElement);
            this.positionTooltip();
            // Avoid multiple announcements if style changes due to positionTooltip()
            setTimeout(() => {
                if (this.tooltipElement) {
                    this.tooltipElement.setAttribute('aria-live', 'off');
                }
            }, 300);
            this.isVisible = true;
        }
    }
    removeTooltip() {
        if (this.isVisible && this.tooltipElement) {
            this.renderer.removeChild(document.body, this.tooltipElement);
            this.tooltipElement = null;
            this.isVisible = false;
        }
    }
    positionTooltip() {
        if (!this.tooltipElement)
            return;
        this.tooltipElement.style.position = 'absolute';
        this.tooltipElement.innerText = this.tooltip;
        const hostRect = this.el.nativeElement.getBoundingClientRect();
        const tooltipRect = this.tooltipElement.getBoundingClientRect();
        let top, left;
        const offset = 10;
        switch (this.tooltipPosition()) {
            case 'bottom':
                top = hostRect.bottom + window.scrollY + offset;
                left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2 + window.scrollX;
                break;
            case 'top':
                top = hostRect.top - tooltipRect.height + window.scrollY - offset;
                left = hostRect.left + hostRect.width / 2 - tooltipRect.width / 2 + window.scrollX;
                break;
            case 'left':
                top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2 + window.scrollY;
                left = hostRect.left - tooltipRect.width + window.scrollX - offset;
                break;
            case 'right':
                top = hostRect.top + hostRect.height / 2 - tooltipRect.height / 2 + window.scrollY;
                left = hostRect.right + window.scrollX + offset;
                break;
        }
        this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    }
    makeHostFocusable() {
        if (!this.el.nativeElement.hasAttribute('tabIndex')) {
            this.renderer.setAttribute(this.el.nativeElement, 'tabIndex', '0');
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TooltipDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: TooltipDirective, isStandalone: true, selector: "[sag-ui-tooltip]", inputs: { tooltip: { classPropertyName: "tooltip", publicName: "tooltip", isSignal: false, isRequired: false, transformFunction: null }, tooltipPosition: { classPropertyName: "tooltipPosition", publicName: "tooltipPosition", isSignal: true, isRequired: false, transformFunction: null }, showDelay: { classPropertyName: "showDelay", publicName: "showDelay", isSignal: true, isRequired: false, transformFunction: null }, hideDelay: { classPropertyName: "hideDelay", publicName: "hideDelay", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "focus": "onFocus()", "blur": "onFocusLeave()", "mouseenter": "onMouseEnter()", "mousemove": "onMouseMove()", "mouseleave": "onMouseLeave()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-tooltip]',
                    standalone: true
                }]
        }], ctorParameters: () => [], propDecorators: { tooltip: [{
                type: Input
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onFocusLeave: [{
                type: HostListener,
                args: ['blur']
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseMove: [{
                type: HostListener,
                args: ['mousemove']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

const datePickerContainerBaseStyles = ['max-w-[400px]', 'shadow-md'];
const datePickerContainerSAG = cva([...datePickerContainerBaseStyles, 'bg-[#F3F3F0]', 'dark:bg-[#23233C]']);
const datePickerContainerSHS = cva([...datePickerContainerBaseStyles, 'bg-white', 'dark:bg-[#1a1a1a]']);
const datePickerButtonSAG = cva([
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerButtonSHS = cva([
    'text-[#666666]',
    'hover:text-[#1a1a1a]',
    'dark:text-[#999999]',
    'dark:hover:text-white',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerTopTextBaseStyles = ['text-md', 'font-medium'];
const datePickerTopTextSAG = cva([
    ...datePickerTopTextBaseStyles,
    'text-[#007993]',
    'dark:text-[#00CCCC]',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerTopTextSHS = cva([
    ...datePickerTopTextBaseStyles,
    'text-[#666666]',
    'dark:text-[#999999]',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerDayTextBaseStyles = [
    'text-sm',
    'font-semibold',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'aspect-square',
    'p-2'
];
const datePickerDayTextSAG = cva([...datePickerDayTextBaseStyles, 'text-[#000028]', 'dark:text-white']);
const datePickerDayTextSHS = cva([...datePickerDayTextBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);
const datePickerDayBoxBaseStyles = [
    'text-sm',
    'cursor-pointer',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'aspect-square',
    'p-2'
];
const datePickerDisabledDayBoxSAG = cva([...datePickerDayBoxBaseStyles, 'text-[#66667E]', 'dark:text-[#9999A9]']);
const datePickerDisabledDayBoxSHS = cva([...datePickerDayBoxBaseStyles, 'text-[#999999]', 'dark:text-[#666666]']);
const datePickerDayBoxSAG = cva([
    ...datePickerDayBoxBaseStyles,
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'dark:data-[selected=true]:bg-[#00FFB9]',
    'dark:data-[selected=true]:text-[#000028]',
    'dark:data-[selected=true]:font-semibold',
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#196269]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerDayBoxSHS = cva([
    ...datePickerDayBoxBaseStyles,
    'dark:text-white',
    'dark:hover:bg-[#313131]',
    'dark:data-[selected=true]:bg-[#cf4b00]',
    'dark:data-[selected=true]:text-white',
    'dark:data-[selected=true]:font-semibold',
    'text-[#666666]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#cf4b00]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerMonthBoxBaseStyles = [
    'text-sm',
    'cursor-pointer',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'p-4'
];
const datePickerMonthBoxSAG = cva([
    ...datePickerMonthBoxBaseStyles,
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'dark:data-[selected=true]:bg-[#00FFB9]',
    'dark:data-[selected=true]:text-[#000028]',
    'dark:data-[selected=true]:font-semibold',
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#196269]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerMonthBoxSHS = cva([
    ...datePickerMonthBoxBaseStyles,
    'dark:text-white',
    'dark:hover:bg-[#313131]',
    'dark:data-[selected=true]:bg-[#cf4b00]',
    'dark:data-[selected=true]:text-white',
    'dark:data-[selected=true]:font-semibold',
    'text-[#666666]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#cf4b00]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerTimeInputBaseStyles = [
    'border-none',
    'text-center',
    'text-md',
    'font-semibold',
    'p-2',
    'rounded-md',
    'w-[50px]',
    '[appearance:textfield]',
    '[&::-webkit-outer-spin-button]:appearance-none',
    '[&::-webkit-inner-spin-button]:appearance-none'
];
const datePickerTimeInputSAG = cva([
    ...datePickerTimeInputBaseStyles,
    'bg-[#E8E8E3]',
    'text-[#000028]',
    'dark:bg-[#37374D]',
    'dark:text-white',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerTimeInputSHS = cva([
    ...datePickerTimeInputBaseStyles,
    'bg-white',
    'text-[#666666]',
    'dark:bg-[#1a1a1a]',
    'dark:text-[#999999]',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const datePickerTimeDividerBaseStyles = ['text-lg', 'font-bold'];
const datePickerTimeDividerSAG = cva([...datePickerTimeDividerBaseStyles, 'text-[#000028]', 'dark:text-white']);
const datePickerTimeDividerSHS = cva([...datePickerTimeDividerBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);
const datePickerDividerSAG = cva(['border-[#E8E8E3]', 'dark:border-[#373750]']);
const datePickerDividerSHS = cva(['border-[#666666]', 'dark:border-[#999999]']);

class DatePickerComponent {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    time = input(null);
    lang = input();
    date = input(new Date());
    dateChanged = output();
    selectedMonth = this.date().getMonth();
    selectedYear = this.date().getFullYear();
    selectedDay = this.date().getDate();
    selectedHours = this.date().getHours();
    selectedMinutes = this.date().getMinutes();
    selectedSeconds = this.date().getSeconds();
    previousMonthsDates = [];
    dates = [];
    nextMonthsDates = [];
    view = 'days';
    displayedYears = Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + i);
    Math = Math;
    ngOnInit() {
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
    }
    ngOnChanges(changes) {
        const date = this.date();
        if (changes['date'] && date) {
            this.selectedMonth = date.getMonth();
            this.selectedYear = date.getFullYear();
            this.selectedDay = date.getDate();
            this.selectedHours = date.getHours();
            this.selectedMinutes = date.getMinutes();
            this.selectedSeconds = date.getSeconds();
            this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        }
    }
    handleDayKeyDown(event, day) {
        const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToDate(this.calculateNextDay(currentDate));
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToDate(this.calculatePreviousDay(currentDate));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToDate(this.calculatePreviousWeekDate(currentDate));
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToDate(this.calculateNextWeekDate(currentDate));
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.dateSelected(day);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    handleMonthKeyDown(event, monthIndex) {
        const monthsPerRow = 4;
        const totalMonths = 12;
        const currentCol = monthIndex % monthsPerRow;
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToMonth(this.calculateNextMonth(monthIndex, totalMonths));
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToMonth(this.calculatePreviousMonth(monthIndex, totalMonths));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToMonth(this.calculatePreviousRowMonth(monthIndex, monthsPerRow, currentCol));
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToMonth(this.calculateNextRowMonth(monthIndex, monthsPerRow, totalMonths, currentCol));
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.monthSelected(monthIndex);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    handleYearKeyDown(event, year) {
        const yearIndex = this.displayedYears.indexOf(year);
        const yearsPerRow = 4;
        const totalYears = this.displayedYears.length;
        const currentCol = yearIndex % yearsPerRow;
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToNextYear(yearIndex, totalYears);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToPreviousYear(yearIndex);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToYearAbove(yearIndex, yearsPerRow, currentCol);
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToYearBelow(yearIndex, yearsPerRow, totalYears, currentCol);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.yearSelected(year);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    // Close calendar method
    closeCalendar() {
        this.dateChanged.emit({
            value: this.date(),
            emit: true
        });
    }
    // Time controls methods
    increaseHours() {
        this.selectedHours = Math.min(this.selectedHours + 1, 23);
        this.updateTime();
    }
    decreaseHours() {
        this.selectedHours = Math.max(this.selectedHours - 1, 0);
        this.updateTime();
    }
    increaseMinutes() {
        this.selectedMinutes = Math.min(this.selectedMinutes + 1, 59);
        this.updateTime();
    }
    decreaseMinutes() {
        this.selectedMinutes = Math.max(this.selectedMinutes - 1, 0);
        this.updateTime();
    }
    increaseSeconds() {
        this.selectedSeconds = Math.min(this.selectedSeconds + 1, 59);
        this.updateTime();
    }
    decreaseSeconds() {
        this.selectedSeconds = Math.max(this.selectedSeconds - 1, 0);
        this.updateTime();
    }
    // Selection methods with improved accessibility
    monthSelected(monthIndex) {
        this.selectedMonth = monthIndex;
        this.view = 'days';
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        // Set focus on first day when returning to day view
        setTimeout(() => {
            if (this.dates.length > 0) {
                this.focusElement(`day-${this.dates[0]}`);
            }
        });
    }
    yearSelected(year) {
        this.selectedYear = year;
        this.view = 'months';
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceYearSelection(year);
        // Set focus on current month when returning to month view
        setTimeout(() => {
            this.focusElement(`month-${this.selectedMonth}`);
        });
    }
    isDaySelected(day) {
        const date = this.date();
        if (!date)
            return false;
        return (day === date.getDate() && this.selectedMonth === date.getMonth() && this.selectedYear === date.getFullYear());
    }
    get userLanguage() {
        return this.lang() || navigator.language;
    }
    get localizedWeekdays() {
        const isGerman = this.userLanguage.startsWith('de');
        return isGerman
            ? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }
    get localizedMonths() {
        const isGerman = this.userLanguage.startsWith('de');
        return isGerman
            ? [
                'Januar',
                'Februar',
                'März',
                'April',
                'Mai',
                'Juni',
                'Juli',
                'August',
                'September',
                'Oktober',
                'November',
                'Dezember'
            ]
            : [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December'
            ];
    }
    parseNumber(value) {
        return typeof value === 'string' ? parseInt(value, 10) : value;
    }
    updateTimeInput(event, type) {
        event.preventDefault();
        event.stopPropagation();
        const value = this.parseNumber(event.target.value);
        switch (type) {
            case 'hours':
                this.selectedHours = Math.min(Math.max(value, 0), 23);
                break;
            case 'minutes':
                this.selectedMinutes = Math.min(Math.max(value, 0), 59);
                break;
            case 'seconds':
                this.selectedSeconds = Math.min(Math.max(value, 0), 59);
                break;
        }
        this.updateTime();
    }
    updateTime() {
        this.dateChanged.emit({
            value: new Date(this.selectedYear, this.selectedMonth, this.selectedDay, this.selectedHours, this.selectedMinutes, this.selectedSeconds),
            emit: false
        });
    }
    dateSelected(day) {
        this.selectedDay = day;
        // Create the date object
        const selectedDate = new Date(this.selectedYear, this.selectedMonth, day, this.selectedHours, this.selectedMinutes, this.selectedSeconds);
        this.dateChanged.emit({
            value: selectedDate,
            emit: !this.time()
        });
        // Announce the selected date to screen readers
        this.announceDateSelection(selectedDate);
    }
    changeView(view) {
        this.view = view;
        // Set initial focus when changing views
        setTimeout(() => {
            if (view === 'months') {
                this.focusElement(`month-${this.selectedMonth}`);
            }
            else if (view === 'years') {
                const yearIndex = this.displayedYears.indexOf(this.selectedYear);
                if (yearIndex >= 0) {
                    this.focusElement(`year-${this.selectedYear}`);
                }
                else {
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            }
        });
    }
    previousYears() {
        this.displayedYears = this.displayedYears.map(year => year - 12);
        this.announceYearRange();
    }
    nextYears() {
        this.displayedYears = this.displayedYears.map(year => year + 12);
        this.announceYearRange();
    }
    previousMonth() {
        this.selectedMonth--;
        if (this.selectedMonth < 0) {
            this.selectedMonth = 11;
            this.selectedYear--;
        }
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceMonthChange();
    }
    nextMonth() {
        this.selectedMonth++;
        if (this.selectedMonth > 11) {
            this.selectedMonth = 0;
            this.selectedYear++;
        }
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceMonthChange();
    }
    updateDisplayedDates(year, month) {
        const daysInWeek = 7;
        const weeksInGrid = 5;
        const totalDays = daysInWeek * weeksInGrid;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const firstDayOfWeek = firstDayOfMonth.getDay() || 7; // Adjusting for Monday as first day
        const daysInMonth = lastDayOfMonth.getDate();
        this.previousMonthsDates = [];
        this.dates = [];
        this.nextMonthsDates = [];
        // Fill previous month days
        for (let i = firstDayOfWeek - 1; i > 0; i--) {
            const day = new Date(year, month, 1 - i).getDate();
            this.previousMonthsDates.push(day);
        }
        // Fill current month days
        for (let i = 1; i <= daysInMonth; i++) {
            this.dates.push(i);
        }
        // Fill next month days
        let nextMonthDay = 1;
        while (this.dates.length + this.previousMonthsDates.length + this.nextMonthsDates.length < totalDays) {
            this.nextMonthsDates.push(nextMonthDay++);
        }
    }
    announceMonthChange() {
        // Update the live region content to trigger an announcement
        const announcement = `${this.localizedMonths[this.selectedMonth]} ${this.selectedYear}`;
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#month-announcement');
            if (liveRegion) {
                // Force a DOM change to trigger screen reader announcement
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    announceDateSelection(date) {
        // Format the date in a screen reader friendly way
        const formattedDate = this.formatDateForScreenReader(date);
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#month-announcement');
            if (liveRegion) {
                // Force a DOM change to trigger screen reader announcement
                liveRegion.textContent = '';
                liveRegion.textContent = `Selected date: ${formattedDate}`;
            }
        });
    }
    formatDateForScreenReader(date) {
        const day = date.getDate();
        const month = this.localizedMonths[date.getMonth()];
        const year = date.getFullYear();
        // Format time if needed
        let timeString = '';
        if (this.time()) {
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            if (this.time() === 'hh:mm') {
                timeString = `, ${hours}:${minutes}`;
            }
            else if (this.time() === 'hh:mm:ss') {
                const seconds = date.getSeconds().toString().padStart(2, '0');
                timeString = `, ${hours}:${minutes}:${seconds}`;
            }
        }
        // Return formatted date string
        return `${day} ${month} ${year}${timeString}`;
    }
    announceYearRange() {
        if (this.displayedYears.length > 0) {
            const firstYear = this.displayedYears[0];
            const lastYear = this.displayedYears[this.displayedYears.length - 1];
            const announcement = `Years ${firstYear} to ${lastYear}`;
            setTimeout(() => {
                const liveRegion = this.elementRef.nativeElement.querySelector('#month-announcement');
                if (liveRegion) {
                    liveRegion.textContent = '';
                    liveRegion.textContent = announcement;
                }
            });
        }
    }
    announceYearSelection(year) {
        const announcement = `Year ${year} selected. Now select a month.`;
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#month-announcement');
            if (liveRegion) {
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    // Add this helper method to navigate to a specific date
    navigateToDate(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        // Check if we need to change the month/year
        if (month !== this.selectedMonth || year !== this.selectedYear) {
            this.selectedMonth = month;
            this.selectedYear = year;
            this.updateDisplayedDates(year, month);
            // Wait for the view to update
            setTimeout(() => {
                // Find and focus the target day
                const targetElement = this.elementRef.nativeElement.querySelector(`#day-${day}`);
                if (targetElement) {
                    targetElement.focus();
                }
            });
        }
        else {
            // Same month, just focus the day
            this.focusElement(`day-${day}`);
        }
    }
    // Helper methods for date calculations
    calculateNextDay(date) {
        const nextDate = new Date(date);
        nextDate.setDate(date.getDate() + 1);
        return nextDate;
    }
    calculatePreviousDay(date) {
        const prevDate = new Date(date);
        prevDate.setDate(date.getDate() - 1);
        return prevDate;
    }
    calculateNextWeekDate(date) {
        const nextWeekDate = new Date(date);
        nextWeekDate.setDate(date.getDate() + 7);
        return nextWeekDate;
    }
    calculatePreviousWeekDate(date) {
        const prevWeekDate = new Date(date);
        prevWeekDate.setDate(date.getDate() - 7);
        return prevWeekDate;
    }
    // Helper methods for month calculations
    calculateNextMonth(monthIndex, totalMonths) {
        return monthIndex < totalMonths - 1 ? monthIndex + 1 : 0;
    }
    calculatePreviousMonth(monthIndex, totalMonths) {
        return monthIndex > 0 ? monthIndex - 1 : totalMonths - 1;
    }
    calculatePreviousRowMonth(monthIndex, monthsPerRow, currentCol) {
        if (monthIndex - monthsPerRow >= 0) {
            return monthIndex - monthsPerRow;
        }
        else {
            // Wrap from top row to bottom row, same column
            return 2 * monthsPerRow + currentCol; // Last row, same column
        }
    }
    calculateNextRowMonth(monthIndex, monthsPerRow, totalMonths, currentCol) {
        if (monthIndex + monthsPerRow < totalMonths) {
            return monthIndex + monthsPerRow;
        }
        else {
            // Wrap from bottom row to top row, same column
            return currentCol; // First row, same column
        }
    }
    navigateToMonth(monthIndex) {
        this.focusElement(`month-${monthIndex}`);
    }
    // Helper methods for year calculations
    navigateToNextYear(yearIndex, totalYears) {
        if (yearIndex < totalYears - 1) {
            // Move to next year in current view
            this.focusElement(`year-${this.displayedYears[yearIndex + 1]}`);
        }
        else {
            // Move to next set of years
            this.nextYears();
            setTimeout(() => {
                // Focus first year in new view
                if (this.displayedYears.length > 0) {
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            });
        }
    }
    navigateToPreviousYear(yearIndex) {
        if (yearIndex > 0) {
            // Move to previous year in current view
            this.focusElement(`year-${this.displayedYears[yearIndex - 1]}`);
        }
        else {
            // Move to previous set of years
            this.previousYears();
            setTimeout(() => {
                // Focus last year in new view
                if (this.displayedYears.length > 0) {
                    this.focusElement(`year-${this.displayedYears[this.displayedYears.length - 1]}`);
                }
            });
        }
    }
    navigateToYearAbove(yearIndex, yearsPerRow, currentCol) {
        if (yearIndex - yearsPerRow >= 0) {
            // Move up within current view
            this.focusElement(`year-${this.displayedYears[yearIndex - yearsPerRow]}`);
        }
        else {
            // Move to previous set of years
            this.previousYears();
            setTimeout(() => {
                // Try to focus same position in previous view
                const targetIndex = this.displayedYears.length - yearsPerRow + currentCol;
                if (targetIndex >= 0 && targetIndex < this.displayedYears.length) {
                    this.focusElement(`year-${this.displayedYears[targetIndex]}`);
                }
                else {
                    // Fallback to last year in view
                    this.focusElement(`year-${this.displayedYears[this.displayedYears.length - 1]}`);
                }
            });
        }
    }
    navigateToYearBelow(yearIndex, yearsPerRow, totalYears, currentCol) {
        if (yearIndex + yearsPerRow < totalYears) {
            // Move down within current view
            this.focusElement(`year-${this.displayedYears[yearIndex + yearsPerRow]}`);
        }
        else {
            // Move to next set of years
            this.nextYears();
            setTimeout(() => {
                // Try to focus same position in next view
                const targetIndex = currentCol;
                if (targetIndex < this.displayedYears.length) {
                    this.focusElement(`year-${this.displayedYears[targetIndex]}`);
                }
                else {
                    // Fallback to first year in view
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            });
        }
    }
    // Helper method to focus elements
    focusElement(id) {
        setTimeout(() => {
            const element = this.elementRef.nativeElement.querySelector(`#${id}`);
            if (element) {
                element.focus();
            }
        });
    }
    get containerStyles() {
        return componentStylesByTheme(this.theme, datePickerContainerSAG, datePickerContainerSHS)();
    }
    get pickerButtonStyles() {
        return componentStylesByTheme(this.theme, datePickerButtonSAG, datePickerButtonSHS)();
    }
    get topTextStyles() {
        return componentStylesByTheme(this.theme, datePickerTopTextSAG, datePickerTopTextSHS)();
    }
    get dayTextStyles() {
        return componentStylesByTheme(this.theme, datePickerDayTextSAG, datePickerDayTextSHS)();
    }
    get dayBoxDisabledStyles() {
        return componentStylesByTheme(this.theme, datePickerDisabledDayBoxSAG, datePickerDisabledDayBoxSHS)();
    }
    get dayBoxStyles() {
        return componentStylesByTheme(this.theme, datePickerDayBoxSAG, datePickerDayBoxSHS)();
    }
    get monthBoxStyles() {
        return componentStylesByTheme(this.theme, datePickerMonthBoxSAG, datePickerMonthBoxSHS)();
    }
    get timeInputStyles() {
        return componentStylesByTheme(this.theme, datePickerTimeInputSAG, datePickerTimeInputSHS)();
    }
    get timeDividerStyles() {
        return componentStylesByTheme(this.theme, datePickerTimeDividerSAG, datePickerTimeDividerSHS)();
    }
    get dividerStyles() {
        return componentStylesByTheme(this.theme, datePickerDividerSAG, datePickerDividerSHS)();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DatePickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: DatePickerComponent, isStandalone: true, selector: "sag-ui-date-picker", inputs: { time: { classPropertyName: "time", publicName: "time", isSignal: true, isRequired: false, transformFunction: null }, lang: { classPropertyName: "lang", publicName: "lang", isSignal: true, isRequired: false, transformFunction: null }, date: { classPropertyName: "date", publicName: "date", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { dateChanged: "dateChanged" }, usesOnChanges: true, ngImport: i0, template: `
        <div [class]="containerStyles" role="dialog" aria-label="Date picker" (keydown.escape)="closeCalendar()">
            <div class="flex items-center justify-between p-6">
                <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="month-announcement">
                    {{ localizedMonths[selectedMonth] }} {{ selectedYear }}
                </div>
                <span
                    [class]="pickerButtonStyles + ' icon-chevron-left rounded-full cursor-pointer p-2'"
                    (click)="previousMonth()"
                    tabindex="0"
                    role="button"
                    aria-label="Previous month"
                    (keydown.enter)="previousMonth()"
                    (keydown.space)="previousMonth(); $event.preventDefault()"
                ></span>
                <span [class]="topTextStyles">
                    <span
                        (click)="changeView('months')"
                        class="mr-1 cursor-pointer"
                        tabindex="0"
                        role="button"
                        aria-label="Select month"
                        (keydown.enter)="changeView('months')"
                        (keydown.space)="changeView('months'); $event.preventDefault()"
                        >{{ localizedMonths[selectedMonth] }}</span
                    >
                    <span
                        (click)="changeView('years')"
                        class="cursor-pointer"
                        tabindex="0"
                        role="button"
                        aria-label="Select year"
                        (keydown.enter)="changeView('years')"
                        (keydown.space)="changeView('years'); $event.preventDefault()"
                        >{{ selectedYear }}</span
                    ></span
                >
                <span
                    [class]="pickerButtonStyles + ' icon-chevron-right rounded-full cursor-pointer p-2'"
                    (click)="nextMonth()"
                    tabindex="0"
                    role="button"
                    aria-label="Next month"
                    (keydown.enter)="nextMonth()"
                    (keydown.space)="nextMonth(); $event.preventDefault()"
                ></span>
            </div>
            <hr [class]="dividerStyles" />
            @switch (view) {
                @case ('days') {
                    <div
                        class="grid grid-cols-7 grid-rows-6 items-center justify-center gap-2 p-6"
                        role="grid"
                        aria-rowcount="6"
                        aria-colcount="7"
                        aria-label="Calendar days"
                    >
                        @for (day of localizedWeekdays; track day) {
                            <div [class]="dayTextStyles" role="columnheader">
                                {{ day }}
                            </div>
                        }
                        @for (day of previousMonthsDates; track day) {
                            <div [class]="dayBoxDisabledStyles" aria-disabled="true">
                                {{ day }}
                            </div>
                        }
                        @for (day of dates; track day) {
                            <div
                                [class]="dayBoxStyles + (isDaySelected(day) ? ' selected-day' : '') + ' '"
                                [attr.data-selected]="isDaySelected(day)"
                                (click)="dateSelected(day)"
                                [id]="'day-' + day"
                                tabindex="0"
                                role="gridcell"
                                [attr.aria-selected]="isDaySelected(day)"
                                [attr.aria-label]="localizedMonths[selectedMonth] + ' ' + day + ', ' + selectedYear"
                                (keydown)="handleDayKeyDown($event, day)"
                            >
                                {{ day }}
                            </div>
                        }
                        @for (day of nextMonthsDates; track day) {
                            <div [class]="dayBoxDisabledStyles" aria-disabled="true">
                                {{ day }}
                            </div>
                        }
                    </div>
                }
                @case ('months') {
                    <div
                        class="grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 p-6"
                        role="region"
                        aria-label="Month selection table with 3 rows and 4 columns"
                    >
                        @for (month of localizedMonths; track month; let i = $index) {
                            <div
                                [class]="monthBoxStyles + (selectedMonth === i ? ' selected-month' : '') + ' '"
                                [attr.data-selected]="selectedMonth === i"
                                (click)="monthSelected(i)"
                                [id]="'month-' + i"
                                tabindex="0"
                                role="button"
                                [attr.aria-selected]="selectedMonth === i"
                                (keydown)="handleMonthKeyDown($event, i)"
                            >
                                {{ month }}
                            </div>
                        }
                    </div>
                }
                @case ('years') {
                    <div class="p-6 py-2">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' icon-chevron-up rounded-md flex items-center justify-center w-full cursor-pointer p-2'
                            "
                            (click)="previousYears()"
                            tabindex="0"
                            role="button"
                            [attr.aria-label]="
                                'Show earlier years: ' + (displayedYears[0] - 12) + ' to ' + (displayedYears[0] - 1)
                            "
                            (keydown.enter)="previousYears()"
                            (keydown.space)="previousYears(); $event.preventDefault()"
                        ></span>
                        <div
                            class="grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 py-2"
                            role="region"
                            aria-label="Year selection table with 3 rows and 4 columns"
                        >
                            @for (year of displayedYears; track year) {
                                <div
                                    [class]="monthBoxStyles + (selectedYear === year ? ' selected-year' : '') + ' '"
                                    [attr.data-selected]="selectedYear === year"
                                    (click)="yearSelected(year)"
                                    [id]="'year-' + year"
                                    tabindex="0"
                                    role="button"
                                    [attr.aria-selected]="selectedYear === year"
                                    (keydown)="handleYearKeyDown($event, year)"
                                >
                                    {{ year }}
                                </div>
                            }
                        </div>
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' icon-chevron-down rounded-md flex items-center justify-center w-full cursor-pointer p-2'
                            "
                            (click)="nextYears()"
                            tabindex="0"
                            role="button"
                            [attr.aria-label]="
                                'Show later years: ' +
                                (displayedYears[displayedYears.length - 1] + 1) +
                                ' to ' +
                                (displayedYears[displayedYears.length - 1] + 12)
                            "
                            (keydown.enter)="nextYears()"
                            (keydown.space)="nextYears(); $event.preventDefault()"
                        ></span>
                    </div>
                }
            }
            @if (time()) {
                <hr [class]="dividerStyles" />
                <div class="flex items-center justify-center gap-2 p-4" role="group" aria-label="Time selection">
                    <div class="flex flex-col">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="increaseHours()"
                            tabindex="0"
                            role="button"
                            aria-label="Increase hours"
                            (keydown.enter)="increaseHours()"
                            (keydown.space)="increaseHours(); $event.preventDefault()"
                        ></span>
                        <input
                            [class]="timeInputStyles + ' '"
                            [(ngModel)]="selectedHours"
                            (change)="updateTimeInput($event, 'hours')"
                            type="number"
                            min="0"
                            max="23"
                            aria-label="Hours"
                            role="spinbutton"
                            aria-valuemin="0"
                            aria-valuemax="23"
                            [attr.aria-valuenow]="selectedHours"
                        />
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="decreaseHours()"
                            tabindex="0"
                            role="button"
                            aria-label="Decrease hours"
                            (keydown.enter)="decreaseHours()"
                            (keydown.space)="decreaseHours(); $event.preventDefault()"
                        ></span>
                    </div>
                    <span [class]="timeDividerStyles" aria-hidden="true">:</span>
                    <div class="flex flex-col">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="increaseMinutes()"
                            tabindex="0"
                            role="button"
                            aria-label="Increase minutes"
                            (keydown.enter)="increaseMinutes()"
                            (keydown.space)="increaseMinutes(); $event.preventDefault()"
                        ></span>
                        <input
                            [class]="timeInputStyles + ' '"
                            [(ngModel)]="selectedMinutes"
                            (change)="updateTimeInput($event, 'minutes')"
                            type="number"
                            min="0"
                            max="59"
                            aria-label="Minutes"
                            role="spinbutton"
                            aria-valuemin="0"
                            aria-valuemax="59"
                            [attr.aria-valuenow]="selectedMinutes"
                        />
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="decreaseMinutes()"
                            tabindex="0"
                            role="button"
                            aria-label="Decrease minutes"
                            (keydown.enter)="decreaseMinutes()"
                            (keydown.space)="decreaseMinutes(); $event.preventDefault()"
                        ></span>
                    </div>
                    @if (time() === 'hh:mm:ss') {
                        <span [class]="timeDividerStyles">:</span>
                        <div class="flex flex-col">
                            <span
                                [class]="
                                    pickerButtonStyles +
                                    ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                                "
                                (click)="increaseSeconds()"
                                tabindex="0"
                                role="button"
                                aria-label="Increase seconds"
                                (keydown.enter)="increaseSeconds()"
                                (keydown.space)="increaseSeconds(); $event.preventDefault()"
                            ></span>
                            <input
                                [class]="timeInputStyles + ' '"
                                [(ngModel)]="selectedSeconds"
                                (change)="updateTimeInput($event, 'seconds')"
                                type="number"
                                min="0"
                                max="59"
                                aria-label="Seconds"
                                role="spinbutton"
                                aria-valuemin="0"
                                aria-valuemax="59"
                                [attr.aria-valuenow]="selectedSeconds"
                            />
                            <span
                                [class]="
                                    pickerButtonStyles +
                                    ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                                "
                                (click)="decreaseSeconds()"
                                tabindex="0"
                                role="button"
                                aria-label="Decrease seconds"
                                (keydown.enter)="decreaseSeconds()"
                                (keydown.space)="decreaseSeconds(); $event.preventDefault()"
                            ></span>
                        </div>
                    }
                </div>
            }
        </div>
    `, isInline: true, dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NumberValueAccessor, selector: "input[type=number][formControlName],input[type=number][formControl],input[type=number][ngModel]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.MinValidator, selector: "input[type=number][min][formControlName],input[type=number][min][formControl],input[type=number][min][ngModel]", inputs: ["min"] }, { kind: "directive", type: i1.MaxValidator, selector: "input[type=number][max][formControlName],input[type=number][max][formControl],input[type=number][max][ngModel]", inputs: ["max"] }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DatePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-date-picker',
                    standalone: true,
                    template: `
        <div [class]="containerStyles" role="dialog" aria-label="Date picker" (keydown.escape)="closeCalendar()">
            <div class="flex items-center justify-between p-6">
                <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="month-announcement">
                    {{ localizedMonths[selectedMonth] }} {{ selectedYear }}
                </div>
                <span
                    [class]="pickerButtonStyles + ' icon-chevron-left rounded-full cursor-pointer p-2'"
                    (click)="previousMonth()"
                    tabindex="0"
                    role="button"
                    aria-label="Previous month"
                    (keydown.enter)="previousMonth()"
                    (keydown.space)="previousMonth(); $event.preventDefault()"
                ></span>
                <span [class]="topTextStyles">
                    <span
                        (click)="changeView('months')"
                        class="mr-1 cursor-pointer"
                        tabindex="0"
                        role="button"
                        aria-label="Select month"
                        (keydown.enter)="changeView('months')"
                        (keydown.space)="changeView('months'); $event.preventDefault()"
                        >{{ localizedMonths[selectedMonth] }}</span
                    >
                    <span
                        (click)="changeView('years')"
                        class="cursor-pointer"
                        tabindex="0"
                        role="button"
                        aria-label="Select year"
                        (keydown.enter)="changeView('years')"
                        (keydown.space)="changeView('years'); $event.preventDefault()"
                        >{{ selectedYear }}</span
                    ></span
                >
                <span
                    [class]="pickerButtonStyles + ' icon-chevron-right rounded-full cursor-pointer p-2'"
                    (click)="nextMonth()"
                    tabindex="0"
                    role="button"
                    aria-label="Next month"
                    (keydown.enter)="nextMonth()"
                    (keydown.space)="nextMonth(); $event.preventDefault()"
                ></span>
            </div>
            <hr [class]="dividerStyles" />
            @switch (view) {
                @case ('days') {
                    <div
                        class="grid grid-cols-7 grid-rows-6 items-center justify-center gap-2 p-6"
                        role="grid"
                        aria-rowcount="6"
                        aria-colcount="7"
                        aria-label="Calendar days"
                    >
                        @for (day of localizedWeekdays; track day) {
                            <div [class]="dayTextStyles" role="columnheader">
                                {{ day }}
                            </div>
                        }
                        @for (day of previousMonthsDates; track day) {
                            <div [class]="dayBoxDisabledStyles" aria-disabled="true">
                                {{ day }}
                            </div>
                        }
                        @for (day of dates; track day) {
                            <div
                                [class]="dayBoxStyles + (isDaySelected(day) ? ' selected-day' : '') + ' '"
                                [attr.data-selected]="isDaySelected(day)"
                                (click)="dateSelected(day)"
                                [id]="'day-' + day"
                                tabindex="0"
                                role="gridcell"
                                [attr.aria-selected]="isDaySelected(day)"
                                [attr.aria-label]="localizedMonths[selectedMonth] + ' ' + day + ', ' + selectedYear"
                                (keydown)="handleDayKeyDown($event, day)"
                            >
                                {{ day }}
                            </div>
                        }
                        @for (day of nextMonthsDates; track day) {
                            <div [class]="dayBoxDisabledStyles" aria-disabled="true">
                                {{ day }}
                            </div>
                        }
                    </div>
                }
                @case ('months') {
                    <div
                        class="grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 p-6"
                        role="region"
                        aria-label="Month selection table with 3 rows and 4 columns"
                    >
                        @for (month of localizedMonths; track month; let i = $index) {
                            <div
                                [class]="monthBoxStyles + (selectedMonth === i ? ' selected-month' : '') + ' '"
                                [attr.data-selected]="selectedMonth === i"
                                (click)="monthSelected(i)"
                                [id]="'month-' + i"
                                tabindex="0"
                                role="button"
                                [attr.aria-selected]="selectedMonth === i"
                                (keydown)="handleMonthKeyDown($event, i)"
                            >
                                {{ month }}
                            </div>
                        }
                    </div>
                }
                @case ('years') {
                    <div class="p-6 py-2">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' icon-chevron-up rounded-md flex items-center justify-center w-full cursor-pointer p-2'
                            "
                            (click)="previousYears()"
                            tabindex="0"
                            role="button"
                            [attr.aria-label]="
                                'Show earlier years: ' + (displayedYears[0] - 12) + ' to ' + (displayedYears[0] - 1)
                            "
                            (keydown.enter)="previousYears()"
                            (keydown.space)="previousYears(); $event.preventDefault()"
                        ></span>
                        <div
                            class="grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 py-2"
                            role="region"
                            aria-label="Year selection table with 3 rows and 4 columns"
                        >
                            @for (year of displayedYears; track year) {
                                <div
                                    [class]="monthBoxStyles + (selectedYear === year ? ' selected-year' : '') + ' '"
                                    [attr.data-selected]="selectedYear === year"
                                    (click)="yearSelected(year)"
                                    [id]="'year-' + year"
                                    tabindex="0"
                                    role="button"
                                    [attr.aria-selected]="selectedYear === year"
                                    (keydown)="handleYearKeyDown($event, year)"
                                >
                                    {{ year }}
                                </div>
                            }
                        </div>
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' icon-chevron-down rounded-md flex items-center justify-center w-full cursor-pointer p-2'
                            "
                            (click)="nextYears()"
                            tabindex="0"
                            role="button"
                            [attr.aria-label]="
                                'Show later years: ' +
                                (displayedYears[displayedYears.length - 1] + 1) +
                                ' to ' +
                                (displayedYears[displayedYears.length - 1] + 12)
                            "
                            (keydown.enter)="nextYears()"
                            (keydown.space)="nextYears(); $event.preventDefault()"
                        ></span>
                    </div>
                }
            }
            @if (time()) {
                <hr [class]="dividerStyles" />
                <div class="flex items-center justify-center gap-2 p-4" role="group" aria-label="Time selection">
                    <div class="flex flex-col">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="increaseHours()"
                            tabindex="0"
                            role="button"
                            aria-label="Increase hours"
                            (keydown.enter)="increaseHours()"
                            (keydown.space)="increaseHours(); $event.preventDefault()"
                        ></span>
                        <input
                            [class]="timeInputStyles + ' '"
                            [(ngModel)]="selectedHours"
                            (change)="updateTimeInput($event, 'hours')"
                            type="number"
                            min="0"
                            max="23"
                            aria-label="Hours"
                            role="spinbutton"
                            aria-valuemin="0"
                            aria-valuemax="23"
                            [attr.aria-valuenow]="selectedHours"
                        />
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="decreaseHours()"
                            tabindex="0"
                            role="button"
                            aria-label="Decrease hours"
                            (keydown.enter)="decreaseHours()"
                            (keydown.space)="decreaseHours(); $event.preventDefault()"
                        ></span>
                    </div>
                    <span [class]="timeDividerStyles" aria-hidden="true">:</span>
                    <div class="flex flex-col">
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="increaseMinutes()"
                            tabindex="0"
                            role="button"
                            aria-label="Increase minutes"
                            (keydown.enter)="increaseMinutes()"
                            (keydown.space)="increaseMinutes(); $event.preventDefault()"
                        ></span>
                        <input
                            [class]="timeInputStyles + ' '"
                            [(ngModel)]="selectedMinutes"
                            (change)="updateTimeInput($event, 'minutes')"
                            type="number"
                            min="0"
                            max="59"
                            aria-label="Minutes"
                            role="spinbutton"
                            aria-valuemin="0"
                            aria-valuemax="59"
                            [attr.aria-valuenow]="selectedMinutes"
                        />
                        <span
                            [class]="
                                pickerButtonStyles +
                                ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                            "
                            (click)="decreaseMinutes()"
                            tabindex="0"
                            role="button"
                            aria-label="Decrease minutes"
                            (keydown.enter)="decreaseMinutes()"
                            (keydown.space)="decreaseMinutes(); $event.preventDefault()"
                        ></span>
                    </div>
                    @if (time() === 'hh:mm:ss') {
                        <span [class]="timeDividerStyles">:</span>
                        <div class="flex flex-col">
                            <span
                                [class]="
                                    pickerButtonStyles +
                                    ' text-sm font-semibold icon-chevron-up w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                                "
                                (click)="increaseSeconds()"
                                tabindex="0"
                                role="button"
                                aria-label="Increase seconds"
                                (keydown.enter)="increaseSeconds()"
                                (keydown.space)="increaseSeconds(); $event.preventDefault()"
                            ></span>
                            <input
                                [class]="timeInputStyles + ' '"
                                [(ngModel)]="selectedSeconds"
                                (change)="updateTimeInput($event, 'seconds')"
                                type="number"
                                min="0"
                                max="59"
                                aria-label="Seconds"
                                role="spinbutton"
                                aria-valuemin="0"
                                aria-valuemax="59"
                                [attr.aria-valuenow]="selectedSeconds"
                            />
                            <span
                                [class]="
                                    pickerButtonStyles +
                                    ' text-sm font-semibold icon-chevron-down w-[50px] cursor-pointer flex items-center justify-center mb-[2px] rounded-sm '
                                "
                                (click)="decreaseSeconds()"
                                tabindex="0"
                                role="button"
                                aria-label="Decrease seconds"
                                (keydown.enter)="decreaseSeconds()"
                                (keydown.space)="decreaseSeconds(); $event.preventDefault()"
                            ></span>
                        </div>
                    }
                </div>
            }
        </div>
    `,
                    imports: [FormsModule]
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class InputComponent {
    el = inject(ElementRef);
    datePipe = inject(DatePipe);
    id = input(null);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    invalid = input(null);
    error = input('');
    label = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = '';
    placeholder = input('');
    type = input('text');
    timeFormat = input();
    dateFormat = input('dd.MM.yyyy');
    lang = input();
    readOnly = input(false);
    hint = input(null);
    floatLabel = input(false);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // Outputs for events
    change = output();
    input = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    dateChange = output();
    inputId = '';
    labelId = '';
    errorId = '';
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            if (this.showDatePicker) {
                this.markAsTouched();
            }
            this.showDatePicker = false;
        }
    }
    onKeyDown(event) {
        switch (event.key) {
            case 'Enter':
            case ' ':
                this.displayDatePicker();
                break;
            case 'Escape':
                this.closeDatePicker();
                break;
        }
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.target);
        if (!focusedInside) {
            this.showDatePicker = false;
            this.markAsTouched();
        }
    }
    constructor() {
        const baseId = this.id() || this.randomId();
        this.inputId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    dateValue = null;
    showDatePicker = false;
    focused = false;
    handleDateChange(event) {
        this.dateValue = event.value;
        this.value = event.value.toISOString();
        this.onChangeEvent(this.value);
        this.dateChange.emit(this.value);
        if (event.emit) {
            setTimeout(() => {
                this.showDatePicker = false;
                this.el.nativeElement.blur();
            }, 100);
        }
    }
    touched = false;
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        this.value = obj;
        if (this.type() === 'date') {
            this.dateValue = obj === null ? null : new Date(obj);
        }
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    onClick(event) {
        event.stopPropagation();
        this.click.emit(event);
        if (this.type() === 'date') {
            this.showDatePicker = !this.showDatePicker;
        }
    }
    onFocus(event) {
        event.stopPropagation();
        this.focus.emit(event);
        this.focused = true;
    }
    onBlur(event) {
        event.stopPropagation();
        this.focused = false;
        if (this.type() === 'date' && !this.showDatePicker) {
            setTimeout(() => {
                this.markAsTouched();
            }, 100);
        }
        this.blur.emit(event);
    }
    getAriaDescribedby() {
        // Combine user-provided describedby with error ID when there's an error
        if (this.error()) {
            return this.ariaDescribedby() ? `${this.ariaDescribedby()} ${this.errorId}` : this.errorId;
        }
        return this.ariaDescribedby() || null;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onInputChange(event) {
        event.stopPropagation();
        event.preventDefault();
        // Block input for date type
        if (this.type() === 'date') {
            if (event.target && event.target['value']) {
                event.target['value'] = this.displayValue;
            }
            return;
        }
        const input = event.target;
        this.value = input.value;
        this.onChangeEvent(this.value);
        this.input.emit(event);
    }
    validate(_) {
        if (this.type() === 'date' && this.value && !this.isDate(this.value)) {
            return { invalidDate: true };
        }
        return this.required() && !this.value ? { required: true } : null;
    }
    isArray(value) {
        return Array.isArray(value);
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    get mappedType() {
        const type = this.type();
        switch (type) {
            case 'date':
            case 'datetime':
                return 'text';
            default:
                return type;
        }
    }
    get displayValue() {
        const type = this.type();
        const timeFormat = this.timeFormat();
        if (type === 'date' && this.dateValue && timeFormat) {
            if (timeFormat === 'hh:mm') {
                return `${this.datePipe.transform(this.dateValue, this.dateFormat())} ${this.dateValue.toLocaleTimeString().split(':').slice(0, 2).join(':')}`;
            }
            return `${this.datePipe.transform(this.dateValue, this.dateFormat())} ${this.dateValue.toLocaleTimeString()}`;
        }
        if (type === 'date' && this.dateValue) {
            return this.datePipe.transform(this.dateValue, this.dateFormat());
        }
        return this.value;
    }
    isDate(value) {
        // important
        return !isNaN(Date.parse(value));
    }
    displayDatePicker() {
        if (this.type() === 'date') {
            this.showDatePicker = true;
        }
    }
    closeDatePicker() {
        if (this.type() === 'date') {
            this.showDatePicker = false;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: InputComponent, isStandalone: true, selector: "sag-ui-input", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, timeFormat: { classPropertyName: "timeFormat", publicName: "timeFormat", isSignal: true, isRequired: false, transformFunction: null }, dateFormat: { classPropertyName: "dateFormat", publicName: "dateFormat", isSignal: true, isRequired: false, transformFunction: null }, lang: { classPropertyName: "lang", publicName: "lang", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, floatLabel: { classPropertyName: "floatLabel", publicName: "floatLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", input: "input", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave", dateChange: "dateChange" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "keydown": "onKeyDown($event)", "focusout": "onFocusOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => InputComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => InputComponent),
                multi: true
            },
            DatePipe
        ], ngImport: i0, template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="inputId"
                    [type]="mappedType"
                    [disabled]="disabled"
                    [required]="invalid() || (required() && touched)"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [placeholder]="floatLabel() ? '' : placeholder()"
                    [value]="displayValue"
                    [readOnly]="readOnly()"
                    (input)="onInputChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    (keydown)="keydown.emit($event)"
                    (keyup)="keyup.emit($event)"
                    (mouseenter)="mouseenter.emit($event)"
                    (mouseleave)="mouseleave.emit($event)"
                    [attr.aria-required]="required()"
                    [attr.aria-readonly]="readOnly()"
                    [attr.aria-invalid]="invalid()"
                    [attr.aria-placeholder]="placeholder()"
                    [attr.aria-label]="ariaLabel()"
                    [attr.aria-labelledby]="ariaLabelledby() || labelId"
                    [attr.aria-describedby]="getAriaDescribedby()"
                    [attr.data-float-label]="floatLabel()"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="inputId"
                    [attr.data-float]="floatLabel() ? focused || !!value : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute left-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="start-decorator"></ng-content>
                </span>
                <span
                    class="absolute right-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="end-decorator"></ng-content>
                </span>
                @if (type() === 'date') {
                    <span class="absolute right-4 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"></span>
                }
                <div class="absolute top-full right-0 flex items-center z-20">
                    @if (type() === 'date' && showDatePicker) {
                        <sag-ui-date-picker
                            [date]="dateValue!"
                            [time]="timeFormat()"
                            [lang]="lang()"
                            (dateChanged)="handleDateChange($event)"
                        ></sag-ui-date-picker>
                    }
                </div>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: DatePickerComponent, selector: "sag-ui-date-picker", inputs: ["time", "lang", "date"], outputs: ["dateChanged"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-input',
                    standalone: true,
                    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="inputId"
                    [type]="mappedType"
                    [disabled]="disabled"
                    [required]="invalid() || (required() && touched)"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [placeholder]="floatLabel() ? '' : placeholder()"
                    [value]="displayValue"
                    [readOnly]="readOnly()"
                    (input)="onInputChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    (keydown)="keydown.emit($event)"
                    (keyup)="keyup.emit($event)"
                    (mouseenter)="mouseenter.emit($event)"
                    (mouseleave)="mouseleave.emit($event)"
                    [attr.aria-required]="required()"
                    [attr.aria-readonly]="readOnly()"
                    [attr.aria-invalid]="invalid()"
                    [attr.aria-placeholder]="placeholder()"
                    [attr.aria-label]="ariaLabel()"
                    [attr.aria-labelledby]="ariaLabelledby() || labelId"
                    [attr.aria-describedby]="getAriaDescribedby()"
                    [attr.data-float-label]="floatLabel()"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="inputId"
                    [attr.data-float]="floatLabel() ? focused || !!value : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute left-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="start-decorator"></ng-content>
                </span>
                <span
                    class="absolute right-0 bottom-0 h-[42px] flex justify-center items-baseline select-none pointer-events-none"
                >
                    <ng-content select="end-decorator"></ng-content>
                </span>
                @if (type() === 'date') {
                    <span class="absolute right-4 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"></span>
                }
                <div class="absolute top-full right-0 flex items-center z-20">
                    @if (type() === 'date' && showDatePicker) {
                        <sag-ui-date-picker
                            [date]="dateValue!"
                            [time]="timeFormat()"
                            [lang]="lang()"
                            (dateChanged)="handleDateChange($event)"
                        ></sag-ui-date-picker>
                    }
                </div>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => InputComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => InputComponent),
                            multi: true
                        },
                        DatePipe
                    ],
                    imports: [InputDirective, InputLabelDirective, TooltipDirective, DatePickerComponent, InputErrorDirective]
                }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class TextareaComponent {
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    invalid = input(null);
    error = input('');
    label = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = '';
    placeholder = input('');
    readOnly = input(false);
    hint = input(null);
    floatLabel = input(false);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // Outputs for events
    change = output();
    input = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    textareaId = '';
    labelId = '';
    errorId = '';
    touched = false;
    focused = false;
    onChangeEvent = (_) => { };
    onTouched = () => { };
    textareaElement = viewChild('textareaElement');
    scrollbarControl = viewChild('scrollbarControl');
    scrollPosition = signal(0);
    maxScroll = signal(0);
    thumbPosition = signal(0);
    wasScrollable = signal(false);
    constructor() {
        const baseId = this.id() || this.randomId();
        this.textareaId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    ngAfterViewInit() {
        this.updateMaxScroll();
        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
                this.updateMaxScroll();
            });
            const textAreaHtmlElement = this.textareaElement()?.nativeElement;
            if (textAreaHtmlElement) {
                resizeObserver.observe(textAreaHtmlElement);
            }
        }
    }
    onTextAreaScroll() {
        const textAreaHtmlElement = this.textareaElement().nativeElement;
        if (!textAreaHtmlElement)
            return;
        this.scrollPosition.set(textAreaHtmlElement.scrollTop);
        const newThumbPosition = (textAreaHtmlElement.scrollTop / (this.maxScroll() || 1)) * 100;
        this.thumbPosition.set(newThumbPosition);
    }
    onScrollbarKeydown(event) {
        if (event.key === 'Tab')
            return;
        const textAreaHtmlElement = this.textareaElement().nativeElement;
        if (!textAreaHtmlElement)
            return;
        const stepSize = 20;
        if (event.key === 'ArrowDown') {
            textAreaHtmlElement.scrollTop += stepSize;
            event.preventDefault();
        }
        else if (event.key === 'ArrowUp') {
            textAreaHtmlElement.scrollTop -= stepSize;
            event.preventDefault();
        }
        this.onTextAreaScroll();
    }
    onResizeKeyDown(event) {
        if (event.key === 'Tab')
            return;
        const textAreaHtmlElement = this.textareaElement().nativeElement;
        if (!textAreaHtmlElement)
            return;
        const stepSize = 10;
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            if (event.key === 'ArrowUp') {
                textAreaHtmlElement.style.height = `${Math.max(textAreaHtmlElement.clientHeight - stepSize, 50)}px`;
            }
            else if (event.key === 'ArrowDown') {
                textAreaHtmlElement.style.height = `${textAreaHtmlElement.clientHeight + stepSize}px`;
            }
            this.updateMaxScroll();
        }
    }
    getAriaDescribedby() {
        if (this.error()) {
            return this.ariaDescribedby() ? `${this.ariaDescribedby()} ${this.errorId}` : this.errorId;
        }
        return this.ariaDescribedby() || null;
    }
    updateMaxScroll() {
        const textAreaHtmlElement = this.textareaElement()?.nativeElement;
        if (!textAreaHtmlElement)
            return;
        const newMaxScroll = Math.max(0, textAreaHtmlElement.scrollHeight - textAreaHtmlElement.clientHeight);
        this.maxScroll.set(newMaxScroll);
        // Track scrollability changes
        const isNowScrollable = newMaxScroll > 0;
        // Only execute special handling when scrollability state changes
        if (this.wasScrollable() !== isNowScrollable) {
            this.wasScrollable.set(isNowScrollable);
            // If it became scrollable and we need to fix tab order
            if (isNowScrollable && this.scrollbarControl()?.nativeElement) {
                // This trick forces the browser to recalculate the tab order
                setTimeout(() => {
                    const scrollbar = this.scrollbarControl().nativeElement;
                    scrollbar.style.display = 'none';
                    // Force reflow
                    void scrollbar.offsetWidth;
                    scrollbar.style.display = 'block';
                });
            }
        }
    }
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    onBlur(event) {
        event.stopPropagation();
        this.touched = true;
        this.focused = false;
        this.onTouched();
        this.blur.emit(event);
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(event) {
        event.stopPropagation();
        event.preventDefault();
        const input = event.target;
        this.value = input.value;
        this.onChangeEvent(this.value);
        this.change.emit(event);
        this.updateMaxScroll();
    }
    validate(_) {
        return this.required() && !this.value ? { required: true } : null;
    }
    isArray(value) {
        return Array.isArray(value);
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TextareaComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: TextareaComponent, isStandalone: true, selector: "sag-ui-textarea", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, floatLabel: { classPropertyName: "floatLabel", publicName: "floatLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", input: "input", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TextareaComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => TextareaComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "textareaElement", first: true, predicate: ["textareaElement"], descendants: true, isSignal: true }, { propertyName: "scrollbarControl", first: true, predicate: ["scrollbarControl"], descendants: true, isSignal: true }], ngImport: i0, template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <div class="relative w-full">
                    <textarea
                        #textareaElement
                        sag-ui-input
                        [id]="textareaId"
                        [disabled]="disabled"
                        [required]="invalid() || (required() && touched)"
                        [attr.data-invalid]="invalid()"
                        [placeholder]="!floatLabel() && !value ? placeholder() : null"
                        [value]="value"
                        (change)="onChange($event)"
                        (click)="click.emit($event)"
                        (focus)="focused = true; focus.emit($event)"
                        (blur)="onBlur($event)"
                        (keydown)="keydown.emit($event)"
                        (keyup)="keyup.emit($event)"
                        (scroll)="onTextAreaScroll()"
                        (mouseenter)="mouseenter.emit($event)"
                        (mouseleave)="mouseleave.emit($event)"
                        [attr.aria-label]="ariaLabel()"
                        [attr.aria-labelledby]="ariaLabelledby() || labelId"
                        [attr.aria-describedby]="getAriaDescribedby()"
                        [attr.data-float-label]="floatLabel()"
                        [class.resize-none]="disabled"
                    ></textarea>

                    @if (!disabled) {
                        <div
                            #scrollbarControl
                            role="slider"
                            [tabindex]="maxScroll() > 0 ? '0' : '-1'"
                            [attr.tabindex]="maxScroll() > 0 ? '0' : '-1'"
                            [attr.aria-valuenow]="scrollPosition()"
                            aria-label="textarea scroll bar. Use it to navigate through the text"
                            (keydown)="onScrollbarKeydown($event)"
                        >
                            <div class="scroll-thumb" [style.top.%]="thumbPosition()"></div>
                        </div>

                        <div
                            tabindex="0"
                            role="slider"
                            [attr.aria-valuenow]="scrollPosition()"
                            aria-label="textarea resize handler. Use it to resize the textarea"
                            (keydown)="onResizeKeyDown($event)"
                        ></div>
                    }
                </div>

                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="textareaId"
                    [attr.data-float]="floatLabel() ? focused || !!value : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TextareaComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-textarea',
                    standalone: true,
                    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <div class="relative w-full">
                    <textarea
                        #textareaElement
                        sag-ui-input
                        [id]="textareaId"
                        [disabled]="disabled"
                        [required]="invalid() || (required() && touched)"
                        [attr.data-invalid]="invalid()"
                        [placeholder]="!floatLabel() && !value ? placeholder() : null"
                        [value]="value"
                        (change)="onChange($event)"
                        (click)="click.emit($event)"
                        (focus)="focused = true; focus.emit($event)"
                        (blur)="onBlur($event)"
                        (keydown)="keydown.emit($event)"
                        (keyup)="keyup.emit($event)"
                        (scroll)="onTextAreaScroll()"
                        (mouseenter)="mouseenter.emit($event)"
                        (mouseleave)="mouseleave.emit($event)"
                        [attr.aria-label]="ariaLabel()"
                        [attr.aria-labelledby]="ariaLabelledby() || labelId"
                        [attr.aria-describedby]="getAriaDescribedby()"
                        [attr.data-float-label]="floatLabel()"
                        [class.resize-none]="disabled"
                    ></textarea>

                    @if (!disabled) {
                        <div
                            #scrollbarControl
                            role="slider"
                            [tabindex]="maxScroll() > 0 ? '0' : '-1'"
                            [attr.tabindex]="maxScroll() > 0 ? '0' : '-1'"
                            [attr.aria-valuenow]="scrollPosition()"
                            aria-label="textarea scroll bar. Use it to navigate through the text"
                            (keydown)="onScrollbarKeydown($event)"
                        >
                            <div class="scroll-thumb" [style.top.%]="thumbPosition()"></div>
                        </div>

                        <div
                            tabindex="0"
                            role="slider"
                            [attr.aria-valuenow]="scrollPosition()"
                            aria-label="textarea resize handler. Use it to resize the textarea"
                            (keydown)="onResizeKeyDown($event)"
                        ></div>
                    }
                </div>

                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="textareaId"
                    [attr.data-float]="floatLabel() ? focused || !!value : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TextareaComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => TextareaComponent),
                            multi: true
                        }
                    ],
                    imports: [InputDirective, InputLabelDirective, TooltipDirective, InputErrorDirective]
                }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

const checkboxBaseStyles = [
    'peer',
    'form-checkbox',
    'appearance-none',
    'cursor-pointer',
    'transition-all',
    'border-1',
    'border-solid',
    'w-4',
    'h-4',
    'focus:ring-0',
    'focus:ring-transparent',
    'focus:ring-offset-0',
    'focus:outline-none',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1',
    'disabled:cursor-not-allowed'
];
const checkboxStylesSAG = cva([
    ...checkboxBaseStyles,
    'border-[#000028]',
    'hover:border-[#005159]',
    'hover:checked:border-[#196269]',
    'focus:checked:border-[#005159]',
    'focus:border-[#000028]',
    'checked:border-[#005159]',
    'bg-white',
    'hover:bg-[#D1FFF2]',
    'hover:checked:bg-[#196269]',
    'focus:checked:bg-[#005159]',
    'checked:bg-[#005159]',
    'checked:bg-checkbox-sag',
    'hover:checked:bg-checkbox-hover-sag',
    'focus:checked:bg-checkbox-hover-sag',
    'disabled:bg-white',
    'disabled:hover:bg-white',
    'disabled:hover:border-[#B3B3BE]',
    'disabled:border-[#B3B3BE]',
    'disabled:checked:bg-checkbox-disabled-sag',
    'disabled:checked:bg-[#B3B3BE]',
    'disabled:checked:hover:bg-[#B3B3BE]',
    // Dark mode
    'dark:border-white',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:checked:border-[#00E5AA]',
    'dark:focus:checked:border-[#00FFB9]',
    'dark:focus:border-[#FFFFFF]',
    'dark:checked:border-[#00FFB9]',
    'dark:bg-[#000028]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:checked:bg-[#00E5AA]',
    'dark:focus:checked:bg-[#00FFB9]',
    'dark:checked:bg-[#00FFB9]',
    'dark:checked:bg-checkbox-dark-sag',
    'dark:hover:checked:bg-checkbox-hover-dark-sag',
    'dark:focus:checked:bg-checkbox-hover-dark-sag',
    'dark:disabled:bg-[#000028]',
    'dark:disabled:border-[#737389]',
    'dark:disabled:checked:bg-checkbox-disabled-dark-sag',
    'dark:disabled:checked:bg-[#737389]',
    'dark:disabled:hover:border-[#737389]'
]);
const checkboxStylesSHS = cva([
    ...checkboxBaseStyles,
    'rounded-sm',
    'border-[#666666]',
    'hover:border-[#666666]',
    'hover:checked:border-[#D45C01]',
    'focus:checked:border-[#D45C01]',
    'focus:border-[#666666]',
    'checked:border-[#D45C01]',
    'bg-white',
    'hover:bg-[#F2F2F2]',
    'hover:checked:bg-[#D45C01]',
    'focus:checked:bg-[#D45C01]',
    'checked:bg-[#D45C01]',
    'disabled:bg-transparent',
    'disabled:hover:bg-transparent',
    'disabled:hover:border-[#C2C2C2]',
    'disabled:border-[#C2C2C2]',
    'disabled:checked:bg-checkbox-disabled-sag',
    // Dark mode
    'dark:border-[#999999]',
    'dark:hover:border-[#999999]',
    'dark:hover:checked:border-[#D45C01]',
    'dark:focus:checked:border-[#D45C01]',
    'dark:focus:border-[#999999]',
    'dark:checked:border-[#D45C01]',
    'dark:bg-[#1A1A1A]',
    'dark:hover:bg-[#313131]',
    'dark:hover:checked:bg-[#D45C01]',
    'dark:focus:checked:bg-[#D45C01]',
    'dark:checked:bg-[#D45C01]',
    'dark:disabled:bg-transparent',
    'dark:disabled:hover:bg-transparent',
    'dark:disabled:hover:border-[#C2C2C2]',
    'dark:disabled:border-[#C2C2C2]',
    'dark:disabled:checked:bg-checkbox-disabled-sag'
]);

class CheckboxDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, checkboxStylesSAG, checkboxStylesSHS);
        style(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: CheckboxDirective, isStandalone: true, selector: "input[sag-ui-checkbox]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-checkbox]',
                    standalone: true
                }]
        }] });

const checkboxLabelBaseStyles = ['cursor-pointer', 'disabled:cursor-not-allowed', 'text-sm', 'select-none'];
const checkboxLabelStylesSAG = cva([
    ...checkboxLabelBaseStyles,
    'text-[#000028]',
    'peer-disabled:text-[#B3B3BE]',
    // Dark mode
    'dark:text-white',
    'dark:peer-disabled:text-[#737389]'
]);
const checkboxLabelStylesSHS = cva([
    ...checkboxLabelBaseStyles,
    'text-[#1A1A1A]',
    'peer-hover:text-[#1A1A1A]',
    'hover:text-[#1A1A1A]',
    'peer-checked:text-[#1A1A1A]',
    'peer-disabled:text-[#A3A3A3]',
    // Dark mode
    'dark:text-[#E6E6E6]',
    'dark:peer-hover:text-[#E6E6E6]',
    'dark:hover:text-[#E6E6E6]',
    'dark:peer-checked:text-[#E6E6E6]',
    'dark:peer-disabled:text-[#5C5C5C]'
]);

class CheckboxLabelDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const checkboxLabelStyles = componentStylesByTheme(this.theme, checkboxLabelStylesSAG, checkboxLabelStylesSHS);
        style(this.elementRef, checkboxLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: CheckboxLabelDirective, isStandalone: true, selector: "label[sag-ui-checkbox-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[sag-ui-checkbox-label]',
                    standalone: true
                }]
        }] });

class CheckboxComponent {
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    checked = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    intermediate = input(false);
    hint = input('');
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // Outputs for events
    change = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        this.checked = !!obj;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onCheckboxChange(event) {
        event.stopPropagation();
        const input = event.target;
        this.checked = input.checked;
        this.onChangeEvent(this.checked);
        this.change.emit(event);
    }
    onFocus(event) {
        this.focus.emit(event);
    }
    onBlur(event) {
        this.onTouched();
        this.blur.emit(event);
    }
    validate(_) {
        return this.required() && !this.checked ? { required: true } : null;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: CheckboxComponent, isStandalone: true, selector: "sag-ui-checkbox", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, checked: { classPropertyName: "checked", publicName: "checked", isSignal: false, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, intermediate: { classPropertyName: "intermediate", publicName: "intermediate", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => CheckboxComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => CheckboxComponent),
                multi: true
            }
        ], ngImport: i0, template: `
        <div class="flex items-center">
            <input
                type="checkbox"
                [id]="id()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                [indeterminate]="intermediate()"
                [attr.aria-checked]="checked"
                [attr.aria-disabled]="disabled"
                [attr.aria-labelledby]="ariaLabelledby() || id()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                (change)="onCheckboxChange($event)"
                (focus)="onFocus($event)"
                (blur)="onBlur($event)"
                (click)="click.emit($event)"
                (keydown)="keydown.emit($event)"
                (keyup)="keyup.emit($event)"
                (mouseenter)="mouseenter.emit($event)"
                (mouseleave)="mouseleave.emit($event)"
                sag-ui-checkbox
            />
            <label [for]="id()" sag-ui-checkbox-label class="pl-2 flex items-center">
                <ng-content></ng-content>
                @if (required()) {
                    <span aria-hidden="true" class="self-end">*</span>
                }
                @if (hint()) {
                    <span
                        class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                        sag-ui-tooltip
                        [tooltip]="hint()"
                        [attr.aria-label]="hint()"
                    ></span>
                }
            </label>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "directive", type: CheckboxLabelDirective, selector: "label[sag-ui-checkbox-label]" }, { kind: "directive", type: CheckboxDirective, selector: "input[sag-ui-checkbox]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-checkbox',
                    standalone: true,
                    template: `
        <div class="flex items-center">
            <input
                type="checkbox"
                [id]="id()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                [indeterminate]="intermediate()"
                [attr.aria-checked]="checked"
                [attr.aria-disabled]="disabled"
                [attr.aria-labelledby]="ariaLabelledby() || id()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                (change)="onCheckboxChange($event)"
                (focus)="onFocus($event)"
                (blur)="onBlur($event)"
                (click)="click.emit($event)"
                (keydown)="keydown.emit($event)"
                (keyup)="keyup.emit($event)"
                (mouseenter)="mouseenter.emit($event)"
                (mouseleave)="mouseleave.emit($event)"
                sag-ui-checkbox
            />
            <label [for]="id()" sag-ui-checkbox-label class="pl-2 flex items-center">
                <ng-content></ng-content>
                @if (required()) {
                    <span aria-hidden="true" class="self-end">*</span>
                }
                @if (hint()) {
                    <span
                        class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                        sag-ui-tooltip
                        [tooltip]="hint()"
                        [attr.aria-label]="hint()"
                    ></span>
                }
            </label>
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => CheckboxComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => CheckboxComponent),
                            multi: true
                        }
                    ],
                    imports: [TooltipDirective, CheckboxLabelDirective, CheckboxDirective]
                }]
        }], propDecorators: { checked: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

const switchBaseStyles = [
    'peer',
    'form-checkbox',
    'enabled:cursor-pointer',
    'disabled:cursor-not-allowed',
    'appearance-none',
    'w-8',
    'rounded-full',
    'before:w-4',
    'before:h-4',
    'before:absolute',
    'relative',
    'before:top-1/2',
    'checked:bg-none',
    'before:-translate-y-1/2',
    'before:left-0',
    'before:block',
    'before:border-2',
    'before:rounded-full',
    'checked:before:left-4',
    'before:transition-all',
    'focus:ring-0',
    'focus:ring-transparent',
    'focus:ring-offset-0',
    'focus:outline-none',
    'focus-visible:outline-[#1491EB]'
];
const switchStylesSAG = cva([
    ...switchBaseStyles,
    'border-none',
    'bg-[#7D8099]',
    'hover:bg-[#66667E]',
    'text-[#005159]',
    'checked:bg-[#005159]',
    'checked:hover:bg-[#196269]',
    'before:bg-white',
    'before:border-[#7D8099]',
    'before:hover:border-[#66667E]',
    'checked:before:border-[#005159]',
    'checked:before:hover:border-[#196269]',
    'disabled:bg-[#E5E5E9]',
    'disabled:before:border-[#E5E5E9]',
    'disabled:before:bg-[#CCCCD4]',
    'disabled:checked:hover:bg-[#E5E5E9]',
    'dark:bg-[#737389]',
    'dark:hover:bg-[#9999A9]',
    'dark:text-[#00FFB9]',
    'dark:checked:bg-[#00FFB9]',
    'dark:checked:hover:bg-[#00E5AA]',
    'dark:before:bg-[#000028]',
    'dark:before:border-[#737389]',
    'dark:before:hover:border-[#9999A9]',
    'dark:checked:before:border-[#00FFB9]',
    'dark:checked:before:hover:border-[#00E5AA]',
    'dark:disabled:bg-[#333353]',
    'dark:disabled:before:border-[#333353]',
    'dark:disabled:before:bg-[#737389]',
    'dark:disabled:checked:hover:bg-[#333353]'
]);
const switchStylesSHS = cva([
    ...switchBaseStyles,
    'box-content',
    'border',
    'bg-white',
    'text-[#ec6602]',
    'border-[#666666]',
    'checked:border-[#ec6602]',
    'checked:bg-[#ec6602]',
    'checked:hover:bg-[#ec6602]',
    'before:bg-[#666666]',
    'before:border-white',
    'before:hover:border-white',
    'checked:before:bg-white',
    'checked:before:border-[#ec6602]',
    'checked:before:hover:border-[#ec6602]',
    'disabled:bg-white',
    'disabled:before:border-white',
    'disabled:before:bg-[#CCCCD4]',
    'disabled:checked:hover:bg-white',
    'dark:bg-[#1a1a1a]',
    'dark:text-[#ec6602]',
    'dark:border-[#999]',
    'dark:before:bg-[#999]',
    'dark:before:border-[#1a1a1a]',
    'dark:before:hover:border-[#1a1a1a]',
    'dark:checked:before:bg-white',
    'dark:checked:before:border-[#ec6602]',
    'dark:checked:before:hover:border-[#ec6602]',
    'dark:checked:bg-[#ec6602]',
    'dark:checked:border-[#ec6602]',
    'dark:checked:hover:bg-[#ec6602]',
    'dark:disabled:bg-transparent',
    'dark:disabled:before:border-black'
]);

class SwitchDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, switchStylesSAG, switchStylesSHS);
        style(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: SwitchDirective, isStandalone: true, selector: "input[sag-ui-switch]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-switch]',
                    standalone: true
                }]
        }] });

const switchLabelBaseStyles = ['cursor-pointer', 'disabled:cursor-not-allowed', 'select-none'];
const switchLabelStylesSAG = cva([
    ...switchLabelBaseStyles,
    'text-[#4C4C68]',
    'peer-hover:text-[#000028]',
    'hover:text-[#000028]',
    'peer-checked:text-[#000028]',
    'peer-disabled:text-[#7D8099]',
    // Dark mode
    'dark:text-[#B3B3BE]',
    'dark:peer-hover:text-[#FFFFFF]',
    'dark:hover:text-[#FFFFFF]',
    'dark:peer-checked:text-[#FFFFFF]',
    'dark:peer-disabled:text-[#737389]'
]);
const switchLabelStylesSHS = cva([
    ...switchLabelBaseStyles,
    'text-[#1A1A1A]',
    'peer-hover:text-[#1A1A1A]',
    'hover:text-[#1A1A1A]',
    'peer-checked:text-[#1A1A1A]',
    'peer-disabled:text-[#A3A3A3]',
    // Dark mode
    'dark:text-[#E6E6E6]',
    'dark:peer-hover:text-[#E6E6E6]',
    'dark:hover:text-[#E6E6E6]',
    'dark:peer-checked:text-[#E6E6E6]',
    'dark:peer-disabled:text-[#5C5C5C]'
]);

class SwitchLabelDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const switchLabelStyles = componentStylesByTheme(this.theme, switchLabelStylesSAG, switchLabelStylesSHS);
        style(this.elementRef, switchLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: SwitchLabelDirective, isStandalone: true, selector: "label[sag-ui-switch-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[sag-ui-switch-label]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class SwitchComponent {
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    checked = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // eslint-disable-next-line @angular-eslint/no-output-native
    change = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    click = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    focus = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    blur = output();
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        this.checked = !!obj;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(event) {
        event.stopPropagation();
        const input = event.target;
        this.checked = input.checked;
        this.onChangeEvent(this.checked);
        this.change.emit(event);
    }
    validate(_) {
        return this.required() && !this.checked ? { required: true } : null;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: SwitchComponent, isStandalone: true, selector: "sag-ui-switch", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, checked: { classPropertyName: "checked", publicName: "checked", isSignal: false, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SwitchComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => SwitchComponent),
                multi: true
            }
        ], ngImport: i0, template: `
        <div class="flex items-center gap-2">
            <input
                [id]="id()"
                type="checkbox"
                [attr.aria-labelledby]="ariaLabelledby()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                sag-ui-switch
            />
            <label [for]="id()" sag-ui-switch-label>
                <ng-content></ng-content>
            </label>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: SwitchDirective, selector: "input[sag-ui-switch]" }, { kind: "directive", type: SwitchLabelDirective, selector: "label[sag-ui-switch-label]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SwitchComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-switch',
                    standalone: true,
                    template: `
        <div class="flex items-center gap-2">
            <input
                [id]="id()"
                type="checkbox"
                [attr.aria-labelledby]="ariaLabelledby()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                sag-ui-switch
            />
            <label [for]="id()" sag-ui-switch-label>
                <ng-content></ng-content>
            </label>
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SwitchComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => SwitchComponent),
                            multi: true
                        }
                    ],
                    imports: [SwitchDirective, SwitchLabelDirective]
                }]
        }], propDecorators: { checked: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

const selectOptionBaseStyles = ['p-3', 'cursor-pointer', 'flex', 'items-center', 'focus-visible:outline-[#1491EB]'];
const selectOptionStylesSAG = cva([...selectOptionBaseStyles, 'hover:bg-[#62EEC7]', 'dark:hover:bg-[#005159]']);
const selectOptionSelectedStylesSAG = cva([...selectOptionBaseStyles, 'bg-[#62EEC7]', 'dark:bg-[#005159]']);
const selectionOptionTextStylesSAG = cva(['text-[#000028]', 'dark:text-white']);
const selectOptionStylesSHS = cva([...selectOptionBaseStyles, 'hover:bg-[#f2f2f2]', 'dark:hover:bg-[#474747]']);
const selectOptionSelectedStylesSHS = cva([...selectOptionBaseStyles, 'bg-[#e6e6e6]', 'dark:bg-[#666]']);
const selectionOptionTextStylesSHS = cva(['dark:text-[#e6e6e6]']);

class OptionComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value = input();
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    selected = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    multiple = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    hidden = false;
    select = output();
    text = viewChild.required('text');
    get containerClasses() {
        if (this.hidden) {
            return 'hidden';
        }
        const containerStyles = componentStylesByTheme(this.theme, selectOptionStylesSAG, selectOptionStylesSHS);
        const selectedContainerStyles = componentStylesByTheme(this.theme, selectOptionSelectedStylesSAG, selectOptionSelectedStylesSHS);
        return this.selected ? selectedContainerStyles() : containerStyles();
    }
    get textClasses() {
        const textStyles = componentStylesByTheme(this.theme, selectionOptionTextStylesSAG, selectionOptionTextStylesSHS);
        return textStyles();
    }
    focus() {
        const div = this.el.nativeElement.querySelector('div');
        if (div) {
            div.focus();
        }
    }
    onClick() {
        const innerText = this.text().nativeElement.innerText;
        this.select.emit([innerText, this.value()]);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: OptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: OptionComponent, isStandalone: true, selector: "sag-ui-option", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: false, isRequired: false, transformFunction: null }, multiple: { classPropertyName: "multiple", publicName: "multiple", isSignal: false, isRequired: false, transformFunction: null }, hidden: { classPropertyName: "hidden", publicName: "hidden", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { select: "select" }, viewQueries: [{ propertyName: "text", first: true, predicate: ["text"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n  (click)=\"onClick()\"\n  tabindex=\"0\"\n  role=\"option\"\n  [attr.aria-selected]=\"selected\"\n  [class]=\"containerClasses\"\n  >\n  @if (multiple) {\n    <input\n      type=\"checkbox\"\n      [checked]=\"selected\"\n      class=\"mr-2\"\n      tabindex=\"-1\"\n      sag-ui-checkbox\n      />\n  }\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n", dependencies: [{ kind: "directive", type: CheckboxDirective, selector: "input[sag-ui-checkbox]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: OptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-option', standalone: true, imports: [CheckboxDirective], template: "<div\n  (click)=\"onClick()\"\n  tabindex=\"0\"\n  role=\"option\"\n  [attr.aria-selected]=\"selected\"\n  [class]=\"containerClasses\"\n  >\n  @if (multiple) {\n    <input\n      type=\"checkbox\"\n      [checked]=\"selected\"\n      class=\"mr-2\"\n      tabindex=\"-1\"\n      sag-ui-checkbox\n      />\n  }\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" }]
        }], propDecorators: { selected: [{
                type: Input
            }], multiple: [{
                type: Input
            }], hidden: [{
                type: Input
            }] } });

const selectBaseStyles = [
    'absolute',
    'max-h-72',
    'right-0',
    'top-full',
    'border',
    'border-t-0',
    'shadow-md',
    'z-20',
    'w-full',
    'overflow-y-auto'
];
const selectStylesSAG = cva([
    ...selectBaseStyles,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const selectStylesSHS = cva([...selectBaseStyles, 'bg-white', 'border-0', 'dark:bg-[#333333]']);
const selectSearchBaseStyles = ['w-full', 'p-3', 'sticky', 'top-0', 'border-b'];
const selectSearchStylesSAG = cva([
    ...selectSearchBaseStyles,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const selectSearchStylesSHS = cva([
    ...selectSearchBaseStyles,
    'bg-white',
    'border-[#333333]',
    'dark:bg-[#333333]',
    'dark:border-[#999999]'
]);
const selectSearchInputBaseStyles = ['w-full', 'mt-2', 'form-input'];
const selectSearchInputStylesSAG = cva([
    ...selectSearchInputBaseStyles,
    'bg-[#EBF7F8]',
    'border-[#4C4C68]',
    'dark:bg-[#262648]',
    'dark:border-[#B3B3BE]',
    'dark:text-white'
]);
const selectSearchInputStylesSHS = cva([
    ...selectSearchInputBaseStyles,
    'bg-[#ededed]',
    'border-[#666666]',
    'dark:bg-[#1a1a1a]',
    'dark:border-[#999999]',
    'dark:text-white'
]);

class SelectComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    id = input(this.randomId());
    /**
     * The placeholder of the Select.
     */
    placeholder = input('');
    /**
     * Whether you can select multiple options.
     */
    multiple = input(false);
    /*
     * Whether the Select is required.
     */
    required = input(false);
    /**
     * Whether the Select is disabled.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    /**
     * Whether the Select is read-only.
     */
    readOnly = input(false);
    /**
     * The label of the Select.
     */
    label = input('');
    /**
     * Whether the switch is checked.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = '';
    /**
     * Whether the Select is in an error state.
     */
    invalid = input(false);
    /**
     * The error message to display when the Select is in an error state.
     */
    error = input('');
    /**
     * Whether to show a searchbar in the dropdown for multiple selects.
     */
    search = input(false);
    /**
     * A function to compare the selected value with the option value.
     */
    compareWith = input((o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    });
    /**
     * Accessibility inputs
     */
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    selectAllText = input('Select all');
    searchPlaceholder = input('Search...');
    hint = input(null);
    floatLabel = input(false);
    change = output();
    // TODO: Skipped for migration because:
    //  There are references to this query that cannot be migrated automatically.
    optionComponents;
    input = viewChild.required('input');
    inputId = '';
    labelId = '';
    errorId = '';
    constructor() {
        const baseId = this.id() || this.randomId();
        this.inputId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.closePopover();
        }
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.target);
        if (!focusedInside) {
            this.closePopover();
        }
    }
    onKeyDown(event) {
        if (this.isOpen && !this.disabled && !this.readOnly()) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closePopover();
                this.input().nativeElement.focus();
            }
        }
    }
    handleInputKeyDown(event) {
        if (this.disabled || this.readOnly())
            return;
        if (this.isOpen) {
            if (event.key === 'Tab') {
                this.closePopover();
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const visibleOptions = this.optionComponents.toArray().filter(option => !option.hidden);
                if (visibleOptions.length > 0) {
                    const index = event.key === 'ArrowDown' ? 0 : visibleOptions.length - 1;
                    visibleOptions[index].focus();
                }
            }
            else if (event.key === 'Escape') {
                event.preventDefault();
                this.closePopover();
            }
        }
        else {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.isOpen = true;
                setTimeout(() => {
                    const visibleOptions = this.optionComponents.toArray().filter(option => !option.hidden);
                    if (visibleOptions.length > 0) {
                        const index = event.key === 'ArrowUp' ? visibleOptions.length - 1 : 0;
                        visibleOptions[index].focus();
                    }
                });
            }
        }
    }
    touched = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedItems = [];
    isOpen = false;
    focused = false;
    preventReopenOnFocus = false;
    searchValue = '';
    subscriptions = [];
    ngAfterContentInit() {
        this.setupSubscriptions();
        this.subscribeToOptionChanges();
        this.attachEventListeners();
    }
    ngAfterViewInit() {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.initSelectedItems();
        }, 0);
    }
    ngOnChanges(changes) {
        if (changes['value']) {
            this.initSelectedItems();
        }
    }
    ngOnDestroy() {
        this.resetSubscriptions();
        this.optionComponents?.forEach(option => {
            const optionEl = option.el.nativeElement.querySelector('div');
            if (optionEl && optionEl._keydownHandler) {
                optionEl.removeEventListener('keydown', optionEl._keydownHandler);
            }
        });
    }
    resetSubscriptions() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
    setupSubscriptions() {
        this.optionComponents.forEach(option => {
            option.multiple = this.multiple();
            this.subscriptions.push(option.select.subscribe(data => {
                this.handleOptionSelect(data);
            }));
        });
    }
    handleOptionSelect(data) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const [label, value] = data;
        this.input().nativeElement.focus();
        this.markAsTouched();
        if (this.multiple()) {
            const index = this.selectedItems.findIndex(item => this.compareWith()(item[1], value));
            if (index >= 0) {
                this.selectedItems.splice(index, 1);
            }
            else {
                this.selectedItems.push([label, value]);
            }
            this.writeValue(this.selectedItems.map(item => item[1]));
        }
        else {
            this.selectedItems = [[label, value]];
            this.writeValue(this.selectedItems[0] ? this.selectedItems[0][1] : undefined);
            this.closePopover();
            setTimeout(() => {
                this.input().nativeElement.blur();
            }, 50);
        }
        this.updateSelectedOptions();
    }
    togglePopover(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.isOpen) {
            this.closePopover();
            this.input().nativeElement.blur();
        }
        else {
            this.isOpen = true;
            this.input().nativeElement.focus();
        }
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, selectStylesSAG, selectStylesSHS);
        return popoverStyles();
    }
    get searchClasses() {
        const searchStyles = componentStylesByTheme(this.theme, selectSearchStylesSAG, selectSearchStylesSHS);
        return searchStyles();
    }
    get searchInputClasses() {
        const searchInputStyles = componentStylesByTheme(this.theme, selectSearchInputStylesSAG, selectSearchInputStylesSHS);
        return searchInputStyles();
    }
    getAriaDescribedby() {
        // Combine user-provided describedby with error ID when there's an error
        const parts = [];
        if (this.ariaDescribedby()) {
            parts.push(this.ariaDescribedby());
        }
        if (this.error()) {
            parts.push(this.errorId);
        }
        return parts.length > 0 ? parts.join(' ') : null;
    }
    initSelectedItems() {
        this.updateSelectedOptions();
        if (this.optionComponents) {
            this.selectedItems = this.optionComponents
                .filter(option => option.selected)
                .map(option => [option.text().nativeElement.innerText, option.value()]);
        }
    }
    updateSelectedOptions() {
        this.optionComponents?.forEach(option => {
            if (this.multiple()) {
                option.selected = this.value?.some((val) => this.compareWith()(val, option.value())) ?? false;
            }
            else {
                option.selected = this.compareWith()(this.value, option.value());
            }
        });
    }
    get displayValue() {
        return this.selectedItems.map(item => item[0]).join(', ');
    }
    onFocus() {
        if (this.disabled || this.readOnly() || this.preventReopenOnFocus)
            return;
        this.focused = true;
    }
    onClick() {
        if (this.disabled || this.readOnly() || this.preventReopenOnFocus)
            return;
        this.focused = true;
        this.isOpen = true;
    }
    onBlur() {
        this.focused = false;
        setTimeout(() => {
            this.markAsTouched();
        }, 100);
    }
    onChangeEvent = (_) => { };
    // eslint-disable-next-line
    onTouch = () => { };
    // eslint-disable-next-line
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    // eslint-disable-next-line
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val) {
        this.value = val;
        this.onChangeEvent(this.value);
        this.change.emit(this.value);
        try {
            this.initSelectedItems();
        }
        catch (e) {
            console.error(e);
        }
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    // eslint-disable-next-line
    validate(control) {
        return this.required() && !this.value ? { required: true } : null;
    }
    blockChange(event) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (event.target && event.target['value']) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            event.target['value'] = this.displayValue;
        }
    }
    isArray(value) {
        return Array.isArray(value);
    }
    get allSelected() {
        return this.optionComponents?.length === this.selectedItems.length;
    }
    get allSearchedSelected() {
        return this.optionComponents
            .filter(option => !option.hidden)
            .every(option => this.selectedItems.some(selected => this.compareWith()(selected[1], option.value())));
    }
    handleSelectAll() {
        this.isOpen = true;
        if (this.searchValue) {
            if (this.allSearchedSelected) {
                this.selectedItems = this.selectedItems.filter(selected => !this.optionComponents
                    .filter(option => !option.hidden)
                    .some(option => this.compareWith()(selected[1], option.value())));
            }
            else {
                const filteredOptions = this.optionComponents
                    .filter(option => !option.hidden)
                    .map(option => [option.text().nativeElement.innerText, option.value()]);
                this.selectedItems = [
                    ...this.selectedItems,
                    ...filteredOptions.filter(filtered => !this.selectedItems.some(selected => this.compareWith()(selected[1], filtered[1])))
                ];
            }
            this.writeValue(this.selectedItems.map(selected => selected[1]));
        }
        else {
            if (this.allSelected) {
                this.selectedItems = [];
            }
            else {
                this.selectedItems = this.optionComponents.map(option => [
                    option.text().nativeElement.innerText,
                    option.value()
                ]);
            }
            this.writeValue(this.selectedItems.map(selected => selected[1]));
        }
    }
    handleSearch(event) {
        event.stopPropagation();
        const input = event.target;
        const value = input.value;
        this.searchValue = value;
        this.optionComponents.forEach(option => {
            const text = option.text().nativeElement.innerText;
            option.hidden = !text.toLowerCase().includes(value.toLowerCase());
        });
    }
    attachEventListeners() {
        // Using setTimeout to ensure DOM is ready
        setTimeout(() => {
            this.optionComponents.forEach(option => {
                const optionEl = option.el.nativeElement.querySelector('div');
                if (optionEl) {
                    // Remove any existing listener first
                    if (optionEl._keydownHandler) {
                        optionEl.removeEventListener('keydown', optionEl._keydownHandler);
                    }
                    // Create and store a reference to the handler
                    const handler = (event) => this.handleOptionsKeyNav(event, option);
                    optionEl._keydownHandler = handler;
                    // Add the event listener
                    optionEl.addEventListener('keydown', handler);
                }
            });
        });
    }
    handleOptionsKeyNav(event, option) {
        if (option.hidden)
            return;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveFocusOptions(option, 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.moveFocusOptions(option, -1);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                event.stopPropagation();
                this.selectOption(option);
                break;
            case 'Escape':
                event.preventDefault();
                this.closePopover();
                this.input().nativeElement.focus();
                break;
            case 'Tab':
                this.closePopover();
                if (event.shiftKey) {
                    event.preventDefault();
                    setTimeout(() => {
                        this.input().nativeElement.focus();
                    });
                }
                break;
        }
    }
    selectOption(option) {
        const innerText = option.text().nativeElement.innerText;
        const value = option.value();
        this.handleOptionSelect([innerText, value]);
        if (!this.multiple()) {
            this.closePopover();
            this.preventReopenOnFocus = true;
            setTimeout(() => {
                this.input().nativeElement.focus();
                setTimeout(() => {
                    this.preventReopenOnFocus = false;
                }, 200);
            }, 50);
        }
    }
    moveFocusOptions(currentOption, offset) {
        const visibleOptions = this.optionComponents.toArray().filter(option => !option.hidden);
        if (visibleOptions.length === 0)
            return;
        const currentIndex = visibleOptions.indexOf(currentOption);
        if (currentIndex === -1)
            return;
        const totalOptions = visibleOptions.length;
        const nextIndex = (currentIndex + offset + totalOptions) % totalOptions;
        const nextOption = visibleOptions[nextIndex];
        if (nextOption) {
            nextOption.focus();
        }
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    subscribeToOptionChanges() {
        this.optionComponents.changes
            .pipe(tap(() => {
            this.resetSubscriptions();
            this.setupSubscriptions();
            this.initSelectedItems();
            this.attachEventListeners();
        }))
            .subscribe();
    }
    closePopover() {
        this.isOpen = false;
        this.searchValue = '';
        this.optionComponents.forEach(option => {
            option.hidden = false;
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: SelectComponent, isStandalone: true, selector: "sag-ui-select", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, multiple: { classPropertyName: "multiple", publicName: "multiple", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, search: { classPropertyName: "search", publicName: "search", isSignal: true, isRequired: false, transformFunction: null }, compareWith: { classPropertyName: "compareWith", publicName: "compareWith", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null }, selectAllText: { classPropertyName: "selectAllText", publicName: "selectAllText", isSignal: true, isRequired: false, transformFunction: null }, searchPlaceholder: { classPropertyName: "searchPlaceholder", publicName: "searchPlaceholder", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, floatLabel: { classPropertyName: "floatLabel", publicName: "floatLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "focusout": "onFocusOut($event)", "keydown": "onKeyDown($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => SelectComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => SelectComponent),
                multi: true
            }
        ], queries: [{ propertyName: "optionComponents", predicate: OptionComponent }], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      style=\"padding-right: 40px\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"floatLabel() ? '' : placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      role=\"combobox\"\n      [attr.aria-label]=\"ariaLabel() || label() + (required() ? ' required' : '')\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || labelId\"\n      [attr.aria-describedby]=\"getAriaDescribedby()\"\n      [attr.aria-expanded]=\"isOpen\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [attr.data-float-label]=\"floatLabel()\"\n      [attr.aria-required]=\"required()\"\n      [attr.aria-invalid]=\"invalid()\"\n      [attr.aria-haspopup]=\"true\"\n      [value]=\"displayValue\"\n      (input)=\"blockChange($event)\"\n      (focus)=\"onFocus()\"\n      (click)=\"onClick()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeyDown($event)\"\n      #input\n      />\n    <label\n      sag-ui-input-label\n      [id]=\"labelId\"\n      [htmlFor]=\"inputId\"\n      [attr.data-float]=\"floatLabel() ? focused || !!value : undefined\"\n      [attr.data-float-label]=\"floatLabel()\"\n      >\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <span\n      class=\"absolute right-5 bottom-[0.8rem] z-10 text-black dark:text-white\"\n      (click)=\"!disabled && togglePopover($event)\"\n      [class.icon-chevron-up]=\"isOpen\"\n      [class.icon-chevron-down]=\"!isOpen\"\n      [class.opacity-50]=\"disabled\"\n    ></span>\n    <div [class]=\"popoverClasses\" [class.hidden]=\"!isOpen\">\n      <div class=\"relative w-full h-full\">\n        @if (search()) {\n          <div [class]=\"searchClasses\">\n            @if (multiple()) {\n              <sag-ui-checkbox\n                [checked]=\"allSelected || allSearchedSelected\"\n                (change)=\"handleSelectAll()\"\n                >\n                {{ selectAllText() }}\n              </sag-ui-checkbox>\n            }\n            <input\n              type=\"text\"\n              [value]=\"searchValue\"\n              [placeholder]=\"searchPlaceholder()\"\n              [class]=\"searchInputClasses\"\n              (input)=\"handleSearch($event)\"\n              />\n          </div>\n        }\n        <ng-content\n          select=\"sag-ui-option\"\n        ></ng-content>\n      </div>\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  }\n  @if (error() && !isArray(error())) {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n", dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: CheckboxComponent, selector: "sag-ui-checkbox", inputs: ["id", "checked", "disabled", "required", "intermediate", "hint", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["change", "click", "focus", "blur", "keydown", "keyup", "mouseenter", "mouseleave"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-select', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => SelectComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => SelectComponent),
                            multi: true
                        }
                    ], imports: [InputDirective, InputLabelDirective, TooltipDirective, CheckboxComponent, InputErrorDirective], template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      style=\"padding-right: 40px\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"floatLabel() ? '' : placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      role=\"combobox\"\n      [attr.aria-label]=\"ariaLabel() || label() + (required() ? ' required' : '')\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || labelId\"\n      [attr.aria-describedby]=\"getAriaDescribedby()\"\n      [attr.aria-expanded]=\"isOpen\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [attr.data-float-label]=\"floatLabel()\"\n      [attr.aria-required]=\"required()\"\n      [attr.aria-invalid]=\"invalid()\"\n      [attr.aria-haspopup]=\"true\"\n      [value]=\"displayValue\"\n      (input)=\"blockChange($event)\"\n      (focus)=\"onFocus()\"\n      (click)=\"onClick()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeyDown($event)\"\n      #input\n      />\n    <label\n      sag-ui-input-label\n      [id]=\"labelId\"\n      [htmlFor]=\"inputId\"\n      [attr.data-float]=\"floatLabel() ? focused || !!value : undefined\"\n      [attr.data-float-label]=\"floatLabel()\"\n      >\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <span\n      class=\"absolute right-5 bottom-[0.8rem] z-10 text-black dark:text-white\"\n      (click)=\"!disabled && togglePopover($event)\"\n      [class.icon-chevron-up]=\"isOpen\"\n      [class.icon-chevron-down]=\"!isOpen\"\n      [class.opacity-50]=\"disabled\"\n    ></span>\n    <div [class]=\"popoverClasses\" [class.hidden]=\"!isOpen\">\n      <div class=\"relative w-full h-full\">\n        @if (search()) {\n          <div [class]=\"searchClasses\">\n            @if (multiple()) {\n              <sag-ui-checkbox\n                [checked]=\"allSelected || allSearchedSelected\"\n                (change)=\"handleSelectAll()\"\n                >\n                {{ selectAllText() }}\n              </sag-ui-checkbox>\n            }\n            <input\n              type=\"text\"\n              [value]=\"searchValue\"\n              [placeholder]=\"searchPlaceholder()\"\n              [class]=\"searchInputClasses\"\n              (input)=\"handleSearch($event)\"\n              />\n          </div>\n        }\n        <ng-content\n          select=\"sag-ui-option\"\n        ></ng-content>\n      </div>\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  }\n  @if (error() && !isArray(error())) {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], optionComponents: [{
                type: ContentChildren,
                args: [OptionComponent]
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const radioBaseStyles = [
    'peer',
    'form-radio',
    'appearance-none',
    'cursor-pointer',
    'transition-all',
    'border-1',
    'border-solid',
    'w-4',
    'h-4',
    'focus:ring-0',
    'focus:ring-transparent',
    'focus:ring-offset-0',
    'focus:outline-none',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1',
    'disabled:cursor-not-allowed'
];
const radioStylesSAG = cva([
    ...radioBaseStyles,
    'border-[#000028]',
    'hover:border-[#005159]',
    'hover:checked:border-[#196269]',
    'focus:checked:border-[#005159]',
    'focus:border-[#000028]',
    'checked:border-[#005159]',
    'bg-white',
    'hover:bg-[#D1FFF2]',
    'hover:checked:bg-[#196269]',
    'focus:checked:bg-[#005159]',
    'checked:bg-[#005159]',
    'checked:bg-radio-sag',
    'hover:checked:bg-radio-hover-sag',
    'focus:checked:bg-radio-hover-sag',
    'disabled:bg-white',
    'disabled:hover:bg-white',
    'disabled:hover:border-[#B3B3BE]',
    'disabled:border-[#B3B3BE]',
    'disabled:checked:bg-radio-disabled-sag',
    'disabled:checked:bg-[#B3B3BE]',
    'disabled:checked:hover:bg-[#B3B3BE]',
    // Dark mode
    'dark:border-white',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:checked:border-[#00E5AA]',
    'dark:focus:checked:border-[#00FFB9]',
    'dark:focus:border-[#FFFFFF]',
    'dark:checked:border-[#00FFB9]',
    'dark:bg-[#000028]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:checked:bg-[#00E5AA]',
    'dark:focus:checked:bg-[#00FFB9]',
    'dark:checked:bg-[#00FFB9]',
    'dark:checked:bg-radio-dark-sag',
    'dark:hover:checked:bg-radio-hover-dark-sag',
    'dark:focus:checked:bg-radio-hover-dark-sag',
    'dark:disabled:bg-[#000028]',
    'dark:disabled:border-[#737389]',
    'dark:disabled:checked:bg-radio-disabled-dark-sag',
    'dark:disabled:checked:bg-[#737389]',
    'dark:disabled:hover:border-[#737389]'
]);
const radioStylesSHS = cva([
    ...radioBaseStyles,
    'border-[#666666]',
    'hover:border-[#666666]',
    'hover:checked:border-[#D45C01]',
    'focus:checked:border-[#D45C01]',
    'focus:border-[#666666]',
    'checked:border-[#D45C01]',
    'bg-white',
    'hover:bg-[#F2F2F2]',
    'hover:checked:bg-[#D45C01]',
    'focus:checked:bg-[#D45C01]',
    'checked:bg-[#D45C01]',
    'disabled:bg-transparent',
    'disabled:hover:bg-transparent',
    'disabled:hover:border-[#C2C2C2]',
    'disabled:border-[#C2C2C2]',
    'disabled:checked:bg-radio-disabled-sag',
    // Dark mode
    'dark:border-[#999999]',
    'dark:hover:border-[#999999]',
    'dark:hover:checked:border-[#D45C01]',
    'dark:focus:checked:border-[#D45C01]',
    'dark:focus:border-[#999999]',
    'dark:checked:border-[#D45C01]',
    'dark:bg-[#1A1A1A]',
    'dark:hover:bg-[#313131]',
    'dark:hover:checked:bg-[#D45C01]',
    'dark:focus:checked:bg-[#D45C01]',
    'dark:checked:bg-[#D45C01]',
    'dark:disabled:bg-transparent',
    'dark:disabled:hover:bg-transparent',
    'dark:disabled:hover:border-[#C2C2C2]',
    'dark:disabled:border-[#C2C2C2]',
    'dark:disabled:checked:bg-radio-disabled-sag'
]);

class RadioDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, radioStylesSAG, radioStylesSHS);
        style(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: RadioDirective, isStandalone: true, selector: "input[sag-ui-radio]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-radio]',
                    standalone: true
                }]
        }] });

const radioLabelBaseStyles = ['cursor-pointer', 'disabled:cursor-not-allowed', 'text-sm', 'select-none'];
const radioLabelStylesSAG = cva([
    ...radioLabelBaseStyles,
    'text-[#000028]',
    'peer-disabled:text-[#B3B3BE]',
    // Dark mode
    'dark:text-white',
    'dark:peer-disabled:text-[#737389]'
]);
const radioLabelStylesSHS = cva([
    ...radioLabelBaseStyles,
    'text-[#1A1A1A]',
    'peer-hover:text-[#1A1A1A]',
    'hover:text-[#1A1A1A]',
    'peer-checked:text-[#1A1A1A]',
    'peer-disabled:text-[#A3A3A3]',
    // Dark mode
    'dark:text-[#E6E6E6]',
    'dark:peer-hover:text-[#E6E6E6]',
    'dark:hover:text-[#E6E6E6]',
    'dark:peer-checked:text-[#E6E6E6]',
    'dark:peer-disabled:text-[#5C5C5C]'
]);

class RadioLabelDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const radioLabelStyles = componentStylesByTheme(this.theme, radioLabelStylesSAG, radioLabelStylesSHS);
        style(this.elementRef, radioLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: RadioLabelDirective, isStandalone: true, selector: "label[sag-ui-radio-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[sag-ui-radio-label]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class RadioComponent {
    cd = inject(ChangeDetectorRef);
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    checked = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    name = input(null);
    value = input();
    required = input(false);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    click = output();
    focus = output();
    blur = output();
    change = output();
    onChangeEvent = (_) => { };
    onTouched = () => { };
    onKeyDown(event) {
        if (event.key === ' ' || event.key === 'Enter') {
            event.stopPropagation();
            event.preventDefault();
            this.selectRadio();
        }
    }
    writeValue(value) {
        this.checked = value == this.value();
        this.cd.markForCheck();
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.cd.markForCheck();
    }
    onChange(event) {
        event.stopPropagation();
        this.selectRadio();
    }
    validate(_) {
        return null;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    selectRadio() {
        if (!this.checked && !this.disabled) {
            this.checked = true;
            const value = typeof this.value() === 'boolean' ? this.value() === 'true' : this.value();
            this.change.emit(value);
            this.onChangeEvent(value);
            this.cd.markForCheck();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: RadioComponent, isStandalone: true, selector: "sag-ui-radio", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, checked: { classPropertyName: "checked", publicName: "checked", isSignal: false, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, name: { classPropertyName: "name", publicName: "name", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { click: "click", focus: "focus", blur: "blur", change: "change" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RadioComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => RadioComponent),
                multi: true
            }
        ], ngImport: i0, template: `
        <div class="flex items-center">
            <input
                type="radio"
                [id]="id()"
                [attr.name]="name()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                [value]="value()"
                [attr.aria-disabled]="disabled"
                [attr.aria-labelledby]="ariaLabelledby() || id()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                (keydown)="onKeyDown($event)"
                sag-ui-radio
            />
            <label [for]="id()" sag-ui-radio-label class="pl-2">
                <ng-content></ng-content>
            </label>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: RadioDirective, selector: "input[sag-ui-radio]" }, { kind: "directive", type: RadioLabelDirective, selector: "label[sag-ui-radio-label]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-radio',
                    standalone: true,
                    template: `
        <div class="flex items-center">
            <input
                type="radio"
                [id]="id()"
                [attr.name]="name()"
                [checked]="checked"
                [disabled]="disabled"
                [required]="required()"
                [value]="value()"
                [attr.aria-disabled]="disabled"
                [attr.aria-labelledby]="ariaLabelledby() || id()"
                [attr.aria-label]="ariaLabel()"
                [attr.aria-describedby]="ariaDescribedby()"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                (keydown)="onKeyDown($event)"
                sag-ui-radio
            />
            <label [for]="id()" sag-ui-radio-label class="pl-2">
                <ng-content></ng-content>
            </label>
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RadioComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => RadioComponent),
                            multi: true
                        }
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    imports: [RadioDirective, RadioLabelDirective]
                }]
        }], propDecorators: { checked: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });

const radioGroupLabelBaseStyles = ['cursor-pointer', 'disabled:cursor-not-allowed', 'text-sm', 'select-none'];
const radioGroupLabelStylesSAG = cva([
    ...radioGroupLabelBaseStyles,
    'text-[#000028]',
    // Dark mode
    'dark:text-[#FFFFFF]'
]);
const radioGroupLabelStylesSHS = cva([
    ...radioGroupLabelBaseStyles,
    'text-[#1A1A1A]',
    // Dark mode
    'dark:text-[#E6E6E6]'
]);

class RadioGroupLabelDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const radioGroupLabelStyles = componentStylesByTheme(this.theme, radioGroupLabelStylesSAG, radioGroupLabelStylesSHS);
        style(this.elementRef, radioGroupLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioGroupLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: RadioGroupLabelDirective, isStandalone: true, selector: "label[sag-ui-radio-group-label]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioGroupLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[sag-ui-radio-group-label]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class RadioGroupComponent {
    required = input(false);
    label = input();
    direction = input('column');
    gap = input(10);
    groupLabelId = `radio-group-label-${Math.random().toString(36).substring(2)}`;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: RadioGroupComponent, isStandalone: true, selector: "sag-ui-radio-group", inputs: { required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, direction: { classPropertyName: "direction", publicName: "direction", isSignal: true, isRequired: false, transformFunction: null }, gap: { classPropertyName: "gap", publicName: "gap", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: `
        <div
            class="flex flex-col gap-2"
            role="radiogroup"
            [attr.aria-labelledby]="groupLabelId"
            [attr.aria-required]="required"
        >
            <label [id]="groupLabelId" sag-ui-radio-group-label
                >{{ label() }}
                @if (required()) {
                    <sup class="text-[#D72339] dark:text-[#FF2640]">*</sup>
                }
            </label>
            <div class="flex" [class.flex-col]="direction() === 'column'" [style]="{ gap: gap() + 'px' }">
                <ng-content></ng-content>
            </div>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: RadioGroupLabelDirective, selector: "label[sag-ui-radio-group-label]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RadioGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-radio-group',
                    standalone: true,
                    template: `
        <div
            class="flex flex-col gap-2"
            role="radiogroup"
            [attr.aria-labelledby]="groupLabelId"
            [attr.aria-required]="required"
        >
            <label [id]="groupLabelId" sag-ui-radio-group-label
                >{{ label() }}
                @if (required()) {
                    <sup class="text-[#D72339] dark:text-[#FF2640]">*</sup>
                }
            </label>
            <div class="flex" [class.flex-col]="direction() === 'column'" [style]="{ gap: gap() + 'px' }">
                <ng-content></ng-content>
            </div>
        </div>
    `,
                    imports: [RadioGroupLabelDirective]
                }]
        }] });

class TableComponentWrapperComponent {
    injector = inject(Injector);
    viewContainer = viewChild('container', { read: ViewContainerRef });
    component = input.required();
    componentInputs = input();
    componentInit = output();
    ngOnInit() {
        this.viewContainer().clear();
        const componentRef = this.viewContainer().createComponent(this.component(), { injector: this.injector });
        this.componentInputs()?.forEach((value, name) => {
            componentRef.setInput(name, value);
        });
        this.componentInit.emit(componentRef.instance);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableComponentWrapperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: TableComponentWrapperComponent, isStandalone: true, selector: "sag-ui-component-wrapper", inputs: { component: { classPropertyName: "component", publicName: "component", isSignal: true, isRequired: true, transformFunction: null }, componentInputs: { classPropertyName: "componentInputs", publicName: "componentInputs", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { componentInit: "componentInit" }, viewQueries: [{ propertyName: "viewContainer", first: true, predicate: ["container"], descendants: true, read: ViewContainerRef, isSignal: true }], ngImport: i0, template: '<ng-container #container></ng-container>', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableComponentWrapperComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-component-wrapper',
                    template: '<ng-container #container></ng-container>',
                    standalone: true
                }]
        }] });

const tableBaseStyles = ['w-full', 'text-sm', 'text-left', 'rtl:text-right'];
const textBaseStylesSAG = ['text-[#000028]', 'dark:text-white'];
const textBaseStylesSHS = ['text-[#1a1a1a]', 'dark:text-white'];
const backgroundBaseStylesSAG = ['bg-white', 'dark:bg-[#000028]'];
const backgroundBaseStylesSHS = ['bg-white', 'dark:bg-black'];
const textStylesSAG = cva([...textBaseStylesSAG]);
const textStylesSHS = cva([...textBaseStylesSHS]);
const tableStylesSAG = cva([...tableBaseStyles, ...textBaseStylesSAG]);
const tableStylesSHS = cva([...tableBaseStyles, ...textBaseStylesSHS]);
const tableRowStylesSAG = cva([
    'cursor-pointer',
    'text-[#000028]',
    'dark:text-white',
    'hover:text-[#005159]',
    'focus:text-[#005159]',
    'focus:outline-none',
    'hover:border-[#005159]',
    'focus:border-[#005159]',
    'border-b-[1px]',
    'border-[#9999A9]',
    'dark:border-[#262648]',
    'dark:hover:bg-[#001F39]',
    'dark:focus:bg-[#001F39]',
    'dark:hover:text-[#00FFB9]',
    'dark:focus:text-[#00FFB9]',
    'dark:border-[#737389]'
]);
const tableRowStylesSHS = cva([
    'cursor-pointer',
    'text-[#1a1a1a]',
    'bg-white',
    'hover:bg-[#f2f2f2]',
    'focus:bg-[#f2f2f2]',
    'focus:outline-none',
    'border-b-[1px]',
    'border-[#b3b3b3]',
    'dark:bg-black',
    'dark:text-white',
    'dark:hover:bg-[#1a1a1a]',
    'dark:focus:bg-[#1a1a1a]',
    'dark:border-[#4d4d4d]'
]);
const tableStickyHeaderCellSAG = cva([...backgroundBaseStylesSAG]);
const tableStickyHeaderCellSHS = cva([...backgroundBaseStylesSHS]);
const tableStickyCellStyleSAG = cva([
    'sticky',
    'z-[5]',
    'dark:hover:bg-[#001F39]',
    'dark:focus:bg-[#001F39]',
    'dark:group-hover:bg-[#001F39]',
    'dark:group-focus:bg-[#001F39]'
]);
const tableStickyCellStyleSHS = cva([
    'sticky',
    'z-[5]',
    'hover:bg-[#f2f2f2]',
    'focus:bg-[#f2f2f2]',
    'group-hover:bg-[#f2f2f2]',
    'dark:hover:bg-[#1a1a1a]',
    'dark:focus:bg-[#1a1a1a]',
    'dark:group-hover:bg-[#1a1a1a]',
    'dark:group-focus:bg-[#1a1a1a]'
]);
const tableScrollbarStyles = cva([
    '[&::-webkit-scrollbar]:w-2',
    '[&::-webkit-scrollbar-track]:bg-gray-100',
    '[&::-webkit-scrollbar-thumb]:bg-gray-300',
    'dark:[&::-webkit-scrollbar-track]:bg-[#3333534D]',
    'dark:[&::-webkit-scrollbar-thumb]:bg-[#333353]'
]);
const tableHeadStyleSAG = cva([
    'bg-white',
    'text-[#000028]',
    'dark:text-white',
    'border-b-[2px]',
    'border-[#9999A9]',
    'dark:border-[#262648]'
]);
const tableHeadStylesSHS = cva([
    'text-[#1a1a1a]',
    'bg-white',
    'border-b-[1px]',
    'border-[#b3b3b3]',
    'dark:bg-black',
    'dark:text-white'
]);
const tableFilterDialogStylesSAG = cva(['text-[#000028]', 'dark:text-white', 'bg-[#ffffff]', 'dark:bg-[#000028]']);
const tableFilterDialogStylesSHS = cva(['text-[#1a1a1a]', 'dark:text-white bg-white dark:bg-black']);
const tableSortButtonStyleSAG = cva(['text-[#00FFB9]']);
const tableSortButtonStyleSHS = cva(['text-[#cf4b00]']);
const tableFilterDialogFocusedSectionStylesSAG = cva(['flex', 'bg-[#f5f5f5]', 'dark:bg-[#333353]']);
const tableFilterDialogFocusedSectionStylesSHS = cva(['flex', 'bg-[#f5f5f5]', 'dark:bg-[#333353]']);
const tableTitleStylesSAG = cva(['text-[#000028]', 'dark:text-white', 'size-4', 'font-bold', 'w-full']);
const tableTitleStylesSHS = cva(['text-[#000028]', 'dark:text-white', 'size-4', 'font-bold', 'w-full']);
const tableSubTitleStylesSAG = cva(['text-[#000028]', 'dark:text-gray-300', 'w-full', 'size-3', 'font-normal']);
const tableSubTitleStylesSHS = cva(['text-[#000028]', 'dark:text-gray-300', 'w-full', 'size-3', 'font-normal']);

class TableFilterDialogComponent {
    theme = inject(THEME_TOKEN);
    allValues = {};
    elementRef = inject(ElementRef);
    columns = input();
    filterState = input();
    data = input();
    focusedColumn = model();
    filterChange = output();
    close = output();
    localSearchTerms = {};
    filteredValues = {};
    showAllValues = {};
    filterableColumns = computed(() => this.columns().filter(col => col.filterable));
    ngOnInit() {
        this.initializeValues();
    }
    ngOnDestroy() {
        document.removeEventListener('click', this.handleOutsideClick);
    }
    ngAfterViewInit() {
        if (this.focusedColumn()) {
            this.focusAndScrollToFilter();
        }
        setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick);
        });
    }
    initializeValues() {
        this.filterableColumns().forEach(column => {
            const values = new Set(this.data().map(item => item[column.key]));
            this.allValues[column.key] = Array.from(values).sort();
            this.filteredValues[column.key] = [...this.allValues[column.key]];
            this.localSearchTerms[column.key] = '';
        });
    }
    getVisibleValues(columnKey, values) {
        if (this.showAllValues[columnKey] || values.length <= 5) {
            return values;
        }
        return values.slice(0, 5);
    }
    toggleShowAll(columnKey) {
        this.showAllValues[columnKey] = !this.showAllValues[columnKey];
    }
    changeFocusedColumn(column) {
        if (this.focusedColumn() === column) {
            this.focusedColumn.set(null);
        }
        else {
            this.focusedColumn.set(column);
            this.focusAndScrollToFilter();
        }
    }
    focusAndScrollToFilter() {
        setTimeout(() => {
            const section = document.getElementById(`filter-section-${this.focusedColumn()}`);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                const input = document.getElementById(`search-input-${this.focusedColumn()}`);
                if (input) {
                    input.focus();
                }
            }
        }, 100);
    }
    filterOptions(columnKey) {
        const searchTerm = this.localSearchTerms[columnKey].toLowerCase();
        this.filteredValues[columnKey] = this.allValues[columnKey].filter(value => value.toString().toLowerCase().includes(searchTerm));
    }
    toggleValue(columnKey, value) {
        const currentValues = this.filterState()[columnKey].selectedValues;
        if (currentValues.has(value)) {
            currentValues.delete(value);
        }
        else {
            currentValues.add(value);
        }
    }
    onClose() {
        this.filterChange.emit(this.filterState());
        this.close.emit();
    }
    handleOutsideClick = (event) => {
        if (this.elementRef && !this.elementRef.nativeElement.contains(event.target)) {
            this.close.emit();
        }
    };
    get tableFilterDialogStyleClasses() {
        return componentStylesByTheme(this.theme, tableFilterDialogStylesSAG, tableFilterDialogStylesSHS)();
    }
    get tableScrollbarStyles() {
        return componentStylesByTheme(this.theme, tableScrollbarStyles, tableScrollbarStyles)();
    }
    get tableFilterDialogFocusedSectionStyles() {
        return componentStylesByTheme(this.theme, tableFilterDialogFocusedSectionStylesSAG, tableFilterDialogFocusedSectionStylesSHS)();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableFilterDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: TableFilterDialogComponent, isStandalone: true, selector: "sag-ui-table-filter-dialog", inputs: { columns: { classPropertyName: "columns", publicName: "columns", isSignal: true, isRequired: false, transformFunction: null }, filterState: { classPropertyName: "filterState", publicName: "filterState", isSignal: true, isRequired: false, transformFunction: null }, data: { classPropertyName: "data", publicName: "data", isSignal: true, isRequired: false, transformFunction: null }, focusedColumn: { classPropertyName: "focusedColumn", publicName: "focusedColumn", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { focusedColumn: "focusedColumnChange", filterChange: "filterChange", close: "close" }, ngImport: i0, template: "<div class=\"h-full\" [class]=\"tableFilterDialogStyleClasses\">\n    <div (click)=\"$event.stopPropagation()\" class=\"h-full flex flex-col\">\n        <div class=\"p-4 sticky top-0 z-10\">\n            <div class=\"flex justify-between items-center\">\n                <h1 class=\"text-lg\">Filters</h1>\n                <sag-ui-button (click)=\"onClose()\" class=\"text-sm\" color=\"secondary\" size=\"sm\">Save view</sag-ui-button>\n            </div>\n        </div>\n\n        <div class=\"h-full gap-2 overflow-y-auto flex flex-col\" [class]=\"tableScrollbarStyles\">\n            @for (column of filterableColumns(); track column.key) {\n                <div class=\"filter-section\" [id]=\"'filter-section-' + column.key\">\n                    <div class=\"px-4\">\n                        <button class=\"flex gap-2 items-center font-semibold\" (click)=\"changeFocusedColumn(column.key)\">\n                            <span\n                                [class]=\"focusedColumn() === column.key ? 'icon-chevron-down' : 'icon-chevron-right'\"\n                            ></span\n                            >{{ column.title }}\n                        </button>\n                    </div>\n                    <div\n                        [class]=\"focusedColumn() === column.key ? tableFilterDialogFocusedSectionStyles : 'hidden'\"\n                        class=\"flex-col gap-2 p-4\"\n                    >\n                        <sag-ui-input\n                            type=\"text\"\n                            [placeholder]=\"'Search ' + column.title\"\n                            [(ngModel)]=\"localSearchTerms[column.key]\"\n                            (ngModelChange)=\"filterOptions(column.key)\"\n                            [id]=\"'search-input-' + column.key\"\n                            [ariaLabel]=\"'Search Input for' + column.title\"\n                        >\n                            <span\n                                ngProjectAs=\"end-decorator\"\n                                class=\"icon-search text-gray-500 pr-4 h-full flex items-center justify-center\"\n                            ></span>\n                        </sag-ui-input>\n                        <div class=\"flex flex-col gap-2 mb-2\">\n                            @for (value of getVisibleValues(column.key, filteredValues[column.key]); track value) {\n                                <div class=\"value-item\">\n                                    <label>\n                                        <sag-ui-checkbox\n                                            [checked]=\"filterState()[column.key]?.selectedValues.has(value)\"\n                                            (change)=\"toggleValue(column.key, value)\"\n                                        >\n                                            {{ value }}</sag-ui-checkbox\n                                        >\n                                    </label>\n                                </div>\n                            }\n\n                            @if (filteredValues[column.key].length > 5) {\n                                <sag-ui-button (click)=\"toggleShowAll(column.key)\" color=\"secondary\" size=\"sm\">\n                                    {{\n                                        showAllValues[column.key]\n                                            ? 'Show Less'\n                                            : 'View All (' + filteredValues[column.key].length + ')'\n                                    }}\n                                </sag-ui-button>\n                            }\n                          @if(filteredValues[column.key].length === 0) {\n                                <div class=\"text-sm text-gray-500\">\n                                    {{ 'No results found' }}\n                                </div>\n                            }\n                        </div>\n                    </div>\n                </div>\n            }\n        </div>\n    </div>\n</div>\n", dependencies: [{ kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ButtonComponent, selector: "sag-ui-button", inputs: ["color", "size", "iconPosition", "disabled", "loading", "square", "type", "style", "class", "icon", "text", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["blur", "focus"] }, { kind: "component", type: InputComponent, selector: "sag-ui-input", inputs: ["id", "disabled", "required", "invalid", "error", "label", "value", "placeholder", "type", "timeFormat", "dateFormat", "lang", "readOnly", "hint", "floatLabel", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["change", "input", "click", "focus", "blur", "keydown", "keyup", "mouseenter", "mouseleave", "dateChange"] }, { kind: "component", type: CheckboxComponent, selector: "sag-ui-checkbox", inputs: ["id", "checked", "disabled", "required", "intermediate", "hint", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["change", "click", "focus", "blur", "keydown", "keyup", "mouseenter", "mouseleave"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableFilterDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-table-filter-dialog', standalone: true, imports: [FormsModule, ButtonComponent, InputComponent, CheckboxComponent], template: "<div class=\"h-full\" [class]=\"tableFilterDialogStyleClasses\">\n    <div (click)=\"$event.stopPropagation()\" class=\"h-full flex flex-col\">\n        <div class=\"p-4 sticky top-0 z-10\">\n            <div class=\"flex justify-between items-center\">\n                <h1 class=\"text-lg\">Filters</h1>\n                <sag-ui-button (click)=\"onClose()\" class=\"text-sm\" color=\"secondary\" size=\"sm\">Save view</sag-ui-button>\n            </div>\n        </div>\n\n        <div class=\"h-full gap-2 overflow-y-auto flex flex-col\" [class]=\"tableScrollbarStyles\">\n            @for (column of filterableColumns(); track column.key) {\n                <div class=\"filter-section\" [id]=\"'filter-section-' + column.key\">\n                    <div class=\"px-4\">\n                        <button class=\"flex gap-2 items-center font-semibold\" (click)=\"changeFocusedColumn(column.key)\">\n                            <span\n                                [class]=\"focusedColumn() === column.key ? 'icon-chevron-down' : 'icon-chevron-right'\"\n                            ></span\n                            >{{ column.title }}\n                        </button>\n                    </div>\n                    <div\n                        [class]=\"focusedColumn() === column.key ? tableFilterDialogFocusedSectionStyles : 'hidden'\"\n                        class=\"flex-col gap-2 p-4\"\n                    >\n                        <sag-ui-input\n                            type=\"text\"\n                            [placeholder]=\"'Search ' + column.title\"\n                            [(ngModel)]=\"localSearchTerms[column.key]\"\n                            (ngModelChange)=\"filterOptions(column.key)\"\n                            [id]=\"'search-input-' + column.key\"\n                            [ariaLabel]=\"'Search Input for' + column.title\"\n                        >\n                            <span\n                                ngProjectAs=\"end-decorator\"\n                                class=\"icon-search text-gray-500 pr-4 h-full flex items-center justify-center\"\n                            ></span>\n                        </sag-ui-input>\n                        <div class=\"flex flex-col gap-2 mb-2\">\n                            @for (value of getVisibleValues(column.key, filteredValues[column.key]); track value) {\n                                <div class=\"value-item\">\n                                    <label>\n                                        <sag-ui-checkbox\n                                            [checked]=\"filterState()[column.key]?.selectedValues.has(value)\"\n                                            (change)=\"toggleValue(column.key, value)\"\n                                        >\n                                            {{ value }}</sag-ui-checkbox\n                                        >\n                                    </label>\n                                </div>\n                            }\n\n                            @if (filteredValues[column.key].length > 5) {\n                                <sag-ui-button (click)=\"toggleShowAll(column.key)\" color=\"secondary\" size=\"sm\">\n                                    {{\n                                        showAllValues[column.key]\n                                            ? 'Show Less'\n                                            : 'View All (' + filteredValues[column.key].length + ')'\n                                    }}\n                                </sag-ui-button>\n                            }\n                          @if(filteredValues[column.key].length === 0) {\n                                <div class=\"text-sm text-gray-500\">\n                                    {{ 'No results found' }}\n                                </div>\n                            }\n                        </div>\n                    </div>\n                </div>\n            }\n        </div>\n    </div>\n</div>\n" }]
        }] });

const chipBaseStyles = [
    'rounded-full',
    'cursor-pointer',
    'select-none',
    'font-semibold',
    'py-2',
    'px-4',
    'inline-flex',
    'items-center',
    'gap-2',
    'text-sm',
    'leading-none',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const chipStylesSAG = cva([
    ...chipBaseStyles,
    'border',
    'border-[#007993]',
    'text-[#007993]',
    'bg-transparent',
    'hover:border-[#005159]',
    'hover:text-[#005159]',
    'hover:bg-[#D1FFF2]',
    'active:bg-transparent',
    'dark:border-[#00CCCC]',
    'dark:text-[#00CCCC]',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:text-[#00FFB9]',
    'dark:hover:bg-[#001F39]',
    'dark:active:bg-transparent'
]);
const chipStylesSelectedSAG = cva([
    ...chipBaseStyles,
    'border',
    'border-[#007993]',
    'text-white',
    'bg-[#007993]',
    'hover:border-[#005159]',
    'hover:text-[#005159]',
    'hover:bg-[#007993]',
    'active:bg-white',
    'dark:border-[#00FFB9]',
    'dark:text-[#000028]',
    'dark:bg-[#00FFB9]',
    'dark:hover:border-[#00E5AA]',
    'dark:hover:text-[#00E5AA]',
    'dark:hover:bg-[#00FFB9]',
    'dark:active:bg-transparent'
]);
const chipStylesSHS = cva([
    ...chipBaseStyles,
    'border',
    'border-[#b3b3b3]',
    'text-[#1a1a1a]',
    'bg-white',
    'hover:bg-[#f2f2f2]',
    'dark:border-[#4d4d4d]',
    'dark:text-[#e6e6e6]',
    'dark:bg-[#1a1a1a]',
    'dark:hover:bg-[#313131]'
]);
const chipStylesSelectedSHS = cva([
    ...chipBaseStyles,
    'border',
    'border-[#cf4c00]',
    'text-[#e6e6e6]',
    'bg-[#cf4c00]',
    'hover:bg-[#ba4400]',
    'hover:border-[#ba4400]'
]);

class ChipDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    selected = input(false);
    constructor() {
        effect(() => {
            const isSelected = this.selected();
            this.updateStyles(isSelected);
        });
    }
    updateStyles(isSelected) {
        const styles = componentStylesByTheme(this.theme, chipStylesSAG, chipStylesSHS);
        const selectedStyles = componentStylesByTheme(this.theme, chipStylesSelectedSAG, chipStylesSelectedSHS);
        const appliedStyles = isSelected ? selectedStyles() : styles();
        updateElementStyles(this.elementRef, appliedStyles);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: ChipDirective, isStandalone: true, selector: "span[sag-ui-chip]", inputs: { selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'span[sag-ui-chip]',
                    standalone: true
                }]
        }], ctorParameters: () => [] });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class ChipComponent {
    selected = input(false);
    removable = input(false);
    style = input('');
    class = input('');
    click = output();
    blur = output();
    focus = output();
    remove = output();
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ChipComponent, isStandalone: true, selector: "sag-ui-chip", inputs: { selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null }, removable: { classPropertyName: "removable", publicName: "removable", isSignal: true, isRequired: false, transformFunction: null }, style: { classPropertyName: "style", publicName: "style", isSignal: true, isRequired: false, transformFunction: null }, class: { classPropertyName: "class", publicName: "class", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { click: "click", blur: "blur", focus: "focus", remove: "remove" }, ngImport: i0, template: `<span
        [class]="class()"
        [style]="style()"
        [selected]="selected()"
        [attr.aria-selected]="selected()"
        (click)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        (blur)="blur.emit($event)"
        (focus)="focus.emit($event)"
        (keydown.space)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        (keydown.enter)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        role="option"
        sag-ui-chip
        tabindex="0"
    >
        <ng-content></ng-content>
        @if (removable()) {
            <span
                class="icon-close focus-visible:outline-[#1491EB]"
                tabindex="0"
                (click)="remove.emit($event)"
                (keydown.enter)="$event.preventDefault(); remove.emit($event)"
                (keydown.space)="$event.preventDefault(); remove.emit($event)"
                role="button"
                [attr.aria-label]="'Remove Chip'"
                (focus)="deleteFocused = true"
                (blur)="deleteFocused = false"
            ></span>
        }
    </span>`, isInline: true, dependencies: [{ kind: "directive", type: ChipDirective, selector: "span[sag-ui-chip]", inputs: ["selected"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-chip',
                    standalone: true,
                    template: `<span
        [class]="class()"
        [style]="style()"
        [selected]="selected()"
        [attr.aria-selected]="selected()"
        (click)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        (blur)="blur.emit($event)"
        (focus)="focus.emit($event)"
        (keydown.space)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        (keydown.enter)="$event.stopPropagation(); $event.preventDefault(); click.emit($event)"
        role="option"
        sag-ui-chip
        tabindex="0"
    >
        <ng-content></ng-content>
        @if (removable()) {
            <span
                class="icon-close focus-visible:outline-[#1491EB]"
                tabindex="0"
                (click)="remove.emit($event)"
                (keydown.enter)="$event.preventDefault(); remove.emit($event)"
                (keydown.space)="$event.preventDefault(); remove.emit($event)"
                role="button"
                [attr.aria-label]="'Remove Chip'"
                (focus)="deleteFocused = true"
                (blur)="deleteFocused = false"
            ></span>
        }
    </span>`,
                    imports: [ChipDirective]
                }]
        }] });

const menuBaseStyles = ['absolute', 'm-0', 'p-0', 'shadow-md'];
const menuStylesSAG = cva([...menuBaseStyles, 'rounded-md', 'bg-[#F3F3F0]', 'dark:bg-[#23233C]']);
const menuStylesSHS = cva([...menuBaseStyles, 'rounded-md', 'bg-white', 'dark:bg-[#1a1a1a]']);

const menuItemBaseStyles = [
    'min-w-[150px]',
    'py-2',
    'px-4',
    'cursor-pointer',
    'flex',
    'items-center',
    'justify-between',
    'focus-visible:outline-[#1491EB]',
    'rounded-md'
];
const menuItemStylesSAG = cva([
    ...menuItemBaseStyles,
    'text-[#000028]',
    'hover:bg-[#E8E8E3]',
    'dark:text-white',
    'dark:hover:bg-[#37374D]'
]);
const menuItemStylesSHS = cva([
    ...menuItemBaseStyles,
    'text-[#1a1a1a]',
    'hover:bg-[#e6e6e6]',
    'dark:text-[#e6e6e6]',
    'dark:hover:bg-[#4d4d4d]'
]);

class MenuTriggerDirective {
    elementRef = inject(ElementRef);
    menuTriggerFor = input('');
    menuAction = input('toggle');
    menuPosition = input('bottom');
    menuOffsetBottom = input(0);
    menuOffsetLeft = input(0);
    menuOffsetRight = input(0);
    menuOffsetTop = input(0);
    menuOpen = false;
    ngOnInit() {
        const triggerElement = this.elementRef.nativeElement;
        triggerElement.setAttribute('popovertarget', this.menuTriggerFor());
        triggerElement.setAttribute('popovertargetaction', this.menuAction());
        const handleToggle = (event) => {
            switch (event.newState) {
                case 'open':
                    this.menuOpen = true;
                    break;
                case 'closed':
                    this.menuOpen = false;
                    break;
                default:
                    this.menuOpen = false;
                    break;
            }
        };
        triggerElement.addEventListener('mouseenter', () => {
            const menuElement = document.getElementById(this.menuTriggerFor());
            if (!menuElement)
                return;
            menuElement.addEventListener('toggle', handleToggle);
            if (this.elementRef.nativeElement.tagName === 'SAG-UI-MENU-ITEM') {
                menuElement.showPopover();
            }
            this.positionMenu(triggerElement, menuElement);
        });
        triggerElement.addEventListener('keydown', (event) => {
            if (event.code !== 'Space' && event.key !== ' ')
                return;
            const menuElement = document.getElementById(this.menuTriggerFor());
            if (!menuElement)
                return;
            menuElement.addEventListener('toggle', handleToggle);
            if (this.elementRef.nativeElement.tagName === 'SAG-UI-MENU-ITEM') {
                switch (this.menuAction()) {
                    case 'hide':
                        menuElement.hidePopover();
                        break;
                    case 'show':
                        menuElement.showPopover();
                        break;
                    case 'toggle':
                        this.menuOpen = !this.menuOpen;
                        this.menuOpen ? menuElement.showPopover() : menuElement.hidePopover();
                        break;
                }
            }
            this.positionMenu(triggerElement, menuElement);
        });
        triggerElement.addEventListener('click', () => {
            const menuElement = document.getElementById(this.menuTriggerFor());
            if (!menuElement)
                return;
            switch (this.menuAction()) {
                case 'hide':
                    menuElement.hidePopover();
                    break;
                case 'show':
                    menuElement.showPopover();
                    break;
                case 'toggle':
                    this.menuOpen = !this.menuOpen;
                    this.menuOpen ? menuElement.showPopover() : menuElement.hidePopover();
                    break;
            }
            this.positionMenu(triggerElement, menuElement);
        });
        window.addEventListener('resize', () => {
            const menuElement = document.getElementById(this.menuTriggerFor());
            if (!menuElement)
                return;
            this.positionMenu(triggerElement, menuElement);
        });
    }
    positionMenu(trigger, menu) {
        const triggerRect = trigger.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const triggerTop = triggerRect.top + window.scrollY;
        const triggerLeft = triggerRect.left + window.scrollX;
        const triggerBottom = triggerRect.bottom + window.scrollY;
        const triggerRight = triggerRect.right + window.scrollX;
        const menuWidth = menuRect.width;
        const menuHeight = menuRect.height;
        const windowWidth = window.innerWidth;
        let left = 0;
        let top = 0;
        const menuOffsetLeft = this.menuOffsetLeft() || 0;
        const menuOffsetRight = this.menuOffsetRight() || 0;
        const menuOffsetTop = this.menuOffsetTop() || 0;
        const menuOffsetBottom = this.menuOffsetBottom() || 0;
        const menuPosition = this.menuPosition();
        switch (menuPosition) {
            case 'top':
                left = triggerLeft + triggerRect.width / 2 - menuWidth / 2 + menuOffsetLeft - menuOffsetRight;
                top = triggerTop - menuHeight + menuOffsetTop - menuOffsetBottom;
                break;
            case 'bottom':
                left = triggerLeft + triggerRect.width / 2 - menuWidth / 2 + menuOffsetLeft - menuOffsetRight;
                top = triggerBottom + menuOffsetTop - menuOffsetBottom;
                break;
            case 'left':
                left = triggerLeft - menuWidth + menuOffsetLeft - menuOffsetRight;
                top = triggerTop + menuOffsetTop - menuOffsetBottom;
                break;
            case 'right':
                left = triggerRight + menuOffsetLeft - menuOffsetRight;
                top = triggerTop + menuOffsetTop - menuOffsetBottom;
                break;
            default:
                console.error('Invalid menu position:', menuPosition);
                return;
        }
        if (left < 0) {
            left = 0;
        }
        else if (left + menuWidth > windowWidth) {
            left = windowWidth - menuWidth;
        }
        if (top < 0) {
            top = 0;
        }
        menu.style.left = `${left}px`;
        menu.style.top = `${top}px`;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuTriggerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: MenuTriggerDirective, isStandalone: true, selector: "[sag-ui-menu-trigger]", inputs: { menuTriggerFor: { classPropertyName: "menuTriggerFor", publicName: "menuTriggerFor", isSignal: true, isRequired: false, transformFunction: null }, menuAction: { classPropertyName: "menuAction", publicName: "menuAction", isSignal: true, isRequired: false, transformFunction: null }, menuPosition: { classPropertyName: "menuPosition", publicName: "menuPosition", isSignal: true, isRequired: false, transformFunction: null }, menuOffsetBottom: { classPropertyName: "menuOffsetBottom", publicName: "menuOffsetBottom", isSignal: true, isRequired: false, transformFunction: null }, menuOffsetLeft: { classPropertyName: "menuOffsetLeft", publicName: "menuOffsetLeft", isSignal: true, isRequired: false, transformFunction: null }, menuOffsetRight: { classPropertyName: "menuOffsetRight", publicName: "menuOffsetRight", isSignal: true, isRequired: false, transformFunction: null }, menuOffsetTop: { classPropertyName: "menuOffsetTop", publicName: "menuOffsetTop", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuTriggerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-menu-trigger]',
                    standalone: true
                }]
        }] });

class MenuItemComponent {
    theme = inject(THEME_TOKEN);
    trigger = inject(MenuTriggerDirective, { optional: true, host: true });
    class = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    click = () => { };
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    mouseenter = () => { };
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    keydown = () => { };
    handleClick(event) {
        if (this.trigger)
            return;
        this.click(event);
    }
    handleMouseEnter(event) {
        if (this.trigger)
            return;
        this.mouseenter(event);
    }
    handleKeyDown(event) {
        if (this.trigger)
            return;
        if (event.code === 'Space') {
            this.keydown(event);
        }
    }
    get styles() {
        return (componentStylesByTheme(this.theme, menuItemStylesSAG, menuItemStylesSHS)() +
            (this.class() ? ` ${this.class()}` : ''));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: MenuItemComponent, isStandalone: true, selector: "sag-ui-menu-item", inputs: { class: { classPropertyName: "class", publicName: "class", isSignal: true, isRequired: false, transformFunction: null }, click: { classPropertyName: "click", publicName: "click", isSignal: false, isRequired: false, transformFunction: null }, mouseenter: { classPropertyName: "mouseenter", publicName: "mouseenter", isSignal: false, isRequired: false, transformFunction: null }, keydown: { classPropertyName: "keydown", publicName: "keydown", isSignal: false, isRequired: false, transformFunction: null } }, ngImport: i0, template: `
        <div
            [class]="styles"
            (click)="handleClick($event)"
            (mouseenter)="handleMouseEnter($event)"
            (keydown)="handleKeyDown($event)"
            tabindex="0"
        >
            <ng-content></ng-content>
            @if (trigger) {
                <span class="icon-chevron-right"></span>
            }
        </div>
    `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-menu-item',
                    standalone: true,
                    template: `
        <div
            [class]="styles"
            (click)="handleClick($event)"
            (mouseenter)="handleMouseEnter($event)"
            (keydown)="handleKeyDown($event)"
            tabindex="0"
        >
            <ng-content></ng-content>
            @if (trigger) {
                <span class="icon-chevron-right"></span>
            }
        </div>
    `
                }]
        }], propDecorators: { click: [{
                type: Input
            }], mouseenter: [{
                type: Input
            }], keydown: [{
                type: Input
            }] } });

class MenuComponent {
    theme = inject(THEME_TOKEN);
    class = input('');
    menu = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    root;
    menuItems = contentChildren(MenuItemComponent);
    nestedMenus = contentChildren(MenuComponent);
    ngAfterContentInit() {
        this.initMenuItems();
        this.initNestedMenus();
    }
    initMenuItems() {
        this.menuItems().forEach(item => {
            item.click = () => {
                const menuElement = document.getElementById(this.root ?? this.menu());
                if (!menuElement)
                    return;
                menuElement.hidePopover();
            };
            item.keydown = () => {
                const menuElement = document.getElementById(this.root ?? this.menu());
                if (!menuElement)
                    return;
                menuElement.hidePopover();
            };
            item.mouseenter = () => {
                this.nestedMenus().forEach(menu => {
                    const menuElement = document.getElementById(menu.menu());
                    if (!menuElement)
                        return;
                    menuElement.hidePopover();
                });
            };
        });
    }
    initNestedMenus() {
        this.nestedMenus().forEach(menu => {
            menu.root = this.root ?? this.menu();
        });
    }
    get styles() {
        return (componentStylesByTheme(this.theme, menuStylesSAG, menuStylesSHS)() +
            (this.class() ? ` ${this.class()}` : ''));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: MenuComponent, isStandalone: true, selector: "sag-ui-menu", inputs: { class: { classPropertyName: "class", publicName: "class", isSignal: true, isRequired: false, transformFunction: null }, menu: { classPropertyName: "menu", publicName: "menu", isSignal: true, isRequired: false, transformFunction: null }, root: { classPropertyName: "root", publicName: "root", isSignal: false, isRequired: false, transformFunction: null } }, queries: [{ propertyName: "menuItems", predicate: MenuItemComponent, isSignal: true }, { propertyName: "nestedMenus", predicate: MenuComponent, isSignal: true }], ngImport: i0, template: `
        <div [class]="styles" popover [id]="menu()">
            <ng-content></ng-content>
        </div>
    `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-menu',
                    standalone: true,
                    template: `
        <div [class]="styles" popover [id]="menu()">
            <ng-content></ng-content>
        </div>
    `
                }]
        }], propDecorators: { root: [{
                type: Input
            }] } });

var SortDirection;
(function (SortDirection) {
    SortDirection["ASC"] = "asc";
    SortDirection["DESC"] = "desc";
})(SortDirection || (SortDirection = {}));

var TABLE_MODE;
(function (TABLE_MODE) {
    TABLE_MODE["SERVER"] = "server";
    TABLE_MODE["CLIENT"] = "client";
})(TABLE_MODE || (TABLE_MODE = {}));

class TableComponent {
    theme = inject(THEME_TOKEN);
    injector = inject(Injector);
    tableMode = input(TABLE_MODE.CLIENT);
    columns = model();
    data = input([]);
    config = input({});
    configDefault = computed(() => ({
        stickyHeader: this.config()?.stickyHeader ?? true,
        height: this.config()?.height ?? '400px',
        searchable: this.config()?.searchable ?? true,
        showFilterChips: this.config()?.showFilterChips ?? true,
        selectable: this.config()?.selectable ?? true,
        multiSelectable: this.config()?.multiSelectable ?? true,
        selectAllRows: this.config()?.selectAllRows ?? true,
        filterable: this.config()?.filterable ?? true,
        severityColor: this.config()?.severityColor ?? '#00FFB9',
        severityCondition: (row) => this.config()?.severityCondition?.(row) ?? false,
        pagination: this.config()?.pagination ?? true,
        paginationTableRowHint: this.config()?.paginationTableRowHint ?? 'Rows per page',
        searchInputFullSize: this.config()?.searchInputFullSize ?? false,
        ...(this.config()?.backgroundColor ? { backgroundColor: this.config()?.backgroundColor } : {})
    }));
    rowMenuActions = input([]);
    title = input('');
    subtitle = input('');
    SortDirection = SortDirection;
    page = model(1);
    pageSizeOptions = model([10, 25, 50, 100]);
    totalItems = model(0);
    paginationPageRange = computed(() => {
        const displayedDataLength = this.tableMode() === TABLE_MODE.CLIENT ? this.filteredAndSortedData().length : this.totalItems();
        const startIndex = (this.page() - 1) * this.paginationPageSize() + 1;
        const endIndex = Math.min(this.page() * this.paginationPageSize(), displayedDataLength);
        return `${startIndex}-${endIndex} of ${displayedDataLength}`;
    });
    isLoading = input(false);
    rowClick = output();
    filterChange = output();
    filterChangeOutput = computed(() => ({
        filterState: this.filterState(),
        searchTerm: this.globalSearchTerm(),
        currentPage: this.page(),
        pageSize: this.paginationPageSize(),
        activeSort: this.sortState()
    }));
    selectedRows = model([]);
    paginationPageSize = model(50);
    sortState = signal({
        column: '',
        direction: ''
    });
    filterState = signal({});
    showFilterDialog = signal(false);
    activeFilters;
    filteredAndSortedData = model([], { alias: 'filteredData' });
    focusedFilterColumn = signal(null);
    globalSearchTerm = model();
    ngOnInit() {
        this.setPaginationPageSizeOptions();
        this.initializeFilterState();
        this.sortStickyColumns();
        this.tableMode() === TABLE_MODE.CLIENT && this.updateClientModeFilteredAndSortedData();
        this.getRows();
        this.initializeColors();
    }
    ngOnChanges(changes) {
        if (changes['data'] || changes['columns']) {
            this.initializeFilterState();
            if (this.tableMode() === TABLE_MODE.CLIENT) {
                this.updateClientModeFilteredAndSortedData();
            }
            else {
                this.filterChange.emit(this.filterChangeOutput());
            }
        }
    }
    initializeColors() {
        if (this.config()?.backgroundColor) {
            document.documentElement.style.setProperty('--light-bg', this.config()?.backgroundColor.light);
            document.documentElement.style.setProperty('--dark-bg', this.config()?.backgroundColor.dark);
        }
    }
    getRows() {
        if (this.configDefault()?.pagination) {
            if (this.tableMode() === TABLE_MODE.CLIENT) {
                const start = (this.page() - 1) * this.paginationPageSize();
                const end = start + this.paginationPageSize();
                return this.filteredAndSortedData().slice(start, end);
            }
            else
                return this.data();
        }
    }
    get rows() {
        if (this.configDefault()?.pagination) {
            return this.getRows();
        }
        else {
            return this.tableMode() === TABLE_MODE.CLIENT ? this.filteredAndSortedData() : this.data();
        }
    }
    emitFilterChange() {
        this.tableMode() === TABLE_MODE.SERVER && this.filterChange.emit(this.filterChangeOutput());
    }
    onPageSizeChange(pageSize) {
        this.paginationPageSize.set(Number(pageSize));
        this.page.set(1);
        this.emitFilterChange();
    }
    previousPage() {
        if (this.page() > 1) {
            this.page.set(this.page() - 1);
            this.emitFilterChange();
        }
    }
    nextPage() {
        const displayedDataLength = this.tableMode() === TABLE_MODE.CLIENT ? this.filteredAndSortedData().length : this.totalItems();
        if (this.page() < Math.ceil(displayedDataLength / this.paginationPageSize())) {
            this.page.set(this.page() + 1);
            this.emitFilterChange();
        }
    }
    toggleSelect(row) {
        if (this.selectedRows().includes(row)) {
            this.selectedRows.update(rows => rows.filter(r => r !== row));
        }
        else {
            this.selectedRows.update(rows => [...rows, row]);
        }
    }
    toggleSelectAll() {
        const data = this.tableMode() === TABLE_MODE.CLIENT ? this.filteredAndSortedData() : this.data();
        if (this.selectedRows().length === data.length) {
            this.selectedRows.set([]);
        }
        else {
            this.selectedRows.set(data);
        }
    }
    toggleFilterDialog() {
        if (this.showFilterDialog()) {
            this.focusedFilterColumn.set(null);
        }
        this.showFilterDialog.update(prev => !prev);
    }
    openFocusedFilterDialog(column) {
        this.focusedFilterColumn.set(column.key);
        this.showFilterDialog.set(true);
    }
    get isAllRowsSelected() {
        const displayedRows = this.tableMode() === TABLE_MODE.CLIENT ? this.filteredAndSortedData().length : this.totalItems();
        return displayedRows === this.selectedRows().length;
    }
    isRowSelected(row) {
        return this.selectedRows().includes(row);
    }
    rowClicked(row) {
        this.rowClick.emit(row);
    }
    handleKeydown(event, row) {
        event.preventDefault();
        if (event.key === 'Enter' || event.key === ' ') {
            this.rowClick.emit(row);
        }
    }
    setPaginationPageSizeOptions() {
        if (this.configDefault()?.pagination) {
            this.pageSizeOptions.update(options => [...new Set([...options, this.paginationPageSize()])].sort((a, b) => a - b));
        }
    }
    initializeFilterState() {
        const filterState = {};
        if (this.configDefault()?.filterable) {
            for (const column of this.columns()) {
                if (column.filterable) {
                    filterState[column.key] = {
                        searchTerm: '',
                        selectedValues: new Set(column.defaultFilter || [])
                    };
                }
            }
        }
        this.filterState.set(filterState);
        this.initializeActiveFilters();
    }
    initializeActiveFilters() {
        this.activeFilters = Object.entries(this.filterState())
            .filter(([_, { searchTerm, selectedValues }]) => searchTerm.trim() !== '' || selectedValues.size > 0)
            .map(([key]) => this.columns().find(c => c.key === key));
    }
    sort(column, direction) {
        if (this.sortState().column === column && this.sortState().direction === direction) {
            this.sortState.set({ column: '', direction: '' });
        }
        else {
            this.sortState.set({ column, direction });
        }
        this.updateData();
    }
    sortStickyColumns() {
        this.columns.update(() => this.columns().sort((a, b) => {
            return (b.sticky ? 1 : 0) - (a.sticky ? 1 : 0);
        }));
    }
    onGlobalSearch() {
        this.updateData();
    }
    updateClientModeFilteredAndSortedData() {
        this.totalItems.set(this.data().length);
        // Apply filters
        let result = this.data().filter(row => {
            // Apply global search
            if (this.globalSearchTerm()) {
                const searchableColumns = this.columns().filter(col => col.searchable);
                const matchesGlobalSearch = searchableColumns.some(col => row[col.key].toString().toLowerCase().includes(this.globalSearchTerm()?.toLowerCase()));
                if (!matchesGlobalSearch)
                    return false;
            }
            // Apply column filters
            return Object.entries(this.filterState()).every(([key, filter]) => {
                if (!filter.searchTerm && filter.selectedValues?.size === 0) {
                    return true;
                }
                const value = row[key];
                return filter.selectedValues?.size === 0 || filter.selectedValues?.has(value);
            });
        });
        // Apply sorting
        if (this.sortState().direction) {
            result = [...result].sort((a, b) => {
                const aValue = a[this.sortState().column];
                const bValue = b[this.sortState().column];
                if (aValue === bValue)
                    return 0;
                const modifier = this.sortState().direction === SortDirection.ASC ? 1 : -1;
                return aValue > bValue ? modifier : -modifier;
            });
        }
        this.filteredAndSortedData.set(result);
    }
    updateData() {
        this.tableMode() === TABLE_MODE.CLIENT ? this.updateClientModeFilteredAndSortedData() : this.emitFilterChange();
    }
    onFilterChange(newFilterState) {
        this.filterState.set(newFilterState);
        this.initializeActiveFilters();
        this.updateData();
    }
    removeFilter(filter, event) {
        event.stopPropagation();
        this.filterState.update(state => ({
            ...state,
            [filter.key]: {
                searchTerm: '',
                selectedValues: new Set()
            }
        }));
        this.initializeActiveFilters();
        this.updateData();
    }
    /* Get left fixed position in px for sticky columns */
    getStickyLeft(column) {
        if (!column.sticky)
            return null;
        let left = 0;
        for (const col of this.columns()) {
            if (col === column)
                break;
            if (col.sticky) {
                left += parseInt(col.width || '100px', 10);
            }
        }
        return `${left}px`;
    }
    /* Style Classes:  */
    get tableBaseStyleClasses() {
        return componentStylesByTheme(this.theme, tableStylesSAG, tableStylesSHS)();
    }
    get tableRowStyleClasses() {
        return componentStylesByTheme(this.theme, tableRowStylesSAG, tableRowStylesSHS)();
    }
    get tableScrollbarStyles() {
        return componentStylesByTheme(this.theme, tableScrollbarStyles, tableScrollbarStyles)();
    }
    get tableHeadStyleClasses() {
        return componentStylesByTheme(this.theme, tableHeadStyleSAG, tableHeadStylesSHS)();
    }
    get textStyleClasses() {
        return componentStylesByTheme(this.theme, textStylesSAG, textStylesSHS)();
    }
    get stickyCellStyleClasses() {
        return componentStylesByTheme(this.theme, tableStickyCellStyleSAG, tableStickyCellStyleSHS)();
    }
    get stickyHeaderCellStyleClasses() {
        return this.config()?.backgroundColor
            ? 'bg-[var(--light-bg)] dark:bg-[var(--dark-bg)]'
            : componentStylesByTheme(this.theme, tableStickyHeaderCellSAG, tableStickyHeaderCellSHS)();
    }
    get sortButtonStyleClasses() {
        return componentStylesByTheme(this.theme, tableSortButtonStyleSAG, tableSortButtonStyleSHS)();
    }
    get tableTitleStyleClasses() {
        return componentStylesByTheme(this.theme, tableTitleStylesSAG, tableTitleStylesSHS)();
    }
    get tableSubTitleStyleClasses() {
        return componentStylesByTheme(this.theme, tableSubTitleStylesSAG, tableSubTitleStylesSHS)();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: TableComponent, isStandalone: true, selector: "sag-ui-table", inputs: { tableMode: { classPropertyName: "tableMode", publicName: "tableMode", isSignal: true, isRequired: false, transformFunction: null }, columns: { classPropertyName: "columns", publicName: "columns", isSignal: true, isRequired: false, transformFunction: null }, data: { classPropertyName: "data", publicName: "data", isSignal: true, isRequired: false, transformFunction: null }, config: { classPropertyName: "config", publicName: "config", isSignal: true, isRequired: false, transformFunction: null }, rowMenuActions: { classPropertyName: "rowMenuActions", publicName: "rowMenuActions", isSignal: true, isRequired: false, transformFunction: null }, title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, subtitle: { classPropertyName: "subtitle", publicName: "subtitle", isSignal: true, isRequired: false, transformFunction: null }, page: { classPropertyName: "page", publicName: "page", isSignal: true, isRequired: false, transformFunction: null }, pageSizeOptions: { classPropertyName: "pageSizeOptions", publicName: "pageSizeOptions", isSignal: true, isRequired: false, transformFunction: null }, totalItems: { classPropertyName: "totalItems", publicName: "totalItems", isSignal: true, isRequired: false, transformFunction: null }, isLoading: { classPropertyName: "isLoading", publicName: "isLoading", isSignal: true, isRequired: false, transformFunction: null }, selectedRows: { classPropertyName: "selectedRows", publicName: "selectedRows", isSignal: true, isRequired: false, transformFunction: null }, paginationPageSize: { classPropertyName: "paginationPageSize", publicName: "paginationPageSize", isSignal: true, isRequired: false, transformFunction: null }, filteredAndSortedData: { classPropertyName: "filteredAndSortedData", publicName: "filteredData", isSignal: true, isRequired: false, transformFunction: null }, globalSearchTerm: { classPropertyName: "globalSearchTerm", publicName: "globalSearchTerm", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { columns: "columnsChange", page: "pageChange", pageSizeOptions: "pageSizeOptionsChange", totalItems: "totalItemsChange", rowClick: "rowClick", filterChange: "filterChange", selectedRows: "selectedRowsChange", paginationPageSize: "paginationPageSizeChange", filteredAndSortedData: "filteredDataChange", globalSearchTerm: "globalSearchTermChange" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"p-4 flex flex-col gap-4\">\n    @if(title() !== '' && subtitle() !== '') {\n      <div class=\"flex flex-col gap-2 w-full\">\n        <span [class]=\"tableTitleStyleClasses\" [attr.aria-label]=\"'Table Title:' + title()\" role=\"heading\" aria-level=\"1\">{{ title() }}</span>\n        <span [class]=\"tableSubTitleStyleClasses\" [attr.aria-label]=\"'Table Subtitle:' + subtitle()\" role=\"heading\" aria-level=\"2\">{{ subtitle() }}</span>\n      </div>\n    }\n    <div class=\"flex justify-between items-center gap-4\" [class]=\"config()?.searchInputFullSize ? 'flex-col' : 'flex-row'\">\n        <div class=\"flex gap-[12px] items-center\" [class]=\"config()?.searchInputFullSize ? 'justify-start w-full' : 'justify-between'\">\n            @if (configDefault()?.filterable) {\n                <div class=\"relative\">\n                    <sag-ui-button\n                        icon=\"filter\"\n                        (click)=\"toggleFilterDialog()\"\n                        aria-label=\"Toggle Filter Dialog\"\n                        role=\"menu\"\n                    >\n                        <span>Filter</span>\n                    </sag-ui-button>\n                    @if (showFilterDialog()) {\n                        <div\n                            class=\"absolute bg-white w-[300px] h-[400px] border border-gray-300 rounded shadow-lg z-[1000] overflow-hidden\"\n                            aria-label=\"Filter Dialog\"\n                        >\n                            <sag-ui-table-filter-dialog\n                                [columns]=\"columns()\"\n                                [data]=\"data()\"\n                                [filterState]=\"filterState()\"\n                                [focusedColumn]=\"focusedFilterColumn()\"\n                                (filterChange)=\"onFilterChange($event)\"\n                                (close)=\"showFilterDialog.set(false)\"\n                            >\n                            </sag-ui-table-filter-dialog>\n                        </div>\n                    }\n                </div>\n                @if (activeFilters.length > 0 && configDefault()?.showFilterChips) {\n                    <div class=\"flex flex-wrap gap-2\" aria-label=\"Active Filters\" role=\"menu\">\n                        @for (filter of activeFilters; track filter.key) {\n                            <sag-ui-chip\n                                class=\"relative\"\n                                (click)=\"openFocusedFilterDialog(filter)\"\n                                [selected]=\"true\"\n                                [removable]=\"true\"\n                                (remove)=\"removeFilter(filter, $event)\"\n                                >{{ filter.title }}</sag-ui-chip\n                            >\n                        }\n                    </div>\n                }\n            }\n        </div>\n        @if (configDefault()?.searchable) {\n            <div class=\"search-section\" [class]=\"config()?.searchInputFullSize ? 'w-full' : ''\">\n                <input\n                    sag-ui-input\n                    type=\"text\"\n                    [(ngModel)]=\"globalSearchTerm\"\n                    (ngModelChange)=\"onGlobalSearch()\"\n                    placeholder=\"Search...\"\n                    aria-label=\"Global Search Input\"\n                    class=\"w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10\"\n                />\n            </div>\n        }\n    </div>\n\n    <div\n        class=\"w-full overflow-auto\"\n        [class]=\"tableScrollbarStyles\"\n        [style.height]=\"configDefault()?.height\"\n        aria-label=\"Table container\"\n        role=\"region\"\n    >\n        <table class=\"w-full border-collapse\" [class]=\"tableBaseStyleClasses\" aria-label=\"Table\" role=\"table\">\n            <thead>\n                <tr\n                    [class]=\"\n                        (configDefault()?.stickyHeader\n                            ? ('sticky top-0 z-10 ' + stickyHeaderCellStyleClasses)\n                            : stickyHeaderCellStyleClasses) +\n                        ' ' +\n                        tableHeadStyleClasses\n                    \"\n                >\n                    @if (configDefault()?.selectable) {\n                        <th\n                            class=\"font-semibold py-3 px-4 text-left\"\n                            role=\"columnheader\"\n                            aria-label=\"Table Header Checkbox\"\n                        >\n                            <div>\n                                @if (configDefault()?.selectAllRows) {\n                                    <sag-ui-checkbox\n                                        [checked]=\"isAllRowsSelected\"\n                                        [attr.aria-checked]=\"filteredAndSortedData().length === selectedRows().length\"\n                                        (change)=\"toggleSelectAll()\"\n                                        role=\"checkbox\"\n                                        ariaLabel=\"Checkbox Select All Rows\"\n                                    ></sag-ui-checkbox>\n                                }\n                            </div>\n                        </th>\n                    }\n                    @for (col of columns(); track col.key) {\n                        <th\n                            [style.width]=\"col.width\"\n                            [style.left]=\"getStickyLeft(col)\"\n                            class=\"py-3 px-4 text-left\"\n                            [class]=\"\n                                col.sticky\n                                    ? 'sticky z-10 ' + stickyHeaderCellStyleClasses\n                                    : stickyHeaderCellStyleClasses\n                            \"\n                            [attr.aria-label]=\"'Column Title:' + col.title\"\n                            role=\"columnheader\"\n                        >\n                            <div class=\"th-content flex items-center gap-2 group\">\n                                <span [attr.aria-label]=\"'Column title' + col.title\" tabindex=\"0\">{{ col.title }}</span>\n                                @if (col.sortable) {\n                                    <div class=\"flex sort-buttons\">\n                                        <button\n                                            class=\"sort-button group\"\n                                            [attr.aria-label]=\"'Sort Button Ascending for column ' + col.title\"\n                                            (click)=\"sort(col.key, SortDirection.ASC)\"\n                                            (keydown.space)=\"$event.preventDefault(); sort(col.key, SortDirection.ASC)\"\n                                        >\n                                            <span\n                                                class=\"icon-arrow-up cursor-pointer transition-all duration-200 group-hover:visible group-focus:visible dark:hover:text-[#00FFB9] dark:focus:text-[#00FFB9]\"\n                                                [class]=\"\n                                                    (sortState().column === col.key &&\n                                                    sortState().direction === SortDirection.ASC\n                                                        ? sortButtonStyleClasses\n                                                        : '') +\n                                                    ' ' +\n                                                    (sortState().column === col.key ? 'visible' : 'invisible')\n                                                \"\n                                                aria-hidden=\"true\"\n                                            ></span>\n                                        </button>\n                                        <button\n                                            class=\"sort-button group\"\n                                            [attr.aria-label]=\"'Sort Button Descending for column ' + col.title\"\n                                            (click)=\"sort(col.key, SortDirection.DESC)\"\n                                            (keydown.space)=\"$event.preventDefault(); sort(col.key, SortDirection.DESC)\"\n                                        >\n                                            <span\n                                                class=\"icon-arrow-down cursor-pointer transition-all duration-200 group-hover:visible group-focus:visible dark:hover:text-[#00FFB9] dark:focus:text-[#00FFB9]\"\n                                                [class]=\"\n                                                    (sortState().column === col.key &&\n                                                    sortState().direction === SortDirection.DESC\n                                                        ? sortButtonStyleClasses\n                                                        : '') +\n                                                    ' ' +\n                                                    (sortState().column === col.key ? 'visible' : 'invisible')\n                                                \"\n                                                aria-hidden=\"true\"\n                                            ></span>\n                                        </button>\n                                    </div>\n                                }\n                            </div>\n                        </th>\n                    }\n                    @if (rowMenuActions().length > 0) {\n                        <th aria-label=\"Column Row Menu Actions\">\n                            <span class=\"sr-only\">Column Row Menu Actions</span>\n                        </th>\n                    }\n                </tr>\n            </thead>\n            <tbody>\n                @for (row of rows; track row[0] + '_' + i; let i = $index) {\n                    <tr (click)=\"rowClicked(row)\" (keydown)=\"handleKeydown($event, row)\" [class]=\"tableRowStyleClasses\" class=\"group\"  tabindex=\"0\">\n                        @if (configDefault()?.selectable) {\n                            <td class=\"relative h-full py-3 px-4 text-left\" role=\"cell\">\n                                @if (configDefault().severityCondition(row)) {\n                                    <div\n                                        class=\"absolute h-[70%] top-[15%] rounded-[10px] left-0 border-l-4\"\n                                        [style.border-left-color]=\"configDefault()?.severityColor\"\n                                    ></div>\n                                }\n                                <sag-ui-checkbox\n                                    [checked]=\"isRowSelected(row)\"\n                                    [attr.aria-checked]=\"isRowSelected(row)\"\n                                    ariaLabel=\"Checkbox for row\"\n                                    (change)=\"toggleSelect(row)\"\n                                    role=\"checkbox\"\n                                ></sag-ui-checkbox>\n                            </td>\n                        }\n                        @for (col of columns(); track col.key; let i = $index) {\n                            <td\n                                [class]=\"col.sticky ? stickyCellStyleClasses + ' ' + stickyHeaderCellStyleClasses : ''\"\n                                [style.width]=\"col.width\"\n                                class=\"relative h-full py-3 px-4 text-left z-0\"\n                                [style.left]=\"getStickyLeft(col)\"\n                                [attr.aria-label]=\"'Row' + row[col.key] + 'Column' + col.title\"\n                            >\n                                @if (\n                                    !configDefault()?.selectable && i === 0 && configDefault().severityCondition(row)\n                                ) {\n                                    <div\n                                        class=\"absolute h-[70%] top-[15%] rounded-[10px] left-0 border-l-4\"\n                                        [style.border-left-color]=\"configDefault()?.severityColor\"\n                                    ></div>\n                                }\n                                @if (col.component) {\n                                    <div>\n                                        <sag-ui-component-wrapper\n                                            [component]=\"col.component\"\n                                            [componentInputs]=\"col?.componentInputs\"\n                                            (componentInit)=\"col?.componentInit($event, row, injector)\"\n                                        ></sag-ui-component-wrapper>\n                                    </div>\n                                } @else {\n                                    {{ row[col.key] }}\n                                }\n                            </td>\n                        }\n                        @if (rowMenuActions().length > 0) {\n                            <td>\n                                <sag-ui-button\n                                    sag-ui-menu-trigger\n                                    [menuTriggerFor]=\"'rowMenu' + row.name\"\n                                    [square]=\"true\"\n                                    icon=\"options-vertical\"\n                                    color=\"tertiary\"\n                                    ariaLabel=\"Row Menu Button\"\n                                ></sag-ui-button>\n                                <sag-ui-menu [menu]=\"'rowMenu' + row.name\">\n                                    @for (action of rowMenuActions(); track action.label) {\n                                        <sag-ui-menu-item\n                                            (click)=\"$event.stopPropagation(); action.onClick(row)\"\n                                            role=\"menuitem\"\n                                        >\n                                            {{ action.label }}\n                                        </sag-ui-menu-item>\n                                    }\n                                </sag-ui-menu>\n                            </td>\n                        }\n                    </tr>\n                }\n            </tbody>\n        </table>\n    </div>\n</div>\n@if (configDefault()?.pagination) {\n    <div class=\"w-full\">\n        <div class=\"flex justify-end items-center gap-2\">\n            <span [class]=\"textStyleClasses\">{{ configDefault()?.paginationTableRowHint }}</span>\n            <sag-ui-select (change)=\"onPageSizeChange($event)\" [value]=\"paginationPageSize()\" class=\"mx-2 w-[80px]\" [attr.aria-label]=\"'Current Page Size:' + paginationPageSize()\">\n                @for (option of pageSizeOptions(); track option) {\n                    <sag-ui-option [value]=\"option\" [attr.aria-label]=\"'Page Size Option: ' + option\">{{ option }}</sag-ui-option>\n                }\n            </sag-ui-select>\n            <span [class]=\"textStyleClasses\" [attr.aria-label]=\"'Pagination Page Range: ' + paginationPageRange()\">{{\n                paginationPageRange()\n            }}</span>\n            <button (click)=\"previousPage()\" aria-label=\"Button previous page\">\n                <span class=\"icon-chevron-left\" [class]=\"textStyleClasses\"></span>\n            </button>\n            <button (click)=\"nextPage()\" aria-label=\"Button next page\">\n                <span class=\"icon-chevron-right\" [class]=\"textStyleClasses\"></span>\n            </button>\n        </div>\n    </div>\n}\n", dependencies: [{ kind: "component", type: TableComponentWrapperComponent, selector: "sag-ui-component-wrapper", inputs: ["component", "componentInputs"], outputs: ["componentInit"] }, { kind: "component", type: CheckboxComponent, selector: "sag-ui-checkbox", inputs: ["id", "checked", "disabled", "required", "intermediate", "hint", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["change", "click", "focus", "blur", "keydown", "keyup", "mouseenter", "mouseleave"] }, { kind: "component", type: ButtonComponent, selector: "sag-ui-button", inputs: ["color", "size", "iconPosition", "disabled", "loading", "square", "type", "style", "class", "icon", "text", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["blur", "focus"] }, { kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: ChipComponent, selector: "sag-ui-chip", inputs: ["selected", "removable", "style", "class"], outputs: ["click", "blur", "focus", "remove"] }, { kind: "component", type: TableFilterDialogComponent, selector: "sag-ui-table-filter-dialog", inputs: ["columns", "filterState", "data", "focusedColumn"], outputs: ["focusedColumnChange", "filterChange", "close"] }, { kind: "component", type: SelectComponent, selector: "sag-ui-select", inputs: ["id", "placeholder", "multiple", "required", "disabled", "readOnly", "label", "value", "invalid", "error", "search", "compareWith", "ariaLabel", "ariaLabelledby", "ariaDescribedby", "selectAllText", "searchPlaceholder", "hint", "floatLabel"], outputs: ["change"] }, { kind: "component", type: OptionComponent, selector: "sag-ui-option", inputs: ["value", "selected", "multiple", "hidden"], outputs: ["select"] }, { kind: "component", type: MenuComponent, selector: "sag-ui-menu", inputs: ["class", "menu", "root"] }, { kind: "component", type: MenuItemComponent, selector: "sag-ui-menu-item", inputs: ["class", "click", "mouseenter", "keydown"] }, { kind: "directive", type: MenuTriggerDirective, selector: "[sag-ui-menu-trigger]", inputs: ["menuTriggerFor", "menuAction", "menuPosition", "menuOffsetBottom", "menuOffsetLeft", "menuOffsetRight", "menuOffsetTop"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-table', standalone: true, imports: [
                        TableComponentWrapperComponent,
                        CheckboxComponent,
                        ButtonComponent,
                        InputDirective,
                        FormsModule,
                        ChipComponent,
                        TableFilterDialogComponent,
                        SelectComponent,
                        OptionComponent,
                        MenuComponent,
                        MenuItemComponent,
                        MenuTriggerDirective
                    ], template: "<div class=\"p-4 flex flex-col gap-4\">\n    @if(title() !== '' && subtitle() !== '') {\n      <div class=\"flex flex-col gap-2 w-full\">\n        <span [class]=\"tableTitleStyleClasses\" [attr.aria-label]=\"'Table Title:' + title()\" role=\"heading\" aria-level=\"1\">{{ title() }}</span>\n        <span [class]=\"tableSubTitleStyleClasses\" [attr.aria-label]=\"'Table Subtitle:' + subtitle()\" role=\"heading\" aria-level=\"2\">{{ subtitle() }}</span>\n      </div>\n    }\n    <div class=\"flex justify-between items-center gap-4\" [class]=\"config()?.searchInputFullSize ? 'flex-col' : 'flex-row'\">\n        <div class=\"flex gap-[12px] items-center\" [class]=\"config()?.searchInputFullSize ? 'justify-start w-full' : 'justify-between'\">\n            @if (configDefault()?.filterable) {\n                <div class=\"relative\">\n                    <sag-ui-button\n                        icon=\"filter\"\n                        (click)=\"toggleFilterDialog()\"\n                        aria-label=\"Toggle Filter Dialog\"\n                        role=\"menu\"\n                    >\n                        <span>Filter</span>\n                    </sag-ui-button>\n                    @if (showFilterDialog()) {\n                        <div\n                            class=\"absolute bg-white w-[300px] h-[400px] border border-gray-300 rounded shadow-lg z-[1000] overflow-hidden\"\n                            aria-label=\"Filter Dialog\"\n                        >\n                            <sag-ui-table-filter-dialog\n                                [columns]=\"columns()\"\n                                [data]=\"data()\"\n                                [filterState]=\"filterState()\"\n                                [focusedColumn]=\"focusedFilterColumn()\"\n                                (filterChange)=\"onFilterChange($event)\"\n                                (close)=\"showFilterDialog.set(false)\"\n                            >\n                            </sag-ui-table-filter-dialog>\n                        </div>\n                    }\n                </div>\n                @if (activeFilters.length > 0 && configDefault()?.showFilterChips) {\n                    <div class=\"flex flex-wrap gap-2\" aria-label=\"Active Filters\" role=\"menu\">\n                        @for (filter of activeFilters; track filter.key) {\n                            <sag-ui-chip\n                                class=\"relative\"\n                                (click)=\"openFocusedFilterDialog(filter)\"\n                                [selected]=\"true\"\n                                [removable]=\"true\"\n                                (remove)=\"removeFilter(filter, $event)\"\n                                >{{ filter.title }}</sag-ui-chip\n                            >\n                        }\n                    </div>\n                }\n            }\n        </div>\n        @if (configDefault()?.searchable) {\n            <div class=\"search-section\" [class]=\"config()?.searchInputFullSize ? 'w-full' : ''\">\n                <input\n                    sag-ui-input\n                    type=\"text\"\n                    [(ngModel)]=\"globalSearchTerm\"\n                    (ngModelChange)=\"onGlobalSearch()\"\n                    placeholder=\"Search...\"\n                    aria-label=\"Global Search Input\"\n                    class=\"w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10\"\n                />\n            </div>\n        }\n    </div>\n\n    <div\n        class=\"w-full overflow-auto\"\n        [class]=\"tableScrollbarStyles\"\n        [style.height]=\"configDefault()?.height\"\n        aria-label=\"Table container\"\n        role=\"region\"\n    >\n        <table class=\"w-full border-collapse\" [class]=\"tableBaseStyleClasses\" aria-label=\"Table\" role=\"table\">\n            <thead>\n                <tr\n                    [class]=\"\n                        (configDefault()?.stickyHeader\n                            ? ('sticky top-0 z-10 ' + stickyHeaderCellStyleClasses)\n                            : stickyHeaderCellStyleClasses) +\n                        ' ' +\n                        tableHeadStyleClasses\n                    \"\n                >\n                    @if (configDefault()?.selectable) {\n                        <th\n                            class=\"font-semibold py-3 px-4 text-left\"\n                            role=\"columnheader\"\n                            aria-label=\"Table Header Checkbox\"\n                        >\n                            <div>\n                                @if (configDefault()?.selectAllRows) {\n                                    <sag-ui-checkbox\n                                        [checked]=\"isAllRowsSelected\"\n                                        [attr.aria-checked]=\"filteredAndSortedData().length === selectedRows().length\"\n                                        (change)=\"toggleSelectAll()\"\n                                        role=\"checkbox\"\n                                        ariaLabel=\"Checkbox Select All Rows\"\n                                    ></sag-ui-checkbox>\n                                }\n                            </div>\n                        </th>\n                    }\n                    @for (col of columns(); track col.key) {\n                        <th\n                            [style.width]=\"col.width\"\n                            [style.left]=\"getStickyLeft(col)\"\n                            class=\"py-3 px-4 text-left\"\n                            [class]=\"\n                                col.sticky\n                                    ? 'sticky z-10 ' + stickyHeaderCellStyleClasses\n                                    : stickyHeaderCellStyleClasses\n                            \"\n                            [attr.aria-label]=\"'Column Title:' + col.title\"\n                            role=\"columnheader\"\n                        >\n                            <div class=\"th-content flex items-center gap-2 group\">\n                                <span [attr.aria-label]=\"'Column title' + col.title\" tabindex=\"0\">{{ col.title }}</span>\n                                @if (col.sortable) {\n                                    <div class=\"flex sort-buttons\">\n                                        <button\n                                            class=\"sort-button group\"\n                                            [attr.aria-label]=\"'Sort Button Ascending for column ' + col.title\"\n                                            (click)=\"sort(col.key, SortDirection.ASC)\"\n                                            (keydown.space)=\"$event.preventDefault(); sort(col.key, SortDirection.ASC)\"\n                                        >\n                                            <span\n                                                class=\"icon-arrow-up cursor-pointer transition-all duration-200 group-hover:visible group-focus:visible dark:hover:text-[#00FFB9] dark:focus:text-[#00FFB9]\"\n                                                [class]=\"\n                                                    (sortState().column === col.key &&\n                                                    sortState().direction === SortDirection.ASC\n                                                        ? sortButtonStyleClasses\n                                                        : '') +\n                                                    ' ' +\n                                                    (sortState().column === col.key ? 'visible' : 'invisible')\n                                                \"\n                                                aria-hidden=\"true\"\n                                            ></span>\n                                        </button>\n                                        <button\n                                            class=\"sort-button group\"\n                                            [attr.aria-label]=\"'Sort Button Descending for column ' + col.title\"\n                                            (click)=\"sort(col.key, SortDirection.DESC)\"\n                                            (keydown.space)=\"$event.preventDefault(); sort(col.key, SortDirection.DESC)\"\n                                        >\n                                            <span\n                                                class=\"icon-arrow-down cursor-pointer transition-all duration-200 group-hover:visible group-focus:visible dark:hover:text-[#00FFB9] dark:focus:text-[#00FFB9]\"\n                                                [class]=\"\n                                                    (sortState().column === col.key &&\n                                                    sortState().direction === SortDirection.DESC\n                                                        ? sortButtonStyleClasses\n                                                        : '') +\n                                                    ' ' +\n                                                    (sortState().column === col.key ? 'visible' : 'invisible')\n                                                \"\n                                                aria-hidden=\"true\"\n                                            ></span>\n                                        </button>\n                                    </div>\n                                }\n                            </div>\n                        </th>\n                    }\n                    @if (rowMenuActions().length > 0) {\n                        <th aria-label=\"Column Row Menu Actions\">\n                            <span class=\"sr-only\">Column Row Menu Actions</span>\n                        </th>\n                    }\n                </tr>\n            </thead>\n            <tbody>\n                @for (row of rows; track row[0] + '_' + i; let i = $index) {\n                    <tr (click)=\"rowClicked(row)\" (keydown)=\"handleKeydown($event, row)\" [class]=\"tableRowStyleClasses\" class=\"group\"  tabindex=\"0\">\n                        @if (configDefault()?.selectable) {\n                            <td class=\"relative h-full py-3 px-4 text-left\" role=\"cell\">\n                                @if (configDefault().severityCondition(row)) {\n                                    <div\n                                        class=\"absolute h-[70%] top-[15%] rounded-[10px] left-0 border-l-4\"\n                                        [style.border-left-color]=\"configDefault()?.severityColor\"\n                                    ></div>\n                                }\n                                <sag-ui-checkbox\n                                    [checked]=\"isRowSelected(row)\"\n                                    [attr.aria-checked]=\"isRowSelected(row)\"\n                                    ariaLabel=\"Checkbox for row\"\n                                    (change)=\"toggleSelect(row)\"\n                                    role=\"checkbox\"\n                                ></sag-ui-checkbox>\n                            </td>\n                        }\n                        @for (col of columns(); track col.key; let i = $index) {\n                            <td\n                                [class]=\"col.sticky ? stickyCellStyleClasses + ' ' + stickyHeaderCellStyleClasses : ''\"\n                                [style.width]=\"col.width\"\n                                class=\"relative h-full py-3 px-4 text-left z-0\"\n                                [style.left]=\"getStickyLeft(col)\"\n                                [attr.aria-label]=\"'Row' + row[col.key] + 'Column' + col.title\"\n                            >\n                                @if (\n                                    !configDefault()?.selectable && i === 0 && configDefault().severityCondition(row)\n                                ) {\n                                    <div\n                                        class=\"absolute h-[70%] top-[15%] rounded-[10px] left-0 border-l-4\"\n                                        [style.border-left-color]=\"configDefault()?.severityColor\"\n                                    ></div>\n                                }\n                                @if (col.component) {\n                                    <div>\n                                        <sag-ui-component-wrapper\n                                            [component]=\"col.component\"\n                                            [componentInputs]=\"col?.componentInputs\"\n                                            (componentInit)=\"col?.componentInit($event, row, injector)\"\n                                        ></sag-ui-component-wrapper>\n                                    </div>\n                                } @else {\n                                    {{ row[col.key] }}\n                                }\n                            </td>\n                        }\n                        @if (rowMenuActions().length > 0) {\n                            <td>\n                                <sag-ui-button\n                                    sag-ui-menu-trigger\n                                    [menuTriggerFor]=\"'rowMenu' + row.name\"\n                                    [square]=\"true\"\n                                    icon=\"options-vertical\"\n                                    color=\"tertiary\"\n                                    ariaLabel=\"Row Menu Button\"\n                                ></sag-ui-button>\n                                <sag-ui-menu [menu]=\"'rowMenu' + row.name\">\n                                    @for (action of rowMenuActions(); track action.label) {\n                                        <sag-ui-menu-item\n                                            (click)=\"$event.stopPropagation(); action.onClick(row)\"\n                                            role=\"menuitem\"\n                                        >\n                                            {{ action.label }}\n                                        </sag-ui-menu-item>\n                                    }\n                                </sag-ui-menu>\n                            </td>\n                        }\n                    </tr>\n                }\n            </tbody>\n        </table>\n    </div>\n</div>\n@if (configDefault()?.pagination) {\n    <div class=\"w-full\">\n        <div class=\"flex justify-end items-center gap-2\">\n            <span [class]=\"textStyleClasses\">{{ configDefault()?.paginationTableRowHint }}</span>\n            <sag-ui-select (change)=\"onPageSizeChange($event)\" [value]=\"paginationPageSize()\" class=\"mx-2 w-[80px]\" [attr.aria-label]=\"'Current Page Size:' + paginationPageSize()\">\n                @for (option of pageSizeOptions(); track option) {\n                    <sag-ui-option [value]=\"option\" [attr.aria-label]=\"'Page Size Option: ' + option\">{{ option }}</sag-ui-option>\n                }\n            </sag-ui-select>\n            <span [class]=\"textStyleClasses\" [attr.aria-label]=\"'Pagination Page Range: ' + paginationPageRange()\">{{\n                paginationPageRange()\n            }}</span>\n            <button (click)=\"previousPage()\" aria-label=\"Button previous page\">\n                <span class=\"icon-chevron-left\" [class]=\"textStyleClasses\"></span>\n            </button>\n            <button (click)=\"nextPage()\" aria-label=\"Button next page\">\n                <span class=\"icon-chevron-right\" [class]=\"textStyleClasses\"></span>\n            </button>\n        </div>\n    </div>\n}\n" }]
        }] });

class FileDropDirective {
    elementRef = inject(ElementRef);
    renderer = inject(Renderer2);
    accept = '*/*';
    ngOnInit() {
        const containerStyles = ['absolute', 'top-0', 'left-0', 'w-full', 'h-full', 'cursor-pointer'];
        const container = this.renderer.createElement('label');
        const input = this.elementRef.nativeElement;
        this.accept = input.accept;
        style(this.elementRef, 'hidden peer');
        this.renderer.setAttribute(container, 'class', containerStyles.join(' '));
        this.renderer.setAttribute(container, 'for', input.id);
        this.renderer.listen(container, 'dragover', event => {
            event.preventDefault();
            event.stopPropagation();
        });
        this.renderer.listen(container, 'dragenter', event => {
            event.preventDefault();
            event.stopPropagation();
        });
        this.renderer.listen(container, 'drop', event => {
            event.preventDefault();
            event.stopPropagation();
            const files = Array.from(event.dataTransfer.files);
            const acceptedFiles = files.filter(file => this.isFileAccepted(file));
            if (acceptedFiles.length > 0) {
                const dataTransfer = new DataTransfer();
                acceptedFiles.forEach(file => dataTransfer.items.add(file));
                input.files = dataTransfer.files;
                const changeEvent = new Event('change', { bubbles: true });
                input.dispatchEvent(changeEvent);
            }
        });
        this.renderer.insertBefore(this.elementRef.nativeElement.parentElement, container, this.elementRef.nativeElement);
    }
    isFileAccepted(file) {
        if (this.accept === '*' || this.accept === '*/*' || this.accept === '' || !this.accept) {
            return true; // Accept all files
        }
        const acceptedTypes = this.accept.split(',').map(type => type.trim());
        return acceptedTypes.some(type => {
            if (type.startsWith('.')) {
                // Match by file extension
                return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            else {
                // Match by MIME type
                const [typeMain, typeSubtype] = type.split('/');
                const [fileMain, fileSubtype] = file.type.split('/');
                return ((typeMain === '*' || typeMain === fileMain) && (typeSubtype === '*' || typeSubtype === fileSubtype));
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: FileDropDirective, isStandalone: true, selector: "input[sag-ui-filedrop]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-filedrop]',
                    standalone: true
                }]
        }] });

const fileDropZoneBaseStyles = [
    'relative',
    'border',
    'border-2',
    'border-dashed',
    'flex',
    'items-center',
    'justify-center',
    'flex-col',
    'gap-2',
    'w-full',
    'rounded',
    'p-4',
    'cursor-pointer',
    'select-none',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1',
    'h-[250px]'
];
const fileDropZoneStylesSAG = cva([
    ...fileDropZoneBaseStyles,
    'border-[#007993]',
    'text-[#007993]',
    'hover:bg-[#D1FFF2]',
    'hover:border-[#005159]',
    'hover:text-[#005159]',
    'dark:border-[#00CCCC]',
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:text-[#00FFB9]'
]);
const fileDropZoneDisabledStylesSAG = cva([
    ...fileDropZoneBaseStyles,
    'border-[#7D8099]',
    'text-[#7D8099]',
    'cursor-not-allowed',
    'dark:border-[#737389]',
    'dark:text-[#737389]',
    'dark:hover:bg-transparent'
]);
const fileDropZoneStylesSHS = cva([
    ...fileDropZoneBaseStyles,
    'border-[#cf4c00]',
    'text-[#cf4c00]',
    'hover:bg-[#ec660211]',
    'hover:border-[#cf4b00]',
    'hover:text-[#cf4c00]'
]);
const fileDropZoneDisabledStylesSHS = cva([
    ...fileDropZoneBaseStyles,
    'border-[#c2c2c2]',
    'text-[#c2c2c2]',
    'bg-transparent',
    'dark:border-[#3d3d3d]',
    'dark:text-[#3d3d3d]',
    'dark:bg-transparent',
    'cursor-not-allowed'
]);

class FileDropZoneDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    disabled = input(false);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, fileDropZoneStylesSAG, fileDropZoneStylesSHS);
        const disabledStyles = componentStylesByTheme(this.theme, fileDropZoneDisabledStylesSAG, fileDropZoneDisabledStylesSHS);
        style(this.elementRef, this.disabled() ? disabledStyles() : inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropZoneDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: FileDropZoneDirective, isStandalone: true, selector: "div[sag-ui-filedrop-zone]", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropZoneDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'div[sag-ui-filedrop-zone]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class FileDropComponent {
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    accept = input('*/*');
    multiple = input(false);
    ariaLabel = input('File Upload');
    // eslint-disable-next-line @angular-eslint/no-output-native
    change = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    click = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    focus = output();
    // eslint-disable-next-line @angular-eslint/no-output-native
    blur = output();
    files = null;
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(files) {
        this.files = files;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(event) {
        event.stopPropagation();
        event.preventDefault();
        const input = event.target;
        this.files = input.files;
        this.onChangeEvent(this.files);
        this.change.emit(this.files);
    }
    validate(_) {
        return this.required() && !this.files ? { required: true } : null;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    triggerFileInput(fileInput) {
        if (!this.disabled) {
            fileInput.click();
        }
    }
    handleKeyDown(event, fileInput) {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            event.stopPropagation();
            this.triggerFileInput(fileInput);
        }
    }
    handleClick(event, fileInput) {
        event.preventDefault();
        event.stopPropagation();
        this.click.emit(event);
        this.triggerFileInput(fileInput);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: FileDropComponent, isStandalone: true, selector: "sag-ui-filedrop", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, accept: { classPropertyName: "accept", publicName: "accept", isSignal: true, isRequired: false, transformFunction: null }, multiple: { classPropertyName: "multiple", publicName: "multiple", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => FileDropComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => FileDropComponent),
                multi: true
            }
        ], ngImport: i0, template: `
        <div
            sag-ui-filedrop-zone
            [disabled]="disabled"
            tabindex="0"
            (keydown)="handleKeyDown($event, fileInput)"
            (click)="handleClick($event, fileInput)"
            role="region"
            [attr.aria-disabled]="disabled"
            [attr.aria-required]="required()"
            [attr.aria-label]="ariaLabel() + (required() ? ' required' : '')"
        >
            <span class="sr-only">{{ disabled ? 'Disabled' : 'Enter or Space to open file selector' }}</span>
            <div [attr.aria-hidden]="disabled">
                <ng-content></ng-content>
            </div>
            <input
                #fileInput
                type="file"
                [id]="id()"
                [disabled]="disabled"
                [required]="required()"
                [multiple]="multiple()"
                tabindex="-1"
                [attr.accept]="accept()"
                (change)="onChange($event)"
                (click)="$event.stopPropagation()"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                sag-ui-filedrop
            />
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: FileDropZoneDirective, selector: "div[sag-ui-filedrop-zone]", inputs: ["disabled"] }, { kind: "directive", type: FileDropDirective, selector: "input[sag-ui-filedrop]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FileDropComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-filedrop',
                    standalone: true,
                    template: `
        <div
            sag-ui-filedrop-zone
            [disabled]="disabled"
            tabindex="0"
            (keydown)="handleKeyDown($event, fileInput)"
            (click)="handleClick($event, fileInput)"
            role="region"
            [attr.aria-disabled]="disabled"
            [attr.aria-required]="required()"
            [attr.aria-label]="ariaLabel() + (required() ? ' required' : '')"
        >
            <span class="sr-only">{{ disabled ? 'Disabled' : 'Enter or Space to open file selector' }}</span>
            <div [attr.aria-hidden]="disabled">
                <ng-content></ng-content>
            </div>
            <input
                #fileInput
                type="file"
                [id]="id()"
                [disabled]="disabled"
                [required]="required()"
                [multiple]="multiple()"
                tabindex="-1"
                [attr.accept]="accept()"
                (change)="onChange($event)"
                (click)="$event.stopPropagation()"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                sag-ui-filedrop
            />
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => FileDropComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => FileDropComponent),
                            multi: true
                        }
                    ],
                    imports: [FileDropZoneDirective, FileDropDirective]
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }] } });

const avatarBaseStyles = [
    'rounded-full',
    'font-semibold',
    'text-[15px]',
    'w-[46px]',
    'aspect-square',
    'flex',
    'items-center',
    'justify-center',
    'text-center',
    'select-none',
    'outline-none', // Remove default outline
    'transition-all'
];
const avatarStylesSAG = cva([
    ...avatarBaseStyles,
    'border-2',
    'border-solid',
    'border-[#4C4C68]',
    'bg-[#EBF7F8]',
    'text-[#000028]',
    // Dark mode
    'dark:bg-[#00183B]',
    'dark:text-[#B3B3BE]',
    'dark:border-[#B3B3BE]'
]);
const avatarStylesSHS = cva([
    ...avatarBaseStyles,
    'border-2',
    'border-solid',
    'border-[#666666]',
    'bg-[#ededed]',
    'text-[#1a1a1a]',
    // Dark mode
    'dark:bg-[#1a1a1a]',
    'dark:text-[#e6e6e6]',
    'dark:border-[#999999]'
]);
// Styles for the image inside the avatar
const avatarImageStyles = ['w-full', 'h-full', 'object-cover', 'rounded-full'];

class AvatarDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    imageUrl = input(null);
    altText = input('');
    ngOnInit() {
        this.applyStyles();
        // Remove focusability
        this.elementRef.nativeElement.removeAttribute('tabindex');
        // Set role for better accessibility
        this.elementRef.nativeElement.setAttribute('role', 'img');
        this.elementRef.nativeElement.setAttribute('aria-label', this.altText() || 'User avatar');
    }
    ngOnChanges(changes) {
        if ('imageUrl' in changes || 'altText' in changes) {
            this.applyStyles();
        }
    }
    applyStyles() {
        const styles = componentStylesByTheme(this.theme, avatarStylesSAG, avatarStylesSHS);
        style(this.elementRef, styles());
        // Store original content if not already stored
        if (!this.elementRef.nativeElement.dataset.originalContent) {
            this.elementRef.nativeElement.dataset.originalContent = this.elementRef.nativeElement.innerHTML;
        }
        const originalContent = this.elementRef.nativeElement.dataset.originalContent || '';
        this.elementRef.nativeElement.innerHTML = '';
        if (this.imageUrl()) {
            const imgElement = document.createElement('img');
            imgElement.src = this.imageUrl();
            imgElement.alt = this.altText() || 'User avatar'; // Provide descriptive alt text
            avatarImageStyles.forEach(className => imgElement.classList.add(className));
            this.elementRef.nativeElement.appendChild(imgElement);
        }
        else if (originalContent.trim()) {
            // Show initials directly
            this.elementRef.nativeElement.textContent = originalContent.trim();
        }
        else {
            // Fallback: icon
            const iconSpan = document.createElement('span');
            iconSpan.classList.add('icon-user');
            iconSpan.setAttribute('role', 'img');
            iconSpan.setAttribute('aria-label', this.altText() || 'User avatar');
            this.elementRef.nativeElement.appendChild(iconSpan);
        }
        // Remove unnecessary accessibility clutter
        this.elementRef.nativeElement.removeAttribute('tabindex');
        this.elementRef.nativeElement.removeAttribute('role');
        this.elementRef.nativeElement.removeAttribute('aria-label');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: AvatarDirective, isStandalone: true, selector: "[sag-ui-avatar]", inputs: { imageUrl: { classPropertyName: "imageUrl", publicName: "imageUrl", isSignal: true, isRequired: false, transformFunction: null }, altText: { classPropertyName: "altText", publicName: "altText", isSignal: true, isRequired: false, transformFunction: null } }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-avatar]',
                    standalone: true
                }]
        }] });

class AvatarBaseDirective {
    elementRef = inject(ElementRef);
    ngOnInit() {
        style(this.elementRef, 'flex flex-row items-center justify-start gap-2.5');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: AvatarBaseDirective, isStandalone: true, selector: "[sag-ui-avatar-base]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarBaseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-avatar-base]',
                    standalone: true
                }]
        }] });

const avatarNameLabelBaseStyles = [
    'text-[15px]',
    'font-semibold',
    'flex',
    'overflow-hidden',
    'text-ellipsis',
    'whitespace-nowrap',
    'block',
    'select-none',
    'truncate'
];
const avatarNameLabelStylesSAG = cva([...avatarNameLabelBaseStyles, 'text-[#000028]', 'dark:text-white']);
const avatarNameLabelStylesSHS = cva([...avatarNameLabelBaseStyles, 'text-[#666666]', 'dark:text-white']);

class AvatarNameDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const avatarNameLabelStyles = componentStylesByTheme(this.theme, avatarNameLabelStylesSAG, avatarNameLabelStylesSHS);
        style(this.elementRef, avatarNameLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarNameDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: AvatarNameDirective, isStandalone: true, selector: "[sag-ui-avatar-name]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarNameDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-avatar-name]',
                    standalone: true
                }]
        }] });

const avatarOrgLabelBaseStyles = [
    'text-xs',
    'flex',
    'overflow-hidden',
    'text-ellipsis',
    'whitespace-nowrap',
    'block',
    'select-none',
    'truncate'
];
const avatarOrgLabelStylesSAG = cva([...avatarOrgLabelBaseStyles, 'text-[#000028]', 'dark:text-white']);
const avatarOrgLabelStylesSHS = cva([...avatarOrgLabelBaseStyles, 'text-[#666666]', 'dark:text-white']);

class AvatarOrgDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const avatarNameLabelStyles = componentStylesByTheme(this.theme, avatarOrgLabelStylesSAG, avatarOrgLabelStylesSHS);
        style(this.elementRef, avatarNameLabelStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarOrgDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: AvatarOrgDirective, isStandalone: true, selector: "[sag-ui-avatar-org]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AvatarOrgDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-avatar-org]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class ChipInputComponent {
    id = input(this.randomId());
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    required = input(false);
    invalid = input(false);
    label = input('');
    error = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    // Changed from string[] to ChipItem[]
    chipItems = [];
    placeholder = input('');
    delemiters = input([',', ' ', ';', '\n']);
    hint = input(null);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    change = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    input = viewChild.required('input');
    chips = viewChild.required('chips');
    chipFocusIndex = signal(null);
    _value = '';
    paddingLeft = 10;
    // Computed property to maintain backward compatibility
    get value() {
        return this.chipItems.map(chip => chip.value);
    }
    set value(values) {
        this.chipItems = values.map(value => ({ value, selected: false }));
    }
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        if (Array.isArray(obj)) {
            this.value = obj;
        }
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(value) {
        this.onChangeEvent(this.value);
        this.change.emit(event);
    }
    toggleChipSelection(index) {
        if (index >= 0 && index < this.chipItems.length) {
            this.chipItems[index].selected = !this.chipItems[index].selected;
        }
    }
    handleInputChange(event) {
        const target = event.target;
        const targetValue = target.value;
        if (targetValue.trim() === '') {
            return;
        }
        const delemiters = this.delemiters();
        if (delemiters.some(delemiter => targetValue.includes(delemiter))) {
            const newValue = targetValue.split(new RegExp(delemiters.join('|'), 'g'))[0];
            this.chipItems.push({ value: newValue, selected: true });
            target.value = '';
        }
        this.onChange(this.value);
        setTimeout(() => {
            this.calculatePaddingLeft();
        }, 0);
    }
    focusChipElement(index) {
        setTimeout(() => {
            const chipElements = this.chips().nativeElement.querySelectorAll('span[sag-ui-chip][tabindex="0"]');
            if (chipElements && chipElements[index]) {
                chipElements[index].focus();
            }
        });
    }
    handleChipKeyDown(event, index) {
        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            event.stopPropagation();
            const currentIndex = this.chipFocusIndex();
            this.removeChip(currentIndex);
            return;
        }
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            const newIndex = (index + this.chipItems.length - 1) % this.chipItems.length;
            this.focusChipElement(newIndex);
            return;
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            const newIndex = (index + 1) % this.chipItems.length;
            this.focusChipElement(newIndex);
            return;
        }
    }
    handleInputKeyDown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            const target = event.target;
            const targetValue = target.value;
            if (targetValue.trim() === '') {
                return;
            }
            const newValue = targetValue.split(new RegExp(this.delemiters().join('|'), 'g'))[0];
            this.chipItems.push({ value: newValue, selected: false });
            target.value = '';
            this.onChange(this.value);
            setTimeout(() => {
                this.calculatePaddingLeft();
            }, 0);
            return;
        }
        if (event.key === 'Tab' && !event.shiftKey && this.chipItems.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            this.chipFocusIndex.set(0);
            this.focusChipElement(this.chipFocusIndex());
            return;
        }
    }
    removeChip(index) {
        this.chipItems = this.chipItems.filter((_, i) => i !== index);
        this.onChange(this.value);
        setTimeout(() => {
            this.calculatePaddingLeft();
        }, 0);
        if (this.chipItems.length > 0) {
            const newIndex = Math.min(index, this.chipItems.length - 1);
            this.chipFocusIndex.set(newIndex);
            this.focusChipElement(newIndex);
        }
        else {
            this.input().nativeElement.focus();
        }
    }
    validate(_) {
        return this.required() ? (this.chipItems.length > 0 ? null : { required: true }) : null;
    }
    isArray(value) {
        return Array.isArray(value);
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    calculatePaddingLeft() {
        this.paddingLeft = this.chips().nativeElement.getBoundingClientRect().width + 10;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipInputComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ChipInputComponent, isStandalone: true, selector: "sag-ui-chip-input", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, chipItems: { classPropertyName: "chipItems", publicName: "chipItems", isSignal: false, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, delemiters: { classPropertyName: "delemiters", publicName: "delemiters", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => ChipInputComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => ChipInputComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }, { propertyName: "chips", first: true, predicate: ["chips"], descendants: true, isSignal: true }], ngImport: i0, template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    type="text"
                    class="pr-8"
                    [id]="id()"
                    [disabled]="disabled"
                    [placeholder]="placeholder()"
                    [required]="required()"
                    [style]="{ paddingLeft: paddingLeft + 'px' }"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [value]="_value"
                    [attr.aria-label]="ariaLabel()"
                    [attr.aria-labelledby]="ariaLabelledby() || id()"
                    [attr.aria-describedby]="ariaDescribedby()"
                    (input)="handleInputChange($event)"
                    (keydown)="handleInputKeyDown($event)"
                    (focus)="focus.emit($event)"
                    (blur)="onTouched(); blur.emit($event)"
                    (keyup)="keyup.emit($event)"
                    (mouseenter)="mouseenter.emit($event)"
                    (mouseleave)="mouseleave.emit($event)"
                    #input
                />
                <label sag-ui-input-label [htmlFor]="id()">
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute left-1 top-[29px] h-[36px] w-auto max-w-[75%] flex justify-start gap-1 items-baseline select-none overflow-x-auto"
                    #chips
                >
                    @for (chip of chipItems; track $index; let i = $index) {
                        <sag-ui-chip
                            class="relative"
                            [selected]="chip.selected"
                            [removable]="true"
                            (click)="toggleChipSelection(i)"
                            (remove)="removeChip(i)"
                            (keydown)="handleChipKeyDown($event, i)"
                            (focus)="chipFocusIndex.set(i)"
                            >{{ chip.value }}</sag-ui-chip
                        >
                    }
                </span>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error>{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error>{{ error() }}</span>
            }
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "component", type: ChipComponent, selector: "sag-ui-chip", inputs: ["selected", "removable", "style", "class"], outputs: ["click", "blur", "focus", "remove"] }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ChipInputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-chip-input',
                    standalone: true,
                    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    type="text"
                    class="pr-8"
                    [id]="id()"
                    [disabled]="disabled"
                    [placeholder]="placeholder()"
                    [required]="required()"
                    [style]="{ paddingLeft: paddingLeft + 'px' }"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [value]="_value"
                    [attr.aria-label]="ariaLabel()"
                    [attr.aria-labelledby]="ariaLabelledby() || id()"
                    [attr.aria-describedby]="ariaDescribedby()"
                    (input)="handleInputChange($event)"
                    (keydown)="handleInputKeyDown($event)"
                    (focus)="focus.emit($event)"
                    (blur)="onTouched(); blur.emit($event)"
                    (keyup)="keyup.emit($event)"
                    (mouseenter)="mouseenter.emit($event)"
                    (mouseleave)="mouseleave.emit($event)"
                    #input
                />
                <label sag-ui-input-label [htmlFor]="id()">
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute left-1 top-[29px] h-[36px] w-auto max-w-[75%] flex justify-start gap-1 items-baseline select-none overflow-x-auto"
                    #chips
                >
                    @for (chip of chipItems; track $index; let i = $index) {
                        <sag-ui-chip
                            class="relative"
                            [selected]="chip.selected"
                            [removable]="true"
                            (click)="toggleChipSelection(i)"
                            (remove)="removeChip(i)"
                            (keydown)="handleChipKeyDown($event, i)"
                            (focus)="chipFocusIndex.set(i)"
                            >{{ chip.value }}</sag-ui-chip
                        >
                    }
                </span>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error>{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error>{{ error() }}</span>
            }
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ChipInputComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => ChipInputComponent),
                            multi: true
                        }
                    ],
                    imports: [InputDirective, InputLabelDirective, ChipComponent, TooltipDirective, InputErrorDirective]
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }], chipItems: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class DialogRef {
    constructor() { }
    close(result) {
        this._afterClosed.next(result);
    }
    _afterClosed = new Subject();
    afterClosed = this._afterClosed.asObservable();
}

class InsertionDirective {
    viewContainerRef = inject(ViewContainerRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InsertionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: InsertionDirective, isStandalone: true, selector: "[sag-ui-dialog-insertion]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: InsertionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-dialog-insertion]',
                    standalone: true
                }]
        }] });

const dialogBaseStyles = ['shadow', 'flex', 'p-2'];
const dialogStylesSAG = cva([...dialogBaseStyles, 'bg-[#F3F3F0]', 'dark:bg-[#23233C]']);
const dialogStylesSHS = cva([...dialogBaseStyles, 'bg-white', 'dark:bg-[#1a1a1a]']);

class FocusTrapComponent {
    elRef = inject(ElementRef);
    start = viewChild('start');
    end = viewChild('end');
    focusableElements = [];
    ngAfterContentInit() {
        setTimeout(() => {
            this.focusableElements = this.getFocusableElements();
            this.focusFirstElement();
        }, 100);
    }
    getFocusableElements() {
        const focusableSelectors = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';
        const elements = this.elRef.nativeElement.querySelectorAll(focusableSelectors);
        return Array.from(elements);
    }
    handleKeyboardEvent(event) {
        const isTabPressed = event.key === 'Tab';
        if (!isTabPressed) {
            return;
        }
        const firstElement = this.focusableElements[0];
        const lastElement = this.focusableElements[this.focusableElements.length - 1];
        if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
                lastElement.focus();
                event.preventDefault();
            }
        }
        else {
            // Tab
            if (document.activeElement === lastElement) {
                firstElement.focus();
                event.preventDefault();
            }
        }
    }
    focusFirstElement() {
        if (this.focusableElements && this.focusableElements.length > 0) {
            this.focusableElements[0].focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FocusTrapComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: FocusTrapComponent, isStandalone: true, selector: "sag-ui-focus-trap", host: { listeners: { "keydown": "handleKeyboardEvent($event)" } }, viewQueries: [{ propertyName: "start", first: true, predicate: ["start"], descendants: true, isSignal: true }, { propertyName: "end", first: true, predicate: ["end"], descendants: true, isSignal: true }], ngImport: i0, template: `<div #start></div>
        <div class="outline-none w-full">
            <ng-content></ng-content>
        </div>
        <div #end></div>`, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FocusTrapComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-focus-trap',
                    standalone: true,
                    template: `<div #start></div>
        <div class="outline-none w-full">
            <ng-content></ng-content>
        </div>
        <div #end></div>`
                }]
        }], propDecorators: { handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class DialogComponent {
    theme = inject(THEME_TOKEN);
    cd = inject(ChangeDetectorRef);
    dialogRef = inject(DialogRef);
    insertionPoint = viewChild.required(InsertionDirective);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    width = 'auto';
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    height = 'auto';
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    backgroundColor = '';
    overlayClickHandler;
    _onClose = new Subject();
    mouseDownInside = false;
    componentRef;
    childComponentType;
    onClose = this._onClose.asObservable();
    ngAfterViewInit() {
        this.loadChildComponent(this.childComponentType);
        this.cd.detectChanges();
    }
    handleOverlayClick() {
        if (this.overlayClickHandler) {
            this.overlayClickHandler();
        }
        else {
            this.dialogRef.close();
        }
    }
    get getDialogStyle() {
        return {
            width: this.width,
            height: this.height,
            backgroundColor: this.backgroundColor
        };
    }
    get dialogClasses() {
        const inputStyles = componentStylesByTheme(this.theme, dialogStylesSAG, dialogStylesSHS);
        return inputStyles();
    }
    loadChildComponent(componentType) {
        const viewContainerRef = this.insertionPoint().viewContainerRef;
        viewContainerRef.clear();
        this.componentRef = viewContainerRef.createComponent(componentType);
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
        }
    }
    close() {
        this._onClose.next({});
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: DialogComponent, isStandalone: true, selector: "sag-ui-dialog", inputs: { width: "width", height: "height", backgroundColor: "backgroundColor", overlayClickHandler: "overlayClickHandler" }, viewQueries: [{ propertyName: "insertionPoint", first: true, predicate: InsertionDirective, descendants: true, isSignal: true }], ngImport: i0, template: ` <div
        class="flex fixed top-0 left-0 bottom-0 right-0 z-[200] items-center justify-center bg-[rgba(0,0,0,0.3)] backdrop-blur-sm"
        (click)="!mouseDownInside && handleOverlayClick(); mouseDownInside = false"
    >
        <div
            [class]="dialogClasses"
            (mousedown)="$event.stopPropagation(); mouseDownInside = true"
            (mouseup)="$event.stopPropagation(); mouseDownInside = false"
            (click)="$event.stopPropagation(); mouseDownInside = true"
            [ngStyle]="getDialogStyle"
        >
            <sag-ui-focus-trap>
                <ng-template sag-ui-dialog-insertion></ng-template>
            </sag-ui-focus-trap>
        </div>
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: InsertionDirective, selector: "[sag-ui-dialog-insertion]" }, { kind: "component", type: FocusTrapComponent, selector: "sag-ui-focus-trap" }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-dialog',
                    standalone: true,
                    template: ` <div
        class="flex fixed top-0 left-0 bottom-0 right-0 z-[200] items-center justify-center bg-[rgba(0,0,0,0.3)] backdrop-blur-sm"
        (click)="!mouseDownInside && handleOverlayClick(); mouseDownInside = false"
    >
        <div
            [class]="dialogClasses"
            (mousedown)="$event.stopPropagation(); mouseDownInside = true"
            (mouseup)="$event.stopPropagation(); mouseDownInside = false"
            (click)="$event.stopPropagation(); mouseDownInside = true"
            [ngStyle]="getDialogStyle"
        >
            <sag-ui-focus-trap>
                <ng-template sag-ui-dialog-insertion></ng-template>
            </sag-ui-focus-trap>
        </div>
    </div>`,
                    imports: [InsertionDirective, FocusTrapComponent, CommonModule]
                }]
        }], propDecorators: { width: [{
                type: Input
            }], height: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], overlayClickHandler: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class DialogConfig {
    width;
    height;
    backgroundColor;
    overlayClickHandler;
    data;
}

class DialogInjector {
    _parentInjector;
    _additionalTokens;
    constructor(_parentInjector, _additionalTokens) {
        this._parentInjector = _parentInjector;
        this._additionalTokens = _additionalTokens;
    }
    get(token, notFoundValue) {
        const value = this._additionalTokens.get(token);
        if (value !== undefined)
            return value;
        return this._parentInjector.get(token, notFoundValue);
    }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
class DialogService {
    componentFactoryResolver = inject(ComponentFactoryResolver);
    appRef = inject(ApplicationRef);
    injector = inject(Injector);
    // Store multiple dialog component references
    dialogStack = [];
    open(componentType, config) {
        const { dialogRef, componentRef } = this.appendDialogComponentToBody(config);
        componentRef.instance.childComponentType = componentType;
        return dialogRef;
    }
    // Get the current active dialog (most recently opened)
    getCurrentDialogRef() {
        if (this.dialogStack.length === 0)
            return null;
        return this.dialogStack[this.dialogStack.length - 1].dialogRef;
    }
    // Close all open dialogs
    closeAll() {
        // Make a copy since closing will modify the array
        const dialogs = [...this.dialogStack];
        dialogs.forEach(dialog => dialog.dialogRef.close());
    }
    appendDialogComponentToBody(config) {
        const map = new WeakMap();
        map.set(DialogConfig, config);
        const dialogRef = new DialogRef();
        map.set(DialogRef, dialogRef);
        const sub = dialogRef.afterClosed.subscribe(() => {
            this.removeDialogComponentFromBody(dialogRef);
            sub.unsubscribe();
        });
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DialogComponent);
        const componentRef = componentFactory.create(new DialogInjector(this.injector, map));
        if (config.width)
            componentRef.instance.width = config.width;
        if (config.height)
            componentRef.instance.height = config.height;
        if (config.backgroundColor)
            componentRef.instance.backgroundColor = config.backgroundColor;
        if (config.overlayClickHandler)
            componentRef.instance.overlayClickHandler = config.overlayClickHandler;
        this.appRef.attachView(componentRef.hostView);
        const domElem = componentRef.hostView.rootNodes[0];
        document.body.appendChild(domElem);
        // Store both references in the stack
        this.dialogStack.push({ ref: componentRef, dialogRef });
        componentRef.instance.onClose.subscribe(() => {
            this.removeDialogComponentFromBody(dialogRef);
        });
        return { dialogRef, componentRef };
    }
    removeDialogComponentFromBody(dialogRef) {
        // Find the dialog in our stack
        const index = this.dialogStack.findIndex(item => item.dialogRef === dialogRef);
        if (index === -1)
            return;
        const { ref } = this.dialogStack[index];
        // Remove it from the stack
        this.dialogStack.splice(index, 1);
        // Detach and destroy
        this.appRef.detachView(ref.hostView);
        ref.destroy();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DialogService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DialogService, providedIn: 'root' // Changed from DialogModule to 'root'
     });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DialogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root' // Changed from DialogModule to 'root'
                }]
        }] });

const tabsTabBaseStyles = [
    'inline-block',
    'p-4',
    'border-b-2',
    'rounded-t-sm',
    'cursor-pointer',
    'user-select-none',
    'w-full',
    'text-center',
    'focus-visible:outline-[#1491EB]'
];
const tabsTabStylesSAG = cva([
    ...tabsTabBaseStyles,
    'border-transparent',
    'hover:bg-[#D1FFF2]',
    'hover:border-[#005159]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:border-[#00FFB9]'
]);
const tabsTabStylesActiveSAG = cva([...tabsTabBaseStyles, 'border-[#005159]', 'dark:border-[#00FFB9]']);
const tabsTabStylesDisabledSAG = cva([
    ...tabsTabBaseStyles,
    'cursor-not-allowed',
    'pointer-events-none',
    'text-[#737389]',
    'dark:text-[#737389]'
]);
const tabsTabStylesSHS = cva([
    ...tabsTabBaseStyles,
    'border-transparent',
    'hover:text-[#1a1a1a]',
    'dark:hover:text-[#e6e6e6]'
]);
const tabsTabStylesActiveSHS = cva([
    ...tabsTabBaseStyles,
    'hover:text-[#1a1a1a]',
    'border-[#ec6602]',
    'dark:hover:text-[#e6e6e6]',
    'dark:border-[#ec6602]'
]);
const tabsTabStylesDisabledSHS = cva([
    ...tabsTabBaseStyles,
    'cursor-not-allowed',
    'pointer-events-none',
    'text-[#ededed]',
    'dark:text-[#ededed]'
]);

class TabsTabDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    disabled = input(false);
    // eslint-disable-next-line @angular-eslint/no-input-rename
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set setActive(active) {
        this.active = active;
        this.applyStyles();
    }
    active = false;
    applyStyles() {
        if (this.disabled()) {
            style(this.elementRef, componentStylesByTheme(this.theme, tabsTabStylesDisabledSAG, tabsTabStylesDisabledSHS)());
            return;
        }
        const activeStyles = componentStylesByTheme(this.theme, tabsTabStylesActiveSAG, tabsTabStylesActiveSHS);
        const defaultStyles = componentStylesByTheme(this.theme, tabsTabStylesSAG, tabsTabStylesSHS);
        updateElementStyles(this.elementRef, this.active ? activeStyles() : defaultStyles());
    }
    ngOnInit() {
        this.applyStyles();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsTabDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: TabsTabDirective, isStandalone: true, selector: "li[sag-ui-tabs-tab]", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, setActive: { classPropertyName: "setActive", publicName: "active", isSignal: false, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsTabDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'li[sag-ui-tabs-tab]',
                    standalone: true
                }]
        }], propDecorators: { setActive: [{
                type: Input,
                args: ['active']
            }] } });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class TabComponent {
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    active;
    label = input.required();
    disabled = input();
    value = input();
    tabContentTemplate = viewChild('tabContentTemplate');
    tabElement = viewChild('tabElement');
    // eslint-disable-next-line @angular-eslint/no-output-native
    click = output();
    keydown = output();
    navigate = output();
    handleKeydown(event) {
        if (this.disabled()) {
            return;
        }
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                this.navigate.emit('prev');
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.navigate.emit('next');
                break;
            case ' ':
            case 'Enter':
                event.preventDefault();
                this.keydown.emit(this.value());
                break;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: TabComponent, isStandalone: true, selector: "sag-ui-tab", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: false, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: true, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { click: "click", keydown: "keydown", navigate: "navigate" }, viewQueries: [{ propertyName: "tabContentTemplate", first: true, predicate: ["tabContentTemplate"], descendants: true, isSignal: true }, { propertyName: "tabElement", first: true, predicate: ["tabElement"], descendants: true, isSignal: true }], ngImport: i0, template: ` <div role="tab" [attr.aria-selected]="active" #tabElement>
        <li
            sag-ui-tabs-tab
            [active]="active"
            [disabled]="disabled()"
            (click)="click.emit(value())"
            tabindex="0"
            (keydown)="handleKeydown($event)"
        >
            <span>{{ label() }}</span>
        </li>
        <ng-template #tabContentTemplate>
            <ng-content></ng-content>
        </ng-template>
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: TabsTabDirective, selector: "li[sag-ui-tabs-tab]", inputs: ["disabled", "active"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-tab',
                    standalone: true,
                    template: ` <div role="tab" [attr.aria-selected]="active" #tabElement>
        <li
            sag-ui-tabs-tab
            [active]="active"
            [disabled]="disabled()"
            (click)="click.emit(value())"
            tabindex="0"
            (keydown)="handleKeydown($event)"
        >
            <span>{{ label() }}</span>
        </li>
        <ng-template #tabContentTemplate>
            <ng-content></ng-content>
        </ng-template>
    </div>`,
                    imports: [TabsTabDirective]
                }]
        }], propDecorators: { active: [{
                type: Input
            }] } });

class TabsListDirective {
    elementRef = inject(ElementRef);
    fullWidth = input(false);
    tabsCount = input(0);
    ngOnInit() {
        if (this.fullWidth()) {
            updateElementStyles(this.elementRef, ['grid', '-mb-px'].join(' '));
            this.elementRef.nativeElement.style.gridTemplateColumns = `repeat(${this.tabsCount()}, 1fr)`;
            return;
        }
        updateElementStyles(this.elementRef, ['flex', 'flex-wrap', '-mb-px'].join(' '));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsListDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: TabsListDirective, isStandalone: true, selector: "ul[sag-ui-tabs-list]", inputs: { fullWidth: { classPropertyName: "fullWidth", publicName: "fullWidth", isSignal: true, isRequired: false, transformFunction: null }, tabsCount: { classPropertyName: "tabsCount", publicName: "tabsCount", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsListDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ul[sag-ui-tabs-list]',
                    standalone: true
                }]
        }] });

const tabsBaseBaseStyles = ['text-sm', 'font-medium'];
const tabsBaseStylesSAG = cva([...tabsBaseBaseStyles, 'text-[#000028]', 'dark:text-white']);
const tabsBaseStylesSHS = cva([...tabsBaseBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);

class TabsBaseDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, tabsBaseStylesSAG, tabsBaseStylesSHS);
        style(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: TabsBaseDirective, isStandalone: true, selector: "[sag-ui-tabs-base]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabsBaseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-tabs-base]',
                    standalone: true
                }]
        }] });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class TabContentComponent {
    template = input.required();
    slide = input.required();
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: TabContentComponent, isStandalone: true, selector: "sag-ui-tab-content", inputs: { template: { classPropertyName: "template", publicName: "template", isSignal: true, isRequired: true, transformFunction: null }, slide: { classPropertyName: "slide", publicName: "slide", isSignal: true, isRequired: true, transformFunction: null } }, ngImport: i0, template: ` <div [ngClass]="{ 'animate-slideLeft': slide(), 'animate-slideRight': !slide() }">
        <ng-container *ngTemplateOutlet="template()"></ng-container>
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-tab-content',
                    standalone: true,
                    template: ` <div [ngClass]="{ 'animate-slideLeft': slide(), 'animate-slideRight': !slide() }">
        <ng-container *ngTemplateOutlet="template()"></ng-container>
    </div>`,
                    imports: [NgTemplateOutlet, NgClass]
                }]
        }] });

class TabViewComponent {
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set value(val) {
        this._value = val;
        this.updateTabs();
    }
    fullWidth = input(false);
    // eslint-disable-next-line @angular-eslint/no-output-native
    change = output();
    tabs = contentChildren(TabComponent);
    _value = 0;
    tabList = [];
    subscriptions = [];
    previousIndex = 0;
    slide$ = new BehaviorSubject(true);
    ngAfterContentInit() {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        this.updateTabs();
        this.subscribeToTabChanges();
    }
    ngOnDestroy() {
        this.resetSubscriptions();
    }
    updateTabs() {
        const tabs = this.tabs();
        if (!tabs)
            return;
        this.tabList.push(...tabs);
        const initialTab = this.tabList
            .filter(tab => !tab.disabled())
            .find((tab, index) => tab.value() === this._value || index === this._value);
        if (initialTab) {
            this.activateTab(initialTab);
        }
        else {
            const defaultTab = this.tabList.filter(tab => !tab.disabled())[0];
            this.activateTab(defaultTab);
        }
    }
    subscribeToTabChanges() {
        this.tabs().forEach(tab => {
            const handleEvent = (value) => {
                this.change.emit(value);
                this.activateTab(tab);
                this.updateSlideAnimation();
            };
            this.subscriptions.push(tab.click.subscribe(handleEvent), tab.keydown.subscribe(handleEvent));
        });
    }
    activateTab(activeTab) {
        this.tabs().forEach(tab => {
            tab.active = tab === activeTab;
        });
    }
    updateSlideAnimation() {
        const currentIndex = this.tabList.findIndex(tab => tab.active);
        const slide = currentIndex > this.previousIndex;
        this.previousIndex = currentIndex;
        this.slide$.next(slide);
    }
    resetSubscriptions() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
    handleKeyboardNavigation(event) {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key))
            return;
        event.preventDefault();
        const currentIndex = this.tabList.findIndex(tab => tab.tabElement()?.nativeElement.contains(document.activeElement));
        if (currentIndex === -1 ||
            (event.key === 'ArrowLeft' && currentIndex === 0) ||
            (event.key === 'ArrowRight' && currentIndex === this.tabs().length - 1)) {
            return;
        }
        const newIndex = currentIndex + (event.key === 'ArrowLeft' ? -1 : 1);
        this.tabList[newIndex].tabElement()?.nativeElement.querySelector('li')?.focus();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabViewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: TabViewComponent, isStandalone: true, selector: "sag-ui-tabview", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, fullWidth: { classPropertyName: "fullWidth", publicName: "fullWidth", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change" }, queries: [{ propertyName: "tabs", predicate: TabComponent, isSignal: true }], ngImport: i0, template: ` <div sag-ui-tabs-base (keydown)="handleKeyboardNavigation($event)">
        <ul sag-ui-tabs-list [fullWidth]="fullWidth()" [tabsCount]="tabs().length" role="tablist">
            <ng-content select="sag-ui-tab"></ng-content>
        </ul>
        @for (tab of tabs(); track tab; let i = $index) {
            @if (tab.active) {
                <sag-ui-tab-content
                    [template]="tab.tabContentTemplate()"
                    [slide]="$any(slide$ | async)"
                ></sag-ui-tab-content>
            }
        }
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: TabsListDirective, selector: "ul[sag-ui-tabs-list]", inputs: ["fullWidth", "tabsCount"] }, { kind: "directive", type: TabsBaseDirective, selector: "[sag-ui-tabs-base]" }, { kind: "component", type: TabContentComponent, selector: "sag-ui-tab-content", inputs: ["template", "slide"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: TabViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-tabview',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    standalone: true,
                    template: ` <div sag-ui-tabs-base (keydown)="handleKeyboardNavigation($event)">
        <ul sag-ui-tabs-list [fullWidth]="fullWidth()" [tabsCount]="tabs().length" role="tablist">
            <ng-content select="sag-ui-tab"></ng-content>
        </ul>
        @for (tab of tabs(); track tab; let i = $index) {
            @if (tab.active) {
                <sag-ui-tab-content
                    [template]="tab.tabContentTemplate()"
                    [slide]="$any(slide$ | async)"
                ></sag-ui-tab-content>
            }
        }
    </div>`,
                    imports: [TabsListDirective, TabsBaseDirective, TabContentComponent, AsyncPipe]
                }]
        }], propDecorators: { value: [{
                type: Input
            }] } });

const autocompleteOptionBaseStyles$3 = [
    'p-3',
    'cursor-pointer',
    'flex',
    'items-center',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const autocompleteOptionStylesSAG$3 = cva([
    ...autocompleteOptionBaseStyles$3,
    'hover:bg-[#62EEC7]',
    'dark:hover:bg-[#005159]'
]);
const autocompleteOptionSelectedStylesSAG$3 = cva([...autocompleteOptionBaseStyles$3, 'bg-[#62EEC7]', 'dark:bg-[#005159]']);
const autocompleteionOptionTextStylesSAG$3 = cva(['text-[#000028]', 'dark:text-white']);
const autocompleteOptionStylesSHS$3 = cva([
    ...autocompleteOptionBaseStyles$3,
    'hover:bg-[#f2f2f2]',
    'dark:hover:bg-[#474747]'
]);
const autocompleteOptionSelectedStylesSHS$3 = cva([...autocompleteOptionBaseStyles$3, 'bg-[#e6e6e6]', 'dark:bg-[#666]']);
const autocompleteionOptionTextStylesSHS$3 = cva(['dark:text-[#e6e6e6]']);

class AutocompleteOptionComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    value = input();
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    selected = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    hidden = false;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    ariaPosinset = null;
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    ariaSetsize = null;
    autocomplete = output();
    text = viewChild.required('text');
    onKeyDown(event) {
        if (event.code === 'Space' || event.code === 'Enter') {
            event.preventDefault();
            this.onClick();
            event.target.focus();
        }
    }
    get containerClasses() {
        const containerStyles = componentStylesByTheme(this.theme, autocompleteOptionStylesSAG$3, autocompleteOptionStylesSHS$3);
        const autocompleteedContainerStyles = componentStylesByTheme(this.theme, autocompleteOptionSelectedStylesSAG$3, autocompleteOptionSelectedStylesSHS$3);
        return this.selected ? autocompleteedContainerStyles() : containerStyles();
    }
    get textClasses() {
        const textStyles = componentStylesByTheme(this.theme, autocompleteionOptionTextStylesSAG$3, autocompleteionOptionTextStylesSHS$3);
        return textStyles();
    }
    focus() {
        this.el.nativeElement.focus();
    }
    onClick() {
        const innerText = this.text().nativeElement.innerText;
        this.autocomplete.emit([innerText, this.value()]);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AutocompleteOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: AutocompleteOptionComponent, isStandalone: true, selector: "sag-ui-autocomplete-option", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: false, isRequired: false, transformFunction: null }, hidden: { classPropertyName: "hidden", publicName: "hidden", isSignal: false, isRequired: false, transformFunction: null }, ariaPosinset: { classPropertyName: "ariaPosinset", publicName: "ariaPosinset", isSignal: false, isRequired: false, transformFunction: null }, ariaSetsize: { classPropertyName: "ariaSetsize", publicName: "ariaSetsize", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { autocomplete: "autocomplete" }, host: { listeners: { "keydown": "onKeyDown($event)" } }, viewQueries: [{ propertyName: "text", first: true, predicate: ["text"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  [class]=\"containerClasses\"\n  [class.hidden]=\"hidden\"\n  role=\"listitem\"\n  [attr.aria-posinset]=\"ariaPosinset\"\n  [attr.aria-setsize]=\"ariaSetsize\"\n  [attr.aria-hidden]=\"hidden\"\n  [attr.aria-selected]=\"selected\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AutocompleteOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-autocomplete-option', standalone: true, template: "<div\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  [class]=\"containerClasses\"\n  [class.hidden]=\"hidden\"\n  role=\"listitem\"\n  [attr.aria-posinset]=\"ariaPosinset\"\n  [attr.aria-setsize]=\"ariaSetsize\"\n  [attr.aria-hidden]=\"hidden\"\n  [attr.aria-selected]=\"selected\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" }]
        }], propDecorators: { selected: [{
                type: Input
            }], hidden: [{
                type: Input
            }], ariaPosinset: [{
                type: Input
            }], ariaSetsize: [{
                type: Input
            }], onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

const autocompleteBaseStyles$2 = [
    'absolute',
    'max-h-48',
    'right-0',
    'top-full',
    'border',
    'border-t-0',
    'shadow-md',
    'z-50',
    'w-full',
    'overflow-y-auto'
];
const autocompleteStylesSAG$2 = cva([
    ...autocompleteBaseStyles$2,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const autocompleteStylesSHS$2 = cva([...autocompleteBaseStyles$2, 'bg-white', 'border-0', 'dark:bg-[#333333]']);

class AutocompleteComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    id = input(null);
    /**
     * The placeholder of the Autocomplete.
     */
    placeholder = input('');
    /*
     * Whether the Autocomplete is required.
     */
    required = input(false);
    /**
     * Whether the Autocomplete is disabled.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    /**
     * Whether the Autocomplete is read-only.
     */
    readOnly = input(false);
    /**
     * The label of the Autocomplete.
     */
    label = input('');
    /**
     * The value of the Autocomplete.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value = '';
    /**
     * Whether the Select is in an error state.
     */
    invalid = input(false);
    /**
     * The error message to display when the Select is in an error state.
     */
    error = input('');
    /**
     * A function to compare the selected value with the option value.
     */
    compareWith = input((o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    });
    hint = input(null);
    floatLabel = input(false);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    // Outputs for events
    change = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    // TODO: Skipped for migration because:
    //  There are references to this query that cannot be migrated automatically.
    optionComponents;
    input = viewChild.required('input');
    dropdownArrow = viewChild.required('dropdownArrow');
    // Accessibility properties:
    selectedItemName = ''; // Used to announce selected item names via a live region
    activeDescendantId = ''; // Used for aria-activedescendant on the listbox
    // IDs for accessibility
    inputId = '';
    labelId = '';
    errorId = '';
    // Getter for the number of visible (non-hidden) options for aria-setsize
    get optionsCount() {
        return this.optionComponents?.filter(option => !option.hidden).length || 0;
    }
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen.set(false);
        }
    }
    globalKeydown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen.set(false);
            this.input().nativeElement.focus();
        }
    }
    checkDropdownPosition() {
        const selectBox = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropdownUpwards = selectBox.bottom + 200 > viewportHeight;
    }
    constructor() {
        const baseId = this.id || this.randomId();
        this.inputId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    touched = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedItems = signal([]);
    isOpen = signal(false);
    focused = false;
    filterValue = signal('');
    isDropdownUpwards = false;
    prevSelectedItems = [];
    subscriptions = [];
    ngAfterViewInit() {
        // Prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.initSelectedItems();
        }, 0);
    }
    ngOnDestroy() {
        this.resetSubscriptions();
    }
    resetSubscriptions() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
    setupSubscriptions() {
        this.optionComponents.forEach(option => {
            this.subscriptions.push(option.autocomplete.subscribe(data => {
                this.handleOptionSelect(data);
            }));
        });
    }
    subscribeToOptionComponentChanges() {
        this.optionComponents.changes.subscribe(() => {
            this.resetSubscriptions();
            this.setupSubscriptions();
            this.initSelectedItems();
        });
    }
    handleOptionSelect(data) {
        const [label, value] = data;
        this.input().nativeElement.focus();
        this.markAsTouched();
        // Announce the selected item for screen readers.
        this.selectedItemName = `Selected item: ${label}`;
        if (this.selectedItems()[0] && this.compareWith()(this.selectedItems()[0][1], value)) {
            this.selectedItems.set([]);
            this.writeValue(null);
        }
        else {
            this.selectedItems.set([[label, value]]);
            this.writeValue(this.selectedItems()[0][1]);
        }
        this.isOpen.set(false);
        setTimeout(() => {
            this.input().nativeElement.blur();
        }, 50);
        this.updateSelectedOptions();
    }
    ngAfterContentInit() {
        this.setupSubscriptions();
        this.subscribeToOptionComponentChanges();
        this.attachEventListeners();
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG$2, autocompleteStylesSHS$2);
        return popoverStyles();
    }
    initSelectedItems() {
        this.updateSelectedOptions();
        if (this.optionComponents) {
            this.selectedItems.set(this.optionComponents
                .filter(option => option.selected)
                .map(option => [option.text().nativeElement.innerText, option.value()]));
        }
    }
    updateSelectedOptions() {
        this.optionComponents?.forEach(option => {
            option.selected = this.compareWith()(this.getValue(this.selectedItems()[0]), option.value());
        });
    }
    displayValue = computed(() => {
        if (this.isOpen()) {
            return this.filterValue();
        }
        return this.selectedItems()
            .map(item => item[0])
            .join(', ');
    });
    onFocus() {
        if (this.disabled || this.readOnly())
            return;
        this.filterValue.set(this.selectedItems()[0] ? this.selectedItems()[0][0] : '');
        this.filterOptions();
        this.prevSelectedItems = this.selectedItems();
        this.focused = true;
        this.isOpen.set(true);
    }
    onBlur() {
        this.focused = false;
        setTimeout(() => {
            this.markAsTouched();
            // Preserve the typed text on blur instead of resetting it.
        }, 100);
    }
    togglePopover(event) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        if (!this.isOpen()) {
            this.input().nativeElement.focus();
        }
        else {
            this.isOpen.set(false);
        }
    }
    removeSelection(event) {
        event.stopPropagation();
        event.preventDefault();
        this.writeValue(null);
        this.onChange(null);
        this.change.emit(new Event('change'));
        this.selectedItems.set([]);
        this.filterValue.set('');
        this.filterOptions();
        this.selectedItemName = 'Selection cleared';
    }
    filterOptions() {
        this.optionComponents.forEach(option => {
            const matches = option
                .text()
                .nativeElement.innerText.toLowerCase()
                .includes(this.filterValue().toLowerCase());
            option.hidden = !matches;
            if (!matches) {
                option.el.nativeElement.setAttribute('aria-hidden', 'true');
            }
            else {
                option.el.nativeElement.removeAttribute('aria-hidden');
            }
        });
        this.updateOptionARIAAttributes();
    }
    handleInput(event) {
        const target = event.target;
        this.filterValue.set(target.value);
        this.isOpen.set(true);
        this.filterOptions();
    }
    handleInputKeydown(event) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const visibleOptions = this.optionComponents.filter(option => !option.hidden);
            if (visibleOptions.length > 0) {
                visibleOptions[0].focus();
                // Update activeDescendantId when focusing the first visible option.
                this.activeDescendantId = visibleOptions[0].el.nativeElement.id;
            }
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen.set(false);
            this.input().nativeElement.focus();
        }
        this.keydown.emit(event);
    }
    handleEnterKey(event) {
        event.preventDefault();
        const activeEl = document.activeElement;
        const option = this.optionComponents.find(opt => opt.el.nativeElement === activeEl);
        if (option) {
            this.handleOptionSelect([option.text().nativeElement.innerText, option.value()]);
        }
    }
    handleEscapeKey(event) {
        event.preventDefault();
        this.isOpen.set(false);
        this.input().nativeElement.focus();
    }
    handleItemKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleOptionSelect([this.selectedItems()[0][0], this.selectedItems()[0][1]]);
        }
    }
    getAriaDescribedby() {
        // Combine user-provided describedby with error ID when there's an error
        if (this.error()) {
            return this.ariaDescribedby() ? `${this.ariaDescribedby()} ${this.errorId}` : this.errorId;
        }
        return this.ariaDescribedby() || null;
    }
    handleChange(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!event.target.value) {
            this.writeValue(null);
            this.onChange(null);
            this.change.emit(undefined);
            this.selectedItems.set([]);
        }
        this.updateSelectedOptions();
    }
    // eslint-disable-next-line
    onChange = (_value) => { };
    // eslint-disable-next-line
    onTouch = () => { };
    // eslint-disable-next-line
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // eslint-disable-next-line
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val) {
        this.value = val;
        this.selectedItems.set([['', val]]);
        try {
            this.initSelectedItems();
        }
        catch (e) {
            console.error(e);
        }
        // Check if the selected items have changed
        if (this.selectedItems().map(this.getValue).toString() === this.prevSelectedItems.map(this.getValue).toString()) {
            return;
        }
        if (this.selectedItems().length) {
            this.onChange(this.getValue(this.selectedItems()[0]));
            this.change.emit(this.getValue(this.selectedItems()[0]));
            this.prevSelectedItems = this.selectedItems();
        }
        else {
            this.onChange(null);
            this.change.emit(undefined);
        }
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    // eslint-disable-next-line
    validate(control) {
        return this.required() && !this.selectedItems().length ? { required: true } : null;
    }
    blockChange(event) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (event.target && event.target['value']) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            event.target['value'] = this.displayValue;
        }
    }
    isArray(value) {
        return Array.isArray(value);
    }
    attachEventListeners() {
        this.optionComponents.forEach(option => {
            const optionEl = option.el.nativeElement;
            optionEl.setAttribute('tabindex', '-1');
            optionEl.addEventListener('keydown', (event) => this.handleOptionsKeyNav(event, option));
        });
    }
    /**
     * Handles key navigation within the list of options.
     */
    handleOptionsKeyNav(event, option) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.moveFocusOptions(option, 1);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.moveFocusOptions(option, -1);
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen.set(false);
            this.input().nativeElement.focus();
        }
    }
    moveFocusOptions(currentOption, offset) {
        const options = this.optionComponents.toArray();
        const currentIndex = options.indexOf(currentOption);
        const totalOptions = options.length;
        let nextIndex = currentIndex;
        for (let i = 0; i < totalOptions; i++) {
            nextIndex = (nextIndex + offset + totalOptions) % totalOptions;
            if (!options[nextIndex].hidden) {
                options[nextIndex].focus();
                this.activeDescendantId = options[nextIndex].el.nativeElement.id;
                break;
            }
        }
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    updateOptionARIAAttributes() {
        const visibleOptions = this.optionComponents.filter(option => !option.hidden);
        let visibleIndex = 0;
        visibleOptions.forEach(option => {
            visibleIndex++;
            option.ariaPosinset = visibleIndex;
            option.ariaSetsize = visibleOptions.length;
            option.el.nativeElement.setAttribute('aria-posinset', String(visibleIndex));
            option.el.nativeElement.setAttribute('aria-setsize', String(visibleOptions.length));
        });
    }
    getValue(item) {
        return item ? item[1] : null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: AutocompleteComponent, isStandalone: true, selector: "sag-ui-autocomplete", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, compareWith: { classPropertyName: "compareWith", publicName: "compareWith", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, floatLabel: { classPropertyName: "floatLabel", publicName: "floatLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "keydown": "globalKeydown($event)", "click": "checkDropdownPosition()", "mouseenter": "checkDropdownPosition()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AutocompleteComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => AutocompleteComponent),
                multi: true
            }
        ], queries: [{ propertyName: "optionComponents", predicate: AutocompleteOptionComponent }], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }, { propertyName: "dropdownArrow", first: true, predicate: ["dropdownArrow"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"sr-only\" aria-live=\"polite\" aria-atomic=\"true\">{{ selectedItemName }}</div>\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"floatLabel() ? '' : placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [value]=\"displayValue()\"\n      [attr.aria-label]=\"ariaLabel()\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || labelId\"\n      [attr.aria-describedby]=\"getAriaDescribedby()\"\n      [attr.data-float-label]=\"floatLabel()\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange($event)\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeydown($event)\"\n      (keydown.enter)=\"handleEnterKey($event)\"\n      (keydown.escape)=\"handleEscapeKey($event)\"\n      (keyup)=\"keyup.emit($event)\"\n      (mouseenter)=\"mouseenter.emit($event)\"\n      (mouseleave)=\"mouseleave.emit($event)\"\n      #input\n      />\n    <label\n      sag-ui-input-label\n      [id]=\"labelId\"\n      [htmlFor]=\"inputId\"\n      [attr.data-float]=\"floatLabel() ? focused || !!value : undefined\"\n      [attr.data-float-label]=\"floatLabel()\"\n      >\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    @if (isOpen()) {\n      <button\n        type=\"button\"\n        class=\"absolute right-10 bottom-[0.8rem] z-20 text-xs text-black dark:text-white icon-close cursor-pointer\"\n        (click)=\"!disabled && removeSelection($event)\"\n        [class.opacity-50]=\"disabled\"\n        aria-label=\"Clear selection\"\n        tabindex=\"0\"\n      ></button>\n    }\n    <button\n      type=\"button\"\n      class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white\"\n      (click)=\"!disabled && togglePopover($event)\"\n      (keydown.space)=\"!disabled && togglePopover($event)\"\n      [class.icon-chevron-up]=\"isOpen()\"\n      [class.icon-chevron-down]=\"!isOpen()\"\n      [class.opacity-50]=\"disabled\"\n      aria-label=\"Toggle dropdown\"\n      [attr.tabindex]=\"disabled ? -1 : 0\"\n      #dropdownArrow\n    ></button>\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen()\"\n      [class.top-full]=\"!isDropdownUpwards\"\n      [class.bottom-input]=\"isDropdownUpwards\"\n      role=\"listbox\"\n      [attr.aria-activedescendant]=\"activeDescendantId\"\n    >\n      <ng-content select=\"sag-ui-autocomplete-option\" (click)=\"handleItemClick($event)\" (keydown)=\"handleItemKeydown($event)\"></ng-content>\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  }\n  @if (error() && !isArray(error())) {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n", dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: AutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-autocomplete', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AutocompleteComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => AutocompleteComponent),
                            multi: true
                        }
                    ], imports: [InputDirective, InputLabelDirective, TooltipDirective, InputErrorDirective], template: "<div class=\"flex flex-col\">\n  <div class=\"sr-only\" aria-live=\"polite\" aria-atomic=\"true\">{{ selectedItemName }}</div>\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"floatLabel() ? '' : placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [value]=\"displayValue()\"\n      [attr.aria-label]=\"ariaLabel()\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || labelId\"\n      [attr.aria-describedby]=\"getAriaDescribedby()\"\n      [attr.data-float-label]=\"floatLabel()\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange($event)\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeydown($event)\"\n      (keydown.enter)=\"handleEnterKey($event)\"\n      (keydown.escape)=\"handleEscapeKey($event)\"\n      (keyup)=\"keyup.emit($event)\"\n      (mouseenter)=\"mouseenter.emit($event)\"\n      (mouseleave)=\"mouseleave.emit($event)\"\n      #input\n      />\n    <label\n      sag-ui-input-label\n      [id]=\"labelId\"\n      [htmlFor]=\"inputId\"\n      [attr.data-float]=\"floatLabel() ? focused || !!value : undefined\"\n      [attr.data-float-label]=\"floatLabel()\"\n      >\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    @if (isOpen()) {\n      <button\n        type=\"button\"\n        class=\"absolute right-10 bottom-[0.8rem] z-20 text-xs text-black dark:text-white icon-close cursor-pointer\"\n        (click)=\"!disabled && removeSelection($event)\"\n        [class.opacity-50]=\"disabled\"\n        aria-label=\"Clear selection\"\n        tabindex=\"0\"\n      ></button>\n    }\n    <button\n      type=\"button\"\n      class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white\"\n      (click)=\"!disabled && togglePopover($event)\"\n      (keydown.space)=\"!disabled && togglePopover($event)\"\n      [class.icon-chevron-up]=\"isOpen()\"\n      [class.icon-chevron-down]=\"!isOpen()\"\n      [class.opacity-50]=\"disabled\"\n      aria-label=\"Toggle dropdown\"\n      [attr.tabindex]=\"disabled ? -1 : 0\"\n      #dropdownArrow\n    ></button>\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen()\"\n      [class.top-full]=\"!isDropdownUpwards\"\n      [class.bottom-input]=\"isDropdownUpwards\"\n      role=\"listbox\"\n      [attr.aria-activedescendant]=\"activeDescendantId\"\n    >\n      <ng-content select=\"sag-ui-autocomplete-option\" (click)=\"handleItemClick($event)\" (keydown)=\"handleItemKeydown($event)\"></ng-content>\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  }\n  @if (error() && !isArray(error())) {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: [{
                type: Input
            }], optionComponents: [{
                type: ContentChildren,
                args: [AutocompleteOptionComponent]
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], globalKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], checkDropdownPosition: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['mouseenter']
            }] } });

const autocompleteBaseStyles$1 = [
    'absolute',
    'max-h-48',
    'right-0',
    'top-full',
    'border',
    'border-t-0',
    'shadow-md',
    'z-50',
    'w-full',
    'overflow-y-auto'
];
const autocompleteStylesSAG$1 = cva([
    ...autocompleteBaseStyles$1,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const autocompleteStylesSHS$1 = cva([...autocompleteBaseStyles$1, 'bg-white', 'border-0', 'dark:bg-[#333333]']);

const autocompleteOptionBaseStyles$2 = [
    'p-3',
    'cursor-pointer',
    'flex',
    'items-center',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const autocompleteOptionStylesSAG$2 = cva([
    ...autocompleteOptionBaseStyles$2,
    'hover:bg-[#62EEC7]',
    'dark:hover:bg-[#005159]'
]);
const autocompleteOptionSelectedStylesSAG$2 = cva([...autocompleteOptionBaseStyles$2, 'bg-[#62EEC7]', 'dark:bg-[#005159]']);
const autocompleteionOptionTextStylesSAG$2 = cva(['text-[#000028]', 'dark:text-white']);
const autocompleteOptionStylesSHS$2 = cva([
    ...autocompleteOptionBaseStyles$2,
    'hover:bg-[#f2f2f2]',
    'dark:hover:bg-[#474747]'
]);
const autocompleteOptionSelectedStylesSHS$2 = cva([...autocompleteOptionBaseStyles$2, 'bg-[#e6e6e6]', 'dark:bg-[#666]']);
const autocompleteionOptionTextStylesSHS$2 = cva(['dark:text-[#e6e6e6]']);

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class RemoteAutocompleteOptionComponent {
    theme = inject(THEME_TOKEN);
    value = input();
    selected = input(false);
    hidden = input(false);
    ariaPosinset = input(null);
    ariaSetsize = input(null);
    autocomplete = output();
    text = viewChild.required('text');
    get containerClasses() {
        const containerStyles = componentStylesByTheme(this.theme, autocompleteOptionStylesSAG$2, autocompleteOptionStylesSHS$2);
        const autocompleteedContainerStyles = componentStylesByTheme(this.theme, autocompleteOptionSelectedStylesSAG$2, autocompleteOptionSelectedStylesSHS$2);
        return this.selected() ? autocompleteedContainerStyles() : containerStyles();
    }
    get textClasses() {
        const textStyles = componentStylesByTheme(this.theme, autocompleteionOptionTextStylesSAG$2, autocompleteionOptionTextStylesSHS$2);
        return textStyles();
    }
    focus() {
        this.el.nativeElement.focus();
    }
    onClick() {
        const innerText = this.text().nativeElement.innerText;
        this.autocomplete.emit([innerText, this.value()]);
    }
    onKeyDown(event) {
        if (event.code === 'Space' || event.code === 'Enter') {
            event.preventDefault();
            this.onClick();
            event.target.focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RemoteAutocompleteOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: RemoteAutocompleteOptionComponent, isStandalone: true, selector: "sag-ui-remote-autocomplete-option", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null }, hidden: { classPropertyName: "hidden", publicName: "hidden", isSignal: true, isRequired: false, transformFunction: null }, ariaPosinset: { classPropertyName: "ariaPosinset", publicName: "ariaPosinset", isSignal: true, isRequired: false, transformFunction: null }, ariaSetsize: { classPropertyName: "ariaSetsize", publicName: "ariaSetsize", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { autocomplete: "autocomplete" }, host: { listeners: { "keydown": "onKeyDown($event)" } }, viewQueries: [{ propertyName: "text", first: true, predicate: ["text"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n    [class]=\"containerClasses\"\n    [class.hidden]=\"hidden()\"\n    (click)=\"onClick()\"\n    (keydown)=\"onKeyDown($event)\"\n    role=\"listitem\"\n    [attr.aria-posinset]=\"ariaPosinset\"\n    [attr.aria-setsize]=\"ariaSetsize\"\n    [attr.aria-hidden]=\"hidden\"\n    [attr.aria-selected]=\"selected\"\n>\n    <span [class]=\"textClasses\" #text>\n        <ng-content></ng-content>\n    </span>\n</div>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RemoteAutocompleteOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-remote-autocomplete-option', standalone: true, template: "<div\n    [class]=\"containerClasses\"\n    [class.hidden]=\"hidden()\"\n    (click)=\"onClick()\"\n    (keydown)=\"onKeyDown($event)\"\n    role=\"listitem\"\n    [attr.aria-posinset]=\"ariaPosinset\"\n    [attr.aria-setsize]=\"ariaSetsize\"\n    [attr.aria-hidden]=\"hidden\"\n    [attr.aria-selected]=\"selected\"\n>\n    <span [class]=\"textClasses\" #text>\n        <ng-content></ng-content>\n    </span>\n</div>\n" }]
        }], propDecorators: { onKeyDown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }] } });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class RemoteAutocompleteComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    id = input(null);
    placeholder = input('');
    required = input(false);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    readOnly = input(false);
    label = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = null;
    invalid = input(false);
    error = input('');
    hint = input(null);
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    options = input([]);
    loading = input(false);
    searchFn = input(null);
    searchFn$ = input(null);
    compareWith = input((o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    });
    change = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    input = viewChild.required('input');
    optionElements = viewChildren('optionItem');
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }
    globalKeydown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen = false;
            this.input().nativeElement.focus();
        }
    }
    checkDropdownPosition() {
        const selectBox = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropdownUpwards = selectBox.bottom + 200 > viewportHeight;
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }
    get computedLoading() {
        return this.loading() || this.internalLoading;
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG$1, autocompleteStylesSHS$1);
        return popoverStyles();
    }
    get displayValue() {
        return this.selectedItem ? this.selectedItem.label : '';
    }
    touched = false;
    selectedItem = null;
    isOpen = false;
    filterValue = '';
    searchSubject = new Subject();
    currentPage = 0;
    internalLoading = false;
    isDropdownUpwards = false;
    activeOptionId = '';
    // Accessibility properties:
    selectedItemName = ''; // Used to announce selected item names via a live region
    activeDescendantId = ''; // Used for aria-activedescendant on the listbox
    // IDs for accessibility
    inputId = '';
    labelId = '';
    errorId = '';
    subscriptions = [];
    constructor() {
        const baseId = this.id || this.randomId();
        this.inputId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    ngOnInit() {
        this.initSearchSubject();
        this.initSelectedItems();
        this.loadOptions('', 1);
    }
    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    onFocus() {
        if (this.disabled || this.readOnly())
            return;
        this.isOpen = true;
    }
    onBlur() {
        this.markAsTouched();
    }
    togglePopover(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.isOpen) {
            this.isOpen = false;
            this.input().nativeElement.blur();
        }
        else {
            this.isOpen = true;
            this.input().nativeElement.focus();
        }
    }
    handleInput(event) {
        const target = event.target;
        this.isOpen = true;
        this.filterValue = target.value;
        this.searchSubject.next(this.filterValue);
    }
    handleChange() {
        this.selectedItem = null;
        this.writeValue(null);
    }
    handleInputKeydown(event) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const visibleOptions = this.optionElements().filter(option => !option.hidden);
            if (visibleOptions.length > 0) {
                visibleOptions[0].nativeElement.focus();
                // Update activeDescendantId when focusing the first visible option.
                this.activeDescendantId = visibleOptions[0].nativeElement.id;
            }
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen = false;
            this.input().nativeElement.focus();
        }
        this.keydown.emit(event);
    }
    handleEscapeKey(event) {
        event.preventDefault();
        this.isOpen = false;
        this.input().nativeElement.focus();
    }
    handleOptionKeydown(event, data) {
        const optionsArray = this.optionElements();
        const currentIndex = optionsArray.findIndex(el => el.nativeElement === event.target);
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextIndex = (currentIndex + 1) % optionsArray.length;
            optionsArray[nextIndex].nativeElement.focus();
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevIndex = (currentIndex - 1 + optionsArray.length) % optionsArray.length;
            optionsArray[prevIndex].nativeElement.focus();
        }
        else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleOptionSelect(data);
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen = false;
            this.input().nativeElement.focus();
        }
    }
    handleOptionSelect(data) {
        const [label, value] = data;
        this.input().nativeElement.focus();
        this.markAsTouched();
        this.selectedItemName = label;
        if (this.selectedItem && this.compareWith()(this.value, value)) {
            this.selectedItem = null;
            this.writeValue(null);
        }
        else {
            this.selectedItem = { label, value };
            this.writeValue(value);
        }
        this.isOpen = false;
        setTimeout(() => {
            this.input().nativeElement.blur();
        }, 50);
    }
    handleKeyDown(event, data) {
        if (event.code === 'Space') {
            event.preventDefault();
            this.handleOptionSelect(data);
        }
    }
    onScroll(event) {
        const target = event.target;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
            this.currentPage++;
            this.loadOptions(this.filterValue, this.currentPage);
        }
    }
    trackByFn(index, option) {
        return option.value;
    }
    getOptionId(option) {
        return `${this.id()}-option-${option.value}`;
    }
    isOptionSelected(option) {
        return this.selectedItem ? this.compareWith()(this.selectedItem.value, option.value) : false;
    }
    isArray(value) {
        return Array.isArray(value);
    }
    // ControlValueAccessor implementation
    onChange = (_value) => { };
    onTouch = () => { };
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    writeValue(val) {
        this.value = val;
        this.onChange(this.value);
        this.change.emit(this.value);
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    validate(_) {
        return this.required() && !this.selectedItem ? { required: true } : null;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    initSearchSubject() {
        this.subscriptions.push(this.searchSubject
            .pipe(debounceTime(500), tap$1(searchText => {
            this.currentPage = 0;
            this.loadOptions(searchText, this.currentPage);
        }))
            .subscribe());
    }
    initSelectedItems() {
        if (this.value) {
            this.selectedItem = this.options().find(option => this.compareWith()(this.value, option.value));
        }
    }
    async loadOptions(search, page) {
        const searchFn = this.searchFn();
        if (searchFn) {
            this.internalLoading = true;
            try {
                await searchFn(search, page);
            }
            finally {
                this.internalLoading = false;
            }
        }
        const searchFn$ = this.searchFn$();
        if (searchFn$) {
            searchFn$(search, page).pipe(finalize(() => (this.internalLoading = false)));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RemoteAutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: RemoteAutocompleteComponent, isStandalone: true, selector: "sag-ui-remote-autocomplete", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null }, options: { classPropertyName: "options", publicName: "options", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, searchFn: { classPropertyName: "searchFn", publicName: "searchFn", isSignal: true, isRequired: false, transformFunction: null }, searchFn$: { classPropertyName: "searchFn$", publicName: "searchFn$", isSignal: true, isRequired: false, transformFunction: null }, compareWith: { classPropertyName: "compareWith", publicName: "compareWith", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "keydown": "globalKeydown($event)", "click": "checkDropdownPosition()", "mouseenter": "checkDropdownPosition()", "focusout": "onFocusOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RemoteAutocompleteComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => RemoteAutocompleteComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }, { propertyName: "optionElements", predicate: ["optionItem"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"sr-only\" aria-live=\"polite\" aria-atomic=\"true\">Selected item: {{ selectedItem?.label }}</div>\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      class=\"pr-8\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [value]=\"displayValue\"\n      [attr.aria-label]=\"ariaLabel()\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n      [attr.aria-describedby]=\"ariaDescribedby()\"\n      [attr.aria-activedescendant]=\"activeOptionId\"\n      [attr.aria-disabled]=\"disabled\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeydown($event)\"\n      (keydown.escape)=\"handleEscapeKey($event)\"\n      (keyup)=\"keyup.emit($event)\"\n      (mouseenter)=\"mouseenter.emit($event)\"\n      (mouseleave)=\"mouseleave.emit($event)\"\n      type=\"text\"\n      sag-ui-input\n      #input\n    />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          [tooltip]=\"hint()\"\n          sag-ui-tooltip\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    @if (computedLoading && !disabled) {\n      <sag-ui-spinner\n        class=\"absolute right-4 top-[37px]\"\n        size=\"sm\"\n      ></sag-ui-spinner>\n    } @else {\n      <span\n        class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white\"\n        aria-label=\"Toggle dropdown\"\n        [attr.data-disabled]=\"disabled\"\n        [attr.data-open]=\"isOpen\"\n        [attr.tabindex]=\"disabled ? -1 : 0\"\n        [class.icon-chevron-up]=\"isOpen\"\n        [class.icon-chevron-down]=\"!isOpen\"\n        [class.opacity-50]=\"disabled\"\n        (click)=\"!disabled && togglePopover($event)\"\n        (keydown.space)=\"!disabled && togglePopover($event)\"\n      ></span>\n    }\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen\"\n      [class.top-full]=\"!isDropdownUpwards\"\n      [class.bottom-input]=\"isDropdownUpwards\"\n      role=\"listbox\"\n      aria-label=\"List of suggestions\"\n      [attr.aria-activedescendant]=\"activeOptionId\"\n      (scroll)=\"onScroll($event)\"\n      style=\"max-height: 200px; overflow-y: auto\"\n    >\n      @if (options() && options().length >0) {\n      @for (option of options(); track option.value) {\n        <div\n          #optionItem\n          role=\"option\"\n          tabindex=\"-1\"\n          [id]=\"getOptionId(option)\"\n          [attr.aria-setsize]=\"options().length\"\n          [attr.aria-posinset]=\"$index + 1\"\n          [attr.aria-selected]=\"isOptionSelected(option)\"\n          (focus)=\"activeOptionId = getOptionId(option)\"\n          (click)=\"handleOptionSelect([option.label, option.value])\"\n          (keydown)=\"handleOptionKeydown($event, [option.label, option.value])\"\n          class=\"autocomplete-option\">\n        <sag-ui-remote-autocomplete-option>\n          {{ option.label }}\n        </sag-ui-remote-autocomplete-option>\n        </div>\n      }\n      } @else {\n        <div class=\"no-options\" role=\"alert\" aria-live=\"assertive\">No matching items found</div>\n      }\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  } @else {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n", dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: SpinnerComponent, selector: "sag-ui-spinner", inputs: ["size", "ariaLabel"] }, { kind: "component", type: RemoteAutocompleteOptionComponent, selector: "sag-ui-remote-autocomplete-option", inputs: ["value", "selected", "hidden", "ariaPosinset", "ariaSetsize"], outputs: ["autocomplete"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RemoteAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-remote-autocomplete', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RemoteAutocompleteComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => RemoteAutocompleteComponent),
                            multi: true
                        }
                    ], imports: [
                        InputDirective,
                        InputLabelDirective,
                        TooltipDirective,
                        SpinnerComponent,
                        RemoteAutocompleteOptionComponent,
                        InputErrorDirective
                    ], template: "<div class=\"flex flex-col\">\n  <div class=\"sr-only\" aria-live=\"polite\" aria-atomic=\"true\">Selected item: {{ selectedItem?.label }}</div>\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      class=\"pr-8\"\n      [id]=\"inputId\"\n      [disabled]=\"disabled\"\n      [placeholder]=\"placeholder()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [value]=\"displayValue\"\n      [attr.aria-label]=\"ariaLabel()\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n      [attr.aria-describedby]=\"ariaDescribedby()\"\n      [attr.aria-activedescendant]=\"activeOptionId\"\n      [attr.aria-disabled]=\"disabled\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      (keydown)=\"handleInputKeydown($event)\"\n      (keydown.escape)=\"handleEscapeKey($event)\"\n      (keyup)=\"keyup.emit($event)\"\n      (mouseenter)=\"mouseenter.emit($event)\"\n      (mouseleave)=\"mouseleave.emit($event)\"\n      type=\"text\"\n      sag-ui-input\n      #input\n    />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          [tooltip]=\"hint()\"\n          sag-ui-tooltip\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    @if (computedLoading && !disabled) {\n      <sag-ui-spinner\n        class=\"absolute right-4 top-[37px]\"\n        size=\"sm\"\n      ></sag-ui-spinner>\n    } @else {\n      <span\n        class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white\"\n        aria-label=\"Toggle dropdown\"\n        [attr.data-disabled]=\"disabled\"\n        [attr.data-open]=\"isOpen\"\n        [attr.tabindex]=\"disabled ? -1 : 0\"\n        [class.icon-chevron-up]=\"isOpen\"\n        [class.icon-chevron-down]=\"!isOpen\"\n        [class.opacity-50]=\"disabled\"\n        (click)=\"!disabled && togglePopover($event)\"\n        (keydown.space)=\"!disabled && togglePopover($event)\"\n      ></span>\n    }\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen\"\n      [class.top-full]=\"!isDropdownUpwards\"\n      [class.bottom-input]=\"isDropdownUpwards\"\n      role=\"listbox\"\n      aria-label=\"List of suggestions\"\n      [attr.aria-activedescendant]=\"activeOptionId\"\n      (scroll)=\"onScroll($event)\"\n      style=\"max-height: 200px; overflow-y: auto\"\n    >\n      @if (options() && options().length >0) {\n      @for (option of options(); track option.value) {\n        <div\n          #optionItem\n          role=\"option\"\n          tabindex=\"-1\"\n          [id]=\"getOptionId(option)\"\n          [attr.aria-setsize]=\"options().length\"\n          [attr.aria-posinset]=\"$index + 1\"\n          [attr.aria-selected]=\"isOptionSelected(option)\"\n          (focus)=\"activeOptionId = getOptionId(option)\"\n          (click)=\"handleOptionSelect([option.label, option.value])\"\n          (keydown)=\"handleOptionKeydown($event, [option.label, option.value])\"\n          class=\"autocomplete-option\">\n        <sag-ui-remote-autocomplete-option>\n          {{ option.label }}\n        </sag-ui-remote-autocomplete-option>\n        </div>\n      }\n      } @else {\n        <div class=\"no-options\" role=\"alert\" aria-live=\"assertive\">No matching items found</div>\n      }\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  } @else {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], globalKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], checkDropdownPosition: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['mouseenter']
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

const autocompleteBaseStyles = [
    'absolute',
    'max-h-48',
    'right-0',
    'top-full',
    'border',
    'border-t-0',
    'shadow-md',
    'z-50',
    'w-full',
    'overflow-y-auto'
];
const autocompleteStylesSAG = cva([
    ...autocompleteBaseStyles,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const autocompleteStylesSHS = cva([...autocompleteBaseStyles, 'bg-white', 'border-0', 'dark:bg-[#333333]']);
const inputBaseStyles = [
    'peer',
    'relative',
    'w-full',
    'select-none',
    'data-[focus=true]:ring-0',
    'rounded-sm',
    'py-2',
    'px-4',
    'text-base', // TODO: Check with Tom (18px in Figma seem to large)
    'border',
    'data-[float-label=true]:pb-0',
    'data-[float-label=true]:pt-4',
    'data-[focus=true]:outline',
    'data-[focus=true]:outline-offset-2',
    'data-[focus=true]:outline-blue-500',
    'data-[float-label=true]:pb-0',
    'data-[float-label=true]:pt-4'
];
const inputStylesSAG = cva([
    ...inputBaseStyles,
    'text-[#000028]',
    'bg-[#EBF7F8]',
    'border-[#4C4C68]',
    'placeholder:text-[#4C4C68]',
    'hover:bg-[#D1FFF2]',
    'hover:border-[#005159]',
    'hover:placeholder:text-[#000028]',
    'data-[focus=true]:bg-[#D1FFF2]',
    'data-[focus=true]:border-[#4C4C68]',
    'data-[focus=true]:placeholder:text-[#4C4C68]',
    'data-[active=true]:bg-[#D1FFF2]',
    'data-[active=true]:border-[#005159]',
    'invalid:bg-[#FCCCD7]',
    'invalid:border-[#D72339]',
    'invalid:placeholder:text-[#000028]',
    'invalid:hover:bg-[#FCCCD7]',
    'invalid:hover:border-[#D72339]',
    'invalid:hover:placeholder:text-[#000028]',
    'invalid:data-[active=true]:bg-[#FCCCD7]',
    'invalid:data-[active=true]:border-[#D72339]',
    'invalid:data-[active=true]:placeholder:text-[#000028]',
    'invalid:data-[focus=true]:bg-[#FCCCD7]',
    'invalid:data-[focus=true]:border-[#D72339]',
    'invalid:data-[focus=true]:placeholder:text-[#000028]',
    'data-[invalid=true]:bg-[#FCCCD7]',
    'data-[invalid=true]:border-[#D72339]',
    'data-[invalid=true]:placeholder:text-[#000028]',
    'data-[invalid=true]:hover:bg-[#FCCCD7]',
    'data-[invalid=true]:hover:border-[#D72339]',
    'data-[invalid=true]:hover:placeholder:text-[#000028]',
    'data-[invalid=true]:data-[active=true]:bg-[#FCCCD7]',
    'data-[invalid=true]:data-[active=true]:border-[#D72339]',
    'data-[invalid=true]:data-[active=true]:placeholder:text-[#000028]',
    'data-[invalid=true]:data-[focus=true]:bg-[#FCCCD7]',
    'data-[invalid=true]:data-[focus=true]:border-[#D72339]',
    'data-[invalid=true]:data-[focus=true]:placeholder:text-[#000028]',
    'data-[disabled=true]:bg-[#EBF7F8]',
    'data-[disabled=true]:border-[#CCCCD4]',
    'data-[disabled=true]:placeholder:text-[#4C4C68]',
    'data-[disabled=true]:hover:bg-[#EBF7F8]',
    'data-[disabled=true]:hover:border-[#CCCCD4]',
    'data-[disabled=true]:hover:placeholder:text-[#4C4C68]',
    'data-[disabled=true]:data-[focus=true]:bg-[#EBF7F8]',
    'data-[disabled=true]:data-[focus=true]:border-[#CCCCD4]',
    'data-[disabled=true]:data-[focus=true]:placeholder:text-[#4C4C68]',
    'data-[read-only=true]:bg-white',
    'data-[read-only=true]:border-[#CCCCD4]',
    'data-[read-only=true]:placeholder:text-[#4C4C68]',
    'data-[read-only=true]:hover:bg-white',
    'data-[read-only=true]:hover:border-[#CCCCD4]',
    'data-[read-only=true]:hover:placeholder:text-[#4C4C68]',
    'data-[read-only=true]:data-[focus=true]:bg-white',
    'data-[read-only=true]:data-[focus=true]:border-[#CCCCD4]',
    'data-[read-only=true]:data-[focus=true]:placeholder:text-[#4C4C68]',
    'dark:text-white',
    'dark:bg-[#00183B]',
    'dark:border-[#B3B3BE]',
    'dark:placeholder:text-[#B3B3BE]',
    'dark:hover:bg-[#001F39]',
    'dark:hover:border-[#00FFB9]',
    'dark:hover:placeholder:text-white',
    'dark:data-[focus=true]:bg-[#001F39]',
    'dark:data-[focus=true]:border-[#00FFB9]',
    'dark:data-[focus=true]:placeholder:text-[#B3B3BE]',
    'dark:data-[active=true]:bg-[#001F39]',
    'dark:data-[active=true]:border-[#00FFB9]',
    'dark:invalid:bg-[#331131]',
    'dark:invalid:border-[#FF2640]',
    'dark:invalid:placeholder:text-[#FFFFFF]',
    'dark:invalid:hover:bg-[#331131]',
    'dark:invalid:hover:border-[#FF2640]',
    'dark:invalid:hover:placeholder:text-[#FFFFFF]',
    'dark:invalid:data-[active=true]:bg-[#331131]',
    'dark:invalid:data-[active=true]:border-[#FF2640]',
    'dark:invalid:data-[active=true]:placeholder:text-[#FFFFFF]',
    'dark:invalid:data-[focus=true]:bg-[#331131]',
    'dark:invalid:data-[focus=true]:border-[#FF2640]',
    'dark:invalid:data-[focus=true]:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:bg-[#331131]',
    'dark:data-[invalid=true]:border-[#FF2640]',
    'dark:data-[invalid=true]:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:hover:bg-[#331131]',
    'dark:data-[invalid=true]:hover:border-[#FF2640]',
    'dark:data-[invalid=true]:hover:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:data-[active=true]:bg-[#331131]',
    'dark:data-[invalid=true]:data-[active=true]:border-[#FF2640]',
    'dark:data-[invalid=true]:data-[active=true]:placeholder:text-[#FFFFFF]',
    'dark:data-[invalid=true]:data-[focus=true]:bg-[#331131]',
    'dark:data-[invalid=true]:data-[focus=true]:border-[#FF2640]',
    'dark:data-[invalid=true]:data-[focus=true]:placeholder:text-[#FFFFFF]',
    'dark:data-[disabled=true]:bg-[#262648]',
    'dark:data-[disabled=true]:border-[#4C4C68]',
    'dark:data-[disabled=true]:placeholder:text-[#737389]',
    'dark:data-[disabled=true]:hover:bg-[#262648]',
    'dark:data-[disabled=true]:hover:border-[#4C4C68]',
    'dark:data-[disabled=true]:hover:placeholder:text-[#737389]',
    'dark:data-[disabled=true]:data-[focus=true]:bg-[#262648]',
    'dark:data-[disabled=true]:data-[focus=true]:border-[#4C4C68]',
    'dark:data-[disabled=true]:data-[focus=true]:placeholder:text-[#737389]',
    'dark:data-[read-only=true]:bg-[#000028]',
    'dark:data-[read-only=true]:border-[#333353]',
    'dark:data-[read-only=true]:placeholder:text-[#B3B3BE]',
    'dark:data-[read-only=true]:hover:bg-[#000028]',
    'dark:data-[read-only=true]:hover:border-[#333353]',
    'dark:data-[read-only=true]:hover:placeholder:text-[#B3B3BE]',
    'dark:data-[read-only=true]:data-[focus=true]:bg-[#000028]',
    'dark:data-[read-only=true]:data-[focus=true]:border-[#333353]',
    'dark:data-[read-only=true]:data-[focus=true]:placeholder:text-[#B3B3BE]'
]);
const inputStylesSHS = cva([
    ...inputBaseStyles,
    'text-[#1a1a1a]',
    'bg-[#ededed]',
    'border-[#666666]',
    'placeholder:text-[#666666]',
    'hover:bg-[#ededed]',
    'hover:border-[#666666]',
    'hover:placeholder:text-[#666666]',
    'data-[focus=true]:bg-[#ededed]',
    'data-[focus=true]:border-[#666666]',
    'data-[focus=true]:placeholder:text-[#666666]',
    'data-[active=true]:bg-[#ededed]',
    'data-[active=true]:border-[#005159]',
    'invalid:bg-[#ededed]',
    'invalid:border-[#e7001e]',
    'invalid:placeholder:text-[#666666]',
    'invalid:hover:bg-[#ededed]',
    'invalid:hover:border-[#e7001e]',
    'invalid:hover:placeholder:text-[#666666]',
    'invalid:data-[active=true]:bg-[#ededed]',
    'invalid:data-[active=true]:border-[#e7001e]',
    'invalid:data-[active=true]:placeholder:text-[#666666]',
    'invalid:data-[focus=true]:bg-[#ededed]',
    'invalid:data-[focus=true]:border-[#e7001e]',
    'invalid:data-[focus=true]:placeholder:text-[#666666]',
    'data-[invalid=true]:bg-[#ededed]',
    'data-[invalid=true]:border-[#e7001e]',
    'data-[invalid=true]:placeholder:text-[#666666]',
    'data-[invalid=true]:hover:bg-[#ededed]',
    'data-[invalid=true]:hover:border-[#e7001e]',
    'data-[invalid=true]:hover:placeholder:text-[#666666]',
    'data-[invalid=true]:data-[active=true]:bg-[#ededed]',
    'data-[invalid=true]:data-[active=true]:border-[#e7001e]',
    'data-[invalid=true]:data-[active=true]:placeholder:text-[#666666]',
    'data-[invalid=true]:data-[focus=true]:bg-[#ededed]',
    'data-[invalid=true]:data-[focus=true]:border-[#e7001e]',
    'data-[invalid=true]:data-[focus=true]:placeholder:text-[#666666]',
    'data-[disabled=true]:bg-[#f8f8f8]',
    'data-[disabled=true]:border-[#c2c2c2]',
    'data-[disabled=true]:placeholder:text-[#4C4C68]',
    'data-[disabled=true]:hover:bg-[#f8f8f8]',
    'data-[disabled=true]:hover:border-[#c2c2c2]',
    'data-[disabled=true]:hover:placeholder:text-[#4C4C68]',
    'data-[disabled=true]:data-[focus=true]:bg-[#f8f8f8]',
    'data-[disabled=true]:data-[focus=true]:border-[#c2c2c2]',
    'data-[disabled=true]:data-[focus=true]:placeholder:text-[#4C4C68]',
    'data-[read-only=true]:bg-[#ededed]',
    'data-[read-only=true]:border-[#666666]',
    'data-[read-only=true]:placeholder:text-[#666666]',
    'data-[read-only=true]:hover:bg-[#ededed]',
    'data-[read-only=true]:hover:border-[#666666]',
    'data-[read-only=true]:hover:placeholder:text-[#666666]',
    'data-[read-only=true]:data-[focus=true]:bg-[#ededed]',
    'data-[read-only=true]:data-[focus=true]:border-[#666666]',
    'data-[read-only=true]:data-[focus=true]:placeholder:text-[#666666]',
    'dark:text-[#e6e6e6]',
    'dark:bg-[#1a1a1a]',
    'dark:border-[#999999]',
    'dark:placeholder:text-[#999999]',
    'dark:hover:bg-[#1a1a1a]',
    'dark:hover:border-[#999999]',
    'dark:hover:placeholder:text-[#999999]',
    'dark:data-[focus=true]:bg-[#1a1a1a]',
    'dark:data-[focus=true]:border-[#999999]',
    'dark:data-[focus=true]:placeholder:text-[#999999]',
    'dark:data-[active=true]:bg-[#1a1a1a]',
    'dark:data-[active=true]:border-[#999999]',
    'dark:invalid:bg-[#1a1a1a]',
    'dark:invalid:border-[#e7001e]',
    'dark:invalid:placeholder:text-[#999999]',
    'dark:invalid:hover:bg-[#1a1a1a]',
    'dark:invalid:hover:border-[#e7001e]',
    'dark:invalid:hover:placeholder:text-[#999999]',
    'dark:invalid:data-[active=true]:bg-[#1a1a1a]',
    'dark:invalid:data-[active=true]:border-[#e7001e]',
    'dark:invalid:data-[active=true]:placeholder:text-[#999999]',
    'dark:invalid:data-[focus=true]:bg-[#1a1a1a]',
    'dark:invalid:data-[focus=true]:border-[#e7001e]',
    'dark:invalid:data-[focus=true]:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:border-[#e7001e]',
    'dark:data-[invalid=true]:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:hover:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:hover:border-[#e7001e]',
    'dark:data-[invalid=true]:hover:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:data-[active=true]:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:data-[active=true]:border-[#e7001e]',
    'dark:data-[invalid=true]:data-[active=true]:placeholder:text-[#999999]',
    'dark:data-[invalid=true]:data-[focus=true]:bg-[#1a1a1a]',
    'dark:data-[invalid=true]:data-[focus=true]:border-[#e7001e]',
    'dark:data-[invalid=true]:data-[focus=true]:placeholder:text-[#999999]',
    'dark:data-[disabled=true]:bg-[#0a0a0a]',
    'dark:data-[disabled=true]:border-[#3d3d3d]',
    'dark:data-[disabled=true]:placeholder:text-[#999999]',
    'dark:data-[disabled=true]:hover:bg-[#0a0a0a]',
    'dark:data-[disabled=true]:hover:border-[#3d3d3d]',
    'dark:data-[disabled=true]:hover:placeholder:text-[#999999]',
    'dark:data-[disabled=true]:data-[focus=true]:bg-[#0a0a0a]',
    'dark:data-[disabled=true]:data-[focus=true]:border-[#3d3d3d]',
    'dark:data-[disabled=true]:data-[focus=true]:placeholder:text-[#999999]',
    'dark:data-[read-only=true]:bg-[#1a1a1a]',
    'dark:data-[read-only=true]:border-[#999999]',
    'dark:data-[read-only=true]:placeholder:text-[#999999]',
    'dark:data-[read-only=true]:hover:bg-[#1a1a1a]',
    'dark:data-[read-only=true]:hover:border-[#999999]',
    'dark:data-[read-only=true]:hover:placeholder:text-[#999999]',
    'dark:data-[read-only=true]:data-[focus=true]:bg-[#1a1a1a]',
    'dark:data-[read-only=true]:data-[focus=true]:border-[#999999]',
    'dark:data-[read-only=true]:data-[focus=true]:placeholder:text-[#999999]'
]);
const autocompleteChipStylesSAG = cva([
    'border-[#005159]',
    'bg-[#D1FFF2]',
    'text-[#005159]',
    'dark:border-[#00FFB9]',
    'dark:bg-[#001F39]',
    'dark:text-[#00FFB9]'
]);
const autocompleteChipStylesSHS = cva([
    'border-[#666666]',
    'bg-[#ededed]',
    'text-[#666666]',
    'dark:border-[#ededed]',
    'dark:bg-[#1a1a1a]',
    'dark:text-[#ededed]'
]);

const autocompleteOptionBaseStyles$1 = [
    'p-3',
    'cursor-pointer',
    'flex',
    'items-center',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const autocompleteOptionStylesSAG$1 = cva([
    ...autocompleteOptionBaseStyles$1,
    'hover:bg-[#62EEC7]',
    'dark:hover:bg-[#005159]'
]);
const autocompleteOptionSelectedStylesSAG$1 = cva([...autocompleteOptionBaseStyles$1, 'bg-[#62EEC7]', 'dark:bg-[#005159]']);
const autocompleteionOptionTextStylesSAG$1 = cva(['text-[#000028]', 'dark:text-white', 'w-full']);
const autocompleteOptionStylesSHS$1 = cva([
    ...autocompleteOptionBaseStyles$1,
    'hover:bg-[#f2f2f2]',
    'dark:hover:bg-[#474747]'
]);
const autocompleteOptionSelectedStylesSHS$1 = cva([...autocompleteOptionBaseStyles$1, 'bg-[#e6e6e6]', 'dark:bg-[#666]']);
const autocompleteionOptionTextStylesSHS$1 = cva(['dark:text-[#e6e6e6]', 'w-full']);

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class MultiAutocompleteOptionComponent {
    theme = inject(THEME_TOKEN);
    value = input();
    selected = input(false);
    hidden = input(false);
    ariaPosinset = input(null);
    ariaSetsize = input(null);
    autocomplete = output();
    text = viewChild.required('text');
    get containerClasses() {
        const containerStyles = componentStylesByTheme(this.theme, autocompleteOptionStylesSAG$1, autocompleteOptionStylesSHS$1);
        const autocompleteedContainerStyles = componentStylesByTheme(this.theme, autocompleteOptionSelectedStylesSAG$1, autocompleteOptionSelectedStylesSHS$1);
        return this.selected() ? autocompleteedContainerStyles() : containerStyles();
    }
    get textClasses() {
        const textStyles = componentStylesByTheme(this.theme, autocompleteionOptionTextStylesSAG$1, autocompleteionOptionTextStylesSHS$1);
        return textStyles();
    }
    onClick() {
        const innerText = this.text().nativeElement.innerText;
        this.autocomplete.emit([innerText, this.value()]);
    }
    onKeyDown(event) {
        if (event.code === 'Space' || event.code === 'Enter') {
            event.preventDefault();
            this.onClick();
            event.target.focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MultiAutocompleteOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: MultiAutocompleteOptionComponent, isStandalone: true, selector: "sag-ui-multi-autocomplete-option", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null }, hidden: { classPropertyName: "hidden", publicName: "hidden", isSignal: true, isRequired: false, transformFunction: null }, ariaPosinset: { classPropertyName: "ariaPosinset", publicName: "ariaPosinset", isSignal: true, isRequired: false, transformFunction: null }, ariaSetsize: { classPropertyName: "ariaSetsize", publicName: "ariaSetsize", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { autocomplete: "autocomplete" }, viewQueries: [{ propertyName: "text", first: true, predicate: ["text"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n  [class]=\"containerClasses\"\n  [class.hidden]=\"hidden()\"\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  [attr.aria-posinset]=\"ariaPosinset()\"\n  [attr.aria-setsize]=\"ariaSetsize()\"\n  [attr.aria-hidden]=\"hidden()\"\n  [attr.aria-selected]=\"selected()\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MultiAutocompleteOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-multi-autocomplete-option', standalone: true, template: "<div\n  [class]=\"containerClasses\"\n  [class.hidden]=\"hidden()\"\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  [attr.aria-posinset]=\"ariaPosinset()\"\n  [attr.aria-setsize]=\"ariaSetsize()\"\n  [attr.aria-hidden]=\"hidden()\"\n  [attr.aria-selected]=\"selected()\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" }]
        }] });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class MultiAutocompleteComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    id = input(null);
    placeholder = input('');
    required = input(false);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    readOnly = input(false);
    label = input('');
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = null;
    invalid = input(false);
    error = input('');
    hint = input(null);
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    options = input([]);
    addButtonLabel = input('Add');
    searchFn = input();
    searchFn$ = input();
    compareWith = input((o1, o2) => {
        if (typeof o1 === 'object' && typeof o2 === 'object') {
            return JSON.stringify(o1) === JSON.stringify(o2);
        }
        return o1 === o2;
    });
    change = output();
    itemCreated = output();
    click = output();
    focus = output();
    blur = output();
    keydown = output();
    keyup = output();
    mouseenter = output();
    mouseleave = output();
    input = viewChild.required('input');
    optionElements = viewChildren('optionItem');
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }
    globalKeydown(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen = false;
            this.input().nativeElement.focus();
        }
    }
    checkDropdownPosition() {
        const selectBox = this.el.nativeElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        this.isDropdownUpwards = selectBox.bottom + 200 > viewportHeight;
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, autocompleteStylesSAG, autocompleteStylesSHS);
        return popoverStyles();
    }
    get inputClasses() {
        const inputStyles = componentStylesByTheme(this.theme, inputStylesSAG, inputStylesSHS);
        return inputStyles();
    }
    get chipClasses() {
        const chipStyles = componentStylesByTheme(this.theme, autocompleteChipStylesSAG, autocompleteChipStylesSHS);
        return chipStyles();
    }
    get inputValue() {
        const inputValue = this.input();
        if (!inputValue) {
            return '';
        }
        return inputValue.nativeElement.value;
    }
    touched = false;
    selectedItems = [];
    isOpen = false;
    filterValue = '';
    searchSubject = new Subject();
    currentPage = 0;
    loading = false;
    isDropdownUpwards = false;
    activeOptionId = '';
    // Accessibility properties:
    selectedItemName = ''; // Used to announce selected item names via a live region
    activeDescendantId = ''; // Used for aria-activedescendant on the listbox
    // IDs for accessibility
    inputId = '';
    labelId = '';
    errorId = '';
    subscriptions = [];
    constructor() {
        const baseId = this.id || this.randomId();
        this.inputId = `input-${baseId}`;
        this.labelId = `label-${baseId}`;
        this.errorId = `error-${baseId}`;
    }
    ngOnInit() {
        this.initSearchSubject();
        this.initSelectedItems();
        this.loadOptions('', 1);
    }
    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
    onFocus() {
        if (this.disabled || this.readOnly())
            return;
        this.isOpen = true;
        this.input().nativeElement.focus();
    }
    onBlur() {
        this.markAsTouched();
    }
    togglePopover(event) {
        event.stopPropagation();
        event.preventDefault();
        if (this.isOpen) {
            this.isOpen = false;
            this.input().nativeElement.blur();
        }
        else {
            this.isOpen = true;
            this.input().nativeElement.focus();
        }
    }
    handleInput(event) {
        const target = event.target;
        this.filterValue = target.value;
        this.isOpen = true;
        this.searchSubject.next(this.filterValue);
    }
    handleInputKeydown(event) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!this.isOpen) {
                this.isOpen = true;
            }
            setTimeout(() => {
                const firstOption = this.optionElements().first;
                if (firstOption) {
                    firstOption.nativeElement.focus();
                }
            }, 0);
        }
        this.keydown.emit(event);
    }
    handleEnterKey(event) {
        event.preventDefault();
        this.handleAddOption();
    }
    handleEscapeKey(event) {
        event.preventDefault();
        this.isOpen = false;
        this.input().nativeElement.focus();
    }
    handleOptionKeydown(event, data, newItem) {
        const optionsArray = this.optionElements().toArray();
        const currentIndex = optionsArray.findIndex(el => el.nativeElement === event.target);
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextIndex = (currentIndex + 1) % optionsArray.length;
            optionsArray[nextIndex].nativeElement.focus();
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevIndex = (currentIndex - 1 + optionsArray.length) % optionsArray.length;
            optionsArray[prevIndex].nativeElement.focus();
        }
        else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (newItem) {
                this.handleAddOption();
            }
            else {
                this.handleOptionSelect(data);
            }
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            this.isOpen = false;
            this.input().nativeElement.focus();
        }
    }
    get displayValue() {
        return this.selectedItems.map(item => item.label).join(', ');
    }
    handleOptionSelect(data) {
        const [label, value] = data;
        this.input().nativeElement.focus();
        this.markAsTouched();
        this.selectedItemName = `Selected item: ${label}`;
        if (this.selectedItems?.some(item => this.compareWith()(item.value, value))) {
            this.selectedItems = this.selectedItems.filter(item => !this.compareWith()(item.value, value));
            this.writeValue(this.selectedItems);
        }
        else {
            this.selectedItems.push({ label, value });
            this.writeValue(this.selectedItems);
        }
        this.input().nativeElement.value = '';
        this.filterValue = '';
        this.searchSubject.next(this.filterValue);
        this.onFocus();
    }
    handleOptionSelectKeyDown(event, data) {
        if (event.code === 'Space') {
            this.handleOptionSelect(data);
        }
    }
    onScroll(event) {
        const target = event.target;
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 1) {
            this.currentPage++;
            this.loadOptions(this.filterValue, this.currentPage);
        }
    }
    isArray(value) {
        return Array.isArray(value);
    }
    // ControlValueAccessor implementation
    onChange = (_value) => { };
    onTouch = () => { };
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    writeValue(val) {
        this.value = val;
        this.onChange(this.value);
        this.change.emit(this.value);
        this.selectedItems = this.value;
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    validate(_) {
        return this.required() && !this.selectedItems.length ? { required: true } : null;
    }
    removeItem(index) {
        if (this.disabled || this.readOnly())
            return;
        this.selectedItems.splice(index, 1);
        this.writeValue(this.selectedItems);
    }
    handleClick(event, index) {
        event.stopPropagation();
        this.removeItem(index);
    }
    onKeyDown(event, index) {
        event.stopPropagation();
        event.preventDefault();
        if (event.code === 'Space') {
            this.removeItem(index);
        }
    }
    trackByFn(index, option) {
        return option.value;
    }
    getOptionId(option) {
        return `${this.id()}-option-${option?.value ?? ''}`;
    }
    handleAddOption() {
        const value = this.input().nativeElement?.value?.trim();
        if (!value) {
            return;
        }
        this.handleOptionSelect([value, value]);
        this.itemCreated.emit(value);
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    initSearchSubject() {
        this.subscriptions.push(this.searchSubject
            .pipe(debounceTime(500), tap$1(searchText => {
            this.currentPage = 0;
            this.loadOptions(searchText, this.currentPage);
        }))
            .subscribe());
    }
    initSelectedItems() {
        if (this.value) {
            this.selectedItems = this.options().filter(option => this.value.some(value => this.compareWith()(value, option.value)));
        }
    }
    async loadOptions(search, page) {
        this.loading = true;
        const searchFn = this.searchFn();
        if (searchFn) {
            try {
                await searchFn(search, page);
            }
            finally {
                this.loading = false;
            }
        }
        const searchFn$ = this.searchFn$();
        if (searchFn$) {
            searchFn$(search, page).pipe(finalize(() => (this.loading = false)));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MultiAutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: MultiAutocompleteComponent, isStandalone: true, selector: "sag-ui-multi-autocomplete", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null }, options: { classPropertyName: "options", publicName: "options", isSignal: true, isRequired: false, transformFunction: null }, addButtonLabel: { classPropertyName: "addButtonLabel", publicName: "addButtonLabel", isSignal: true, isRequired: false, transformFunction: null }, searchFn: { classPropertyName: "searchFn", publicName: "searchFn", isSignal: true, isRequired: false, transformFunction: null }, searchFn$: { classPropertyName: "searchFn$", publicName: "searchFn$", isSignal: true, isRequired: false, transformFunction: null }, compareWith: { classPropertyName: "compareWith", publicName: "compareWith", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { change: "change", itemCreated: "itemCreated", click: "click", focus: "focus", blur: "blur", keydown: "keydown", keyup: "keyup", mouseenter: "mouseenter", mouseleave: "mouseleave" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "keydown": "globalKeydown($event)", "click": "checkDropdownPosition()", "mouseenter": "checkDropdownPosition()", "focusout": "onFocusOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => MultiAutocompleteComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => MultiAutocompleteComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }, { propertyName: "optionElements", predicate: ["optionItem"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <div\n      [class]=\"inputClasses\"\n      [attr.data-focus]=\"isOpen\"\n      [attr.data-read-only]=\"readOnly()\"\n      [attr.data-disabled]=\"disabled\"\n      [attr.data-invalid]=\"invalid()\"\n      [attr.data-touched]=\"touched\"\n      (click)=\"onFocus()\"\n    >\n      <div class=\"flex flex-wrap gap-2\">\n        @for (item of selectedItems; track $index) {\n          <span [class]=\"'whitespace-nowrap px-3 py-0.5 text-sm font-medium rounded-md border flex items-center text-wrap ' + chipClasses\"\n            >{{ item.label }}\n            <span\n              class=\"icon-close cursor-pointer ml-2 focus-visible:outline-[#1491EB]\"\n              [attr.aria-label]=\"'Remove item: ' + item.label\"\n              (click)=\"handleClick($event, $index)\"\n              (keydown.space)=\"handleClick($event, $index)\"\n              (keydown.enter)=\"handleClick($event, $index)\"\n              tabindex=\"0\"\n            ></span>\n          </span>\n        }\n        <input\n          class=\"pr-8 max-w-52 w-full bg-transparent border-0 focus:ring-0 focus:outline-none\"\n          [id]=\"inputId\"\n          [readOnly]=\"false\"\n          [disabled]=\"disabled\"\n          [placeholder]=\"placeholder()\"\n          [required]=\"invalid() || (required() && touched)\"\n          [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n          [attr.aria-label]=\"ariaLabel()\"\n          [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n          [attr.aria-describedby]=\"ariaDescribedby()\"\n          (input)=\"handleInput($event)\"\n          (focus)=\"onFocus()\"\n          (blur)=\"onBlur()\"\n          (keydown)=\"handleInputKeydown($event)\"\n          (keydown.enter)=\"handleEnterKey($event)\"\n          (keydown.escape)=\"handleEscapeKey($event)\"\n          (keyup)=\"keyup.emit($event)\"\n          (mouseenter)=\"mouseenter.emit($event)\"\n          (mouseleave)=\"mouseleave.emit($event)\"\n          type=\"text\"\n          #input\n        />\n      </div>\n    </div>\n    <label [id]=\"labelId\" [htmlFor]=\"inputId\" sag-ui-input-label>\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\" [tooltip]=\"hint()\" sag-ui-tooltip></span>\n      }\n    </label>\n    @if (loading) {\n      <sag-ui-spinner class=\"absolute right-4 top-[37px]\" size=\"sm\"></sag-ui-spinner>\n    } @else {\n      <span\n        class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white focus-visible:outline-[#1491EB]\"\n        aria-label=\"Toggle dropdown\"\n        [attr.data-disabled]=\"disabled\"\n        [attr.data-open]=\"isOpen\"\n        [attr.tabindex]=\"disabled ? -1 : 0\"\n        [class.icon-chevron-up]=\"isOpen\"\n        [class.icon-chevron-down]=\"!isOpen\"\n        [class.opacity-50]=\"disabled\"\n        (click)=\"!disabled && togglePopover($event)\"\n        (keydown.space)=\"!disabled && togglePopover($event)\"\n      ></span>\n    }\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen\"\n      (scroll)=\"onScroll($event)\"\n      style=\"max-height: 200px; overflow-y: auto\"\n      role=\"listbox\"\n      [attr.aria-activedescendant]=\"activeDescendantId\"\n    >\n      @if (inputValue.trim()) {\n        <sag-ui-multi-autocomplete-option class=\"autocomplete-option\" (click)=\"handleAddOption()\">\n          <div class=\"flex items-center justify-between gap-2\">\n            <span>{{ inputValue }}</span>\n            <span class=\"rounded-md text-xs text-white bg-[#005159] px-3 py-0.5 flex items-center gap-2\"><span class=\"icon-plus-large\"></span>{{ addButtonLabel() }}</span>\n          </div>\n        </sag-ui-multi-autocomplete-option>\n      }\n      @for (option of options(); track $index) {\n        <div\n          #optionItem\n          role=\"option\"\n          tabindex=\"-1\"\n          [attr.aria-setsize]=\"options().length + 1\"\n          [attr.aria-posinset]=\"$index + 2\"\n          [attr.aria-selected]=\"activeOptionId === getOptionId(option)\"\n          [id]=\"getOptionId(option)\"\n          (focus)=\"activeOptionId = getOptionId(option)\"\n          class=\"autocomplete-option\"\n          (click)=\"handleOptionSelect([option.label, option.value])\"\n          (keydown)=\"handleOptionKeydown($event, [option.label, option.value], false)\"\n        >\n          <sag-ui-multi-autocomplete-option [ariaSetsize]=\"options().length + 1\" [ariaPosinset]=\"$index + 2\">\n          {{ option.label }}\n        </sag-ui-multi-autocomplete-option>\n        </div>\n      }\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  } @else {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n", dependencies: [{ kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: SpinnerComponent, selector: "sag-ui-spinner", inputs: ["size", "ariaLabel"] }, { kind: "component", type: MultiAutocompleteOptionComponent, selector: "sag-ui-multi-autocomplete-option", inputs: ["value", "selected", "hidden", "ariaPosinset", "ariaSetsize"], outputs: ["autocomplete"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MultiAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-multi-autocomplete', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => MultiAutocompleteComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => MultiAutocompleteComponent),
                            multi: true
                        }
                    ], imports: [
                        InputLabelDirective,
                        TooltipDirective,
                        SpinnerComponent,
                        MultiAutocompleteOptionComponent,
                        InputErrorDirective
                    ], template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <div\n      [class]=\"inputClasses\"\n      [attr.data-focus]=\"isOpen\"\n      [attr.data-read-only]=\"readOnly()\"\n      [attr.data-disabled]=\"disabled\"\n      [attr.data-invalid]=\"invalid()\"\n      [attr.data-touched]=\"touched\"\n      (click)=\"onFocus()\"\n    >\n      <div class=\"flex flex-wrap gap-2\">\n        @for (item of selectedItems; track $index) {\n          <span [class]=\"'whitespace-nowrap px-3 py-0.5 text-sm font-medium rounded-md border flex items-center text-wrap ' + chipClasses\"\n            >{{ item.label }}\n            <span\n              class=\"icon-close cursor-pointer ml-2 focus-visible:outline-[#1491EB]\"\n              [attr.aria-label]=\"'Remove item: ' + item.label\"\n              (click)=\"handleClick($event, $index)\"\n              (keydown.space)=\"handleClick($event, $index)\"\n              (keydown.enter)=\"handleClick($event, $index)\"\n              tabindex=\"0\"\n            ></span>\n          </span>\n        }\n        <input\n          class=\"pr-8 max-w-52 w-full bg-transparent border-0 focus:ring-0 focus:outline-none\"\n          [id]=\"inputId\"\n          [readOnly]=\"false\"\n          [disabled]=\"disabled\"\n          [placeholder]=\"placeholder()\"\n          [required]=\"invalid() || (required() && touched)\"\n          [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n          [attr.aria-label]=\"ariaLabel()\"\n          [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n          [attr.aria-describedby]=\"ariaDescribedby()\"\n          (input)=\"handleInput($event)\"\n          (focus)=\"onFocus()\"\n          (blur)=\"onBlur()\"\n          (keydown)=\"handleInputKeydown($event)\"\n          (keydown.enter)=\"handleEnterKey($event)\"\n          (keydown.escape)=\"handleEscapeKey($event)\"\n          (keyup)=\"keyup.emit($event)\"\n          (mouseenter)=\"mouseenter.emit($event)\"\n          (mouseleave)=\"mouseleave.emit($event)\"\n          type=\"text\"\n          #input\n        />\n      </div>\n    </div>\n    <label [id]=\"labelId\" [htmlFor]=\"inputId\" sag-ui-input-label>\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\" [tooltip]=\"hint()\" sag-ui-tooltip></span>\n      }\n    </label>\n    @if (loading) {\n      <sag-ui-spinner class=\"absolute right-4 top-[37px]\" size=\"sm\"></sag-ui-spinner>\n    } @else {\n      <span\n        class=\"absolute right-4 bottom-[0.8rem] z-20 text-black dark:text-white focus-visible:outline-[#1491EB]\"\n        aria-label=\"Toggle dropdown\"\n        [attr.data-disabled]=\"disabled\"\n        [attr.data-open]=\"isOpen\"\n        [attr.tabindex]=\"disabled ? -1 : 0\"\n        [class.icon-chevron-up]=\"isOpen\"\n        [class.icon-chevron-down]=\"!isOpen\"\n        [class.opacity-50]=\"disabled\"\n        (click)=\"!disabled && togglePopover($event)\"\n        (keydown.space)=\"!disabled && togglePopover($event)\"\n      ></span>\n    }\n    <div\n      [class]=\"popoverClasses\"\n      [class.hidden]=\"!isOpen\"\n      (scroll)=\"onScroll($event)\"\n      style=\"max-height: 200px; overflow-y: auto\"\n      role=\"listbox\"\n      [attr.aria-activedescendant]=\"activeDescendantId\"\n    >\n      @if (inputValue.trim()) {\n        <sag-ui-multi-autocomplete-option class=\"autocomplete-option\" (click)=\"handleAddOption()\">\n          <div class=\"flex items-center justify-between gap-2\">\n            <span>{{ inputValue }}</span>\n            <span class=\"rounded-md text-xs text-white bg-[#005159] px-3 py-0.5 flex items-center gap-2\"><span class=\"icon-plus-large\"></span>{{ addButtonLabel() }}</span>\n          </div>\n        </sag-ui-multi-autocomplete-option>\n      }\n      @for (option of options(); track $index) {\n        <div\n          #optionItem\n          role=\"option\"\n          tabindex=\"-1\"\n          [attr.aria-setsize]=\"options().length + 1\"\n          [attr.aria-posinset]=\"$index + 2\"\n          [attr.aria-selected]=\"activeOptionId === getOptionId(option)\"\n          [id]=\"getOptionId(option)\"\n          (focus)=\"activeOptionId = getOptionId(option)\"\n          class=\"autocomplete-option\"\n          (click)=\"handleOptionSelect([option.label, option.value])\"\n          (keydown)=\"handleOptionKeydown($event, [option.label, option.value], false)\"\n        >\n          <sag-ui-multi-autocomplete-option [ariaSetsize]=\"options().length + 1\" [ariaPosinset]=\"$index + 2\">\n          {{ option.label }}\n        </sag-ui-multi-autocomplete-option>\n        </div>\n      }\n    </div>\n  </div>\n  @if (error() && isArray(error())) {\n    <div class=\"flex flex-col gap-1\">\n      @for (err of error(); track err) {\n        <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ err }}</span>\n      }\n    </div>\n  } @else {\n    <span sag-ui-input-error [id]=\"errorId\" role=\"alert\">{{ error() }}</span>\n  }\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], globalKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], checkDropdownPosition: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['mouseenter']
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

class RangeDirective {
    elementRef = inject(ElementRef);
    nativeElement() {
        return this.elementRef.nativeElement;
    }
    ngOnInit() {
        style(this.elementRef, 'hidden');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: RangeDirective, isStandalone: true, selector: "input[sag-ui-range]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[sag-ui-range]',
                    standalone: true
                }]
        }] });

const rangeThumbBaseStyles = [
    'absolute',
    '-top-2',
    'w-5',
    'h-5',
    'rounded-full',
    'cursor-pointer',
    'before:content-[""]',
    'before:absolute',
    'before:top-1/2',
    'before:left-1/2',
    'before:w-10',
    'before:h-10',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const bullBaseStyles = [
    'absolute',
    'top-1/2',
    'left-1/2',
    'h-2.5',
    'w-2.5',
    'rounded-full',
    'transform',
    '-translate-x-1/2',
    '-translate-y-1/2',
    'cursor-pointer'
];
const rangeThumbStylesSAG = cva([
    ...rangeThumbBaseStyles,
    'border-2',
    'border-[#005159]',
    'bg-[#D1FFF2]',
    'dark:border-[#00FFB9]',
    'dark:bg-[#001F39]'
]);
const rangeThumbDisabledStylesSAG = cva([...rangeThumbBaseStyles, 'border-2', 'border-[#b3b3b3]', 'bg-[#f2f2f2]']);
const bullStylesSAG = cva([...bullBaseStyles, 'bg-[#005159]', 'dark:bg-[#00FFB9]']);
const rangeThumbStylesSHS = cva([
    ...rangeThumbBaseStyles,
    'border',
    'border-[#b3b3b3]',
    'bg-white',
    'hover:bg-[#f2f2f2]',
    'dark:border-[#808080]',
    'dark:bg-[#1a1a1a]',
    'dark:hover:bg-[#313131]'
]);
const bullStylesSHS = cva([...bullBaseStyles, 'hidden']);
const rangeThumbDisabledStylesSHS = cva([
    ...rangeThumbBaseStyles,
    'border',
    'border-[#b3b3b3]',
    'bg-white',
    'cursor-not-allowed',
    'dark:border-[#808080]',
    'dark:bg-[#1a1a1a]'
]);

class RangeThumbDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    renderer = inject(Renderer2);
    disabled = input(false);
    bull = null;
    ngOnInit() {
        const rangeThumbStyles = componentStylesByTheme(this.theme, rangeThumbStylesSAG, rangeThumbStylesSHS);
        const rangeThumbDisabledStyles = componentStylesByTheme(this.theme, rangeThumbDisabledStylesSAG, rangeThumbDisabledStylesSHS);
        style(this.elementRef, this.disabled() ? rangeThumbDisabledStyles() : rangeThumbStyles());
        const bullStyles = componentStylesByTheme(this.theme, bullStylesSAG, bullStylesSHS);
        this.bull = this.renderer.createElement('div');
        this.renderer.setStyle(this.bull, 'display', 'none');
        this.renderer.setAttribute(this.bull, 'class', bullStyles());
        this.renderer.appendChild(this.elementRef.nativeElement, this.bull);
    }
    onMouseEnter() {
        if (!this.bull || this.disabled())
            return;
        this.renderer.setStyle(this.bull, 'display', 'block');
    }
    onMouseLeave() {
        if (!this.bull)
            return;
        this.renderer.setStyle(this.bull, 'display', 'none');
    }
    onKeyEnter() {
        if (!this.bull || this.disabled())
            return;
        this.elementRef.nativeElement.focus();
        this.renderer.setStyle(this.bull, 'display', 'block');
    }
    onKeyUp() {
        if (!this.bull || this.disabled())
            return;
        this.renderer.setStyle(this.bull, 'display', 'none');
    }
    onKeyEscape() {
        if (!this.bull)
            return;
        this.renderer.setStyle(this.bull, 'display', 'none');
    }
    nativeElement() {
        return this.elementRef.nativeElement;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeThumbDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: RangeThumbDirective, isStandalone: true, selector: "[sag-ui-range-thumb]", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()", "keydown": "onKeyEnter()", "keyup": "onKeyUp()", "keyescape": "onKeyEscape()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeThumbDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-range-thumb]',
                    standalone: true
                }]
        }], propDecorators: { onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }], onKeyEnter: [{
                type: HostListener,
                args: ['keydown']
            }], onKeyUp: [{
                type: HostListener,
                args: ['keyup']
            }], onKeyEscape: [{
                type: HostListener,
                args: ['keyescape']
            }] } });

const rangeTrackBaseStyles = ['relative', 'w-full'];
const rangeTrackStylesSAG = cva([...rangeTrackBaseStyles, 'h-1', 'bg-[#CCCCD4]', 'dark:bg-[#66667E]']);
const rangeTrackStylesSHS = cva([...rangeTrackBaseStyles, 'h-0.5', 'bg-[#999999]', 'dark:bg-[#4d4d4d]']);

class RangeTrackDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const rangeTrackStyles = componentStylesByTheme(this.theme, rangeTrackStylesSAG, rangeTrackStylesSHS);
        style(this.elementRef, rangeTrackStyles());
    }
    nativeElement() {
        return this.elementRef.nativeElement;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeTrackDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: RangeTrackDirective, isStandalone: true, selector: "[sag-ui-range-track]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeTrackDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-range-track]',
                    standalone: true
                }]
        }] });

class RangeBaseDirective {
    input;
    thumb;
    track;
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeThumb(element) {
        this.thumb = element;
        this.thumb.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.thumb.addEventListener('keydown', this.onKeyDown.bind(this));
    }
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeTrack(element) {
        this.track = element;
        this.track.addEventListener('click', this.onClick.bind(this));
    }
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeInput(element) {
        this.input = element;
        this.input.addEventListener('input', this.updateThumbPosition.bind(this));
    }
    tooltip = contentChild.required(TooltipDirective);
    get min() {
        return this.input.min ? parseFloat(this.input.min) : 0;
    }
    get max() {
        return this.input.max ? parseFloat(this.input.max) : 100;
    }
    get step() {
        return this.input.step ? parseFloat(this.input.step) : 1;
    }
    get thumbPosition() {
        const percent = (this.input.valueAsNumber - this.min) / (this.max - this.min);
        return percent * 100;
    }
    ngAfterViewInit() {
        this.updateThumbPosition();
    }
    updateThumbPosition() {
        this.thumb.style.left = `${this.thumbPosition}%`;
        const tooltip = this.tooltip();
        if (tooltip) {
            tooltip.tooltip = this.input.value;
        }
    }
    onMouseDown(event) {
        if (this.input.disabled)
            return;
        const clientX = event.clientX;
        event.stopPropagation();
        const { left, width } = this.track.getBoundingClientRect();
        const x = clientX - left;
        const percent = Math.max(0, Math.min(1, x / width));
        const newValue = Math.round(this.min + percent * (this.max - this.min));
        const stepValue = Math.round(newValue / this.step) * this.step;
        this.input.valueAsNumber = stepValue;
        this.updateThumbPosition();
        const onMouseMove = (e) => {
            if (this.input.disabled)
                return;
            const clientX = e.clientX;
            const x = clientX - left - 8;
            const percent = Math.max(0, Math.min(1, x / width));
            const newValue = Math.round(this.min + percent * (this.max - this.min));
            const stepValue = Math.round(newValue / this.step) * this.step;
            this.input.valueAsNumber = stepValue;
            this.updateThumbPosition();
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
    onClick(event) {
        if (this.input.disabled)
            return;
        event.stopPropagation();
        const { left, width } = this.track.getBoundingClientRect();
        const x = event.clientX - left;
        const percent = Math.max(0, Math.min(1, x / width));
        const newValue = Math.round(this.min + percent * (this.max - this.min));
        const stepValue = Math.round(newValue / this.step) * this.step;
        this.input.valueAsNumber = stepValue;
        this.updateThumbPosition();
    }
    onKeyDown(event) {
        if (this.input.disabled)
            return;
        const stepSize = event.shiftKey ? this.step * 10 : this.step;
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.adjustValue(-stepSize);
            this.tooltip().onMouseMove();
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.adjustValue(stepSize);
            this.tooltip().onMouseMove();
        }
    }
    adjustValue(delta) {
        const currentValue = this.input.valueAsNumber;
        const newValue = Math.max(this.min, Math.min(this.max, currentValue + delta));
        this.input.valueAsNumber = Math.round(newValue / this.step) * this.step;
        this.updateThumbPosition();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.2.0", version: "19.2.2", type: RangeBaseDirective, isStandalone: true, selector: "[sag-ui-range-base]", inputs: { rangeThumb: ["thumb", "rangeThumb"], rangeTrack: ["track", "rangeTrack"], rangeInput: ["input", "rangeInput"] }, queries: [{ propertyName: "tooltip", first: true, predicate: TooltipDirective, descendants: true, isSignal: true }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeBaseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-range-base]',
                    standalone: true
                }]
        }], propDecorators: { rangeThumb: [{
                type: Input,
                args: ['thumb']
            }], rangeTrack: [{
                type: Input,
                args: ['track']
            }], rangeInput: [{
                type: Input,
                args: ['input']
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class RangeComponent {
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    min = input(0);
    max = input(100);
    step = input(1);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = this.min();
    change = output();
    click = output();
    focus = output();
    blur = output();
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(event) {
        const input = event.target;
        this.value = Number(input.value);
        this.onChangeEvent(this.value);
        this.change.emit(event);
    }
    validate(_) {
        return null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: RangeComponent, isStandalone: true, selector: "sag-ui-range", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, min: { classPropertyName: "min", publicName: "min", isSignal: true, isRequired: false, transformFunction: null }, max: { classPropertyName: "max", publicName: "max", isSignal: true, isRequired: false, transformFunction: null }, step: { classPropertyName: "step", publicName: "step", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RangeComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => RangeComponent),
                multi: true
            }
        ], ngImport: i0, template: `
        <div sag-ui-range-base [track]="track" [thumb]="thumb" [input]="input" [attr.aria-disabled]="disabled">
            <div sag-ui-range-track #track>
                <span
                    sag-ui-range-thumb
                    sag-ui-tooltip
                    [disabled]="disabled"
                    #thumb
                    [tabindex]="disabled ? -1 : 0"
                    [attr.aria-label]="value"
                ></span>
            </div>
            <input
                sag-ui-range
                type="range"
                [value]="value"
                [min]="min()"
                [max]="max()"
                [step]="step()"
                [disabled]="disabled"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                #input
            />
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: RangeThumbDirective, selector: "[sag-ui-range-thumb]", inputs: ["disabled"] }, { kind: "directive", type: RangeTrackDirective, selector: "[sag-ui-range-track]" }, { kind: "directive", type: RangeBaseDirective, selector: "[sag-ui-range-base]", inputs: ["thumb", "track", "input"] }, { kind: "directive", type: RangeDirective, selector: "input[sag-ui-range]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-range',
                    standalone: true,
                    template: `
        <div sag-ui-range-base [track]="track" [thumb]="thumb" [input]="input" [attr.aria-disabled]="disabled">
            <div sag-ui-range-track #track>
                <span
                    sag-ui-range-thumb
                    sag-ui-tooltip
                    [disabled]="disabled"
                    #thumb
                    [tabindex]="disabled ? -1 : 0"
                    [attr.aria-label]="value"
                ></span>
            </div>
            <input
                sag-ui-range
                type="range"
                [value]="value"
                [min]="min()"
                [max]="max()"
                [step]="step()"
                [disabled]="disabled"
                (change)="onChange($event)"
                (click)="click.emit($event)"
                (focus)="focus.emit($event)"
                (blur)="onTouched(); blur.emit($event)"
                #input
            />
        </div>
    `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RangeComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => RangeComponent),
                            multi: true
                        }
                    ],
                    imports: [RangeThumbDirective, RangeTrackDirective, RangeBaseDirective, RangeDirective, TooltipDirective]
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

class RangeBaseMultiDirective {
    thumbs = [];
    inputs = [];
    track;
    min = input(0);
    max = input(100);
    step = input(1);
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeThumbs(elements) {
        this.thumbs = elements;
        this.thumbs.forEach((thumb, index) => {
            thumb.addEventListener('mousedown', event => this.onMouseDown(event, index));
            thumb.addEventListener('keydown', event => this.onKeyDown(event, index));
        });
    }
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeTrack(element) {
        this.track = element;
        this.track.addEventListener('click', this.onClick.bind(this));
    }
    // TODO: Skipped for migration because:
    //  Accessor inputs cannot be migrated as they are too complex.
    set rangeInput(element) {
        this.inputs = element;
        this.inputs.forEach((input, index) => {
            input.addEventListener('input', () => this.updateThumbPosition(index));
        });
        this.updateAllThumbPositions();
    }
    tooltips = contentChildren(TooltipDirective, { descendants: true });
    ngAfterViewInit() {
        this.updateAllThumbPositions();
    }
    updateThumbPosition(index) {
        const input = this.inputs[index];
        const thumb = this.thumbs[index];
        const tooltips = this.tooltips();
        const tooltip = tooltips ? tooltips[index] : null;
        const percent = ((input.valueAsNumber - this.min()) / (this.max() - this.min())) * 100;
        thumb.style.left = `${percent}%`;
        if (tooltip) {
            tooltip.tooltip = input.value;
        }
    }
    updateAllThumbPositions() {
        this.inputs.forEach((_, index) => this.updateThumbPosition(index));
    }
    onMouseDown(event, index) {
        const input = this.inputs[index];
        if (input.disabled)
            return;
        event.stopPropagation();
        this.initDrag(event, input, this.thumbs[index]);
    }
    onClick(event) {
        event.stopPropagation();
        const distances = this.thumbs.map(thumb => {
            const rect = thumb.getBoundingClientRect();
            return Math.abs(event.clientX - (rect.left + rect.width / 2));
        });
        const closestThumbIndex = distances.indexOf(Math.min(...distances));
        this.moveThumb(event, this.inputs[closestThumbIndex], this.thumbs[closestThumbIndex]);
    }
    initDrag(_, input, thumb) {
        const { left, width } = this.track.getBoundingClientRect();
        const onMouseMove = (e) => {
            if (input.disabled)
                return;
            this.moveThumb(e, input, thumb, left, width);
        };
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
    moveThumb(event, input, _, left = this.track.getBoundingClientRect().left, width = this.track.getBoundingClientRect().width) {
        const x = event.clientX - left;
        const percent = Math.max(0, Math.min(1, x / width));
        const newValue = Math.round(this.min() + percent * (this.max() - this.min()));
        const stepValue = Math.round(newValue / this.step()) * this.step();
        input.valueAsNumber = stepValue;
        this.updateThumbPosition(this.inputs.indexOf(input));
    }
    onKeyDown(event, index) {
        const input = this.inputs[index];
        if (input.disabled)
            return;
        const stepSize = event.shiftKey ? this.step() * 10 : this.step();
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.adjustValue(-stepSize, index);
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.adjustValue(stepSize, index);
        }
    }
    adjustValue(delta, index) {
        const input = this.inputs[index];
        const currentValue = input.valueAsNumber;
        const newValue = Math.max(this.min(), Math.min(this.max(), currentValue + delta));
        input.valueAsNumber = Math.round(newValue / this.step()) * this.step();
        this.updateThumbPosition(index);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeBaseMultiDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.2.0", version: "19.2.2", type: RangeBaseMultiDirective, isStandalone: true, selector: "[sag-ui-range-base-multi]", inputs: { min: { classPropertyName: "min", publicName: "min", isSignal: true, isRequired: false, transformFunction: null }, max: { classPropertyName: "max", publicName: "max", isSignal: true, isRequired: false, transformFunction: null }, step: { classPropertyName: "step", publicName: "step", isSignal: true, isRequired: false, transformFunction: null }, rangeThumbs: { classPropertyName: "rangeThumbs", publicName: "thumbs", isSignal: false, isRequired: false, transformFunction: null }, rangeTrack: { classPropertyName: "rangeTrack", publicName: "track", isSignal: false, isRequired: false, transformFunction: null }, rangeInput: { classPropertyName: "rangeInput", publicName: "inputs", isSignal: false, isRequired: false, transformFunction: null } }, queries: [{ propertyName: "tooltips", predicate: TooltipDirective, descendants: true, isSignal: true }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeBaseMultiDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-range-base-multi]',
                    standalone: true
                }]
        }], propDecorators: { rangeThumbs: [{
                type: Input,
                args: ['thumbs']
            }], rangeTrack: [{
                type: Input,
                args: ['track']
            }], rangeInput: [{
                type: Input,
                args: ['inputs']
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
class RangeMultiComponent {
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    min = input(0);
    max = input(100);
    step = input(1);
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value = [this.min(), this.min()];
    change = output();
    click = output();
    focus = output();
    blur = output();
    onChangeEvent = (_) => { };
    onTouched = () => { };
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.onChangeEvent = fn;
        console.log(this.onChangeEvent);
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onChange(event, index) {
        const input = event.target;
        this.value[index] = parseFloat(input.value);
        this.onChangeEvent(this.value);
        this.change.emit(event);
    }
    validate(_) {
        return null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeMultiComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "19.2.2", type: RangeMultiComponent, isStandalone: true, selector: "sag-ui-range-multi", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, min: { classPropertyName: "min", publicName: "min", isSignal: true, isRequired: false, transformFunction: null }, max: { classPropertyName: "max", publicName: "max", isSignal: true, isRequired: false, transformFunction: null }, step: { classPropertyName: "step", publicName: "step", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { change: "change", click: "click", focus: "focus", blur: "blur" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RangeMultiComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => RangeMultiComponent),
                multi: true
            }
        ], ngImport: i0, template: ` <div
        sag-ui-range-base-multi
        [track]="track"
        [thumbs]="[thumb1, thumb2]"
        [inputs]="[input1, input2]"
        (click)="click.emit($event)"
        (focus)="focus.emit($event)"
        (blur)="onTouched(); blur.emit($event)"
    >
        <div sag-ui-range-track #track>
            <span
                sag-ui-range-thumb
                sag-ui-tooltip
                [disabled]="disabled"
                #thumb1
                [tabindex]="disabled ? -1 : 0"
                [attr.aria-label]="value[0]"
            ></span>
            <span
                sag-ui-range-thumb
                sag-ui-tooltip
                [disabled]="disabled"
                #thumb2
                [tabindex]="disabled ? -1 : 0"
                [attr.aria-label]="value[1]"
            ></span>
        </div>
        <input
            sag-ui-range
            type="range"
            [min]="min()"
            [max]="max()"
            [step]="step()"
            [disabled]="disabled"
            [value]="value[0]"
            (change)="onChange($event, 0)"
            #input1
        />
        <input
            sag-ui-range
            type="range"
            [min]="min()"
            [max]="max()"
            [step]="step()"
            [disabled]="disabled"
            [value]="value[1]"
            (change)="onChange($event, 1)"
            #input2
        />
    </div>`, isInline: true, dependencies: [{ kind: "directive", type: RangeBaseMultiDirective, selector: "[sag-ui-range-base-multi]", inputs: ["min", "max", "step", "thumbs", "track", "inputs"] }, { kind: "directive", type: RangeTrackDirective, selector: "[sag-ui-range-track]" }, { kind: "directive", type: RangeThumbDirective, selector: "[sag-ui-range-thumb]", inputs: ["disabled"] }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "directive", type: RangeDirective, selector: "input[sag-ui-range]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: RangeMultiComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-range-multi',
                    standalone: true,
                    template: ` <div
        sag-ui-range-base-multi
        [track]="track"
        [thumbs]="[thumb1, thumb2]"
        [inputs]="[input1, input2]"
        (click)="click.emit($event)"
        (focus)="focus.emit($event)"
        (blur)="onTouched(); blur.emit($event)"
    >
        <div sag-ui-range-track #track>
            <span
                sag-ui-range-thumb
                sag-ui-tooltip
                [disabled]="disabled"
                #thumb1
                [tabindex]="disabled ? -1 : 0"
                [attr.aria-label]="value[0]"
            ></span>
            <span
                sag-ui-range-thumb
                sag-ui-tooltip
                [disabled]="disabled"
                #thumb2
                [tabindex]="disabled ? -1 : 0"
                [attr.aria-label]="value[1]"
            ></span>
        </div>
        <input
            sag-ui-range
            type="range"
            [min]="min()"
            [max]="max()"
            [step]="step()"
            [disabled]="disabled"
            [value]="value[0]"
            (change)="onChange($event, 0)"
            #input1
        />
        <input
            sag-ui-range
            type="range"
            [min]="min()"
            [max]="max()"
            [step]="step()"
            [disabled]="disabled"
            [value]="value[1]"
            (change)="onChange($event, 1)"
            #input2
        />
    </div>`,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RangeMultiComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => RangeMultiComponent),
                            multi: true
                        }
                    ],
                    imports: [RangeBaseMultiDirective, RangeTrackDirective, RangeThumbDirective, TooltipDirective, RangeDirective]
                }]
        }], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

const scdPickerBaseStyles$1 = [
    'absolute',
    'max-h-48',
    'right-0',
    'top-full',
    'border',
    'border-t-0',
    'shadow-md',
    'z-50',
    'w-full',
    'overflow-y-auto'
];
const scdPickerStylesSAG$1 = cva([
    ...scdPickerBaseStyles$1,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const scdPickerStylesSHS$1 = cva([...scdPickerBaseStyles$1, 'bg-white', 'border-0', 'dark:bg-[#333333]']);

const autocompleteOptionBaseStyles = [
    'p-3',
    'cursor-pointer',
    'flex',
    'items-center',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const autocompleteOptionStylesSAG = cva([
    ...autocompleteOptionBaseStyles,
    'hover:bg-[#62EEC7]',
    'dark:hover:bg-[#005159]'
]);
const autocompleteOptionSelectedStylesSAG = cva([...autocompleteOptionBaseStyles, 'bg-[#62EEC7]', 'dark:bg-[#005159]']);
const autocompleteionOptionTextStylesSAG = cva(['text-[#000028]', 'dark:text-white']);
const autocompleteOptionStylesSHS = cva([
    ...autocompleteOptionBaseStyles,
    'hover:bg-[#f2f2f2]',
    'dark:hover:bg-[#474747]'
]);
const autocompleteOptionSelectedStylesSHS = cva([...autocompleteOptionBaseStyles, 'bg-[#e6e6e6]', 'dark:bg-[#666]']);
const autocompleteionOptionTextStylesSHS = cva(['dark:text-[#e6e6e6]']);

class ScdPickerOptionComponent {
    theme = inject(THEME_TOKEN);
    readonly;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value = input();
    selected = input(false);
    autocomplete = output();
    text = viewChild.required('text');
    get containerClasses() {
        const containerStyles = componentStylesByTheme(this.theme, autocompleteOptionStylesSAG, autocompleteOptionStylesSHS);
        const autocompleteedContainerStyles = componentStylesByTheme(this.theme, autocompleteOptionSelectedStylesSAG, autocompleteOptionSelectedStylesSHS);
        return this.selected() ? autocompleteedContainerStyles() : containerStyles();
    }
    get textClasses() {
        const textStyles = componentStylesByTheme(this.theme, autocompleteionOptionTextStylesSAG, autocompleteionOptionTextStylesSHS);
        return textStyles();
    }
    onClick() {
        const innerText = this.text().nativeElement.innerText;
        this.autocomplete.emit([innerText, this.value()]);
    }
    onKeyDown(event) {
        if (event.code === 'Space') {
            event.preventDefault();
            this.onClick();
            event.target.focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: ScdPickerOptionComponent, isStandalone: true, selector: "sag-ui-scd-picker-option", inputs: { value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { autocomplete: "autocomplete" }, viewQueries: [{ propertyName: "text", first: true, predicate: ["text"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  tabindex=\"0\"\n  [class]=\"containerClasses\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-scd-picker-option', standalone: true, template: "<div\n  (click)=\"onClick()\"\n  (keydown)=\"onKeyDown($event)\"\n  tabindex=\"0\"\n  [class]=\"containerClasses\"\n>\n  <span [class]=\"textClasses\" #text>\n    <ng-content></ng-content>\n  </span>\n</div>\n" }]
        }] });

class ScdPickerComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    id = input(this.randomId());
    /**
     * The placeholder of the ScdPicker.
     */
    placeholder = input('');
    /*
     * Whether the ScdPicker is required.
     */
    required = input(false);
    /**
     * Whether the ScdPicker is disabled.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    /**
     * Whether the ScdPicker is read-only.
     */
    readOnly = input(false);
    /**
     * The label of the ScdPicker.
     */
    label = input('');
    /**
     * Whether the SCD Picker is in an error state.
     */
    invalid = input(false);
    /**
     * The error message to display.
     */
    error = input('');
    /**
     * Whether the switch is checked.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value;
    searchFn = input.required();
    hint = input(null);
    input = viewChild.required('input');
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }
    touched = false;
    selected;
    isOpen = false;
    filterValue = '';
    elementRef = inject(ElementRef);
    options = [];
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, scdPickerStylesSAG$1, scdPickerStylesSHS$1);
        return popoverStyles();
    }
    get displayValue() {
        if (!this.selected)
            return '';
        return `${this.selected?.firstName} ${this.selected?.lastName}`;
    }
    onFocus() {
        if (this.disabled || this.readOnly())
            return;
        this.isOpen = true;
    }
    onBlur() {
        setTimeout(() => {
            this.markAsTouched();
        }, 100);
    }
    handleInput(event) {
        const target = event.target;
        this.filterValue = target.value;
        this.searchFn()(this.filterValue).then(result => {
            this.options = result;
        });
    }
    handleChange() {
        this.selected = undefined;
        this.writeValue(undefined);
    }
    handleSelect(option) {
        this.input().nativeElement.focus();
        this.markAsTouched();
        this.selected = option;
        this.writeValue(option);
        this.isOpen = false;
        setTimeout(() => {
            this.input().nativeElement.blur();
        }, 50);
    }
    handleSelectKeyDown(event, option) {
        if (event.code === 'Space') {
            event.stopPropagation();
            this.handleSelect(option);
        }
    }
    // eslint-disable-next-line
    onChange = (_value) => { };
    // eslint-disable-next-line
    onTouch = () => { };
    // eslint-disable-next-line
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // eslint-disable-next-line
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val) {
        this.value = val;
        this.selected = val;
        this.onChange(this.value);
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    // eslint-disable-next-line
    validate(control) {
        return this.required() && !this.selected ? { required: true } : null;
    }
    blockChange(event) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (event.target && event.target['value']) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            event.target['value'] = this.displayValue;
        }
    }
    focusNext(event) {
        const nextElement = document.querySelector('sag-ui-scd-picker-option > div');
        if (event.key === 'Enter' || event.key === 'ArrowDown') {
            if (nextElement) {
                nextElement.focus();
            }
        }
    }
    focusNextOption(event) {
        const currElement = event.target;
        const allOptions = this.elementRef.nativeElement.querySelectorAll('sag-ui-scd-picker-option > div');
        const currentWrapper = currElement.closest('sag-ui-scd-picker-option > div');
        const currIndex = Array.from(allOptions).indexOf(currentWrapper);
        let next;
        if (event.key === 'ArrowDown') {
            next = allOptions[currIndex + 1];
        }
        else if (event.key === 'ArrowUp') {
            next = allOptions[currIndex - 1];
        }
        if (next) {
            next.focus();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ScdPickerComponent, isStandalone: true, selector: "sag-ui-scd-picker", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, searchFn: { classPropertyName: "searchFn", publicName: "searchFn", isSignal: true, isRequired: true, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "focusout": "onFocusOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => ScdPickerComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => ScdPickerComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [id]=\"id()\"\n      [disabled]=\"disabled\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [placeholder]=\"placeholder()\"\n      [value]=\"displayValue\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      [attr.aria-disabled]=\"disabled\"\n      (keydown)=\"focusNext($event)\"\n      #input\n      />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <span\n      class=\"absolute right-5 bottom-[0.8rem] text-black dark:text-white icon-user\"\n      [class.opacity-50]=\"disabled\"\n    ></span>\n    <div [class]=\"popoverClasses\" [class.hidden]=\"!isOpen\">\n      @for (option of options; track option.gid) {\n        <sag-ui-scd-picker-option\n          [value]=\"option\"\n          [selected]=\"option === selected\"\n          (click)=\"handleSelect(option)\"\n          (keydown.enter)=\"handleSelect(option)\"\n          (keydown.space)=\"handleSelect(option)\"\n          (keydown)=\"focusNextOption($event)\"\n          >\n          {{ option.firstName }} {{ option.lastName }} ({{ option.gid }})\n        </sag-ui-scd-picker-option>\n      }\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: ScdPickerOptionComponent, selector: "sag-ui-scd-picker-option", inputs: ["value", "selected"], outputs: ["autocomplete"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-scd-picker', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ScdPickerComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => ScdPickerComponent),
                            multi: true
                        }
                    ], imports: [InputLabelDirective, InputDirective, TooltipDirective, ScdPickerOptionComponent], template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [id]=\"id()\"\n      [disabled]=\"disabled\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [placeholder]=\"placeholder()\"\n      [value]=\"displayValue\"\n      (input)=\"handleInput($event)\"\n      (change)=\"handleChange()\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      [attr.aria-disabled]=\"disabled\"\n      (keydown)=\"focusNext($event)\"\n      #input\n      />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <span\n      class=\"absolute right-5 bottom-[0.8rem] text-black dark:text-white icon-user\"\n      [class.opacity-50]=\"disabled\"\n    ></span>\n    <div [class]=\"popoverClasses\" [class.hidden]=\"!isOpen\">\n      @for (option of options; track option.gid) {\n        <sag-ui-scd-picker-option\n          [value]=\"option\"\n          [selected]=\"option === selected\"\n          (click)=\"handleSelect(option)\"\n          (keydown.enter)=\"handleSelect(option)\"\n          (keydown.space)=\"handleSelect(option)\"\n          (keydown)=\"focusNextOption($event)\"\n          >\n          {{ option.firstName }} {{ option.lastName }} ({{ option.gid }})\n        </sag-ui-scd-picker-option>\n      }\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { disabled: [{
                type: Input
            }], 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

const scdPickerBaseStyles = [
    'absolute',
    'max-h-48',
    'left-0',
    'border',
    'border-t-0',
    'shadow-md',
    'z-50',
    'w-full',
    'overflow-y-auto'
];
const scdPickerStylesSAG = cva([
    ...scdPickerBaseStyles,
    'bg-[#D1FFF2]',
    'border-[#005159]',
    'dark:bg-[#001F39]',
    'dark:border-[#00FFB9]'
]);
const scdPickerStylesSHS = cva([...scdPickerBaseStyles, 'bg-white', 'border-0', 'dark:bg-[#333333]']);
const textStylesPrimarySAG = cva(['dark:text-white', 'text-[#002828]']);
const textStylesPrimarySHS = cva(['dark:text-white', 'text-black']);
const textStylesSecondarySAG = cva(['dark:text-[#CCCCD4]', 'text-[#002828]']);
const textStylesSecondarySHS = cva(['dark:text-[#CCCCD4]', 'text-black']);
const searchResultStylesSAG = cva([
    'bg-[#E8E8E3]',
    'dark:bg-[#37374D]',
    'hover:bg-[#007793]',
    'hover:text-[#ffffff]',
    'dark:hover:bg-[#00CCCC]',
    'focus-visible:bg-[#007793]',
    'dark:focus-visible:bg-[#00CCCC]',
    'focus-visible:text-[#ffffff]'
]);
const searchResultStylesSHS = cva([
    'bg-[#fafafa]',
    'dark:bg-[#37374D]',
    'hover:bg-[#007793]',
    'hover:text-[#ffffff]',
    'dark:hover:bg-[#00CCCC]',
    'focus-visible:bg-[#007793]',
    'dark:focus-visible:bg-[#00CCCC]',
    'focus-visible:text-[#ffffff]'
]);

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class ScdPickerDialogComponent {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    config = inject(DialogConfig);
    dialogRef = inject(DialogRef);
    formGroup = new FormGroup({
        firstName: new FormControl(''),
        lastName: new FormControl(''),
        gid: new FormControl('')
    });
    searchResults = [];
    loading = false;
    firstNameError = '';
    lastNameError = '';
    get primaryTextClasses() {
        return componentStylesByTheme(this.theme, textStylesPrimarySAG, textStylesPrimarySHS)();
    }
    get secondaryTextClasses() {
        return componentStylesByTheme(this.theme, textStylesSecondarySAG, textStylesSecondarySHS)();
    }
    get searchResultsClasses() {
        return componentStylesByTheme(this.theme, searchResultStylesSAG, searchResultStylesSHS)();
    }
    get translations() {
        return this.config.data.translations;
    }
    searchValid() {
        this.firstNameError = '';
        this.lastNameError = '';
        if (this.formGroup.value.gid) {
            return true;
        }
        if (this.formGroup.value.firstName && !this.formGroup.value.lastName) {
            this.lastNameError = this.translations.dialog.errors.lastNameRequired;
            this.announce(this.lastNameError);
            return false;
        }
        if (this.formGroup.value.lastName && !this.formGroup.value.firstName) {
            this.firstNameError = this.translations.dialog.errors.firstNameRequired;
            this.announce(this.firstNameError);
            return false;
        }
        return true;
    }
    onSearch() {
        this.announce('Search button clicked');
        if (!this.searchValid())
            return;
        this.loading = true;
        this.config.data
            .searchFn(this.formGroup.value)
            .then(results => {
            this.searchResults = results;
        })
            .finally(() => {
            this.loading = false;
        });
    }
    announce(announcement) {
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#click-announcement');
            if (liveRegion) {
                console.log(liveRegion);
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    onSelect(user) {
        this.dialogRef.close(user);
    }
    onClose() {
        this.dialogRef.close();
    }
    focusNext(event) {
        event.preventDefault();
        event.stopPropagation();
        const currElement = event.target;
        if (currElement.tagName === 'BUTTON') {
            currElement.click();
        }
        setTimeout(() => {
            const allInputs = Array.from(this.elementRef.nativeElement.querySelectorAll('sag-ui-input input, sag-ui-button button, div.list-item'));
            const currentWrapper = currElement.closest('sag-ui-input input, sag-ui-button > button, div.list-item');
            const currentIndex = allInputs.indexOf(currentWrapper);
            const next = allInputs[currentIndex + 1];
            if (next) {
                next.focus();
            }
        }, 0);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ScdPickerDialogComponent, isStandalone: true, selector: "sag-ui-scd-picker-dialog", ngImport: i0, template: `
        <div class="p-4">
            <h1 [class]="primaryTextClasses + ' font-bold text-lg p-0'">
                {{ translations.dialog.title }}
            </h1>
            <p [class]="secondaryTextClasses + 'text-md'">
                {{ translations.dialog.subtitle }}
            </p>
            <div class="grid grid-cols-[1fr_1fr_1fr_56px] gap-2 mt-4 items-start" [formGroup]="formGroup">
                <sag-ui-input
                    [label]="translations.dialog.labels.firstName"
                    [ariaLabel]="translations.dialog.labels.firstName"
                    formControlName="firstName"
                    [invalid]="!!firstNameError"
                    [error]="firstNameError"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-input
                    [label]="translations.dialog.labels.lastName"
                    [ariaLabel]="translations.dialog.labels.lastName"
                    formControlName="lastName"
                    [invalid]="!!lastNameError"
                    [error]="lastNameError"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-input
                    [label]="translations.dialog.labels.gid"
                    [ariaLabel]="translations.dialog.labels.gid"
                    formControlName="gid"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-button
                    class="h-[42px] w-[42px] mt-[12px]"
                    (click)="onSearch()"
                    [loading]="loading"
                    [square]="true"
                    ariaLabel="search"
                    (keydown.enter)="focusNext($event)"
                >
                    <span class="icon-search"></span>
                </sag-ui-button>
            </div>
            <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="click-announcement"></div>
            <div class="h-[200px] my-4 overflow-y-auto">
                <div class="block max-h-14 overflow-hidden px-4 py-0 text-white">
                    <div class="grid cursor-pointer grid-cols-3 items-center gap-4 p-2 text-sm">
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.name
                        }}</span>
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.gid
                        }}</span>
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.department
                        }}</span>
                    </div>
                </div>

                @if (loading) {
                    <div class="animate-pulse">
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-100 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-90 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-80 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-70 w-full mb-1'"></div>
                    </div>
                } @else {
                    @if (searchResults.length > 0) {
                        @for (item of searchResults; track $index) {
                            <div
                                [class]="
                                    primaryTextClasses +
                                    ' ' +
                                    searchResultsClasses +
                                    ' block max-h-14 overflow-hidden rounded-lg px-4 py-0 shadow-sm mb-2 list-item'
                                "
                                tabindex="0"
                                (click)="onSelect(item)"
                                (keydown.space)="onSelect(item)"
                                (keydown.enter)="focusNext($event)"
                            >
                                <div class="grid h-14 cursor-pointer grid-cols-3 items-center gap-4 p-2 text-sm">
                                    <span class="truncate">{{ item.firstName }} {{ item.lastName }}</span>
                                    <span class="truncate">{{ item.gid }}</span>
                                    <span class="truncate">{{ item.department }}</span>
                                </div>
                            </div>
                        }
                    } @else {
                        <div class="text-center text-[#CCCCD4] flex items-center justify-center h-full">
                            {{ translations.dialog.noResults }}
                        </div>
                    }
                }
            </div>
            <div class="flex justify-end items-center gap-2 mt-4">
                <sag-ui-button
                    color="secondary"
                    (click)="onClose()"
                    ariaLabel="close"
                    (keydown.enter)="focusNext($event)"
                    >{{ translations.dialog.cancel }}
                </sag-ui-button>
            </div>
        </div>
    `, isInline: true, dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: InputComponent, selector: "sag-ui-input", inputs: ["id", "disabled", "required", "invalid", "error", "label", "value", "placeholder", "type", "timeFormat", "dateFormat", "lang", "readOnly", "hint", "floatLabel", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["change", "input", "click", "focus", "blur", "keydown", "keyup", "mouseenter", "mouseleave", "dateChange"] }, { kind: "component", type: ButtonComponent, selector: "sag-ui-button", inputs: ["color", "size", "iconPosition", "disabled", "loading", "square", "type", "style", "class", "icon", "text", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["blur", "focus"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerDialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-scd-picker-dialog',
                    standalone: true,
                    template: `
        <div class="p-4">
            <h1 [class]="primaryTextClasses + ' font-bold text-lg p-0'">
                {{ translations.dialog.title }}
            </h1>
            <p [class]="secondaryTextClasses + 'text-md'">
                {{ translations.dialog.subtitle }}
            </p>
            <div class="grid grid-cols-[1fr_1fr_1fr_56px] gap-2 mt-4 items-start" [formGroup]="formGroup">
                <sag-ui-input
                    [label]="translations.dialog.labels.firstName"
                    [ariaLabel]="translations.dialog.labels.firstName"
                    formControlName="firstName"
                    [invalid]="!!firstNameError"
                    [error]="firstNameError"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-input
                    [label]="translations.dialog.labels.lastName"
                    [ariaLabel]="translations.dialog.labels.lastName"
                    formControlName="lastName"
                    [invalid]="!!lastNameError"
                    [error]="lastNameError"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-input
                    [label]="translations.dialog.labels.gid"
                    [ariaLabel]="translations.dialog.labels.gid"
                    formControlName="gid"
                    (keydown.enter)="focusNext($event)"
                ></sag-ui-input>
                <sag-ui-button
                    class="h-[42px] w-[42px] mt-[12px]"
                    (click)="onSearch()"
                    [loading]="loading"
                    [square]="true"
                    ariaLabel="search"
                    (keydown.enter)="focusNext($event)"
                >
                    <span class="icon-search"></span>
                </sag-ui-button>
            </div>
            <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="click-announcement"></div>
            <div class="h-[200px] my-4 overflow-y-auto">
                <div class="block max-h-14 overflow-hidden px-4 py-0 text-white">
                    <div class="grid cursor-pointer grid-cols-3 items-center gap-4 p-2 text-sm">
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.name
                        }}</span>
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.gid
                        }}</span>
                        <span [class]="secondaryTextClasses + ' truncate text-xs'">{{
                            translations.dialog.table.header.department
                        }}</span>
                    </div>
                </div>

                @if (loading) {
                    <div class="animate-pulse">
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-100 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-90 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-80 w-full mb-1'"></div>
                        <div [class]="searchResultsClasses + ' h-14 rounded-md opacity-70 w-full mb-1'"></div>
                    </div>
                } @else {
                    @if (searchResults.length > 0) {
                        @for (item of searchResults; track $index) {
                            <div
                                [class]="
                                    primaryTextClasses +
                                    ' ' +
                                    searchResultsClasses +
                                    ' block max-h-14 overflow-hidden rounded-lg px-4 py-0 shadow-sm mb-2 list-item'
                                "
                                tabindex="0"
                                (click)="onSelect(item)"
                                (keydown.space)="onSelect(item)"
                                (keydown.enter)="focusNext($event)"
                            >
                                <div class="grid h-14 cursor-pointer grid-cols-3 items-center gap-4 p-2 text-sm">
                                    <span class="truncate">{{ item.firstName }} {{ item.lastName }}</span>
                                    <span class="truncate">{{ item.gid }}</span>
                                    <span class="truncate">{{ item.department }}</span>
                                </div>
                            </div>
                        }
                    } @else {
                        <div class="text-center text-[#CCCCD4] flex items-center justify-center h-full">
                            {{ translations.dialog.noResults }}
                        </div>
                    }
                }
            </div>
            <div class="flex justify-end items-center gap-2 mt-4">
                <sag-ui-button
                    color="secondary"
                    (click)="onClose()"
                    ariaLabel="close"
                    (keydown.enter)="focusNext($event)"
                    >{{ translations.dialog.cancel }}
                </sag-ui-button>
            </div>
        </div>
    `,
                    imports: [ReactiveFormsModule, InputComponent, ButtonComponent]
                }]
        }] });

class ScdPickerExtendedComponent {
    theme = inject(THEME_TOKEN);
    el = inject(ElementRef);
    dialogService = inject(DialogService);
    id = input(this.randomId());
    /**
     * The placeholder of the ScdPicker.
     */
    placeholder = input('');
    /*
     * Whether the ScdPicker is required.
     */
    required = input(false);
    /**
     * Whether the ScdPicker is disabled.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    disabled = false;
    /**
     * Whether the ScdPicker is read-only.
     */
    readOnly = input(false);
    /**
     * The label of the ScdPicker.
     */
    label = input('');
    /**
     * Whether the SCD Picker is in an error state.
     */
    invalid = input(false);
    /**
     * The error message to display.
     */
    error = input('');
    /**
     * Accessibility inputs
     */
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    hint = input(null);
    translations = input({
        dialog: {
            title: 'Search User',
            subtitle: 'Please enter the user details to search for',
            labels: {
                firstName: 'First Name',
                lastName: 'Last Name',
                gid: 'GID'
            },
            table: {
                header: {
                    name: 'Name',
                    gid: 'GID',
                    department: 'Department',
                    select: 'Select'
                },
                select: 'Select'
            },
            noResults: 'No results found',
            cancel: 'Cancel',
            errors: {
                firstNameRequired: 'First name is required',
                lastNameRequired: 'Last name is required'
            }
        }
    });
    /**
     * Whether the switch is checked.
     */
    // TODO: Skipped for migration because:
    //  Your application code writes to the input. This prevents migration.
    value;
    searchFn = input.required();
    input = viewChild.required('input');
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.isOpen = false;
        }
    }
    onFocusOut(event) {
        const focusedInside = this.el.nativeElement.contains(event.relatedTarget);
        if (!focusedInside) {
            this.isOpen = false;
        }
    }
    touched = false;
    selected;
    isOpen = false;
    openDialog() {
        const config = {
            data: { searchFn: this.searchFn(), translations: this.translations() }
        };
        const dialogRef = this.dialogService.open(ScdPickerDialogComponent, config);
        dialogRef.afterClosed.subscribe(result => {
            if (result) {
                this.writeValue(result);
                this.selected = result;
                this.markAsTouched();
            }
        });
    }
    get popoverClasses() {
        const popoverStyles = componentStylesByTheme(this.theme, scdPickerStylesSAG, scdPickerStylesSHS);
        return popoverStyles();
    }
    get displayValue() {
        if (!this.selected)
            return '';
        return `${this.selected?.firstName} ${this.selected?.lastName}`;
    }
    onFocus() {
        if (this.disabled || this.readOnly())
            return;
        this.isOpen = true;
    }
    onBlur() {
        setTimeout(() => {
            this.markAsTouched();
        }, 100);
    }
    // eslint-disable-next-line
    onChange = (_value) => { };
    // eslint-disable-next-line
    onTouch = () => { };
    // eslint-disable-next-line
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // eslint-disable-next-line
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    writeValue(val) {
        this.value = val;
        this.selected = val;
        this.onChange(this.value);
    }
    markAsTouched() {
        if (!this.touched) {
            this.onTouch();
            this.touched = true;
        }
    }
    setDisabledState(disabled) {
        this.disabled = disabled;
    }
    // eslint-disable-next-line
    validate(control) {
        return this.required() && !this.selected ? { required: true } : null;
    }
    blockChange(event) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (event.target && event.target['value']) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            event.target['value'] = this.displayValue;
        }
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerExtendedComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ScdPickerExtendedComponent, isStandalone: true, selector: "sag-ui-scd-picker-extended", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, translations: { classPropertyName: "translations", publicName: "translations", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, searchFn: { classPropertyName: "searchFn", publicName: "searchFn", isSignal: true, isRequired: true, transformFunction: null } }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "focusout": "onFocusOut($event)" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => ScdPickerExtendedComponent),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef(() => ScdPickerExtendedComponent),
                multi: true
            },
            DialogService
        ], viewQueries: [{ propertyName: "input", first: true, predicate: ["input"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [disabled]=\"disabled\"\n      [attr.aria-disabled]=\"disabled\"\n      [attr.aria-label]=\"ariaLabel() || label() + (required() ? ' required' : '')\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n      [attr.aria-describedby]=\"ariaDescribedby()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [placeholder]=\"placeholder()\"\n      [value]=\"displayValue\"\n      (input)=\"blockChange($event)\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      #input\n    />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <sag-ui-button\n      class=\"absolute !w-[30px] bottom-[3px] right-[3px] z-[10]\"\n      style=\"padding: 0!important;\"\n      [disabled]=\"disabled\"\n      [square]=\"true\"\n      (click)=\"openDialog()\"\n      ariaLabel=\"Open scd picker dialog\"\n    >\n      <span class=\"icon-user\"></span>\n    </sag-ui-button>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }, { kind: "component", type: ButtonComponent, selector: "sag-ui-button", inputs: ["color", "size", "iconPosition", "disabled", "loading", "square", "type", "style", "class", "icon", "text", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["blur", "focus"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdPickerExtendedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-scd-picker-extended', standalone: true, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => ScdPickerExtendedComponent),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef(() => ScdPickerExtendedComponent),
                            multi: true
                        },
                        DialogService
                    ], imports: [InputLabelDirective, InputDirective, TooltipDirective, ButtonComponent], template: "<div class=\"flex flex-col\">\n  <div class=\"flex flex-col-reverse w-full relative\">\n    <input\n      sag-ui-input\n      type=\"text\"\n      class=\"pr-8\"\n      [disabled]=\"disabled\"\n      [attr.aria-disabled]=\"disabled\"\n      [attr.aria-label]=\"ariaLabel() || label() + (required() ? ' required' : '')\"\n      [attr.aria-labelledby]=\"ariaLabelledby() || id()\"\n      [attr.aria-describedby]=\"ariaDescribedby()\"\n      [required]=\"invalid() || (required() && touched)\"\n      [attr.pattern]=\"invalid() ? 'SAG_INVALID_STATE' : null\"\n      [placeholder]=\"placeholder()\"\n      [value]=\"displayValue\"\n      (input)=\"blockChange($event)\"\n      (focus)=\"onFocus()\"\n      (blur)=\"onBlur()\"\n      #input\n    />\n    <label sag-ui-input-label [htmlFor]=\"id()\">\n      @if (required()) {\n        <span aria-hidden=\"true\">*</span>\n      }\n      {{ label() }}\n      @if (hint()) {\n        <span\n          class=\"text-[#999999] dark:text-[#666666] icon-circle-information pl-2\"\n          sag-ui-tooltip\n          [tooltip]=\"hint()\"\n          [attr.aria-label]=\"hint()\"\n        ></span>\n      }\n    </label>\n    <sag-ui-button\n      class=\"absolute !w-[30px] bottom-[3px] right-[3px] z-[10]\"\n      style=\"padding: 0!important;\"\n      [disabled]=\"disabled\"\n      [square]=\"true\"\n      (click)=\"openDialog()\"\n      ariaLabel=\"Open scd picker dialog\"\n    >\n      <span class=\"icon-user\"></span>\n    </sag-ui-button>\n  </div>\n</div>\n" }]
        }], propDecorators: { disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

class ScdExtendedDemoComponent {
    value;
    async searchFn(data) {
        const users = [
            {
                gid: 'Z004123',
                email: 'john.doe@siemens.com',
                firstName: 'John',
                lastName: 'Doe',
                department: 'GBS AM SOL DCS'
            },
            {
                gid: 'Z004124',
                email: 'jane.doe@siemens.com',
                firstName: 'Jane',
                lastName: 'Doe',
                department: 'GBS AM SOL DCS'
            },
            {
                gid: 'Z004125',
                email: 'max.mustermann@siemens.com',
                firstName: 'Max',
                lastName: 'Mustermann',
                department: 'GBS DE SOL DCS'
            }
        ];
        return users.filter(user => {
            if (data.firstName && !user.firstName.toLowerCase().includes(data.firstName.toLowerCase())) {
                return false;
            }
            if (data.lastName && !user.lastName.toLowerCase().includes(data.lastName.toLowerCase())) {
                return false;
            }
            if (data.gid && !user.gid.toLowerCase().includes(data.gid.toLowerCase())) {
                return false;
            }
            return true;
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdExtendedDemoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.2", type: ScdExtendedDemoComponent, isStandalone: true, selector: "sag-ui-scd-extended-demo", ngImport: i0, template: ` <sag-ui-scd-picker-extended label="User" [searchFn]="searchFn" />`, isInline: true, dependencies: [{ kind: "component", type: ScdPickerExtendedComponent, selector: "sag-ui-scd-picker-extended", inputs: ["id", "placeholder", "required", "disabled", "readOnly", "label", "invalid", "error", "ariaLabel", "ariaLabelledby", "ariaDescribedby", "hint", "translations", "value", "searchFn"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdExtendedDemoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-scd-extended-demo', standalone: true, template: ` <sag-ui-scd-picker-extended label="User" [searchFn]="searchFn" />`, imports: [ScdPickerExtendedComponent] }]
        }] });

const stepperLineBaseStylesSAG = cva(['flex', 'items-center', 'justify-center'], {
    variants: {
        orientation: {
            vertical: ['min-h-6', 'h-full', 'w-full'],
            horizontal: ['h-0.5', 'w-full']
        }
    }
});
const stepperLineBaseStylesSHS = cva(['flex', 'items-center', 'justify-center'], {
    variants: {
        orientation: {
            vertical: ['min-h-6', 'h-full', 'w-full'],
            horizontal: ['h-0.5', 'w-full']
        }
    }
});

class StepperLineBaseDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    orientation = input('horizontal');
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, stepperLineBaseStylesSAG, stepperLineBaseStylesSHS);
        style(this.elementRef, inputStyles({ orientation: this.orientation() }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperLineBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: StepperLineBaseDirective, isStandalone: true, selector: "[sag-ui-stepper-line-base]", inputs: { orientation: { classPropertyName: "orientation", publicName: "orientation", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperLineBaseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-stepper-line-base]',
                    standalone: true
                }]
        }] });

const stepperLineStylesSAG = cva([
    'data-[status=done]:bg-[#007993]',
    'dark:data-[status=done]:bg-[#00CCCC]',
    'data-[status=error]:bg-[#D72339]',
    'dark:data-[status=error]:bg-[#FF7687]',
    'data-[status=active]:bg-[#005159]',
    'dark:data-[status=active]:bg-[#00FFB9]',
    'data-[status=default]:bg-[#4C4C68]',
    'dark:data-[status=default]:bg-[#B3B3BE]',
    'data-[status=disabled]:bg-[#B3B3BE]',
    'dark:data-[status=disabled]:bg-[#737389]'
], {
    variants: {
        orientation: {
            vertical: ['min-h-6', 'h-full', 'w-0.5'],
            horizontal: ['h-0.5', 'w-full']
        }
    }
});
const stepperLineStylesSHS = cva([
    'data-[status=done]:bg-[#cf4b00]',
    'dark:data-[status=done]:bg-[#cf4b00]',
    'data-[status=error]:bg-[#e7001d]',
    'dark:data-[status=error]:bg-[#e7001d]',
    'data-[status=active]:bg-[#ec6602]',
    'dark:data-[status=active]:bg-[#ec6602]',
    'data-[status=default]:bg-[#666666]',
    'dark:data-[status=default]:bg-[#ffffff]',
    'data-[status=disabled]:bg-[#ededed]',
    'dark:data-[status=disabled]:bg-[#ededed]'
], {
    variants: {
        orientation: {
            vertical: ['min-h-6', 'h-full', 'w-0.5'],
            horizontal: ['h-0.5', 'w-full']
        }
    }
});

class StepperLineDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    orientation = input('horizontal');
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, stepperLineStylesSAG, stepperLineStylesSHS);
        style(this.elementRef, inputStyles({ orientation: this.orientation() }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperLineDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: StepperLineDirective, isStandalone: true, selector: "[sag-ui-stepper-line]", inputs: { orientation: { classPropertyName: "orientation", publicName: "orientation", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperLineDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-stepper-line]',
                    standalone: true
                }]
        }] });

const stepperKnobStyles = [
    'h-8',
    'w-8',
    'aspect-square',
    'rounded-full',
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'relative',
    'border-[3px]',
    'focus-visible:outline-[#1491EB]'
];
const stepperKnobStylesSAG = cva([
    ...stepperKnobStyles,
    'bg-transparent',
    'data-[status=done]:border-[#007993]',
    'data-[status=done]:text-[#007993]',
    'dark:data-[status=done]:border-[#00CCCC]',
    'dark:data-[status=done]:text-[#00CCCC]',
    'data-[status=error]:border-[#D72339]',
    'data-[status=error]:text-[#D72339]',
    'dark:data-[status=error]:border-[#FF7687]',
    'dark:data-[status=error]:text-[#FF7687]',
    'data-[status=active]:border-[#005159]',
    'data-[status=active]:text-[#005159]',
    'dark:data-[status=active]:border-[#00FFB9]',
    'dark:data-[status=active]:text-[#00FFB9]',
    'data-[status=default]:border-[#4C4C68]',
    'data-[status=default]:text-[#4C4C68]',
    'dark:data-[status=default]:border-[#B3B3BE]',
    'dark:data-[status=default]:text-[#B3B3BE]',
    'data-[status=disabled]:border-[#B3B3BE]',
    'data-[status=disabled]:text-[#B3B3BE]',
    'dark:data-[status=disabled]:border-[#737389]',
    'dark:data-[status=disabled]:text-[#737389]'
]);
const stepperKnobStylesSHS = cva([
    ...stepperKnobStyles,
    'bg-transparent',
    'data-[status=done]:border-[#cf4b00]',
    'data-[status=done]:text-[#cf4b00]',
    'dark:data-[status=done]:border-[#cf4b00]',
    'dark:data-[status=done]:text-[#cf4b00]',
    'data-[status=error]:border-[#e7001d]',
    'data-[status=error]:text-[#e7001d]',
    'dark:data-[status=error]:border-[#e7001d]',
    'dark:data-[status=error]:text-[#e7001d]',
    'data-[status=active]:border-[#ec6602]',
    'data-[status=active]:text-[#ec6602]',
    'dark:data-[status=active]:border-[#ec6602]',
    'dark:data-[status=active]:text-[#ec6602]',
    'data-[status=default]:border-[#666666]',
    'data-[status=default]:text-[#666666]',
    'dark:data-[status=default]:border-[#ffffff]',
    'dark:data-[status=default]:text-[#ffffff]',
    'data-[status=disabled]:border-[#ededed]',
    'data-[status=disabled]:text-[#ededed]',
    'dark:data-[status=disabled]:border-[#ededed]',
    'dark:data-[status=disabled]:text-[#ededed]'
]);

class StepperKnobDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    applyStyles() {
        const styles = componentStylesByTheme(this.theme, stepperKnobStylesSAG, stepperKnobStylesSHS);
        updateElementStyles(this.elementRef, styles());
    }
    ngOnInit() {
        this.applyStyles();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: StepperKnobDirective, isStandalone: true, selector: "[sag-ui-stepper-knob]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-stepper-knob]',
                    standalone: true
                }]
        }] });

const stepperKnobIndicatorStyles = [
    'h-3',
    'aspect-square',
    'rounded-full',
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer'
];
const stepperKnobIndicatorStylesSAG = cva([
    ...stepperKnobIndicatorStyles,
    'data-[status=done]:bg-[#007993]',
    'dark:data-[status=done]:bg-[#00CCCC]',
    'data-[status=error]:bg-[#D72339]',
    'dark:data-[status=error]:bg-[#FF7687]',
    'data-[status=active]:bg-[#005159]',
    'dark:data-[status=active]:bg-[#00FFB9]',
    'data-[status=default]:bg-[#4C4C68]',
    'dark:data-[status=default]:bg-[#B3B3BE]',
    'data-[status=disabled]:bg-[#B3B3BE]',
    'dark:data-[status=disabled]:bg-[#737389]'
]);
const stepperKnobIndicatorStylesSHS = cva([
    ...stepperKnobIndicatorStyles,
    'data-[status=done]:bg-[#cf4b00]',
    'dark:data-[status=done]:bg-[#cf4b00]',
    'data-[status=error]:bg-[#e7001d]',
    'dark:data-[status=error]:bg-[#e7001d]',
    'data-[status=active]:bg-[#ec6602]',
    'dark:data-[status=active]:bg-[#ec6602]',
    'data-[status=default]:bg-[#666666]',
    'dark:data-[status=default]:bg-[#ffffff]',
    'data-[status=disabled]:bg-[#ededed]',
    'dark:data-[status=disabled]:bg-[#ededed]'
]);

class StepperKnobIndicatorDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, stepperKnobIndicatorStylesSAG, stepperKnobIndicatorStylesSHS);
        style(this.elementRef, inputStyles());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobIndicatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.2", type: StepperKnobIndicatorDirective, isStandalone: true, selector: "[sag-ui-stepper-knob-indicator]", ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobIndicatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-stepper-knob-indicator]',
                    standalone: true
                }]
        }] });

const stepperKnobTextStylesSAG = cva([
    'text-[#000028]',
    'dark:text-white',
    'text-base',
    'font-normal',
    'select-none',
    'truncate',
    'data-[status=done]:text-[#007993]',
    'dark:data-[status=done]:text-[#00CCCC]',
    'data-[status=error]:text-[#D72339]',
    'dark:data-[status=error]:text-[#FF7687]',
    'data-[status=active]:text-[#005159]',
    'dark:data-[status=active]:text-[#00FFB9]',
    'data-[status=default]:text-[#000028]',
    'dark:data-[status=default]:text-[#FFFFFF]',
    'data-[status=disabled]:text-[#B3B3BE]',
    'dark:data-[status=disabled]:text-[#737389]'
], {
    variants: {
        orientation: {
            vertical: ['text-left', 'ml-2'],
            horizontal: ['text-center', 'mt-2']
        }
    }
});
const stepperKnobTextStylesSHS = cva([
    'text-[#333333]',
    'dark:text-white',
    'text-base',
    'font-normal',
    'select-none',
    'truncate',
    'data-[status=done]:text-[#cf4b00]',
    'dark:data-[status=done]:text-[#cf4b00]',
    'data-[status=error]:text-[#e7001d]',
    'dark:data-[status=error]:text-[#e7001d]',
    'data-[status=active]:text-[#ec6602]',
    'dark:data-[status=active]:text-[#ec6602]',
    'data-[status=default]:text-[#666666]',
    'dark:data-[status=default]:text-[#ffffff]',
    'data-[status=disabled]:text-[#ededed]',
    'dark:data-[status=disabled]:text-[#ededed]'
], {
    variants: {
        orientation: {
            vertical: ['text-left', 'ml-2'],
            horizontal: ['text-center', 'mt-2']
        }
    }
});

class StepperKnobTextDirective {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    orientation = input('horizontal');
    ngOnInit() {
        const inputStyles = componentStylesByTheme(this.theme, stepperKnobTextStylesSAG, stepperKnobTextStylesSHS);
        style(this.elementRef, inputStyles({ orientation: this.orientation() }));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobTextDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "19.2.2", type: StepperKnobTextDirective, isStandalone: true, selector: "[sag-ui-stepper-knob-text]", inputs: { orientation: { classPropertyName: "orientation", publicName: "orientation", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperKnobTextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[sag-ui-stepper-knob-text]',
                    standalone: true
                }]
        }] });

class StepperComponent {
    steps = input([]);
    active = input(0);
    activeSubStep = input(0);
    invalidSteps = input([]);
    disabledSteps = input([]);
    orientation = input('horizontal');
    styleConfig = input({});
    styleConfigWithDefaults = computed(() => ({
        markActiveSection: this.styleConfig().markActiveSection ?? true,
        fixedHeight: this.styleConfig().fixedHeight ?? true
    }));
    stepperKnobs = viewChildren('stepperKnob');
    selectedChange = output();
    selectedSubStepChange = output();
    getState(index) {
        if (this.invalidSteps().includes(index)) {
            return 'error';
        }
        if (this.disabledSteps().includes(index)) {
            return 'disabled';
        }
        const active = this.active();
        if (index === active) {
            return 'active';
        }
        if (index < active) {
            return 'done';
        }
        return 'default';
    }
    displaySubSteps(step, index) {
        return this.orientation() === 'vertical' && this.active() === index && !!step.subSteps;
    }
    getSubStepState(step, index, type) {
        if (type === 'line') {
            if (index < this.activeSubStep()) {
                return 'active';
            }
        }
        if (this.activeSubStep() === index) {
            return 'active';
        }
        if (step.done)
            return 'done';
        if (step.disabled)
            return 'disabled';
        if (step.invalid)
            return 'error';
        return 'default';
    }
    stepDone(index) {
        return index < this.active();
    }
    containerClasses(index) {
        const baseStyles = this.orientation() === 'horizontal'
            ? 'w-full flex items-center pt-[16px] pb-[16px] rounded-md'
            : 'flex flex-col items-center pl-[16px] pr-[16px] rounded-md';
        return (baseStyles +
            (this.getState(index) === 'active' && this.styleConfig().markActiveSection
                ? ' bg-[#F3F3F0] dark:bg-[#4d4d54] dark:bg-opacity-[45%]'
                : ''));
    }
    isDisabled(index) {
        return this.disabledSteps().includes(index);
    }
    isInvalid(index) {
        return this.invalidSteps().includes(index);
    }
    onArrowNavigate(event, currentIndex) {
        const isRight = event.key === 'ArrowRight';
        const isLeft = event.key === 'ArrowLeft';
        if (!isRight && !isLeft)
            return;
        event.preventDefault();
        const knobs = this.stepperKnobs();
        let nextIndex = currentIndex;
        if (isRight && currentIndex < knobs.length - 1) {
            nextIndex = currentIndex + 1;
        }
        else if (isLeft && currentIndex > 0) {
            nextIndex = currentIndex - 1;
        }
        else {
            return; // Do nothing if at the end/start and trying to go further
        }
        const nextEl = knobs[nextIndex]?.nativeElement;
        if (nextEl && !this.isDisabled(nextIndex)) {
            nextEl.focus();
        }
    }
    changeActiveSubStep(index) {
        this.selectedSubStepChange.emit(index);
    }
    screenY = screenY;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: StepperComponent, isStandalone: true, selector: "sag-ui-stepper", inputs: { steps: { classPropertyName: "steps", publicName: "steps", isSignal: true, isRequired: false, transformFunction: null }, active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, activeSubStep: { classPropertyName: "activeSubStep", publicName: "activeSubStep", isSignal: true, isRequired: false, transformFunction: null }, invalidSteps: { classPropertyName: "invalidSteps", publicName: "invalidSteps", isSignal: true, isRequired: false, transformFunction: null }, disabledSteps: { classPropertyName: "disabledSteps", publicName: "disabledSteps", isSignal: true, isRequired: false, transformFunction: null }, orientation: { classPropertyName: "orientation", publicName: "orientation", isSignal: true, isRequired: false, transformFunction: null }, styleConfig: { classPropertyName: "styleConfig", publicName: "styleConfig", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { selectedChange: "selectedChange", selectedSubStepChange: "selectedSubStepChange" }, viewQueries: [{ propertyName: "stepperKnobs", predicate: ["stepperKnob"], descendants: true, isSignal: true }], ngImport: i0, template: "<div\n    class=\"gap-0\"\n    [class]=\"orientation() === 'horizontal' ? 'grid' : (styleConfigWithDefaults().fixedHeight ? 'grid' : 'flex flex-col')\"\n    [class.h-full]=\"orientation() === 'vertical'\"\n    [class.w-full]=\"orientation() === 'horizontal'\"\n    [style]=\"\n        orientation() === 'horizontal'\n            ? 'grid-template-columns: repeat(' + steps().length + ', 1fr)'\n            :( styleConfigWithDefaults().fixedHeight ? 'grid-template-rows: repeat(' + steps().length + ', 1fr)': '')\n    \"\n>\n    @for (step of steps(); track i; let i = $index) {\n        <div\n            [class]=\"containerClasses(i)\"\n            [class.cursor-not-allowed]=\"isDisabled(i)\"\n            [class.flex-col]=\"orientation() === 'horizontal'\"\n        >\n            <div\n                class=\"gap-0 items-center justify-center\"\n                [class]=\"(orientation() === 'horizontal') ? 'grid' : styleConfigWithDefaults().fixedHeight ? 'grid' : 'flex flex-col'\"\n                [class.w-full]=\"orientation() === 'horizontal'\"\n                [class.h-full]=\"orientation() === 'vertical'\"\n                [style]=\"\n                    orientation() === 'horizontal'\n                        ? 'grid-template-columns: 1fr 32px 1fr'\n                        : ((orientation() === 'horizontal') ? 'grid-template-rows: 1fr 32px 1fr': styleConfigWithDefaults().fixedHeight ? 'grid-template-rows: 1fr 32px 1fr' : '')\n                \"\n            >\n                <div sag-ui-stepper-line-base [orientation]=\"orientation()\" [class.opacity-0]=\"i === 0\">\n                    <div sag-ui-stepper-line [orientation]=\"orientation()\" [attr.data-status]=\"getState(i)\"></div>\n                </div>\n                <div\n                    #stepperKnob\n                    sag-ui-stepper-knob\n                    [attr.aria-label]=\"i + 1 + step.label\"\n                    [attr.data-status]=\"getState(i)\"\n                    (click)=\"!isDisabled(i) && selectedChange.emit(i)\"\n                    (keydown.space)=\"!isDisabled(i) && selectedChange.emit(i)\"\n                    (keydown.arrowRight)=\"onArrowNavigate($event, i)\"\n                    (keydown.arrowLeft)=\"onArrowNavigate($event, i)\"\n                    [tabindex]=\"isDisabled(i)? -1 : 0\"\n                >\n                    @if (step.doneIcon && stepDone(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.doneIcon\"></span>\n                    }\n                    @if (step.icon && !stepDone(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.icon\"></span>\n                    }\n                    @if (step.invalidIcon && isInvalid(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.invalidIcon\"></span>\n                    }\n\n                    @if (active() === i) {\n                        <span sag-ui-stepper-knob-indicator [attr.data-status]=\"getState(i)\"></span>\n                    }\n                </div>\n                <div\n                    sag-ui-stepper-line-base\n                    [orientation]=\"orientation()\"\n                    [class.opacity-0]=\"i === steps().length - 1 && (orientation() === 'horizontal' || !steps()[i].subSteps)\"\n                >\n                    <div sag-ui-stepper-line [orientation]=\"orientation()\" [attr.data-status]=\"getState(i)\"></div>\n                </div>\n            </div>\n            <div class=\"flex items-center justify-center\" [class.w-full]=\"orientation() === 'horizontal'\">\n                @if (step.label) {\n                    <span sag-ui-stepper-knob-text [attr.data-status]=\"getState(i)\" [orientation]=\"orientation()\">{{\n                        step.label\n                    }}</span>\n                }\n            </div>\n        </div>\n        @if (displaySubSteps(step, i)) {\n            <div\n                [class.cursor-not-allowed]=\"isDisabled(i)\"\n                class=\"grid grid-cols-[32px_1fr] pl-[16px] pr-[16px]\"\n            >\n                <div\n                    class=\"grid gap-0 items-center justify-center h-full w-8\"\n                >\n                    @for (subStep of step.subSteps; track subStep.label) {\n                        <div sag-ui-stepper-line-base [orientation]=\"orientation()\">\n                            <div\n                                sag-ui-stepper-line\n                                [orientation]=\"orientation()\"\n                                [attr.data-status]=\"getSubStepState(subStep, i, 'line')\"\n                            ></div>\n                        </div>\n                    }\n                </div>\n                <div\n                    class=\"items-start justify-center flex flex-col gap-4\"\n                >\n                    @for (subStep of step.subSteps; track subStep.label; let i = $index) {\n                        <span\n                            sag-ui-stepper-knob-text\n                            [attr.data-status]=\"getSubStepState(subStep, i, 'text')\"\n                            [orientation]=\"orientation()\"\n                            class=\"ml-4 cursor-pointer hover:font-semibold first:pt-4 last:pb-4\"\n                            (click)=\"!subStep.disabled && changeActiveSubStep(i)\"\n                            (keydown.space)=\"!subStep.disabled && changeActiveSubStep(i)\"\n                            tabindex=\"0\"\n                            >{{ subStep.label }}</span\n                        >\n                    }\n                </div>\n            </div>\n        }\n    }\n</div>\n", dependencies: [{ kind: "directive", type: StepperLineBaseDirective, selector: "[sag-ui-stepper-line-base]", inputs: ["orientation"] }, { kind: "directive", type: StepperLineDirective, selector: "[sag-ui-stepper-line]", inputs: ["orientation"] }, { kind: "directive", type: StepperKnobDirective, selector: "[sag-ui-stepper-knob]" }, { kind: "directive", type: StepperKnobIndicatorDirective, selector: "[sag-ui-stepper-knob-indicator]" }, { kind: "directive", type: StepperKnobTextDirective, selector: "[sag-ui-stepper-knob-text]", inputs: ["orientation"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: StepperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-stepper', standalone: true, imports: [
                        StepperLineBaseDirective,
                        StepperLineDirective,
                        StepperKnobDirective,
                        StepperKnobIndicatorDirective,
                        StepperKnobTextDirective
                    ], template: "<div\n    class=\"gap-0\"\n    [class]=\"orientation() === 'horizontal' ? 'grid' : (styleConfigWithDefaults().fixedHeight ? 'grid' : 'flex flex-col')\"\n    [class.h-full]=\"orientation() === 'vertical'\"\n    [class.w-full]=\"orientation() === 'horizontal'\"\n    [style]=\"\n        orientation() === 'horizontal'\n            ? 'grid-template-columns: repeat(' + steps().length + ', 1fr)'\n            :( styleConfigWithDefaults().fixedHeight ? 'grid-template-rows: repeat(' + steps().length + ', 1fr)': '')\n    \"\n>\n    @for (step of steps(); track i; let i = $index) {\n        <div\n            [class]=\"containerClasses(i)\"\n            [class.cursor-not-allowed]=\"isDisabled(i)\"\n            [class.flex-col]=\"orientation() === 'horizontal'\"\n        >\n            <div\n                class=\"gap-0 items-center justify-center\"\n                [class]=\"(orientation() === 'horizontal') ? 'grid' : styleConfigWithDefaults().fixedHeight ? 'grid' : 'flex flex-col'\"\n                [class.w-full]=\"orientation() === 'horizontal'\"\n                [class.h-full]=\"orientation() === 'vertical'\"\n                [style]=\"\n                    orientation() === 'horizontal'\n                        ? 'grid-template-columns: 1fr 32px 1fr'\n                        : ((orientation() === 'horizontal') ? 'grid-template-rows: 1fr 32px 1fr': styleConfigWithDefaults().fixedHeight ? 'grid-template-rows: 1fr 32px 1fr' : '')\n                \"\n            >\n                <div sag-ui-stepper-line-base [orientation]=\"orientation()\" [class.opacity-0]=\"i === 0\">\n                    <div sag-ui-stepper-line [orientation]=\"orientation()\" [attr.data-status]=\"getState(i)\"></div>\n                </div>\n                <div\n                    #stepperKnob\n                    sag-ui-stepper-knob\n                    [attr.aria-label]=\"i + 1 + step.label\"\n                    [attr.data-status]=\"getState(i)\"\n                    (click)=\"!isDisabled(i) && selectedChange.emit(i)\"\n                    (keydown.space)=\"!isDisabled(i) && selectedChange.emit(i)\"\n                    (keydown.arrowRight)=\"onArrowNavigate($event, i)\"\n                    (keydown.arrowLeft)=\"onArrowNavigate($event, i)\"\n                    [tabindex]=\"isDisabled(i)? -1 : 0\"\n                >\n                    @if (step.doneIcon && stepDone(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.doneIcon\"></span>\n                    }\n                    @if (step.icon && !stepDone(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.icon\"></span>\n                    }\n                    @if (step.invalidIcon && isInvalid(i) && active() !== i) {\n                        <span [class]=\"'icon-' + step.invalidIcon\"></span>\n                    }\n\n                    @if (active() === i) {\n                        <span sag-ui-stepper-knob-indicator [attr.data-status]=\"getState(i)\"></span>\n                    }\n                </div>\n                <div\n                    sag-ui-stepper-line-base\n                    [orientation]=\"orientation()\"\n                    [class.opacity-0]=\"i === steps().length - 1 && (orientation() === 'horizontal' || !steps()[i].subSteps)\"\n                >\n                    <div sag-ui-stepper-line [orientation]=\"orientation()\" [attr.data-status]=\"getState(i)\"></div>\n                </div>\n            </div>\n            <div class=\"flex items-center justify-center\" [class.w-full]=\"orientation() === 'horizontal'\">\n                @if (step.label) {\n                    <span sag-ui-stepper-knob-text [attr.data-status]=\"getState(i)\" [orientation]=\"orientation()\">{{\n                        step.label\n                    }}</span>\n                }\n            </div>\n        </div>\n        @if (displaySubSteps(step, i)) {\n            <div\n                [class.cursor-not-allowed]=\"isDisabled(i)\"\n                class=\"grid grid-cols-[32px_1fr] pl-[16px] pr-[16px]\"\n            >\n                <div\n                    class=\"grid gap-0 items-center justify-center h-full w-8\"\n                >\n                    @for (subStep of step.subSteps; track subStep.label) {\n                        <div sag-ui-stepper-line-base [orientation]=\"orientation()\">\n                            <div\n                                sag-ui-stepper-line\n                                [orientation]=\"orientation()\"\n                                [attr.data-status]=\"getSubStepState(subStep, i, 'line')\"\n                            ></div>\n                        </div>\n                    }\n                </div>\n                <div\n                    class=\"items-start justify-center flex flex-col gap-4\"\n                >\n                    @for (subStep of step.subSteps; track subStep.label; let i = $index) {\n                        <span\n                            sag-ui-stepper-knob-text\n                            [attr.data-status]=\"getSubStepState(subStep, i, 'text')\"\n                            [orientation]=\"orientation()\"\n                            class=\"ml-4 cursor-pointer hover:font-semibold first:pt-4 last:pb-4\"\n                            (click)=\"!subStep.disabled && changeActiveSubStep(i)\"\n                            (keydown.space)=\"!subStep.disabled && changeActiveSubStep(i)\"\n                            tabindex=\"0\"\n                            >{{ subStep.label }}</span\n                        >\n                    }\n                </div>\n            </div>\n        }\n    }\n</div>\n" }]
        }] });

class ScdDemoComponent {
    value;
    async searchFn(searchTerm) {
        const users = [
            {
                gid: 'Z004123',
                email: 'john.doe@siemens.com',
                firstName: 'John',
                lastName: 'Doe'
            },
            {
                gid: 'Z004124',
                email: 'jane.doe@siemens.com',
                firstName: 'Jane',
                lastName: 'Doe'
            },
            {
                gid: 'Z004125',
                email: 'max.mustermann@siemens.com',
                firstName: 'Max',
                lastName: 'Mustermann'
            }
        ];
        return users.filter(user => {
            return (user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.gid.toLowerCase().includes(searchTerm.toLowerCase()));
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdDemoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.2", type: ScdDemoComponent, isStandalone: true, selector: "sag-ui-scd-demo", ngImport: i0, template: ` <sag-ui-scd-picker label="User" [searchFn]="searchFn" />`, isInline: true, dependencies: [{ kind: "component", type: ScdPickerComponent, selector: "sag-ui-scd-picker", inputs: ["id", "placeholder", "required", "disabled", "readOnly", "label", "invalid", "error", "value", "searchFn", "hint"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ScdDemoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-scd-demo', standalone: true, template: ` <sag-ui-scd-picker label="User" [searchFn]="searchFn" />`, imports: [ScdPickerComponent] }]
        }] });

/* eslint-disable @typescript-eslint/no-explicit-any */
class SnackbarRef {
    constructor() { }
    close(result) {
        this._afterClosed.next(result);
    }
    _afterClosed = new Subject();
    afterClosed = this._afterClosed.asObservable();
}

const snackbarBaseStyles = [
    'shadow',
    'flex',
    'p-3',
    'absolute',
    'm-4',
    'min-w-[300px]',
    'rounded-md',
    'z-50',
    'transition-all',
    'duration-500'
];
const snackbarStylesSAG = cva([
    ...snackbarBaseStyles,
    'text-[#000028]',
    'bg-[#F3F3F0]',
    'dark:bg-[#23233C]',
    'dark:text-white'
]);
const snackbarStylesSHS = cva([
    ...snackbarBaseStyles,
    'text-[#666666]',
    'bg-white',
    'dark:bg-[#1a1a1a]',
    'dark:text-white'
]);

/* eslint-disable @typescript-eslint/no-explicit-any */
class SnackbarComponent {
    theme = inject(THEME_TOKEN);
    cd = inject(ChangeDetectorRef);
    snackbarRef = inject(SnackbarRef);
    icon = '';
    iconClass = '';
    type = 'info';
    title;
    description;
    duration = 5000;
    position = 'bottom';
    zIndex = 0;
    _onClose = new Subject();
    closeTimeout;
    onClose = this._onClose.asObservable();
    state = 'out';
    ngAfterViewInit() {
        this.cd.detectChanges();
        setTimeout(() => {
            this.open();
        }, 200);
    }
    handleOverlayClick() {
        this.snackbarRef.close();
    }
    handleSnackbarClick(evt) {
        evt.stopPropagation();
    }
    get snackbarClasses() {
        const styles = componentStylesByTheme(this.theme, snackbarStylesSAG, snackbarStylesSHS);
        return styles();
    }
    get snackbarStyles() {
        switch (this.position) {
            case 'top-left':
                return {
                    top: '1rem',
                    left: '1rem'
                };
            case 'top':
                return {
                    top: '1rem',
                    left: '50%',
                    transform: 'translateX(-50%)'
                };
            case 'top-right':
                return {
                    top: '1rem',
                    right: '1rem'
                };
            case 'bottom-left':
                return {
                    bottom: '1rem',
                    left: '1rem'
                };
            case 'bottom':
                return {
                    bottom: '1rem',
                    left: '50%',
                    transform: 'translateX(-50%)'
                };
            case 'bottom-right':
                return {
                    bottom: '1rem',
                    right: '1rem'
                };
        }
    }
    get animationStart() {
        switch (this.position) {
            case 'top-left':
                return 'translateX(0)';
            case 'top':
                return 'translateY(0) translateX(-50%)';
            case 'top-right':
                return 'translateX(0)';
            case 'bottom-left':
                return 'translateX(0)';
            case 'bottom':
                return 'translateY(0) translateX(-50%)';
            case 'bottom-right':
                return 'translateX(0)';
        }
    }
    get animationEnd() {
        switch (this.position) {
            case 'top-left':
                return 'translateX(-150%)';
            case 'top':
                return 'translateY(-150%) translateX(-50%)';
            case 'top-right':
                return 'translateX(150%)';
            case 'bottom-left':
                return 'translateX(-150%)';
            case 'bottom':
                return 'translateY(150%) translateX(-50%)';
            case 'bottom-right':
                return 'translateX(150%)';
        }
    }
    get iconClasses() {
        const icon = this.icon;
        if (icon) {
            return `icon-${icon} ${this.iconClass} text-xl`;
        }
        const iconsMap = {
            sag: {
                info: 'icon-Information text-[#007EB1] dark:text-[#00BEDC] text-xl',
                success: 'icon-Success text-[#01893A] dark:text-[#01D65A] text-xl',
                warning: 'icon-Warning text-[#E96401] dark:text-[#FF9000] text-xl',
                error: 'icon-Error text-[#D72339] dark:text-[#FF2640] text-xl',
                plain: ''
            },
            shs: {
                info: 'icon-Information text-[#007EB1] dark:text-[#00BEDC] text-xl',
                success: 'icon-Success text-[#01893A] dark:text-[#01D65A] text-xl',
                warning: 'icon-Warning text-[#E96401] dark:text-[#FF9000] text-xl',
                error: 'icon-Error text-[#D72339] dark:text-[#FF2640] text-xl',
                plain: ''
            }
        };
        return iconsMap[this.theme][this.type];
    }
    open() {
        this.state = 'in';
        this.closeTimeout = setTimeout(() => {
            this.close();
        }, this.duration);
    }
    close() {
        this.state = 'out';
        clearTimeout(this.closeTimeout);
        setTimeout(() => {
            this._onClose.next({});
            this._onClose.complete();
        }, 400);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SnackbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: SnackbarComponent, isStandalone: true, selector: "sag-ui-snackbar", inputs: { icon: "icon", iconClass: "iconClass", type: "type", title: "title", description: "description", duration: "duration", position: "position", zIndex: "zIndex" }, ngImport: i0, template: ` <div
        class="flex fixed top-0 left-0 bottom-0 right-0 pointer-events-none"
        [style.z-index]="zIndex"
        (click)="handleOverlayClick()"
    >
        <div
            [class]="snackbarClasses"
            (click)="handleSnackbarClick($event)"
            [ngStyle]="snackbarStyles"
            [@slideInOut]="{
                value: state,
                params: { start: animationStart, end: animationEnd }
            }"
        >
            <div class="flex items-center">
                @if (icon || type !== 'plain') {
                    <div class="flex items-center justify-center p-2">
                        <span [class]="iconClasses"></span>
                    </div>
                }
                <div class="flex flex-col">
                    <div class="font-bold">{{ title }}</div>
                    <div class="text-sm">{{ description }}</div>
                </div>
            </div>
        </div>
    </div>`, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1$1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [
            trigger('slideInOut', [
                state('in', style$1({ transform: '{{ start }}' }), {
                    params: { start: '' }
                }),
                state('out', style$1({ transform: '{{ end }}' }), {
                    params: { end: '' }
                }),
                transition('in => out', animate('300ms ease-in')),
                transition('out => in', animate('300ms ease-in'))
            ])
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SnackbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-snackbar',
                    standalone: true,
                    template: ` <div
        class="flex fixed top-0 left-0 bottom-0 right-0 pointer-events-none"
        [style.z-index]="zIndex"
        (click)="handleOverlayClick()"
    >
        <div
            [class]="snackbarClasses"
            (click)="handleSnackbarClick($event)"
            [ngStyle]="snackbarStyles"
            [@slideInOut]="{
                value: state,
                params: { start: animationStart, end: animationEnd }
            }"
        >
            <div class="flex items-center">
                @if (icon || type !== 'plain') {
                    <div class="flex items-center justify-center p-2">
                        <span [class]="iconClasses"></span>
                    </div>
                }
                <div class="flex flex-col">
                    <div class="font-bold">{{ title }}</div>
                    <div class="text-sm">{{ description }}</div>
                </div>
            </div>
        </div>
    </div>`,
                    animations: [
                        trigger('slideInOut', [
                            state('in', style$1({ transform: '{{ start }}' }), {
                                params: { start: '' }
                            }),
                            state('out', style$1({ transform: '{{ end }}' }), {
                                params: { end: '' }
                            }),
                            transition('in => out', animate('300ms ease-in')),
                            transition('out => in', animate('300ms ease-in'))
                        ])
                    ],
                    imports: [CommonModule]
                }]
        }], propDecorators: { icon: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], type: [{
                type: Input
            }], title: [{
                type: Input
            }], description: [{
                type: Input
            }], duration: [{
                type: Input
            }], position: [{
                type: Input
            }], zIndex: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-explicit-any */
class SnackbarConfig {
    icon;
    type = 'info';
    title;
    description;
    duration = 5000;
    position = 'bottom';
    zIndex;
}

class SnackbarInjector {
    _parentInjector;
    _additionalTokens;
    constructor(_parentInjector, _additionalTokens) {
        this._parentInjector = _parentInjector;
        this._additionalTokens = _additionalTokens;
    }
    get(token, notFoundValue) {
        const value = this._additionalTokens.get(token);
        if (value !== undefined)
            return value;
        return this._parentInjector.get(token, notFoundValue);
    }
}

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
class SnackbarService {
    componentFactoryResolver = inject(ComponentFactoryResolver);
    appRef = inject(ApplicationRef);
    injector = inject(Injector);
    snackbarComponentRef;
    open(config) {
        const snackbarRef = this.appendSnackbarComponentToBody(config);
        return snackbarRef;
    }
    appendSnackbarComponentToBody(config) {
        const map = new WeakMap();
        map.set(SnackbarConfig, config);
        const snackbarRef = new SnackbarRef();
        map.set(SnackbarRef, snackbarRef);
        const sub = snackbarRef.afterClosed.subscribe(() => {
            this.removeSnackbarComponentFromBody();
            sub.unsubscribe();
        });
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(SnackbarComponent);
        const componentRef = componentFactory.create(new SnackbarInjector(this.injector, map));
        for (const key in config) {
            if (config.hasOwnProperty(key)) {
                componentRef.instance[key] = config[key];
            }
        }
        this.appRef.attachView(componentRef.hostView);
        const domElem = componentRef.hostView.rootNodes[0];
        document.body.appendChild(domElem);
        this.snackbarComponentRef = componentRef;
        this.snackbarComponentRef.instance.onClose.subscribe(() => {
            this.removeSnackbarComponentFromBody();
        });
        return snackbarRef;
    }
    removeSnackbarComponentFromBody() {
        if (!this.snackbarComponentRef)
            return;
        this.appRef.detachView(this.snackbarComponentRef.hostView);
        this.snackbarComponentRef.destroy();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SnackbarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SnackbarService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SnackbarService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const menuDividerBaseStyles = ['w-full', 'h-0.5'];
const menuDividerStylesSAG = cva([...menuDividerBaseStyles, 'bg-[#E8E8E3]', 'dark:bg-[#000028]']);
const menuDividerStylesSHS = cva([...menuDividerBaseStyles, 'bg-[#e6e6e6]', 'dark:bg-[#4d4d4d]']);

class MenuDividerComponent {
    theme = inject(THEME_TOKEN);
    get styles() {
        return componentStylesByTheme(this.theme, menuDividerStylesSAG, menuDividerStylesSHS)();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuDividerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.2", type: MenuDividerComponent, isStandalone: true, selector: "sag-ui-menu-item-divider", ngImport: i0, template: ` <div [class]="styles"></div> `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: MenuDividerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-menu-item-divider',
                    standalone: true,
                    template: ` <div [class]="styles"></div> `
                }]
        }] });

const calendarContainerBaseStyles = ['max-w-[400px]', 'shadow-md'];
const calendarContainerSAG = cva([...calendarContainerBaseStyles, 'bg-[#F3F3F0]', 'dark:bg-[#23233C]']);
const calendarContainerSHS = cva([...calendarContainerBaseStyles, 'bg-white', 'dark:bg-[#1a1a1a]']);
const calendarButtonSAG = cva([
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const calendarButtonSHS = cva([
    'text-[#666666]',
    'hover:text-[#1a1a1a]',
    'dark:text-[#999999]',
    'dark:hover:text-white',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
]);
const calendarTopTextBaseStyles = ['text-md', 'font-medium'];
const calendarTopTextSAG = cva([...calendarTopTextBaseStyles, 'text-[#007993]', 'dark:text-[#00CCCC]']);
const calendarTopTextSHS = cva([...calendarTopTextBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);
const calendarDayTextBaseStyles = [
    'text-sm',
    'font-semibold',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'aspect-square',
    'p-2'
];
const calendarDayTextSAG = cva([...calendarDayTextBaseStyles, 'text-[#000028]', 'dark:text-white']);
const calendarDayTextSHS = cva([...calendarDayTextBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);
const calendarDayBoxBaseStyles = [
    'text-sm',
    'cursor-pointer',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'aspect-square',
    'p-2',
    'focus-visible:outline-[#1491EB]',
    'focus-visible:outline-1'
];
const calendarDisabledDayBoxSAG = cva([...calendarDayBoxBaseStyles, 'text-[#66667E]', 'dark:text-[#9999A9]']);
const calendarDisabledDayBoxSHS = cva([...calendarDayBoxBaseStyles, 'text-[#999999]', 'dark:text-[#666666]']);
const calendarDayBoxSAG = cva([
    ...calendarDayBoxBaseStyles,
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'dark:data-[selected=true]:bg-[#00FFB9]',
    'dark:data-[selected=true]:text-[#000028]',
    'dark:data-[selected=true]:font-semibold',
    'dark:data-[range=true]:bg-[#008C78]',
    'dark:data-[range=true]:text-[#000028]',
    'dark:data-[range=true]:font-semibold',
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#196269]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold',
    'data-[range=true]:bg-[#427E84]',
    'data-[range=true]:text-white',
    'data-[range=true]:font-semibold'
]);
const calendarDayBoxSHS = cva([
    ...calendarDayBoxBaseStyles,
    'dark:text-white',
    'dark:hover:bg-[#313131]',
    'dark:data-[selected=true]:bg-[#cf4b00]',
    'dark:data-[selected=true]:text-white',
    'dark:data-[selected=true]:font-semibold',
    'text-[#666666]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#cf4b00]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold'
]);
const calendarMonthBoxBaseStyles = [
    'text-sm',
    'cursor-pointer',
    'select-none',
    'flex',
    'items-center',
    'justify-center',
    'p-4'
];
const calendarMonthBoxSAG = cva([
    ...calendarMonthBoxBaseStyles,
    'dark:text-[#00CCCC]',
    'dark:hover:bg-[#37374D]',
    'dark:data-[selected=true]:bg-[#00FFB9]',
    'dark:data-[selected=true]:text-[#000028]',
    'dark:data-[selected=true]:font-semibold',
    'text-[#007993]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#196269]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold'
]);
const calendarMonthBoxSHS = cva([
    ...calendarMonthBoxBaseStyles,
    'dark:text-white',
    'dark:hover:bg-[#313131]',
    'dark:data-[selected=true]:bg-[#cf4b00]',
    'dark:data-[selected=true]:text-white',
    'dark:data-[selected=true]:font-semibold',
    'text-[#666666]',
    'hover:bg-[#E8E8E3]',
    'data-[selected=true]:bg-[#cf4b00]',
    'data-[selected=true]:text-white',
    'data-[selected=true]:font-semibold'
]);
const calendarTimeInputBaseStyles = [
    'border-none',
    'text-center',
    'text-md',
    'font-semibold',
    'p-2',
    'rounded-md',
    'w-[50px]',
    '[appearance:textfield]',
    '[&::-webkit-outer-spin-button]:appearance-none',
    '[&::-webkit-inner-spin-button]:appearance-none'
];
const calendarTimeInputSAG = cva([
    ...calendarTimeInputBaseStyles,
    'bg-[#E8E8E3]',
    'text-[#000028]',
    'dark:bg-[#37374D]',
    'dark:text-white'
]);
const calendarTimeInputSHS = cva([
    ...calendarTimeInputBaseStyles,
    'bg-white',
    'text-[#666666]',
    'dark:bg-[#1a1a1a]',
    'dark:text-[#999999]'
]);
const calendarTimeDividerBaseStyles = ['text-lg', 'font-bold'];
const calendarTimeDividerSAG = cva([...calendarTimeDividerBaseStyles, 'text-[#000028]', 'dark:text-white']);
const calendarTimeDividerSHS = cva([...calendarTimeDividerBaseStyles, 'text-[#666666]', 'dark:text-[#999999]']);
const calendarDividerSAG = cva(['border-[#E8E8E3]', 'dark:border-[#373750]']);
const calendarDividerSHS = cva(['border-[#666666]', 'dark:border-[#999999]']);

class CalendarRangeComponent {
    theme = inject(THEME_TOKEN);
    elementRef = inject(ElementRef);
    lang = input();
    startDate = input(new Date());
    endDate = input();
    availableRanges = input([]);
    dateRangeChanged = output();
    selectedMonth = this.startDate()?.getMonth() ?? 0;
    selectedYear = this.startDate()?.getFullYear() ?? 0;
    selectedDay = this.startDate()?.getDate() ?? 0;
    selectedHours = this.startDate()?.getHours() ?? 0;
    selectedMinutes = this.startDate()?.getMinutes() ?? 0;
    selectedSeconds = this.startDate()?.getSeconds() ?? 0;
    previousMonthsDates = [];
    dates = [];
    nextMonthsDates = [];
    view = 'days';
    displayedYears = Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + i);
    Math = Math;
    rangeStart = null;
    rangeEnd = null;
    selectionState = 'start';
    ngOnInit() {
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
    }
    getAriaLabelForDay(day) {
        const formattedDate = `${this.localizedMonths[this.selectedMonth]} ${day}, ${this.selectedYear}`;
        if (this.isStart(day)) {
            return `${formattedDate}, Start date of selected range`;
        }
        else if (this.isEnd(day)) {
            return `${formattedDate}, End date of selected range`;
        }
        else if (this.isDayInRange(day)) {
            return `${formattedDate}, Within selected date range`;
        }
        else if (!this.isInAvailableRange(day)) {
            return `${formattedDate}, Unavailable date`;
        }
        return formattedDate;
    }
    ngOnChanges(changes) {
        const startDate = this.startDate();
        if (changes['startDate'] && startDate) {
            this.rangeStart = startDate;
            this.selectedMonth = startDate.getMonth();
            this.selectedYear = startDate.getFullYear();
            this.selectedDay = startDate.getDate();
            this.selectedHours = startDate.getHours();
            this.selectedMinutes = startDate.getMinutes();
            this.selectedSeconds = startDate.getSeconds();
            this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        }
        const endDate = this.endDate();
        if (changes['endDate'] && endDate) {
            this.rangeEnd = endDate;
        }
    }
    isDayInRange(day) {
        if (!this.rangeStart)
            return false;
        const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
        if (this.rangeEnd) {
            return currentDate >= this.rangeStart && currentDate <= this.rangeEnd;
        }
        return currentDate.getTime() === this.rangeStart.getTime();
    }
    isInAvailableRange(day) {
        const availableRanges = this.availableRanges();
        if (availableRanges.length === 0)
            return true;
        const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
        return availableRanges.some(range => currentDate >= range.startDate && currentDate <= range.endDate);
    }
    isStart(day) {
        if (!this.rangeStart)
            return false;
        return (day === this.rangeStart.getDate() &&
            this.selectedMonth === this.rangeStart.getMonth() &&
            this.selectedYear === this.rangeStart.getFullYear());
    }
    isEnd(day) {
        if (!this.rangeEnd)
            return false;
        return (day === this.rangeEnd.getDate() &&
            this.selectedMonth === this.rangeEnd.getMonth() &&
            this.selectedYear === this.rangeEnd.getFullYear());
    }
    isStartOrEndDay(day) {
        return this.isStart(day) || this.isEnd(day);
    }
    get userLanguage() {
        return this.lang() || navigator.language;
    }
    get localizedWeekdays() {
        const isGerman = this.userLanguage.startsWith('de');
        return isGerman
            ? ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
            : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }
    get localizedMonths() {
        const isGerman = this.userLanguage.startsWith('de');
        return isGerman
            ? [
                'Januar',
                'Februar',
                'März',
                'April',
                'Mai',
                'Juni',
                'Juli',
                'August',
                'September',
                'Oktober',
                'November',
                'Dezember'
            ]
            : [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December'
            ];
    }
    dateSelected(day) {
        const selectedDate = new Date(this.selectedYear, this.selectedMonth, day, this.selectedHours, this.selectedMinutes, this.selectedSeconds);
        if (!this.rangeStart || this.rangeEnd) {
            this.rangeStart = selectedDate;
            this.rangeEnd = null;
            this.selectionState = 'end';
            this.announceDateSelection(selectedDate, 'start');
        }
        else if (selectedDate < this.rangeStart) {
            this.rangeEnd = this.rangeStart;
            this.rangeStart = selectedDate;
            this.selectionState = 'start';
            this.announceDateRange();
        }
        else {
            this.rangeEnd = selectedDate;
            this.selectionState = 'start';
            this.announceDateRange();
        }
        this.closeCalendar();
    }
    closeCalendar() {
        this.dateRangeChanged.emit({
            startDate: this.rangeStart,
            endDate: this.rangeEnd
        });
    }
    monthSelected(monthIndex) {
        this.selectedMonth = monthIndex;
        this.view = 'days';
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceMonthChange();
        // Set focus on first day when returning to day view
        setTimeout(() => {
            if (this.dates.length > 0) {
                this.focusElement(`day-${this.dates[0]}`);
            }
        });
    }
    yearSelected(year) {
        this.selectedYear = year;
        this.view = 'months';
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceYearSelection(year);
        // Set focus on current month when returning to month view
        setTimeout(() => {
            this.focusElement(`month-${this.selectedMonth}`);
        });
    }
    changeView(view) {
        this.view = view;
        // Set initial focus when changing views
        setTimeout(() => {
            if (view === 'months') {
                this.focusElement(`month-${this.selectedMonth}`);
            }
            else if (view === 'years') {
                const yearIndex = this.displayedYears.indexOf(this.selectedYear);
                if (yearIndex >= 0) {
                    this.focusElement(`year-${this.selectedYear}`);
                }
                else {
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            }
        });
    }
    previousYears() {
        this.displayedYears = this.displayedYears.map(year => year - 12);
        this.announceYearRange();
    }
    nextYears() {
        this.displayedYears = this.displayedYears.map(year => year + 12);
        this.announceYearRange();
    }
    previousMonth() {
        this.selectedMonth--;
        if (this.selectedMonth < 0) {
            this.selectedMonth = 11;
            this.selectedYear--;
        }
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceMonthChange();
    }
    nextMonth() {
        this.selectedMonth++;
        if (this.selectedMonth > 11) {
            this.selectedMonth = 0;
            this.selectedYear++;
        }
        this.updateDisplayedDates(this.selectedYear, this.selectedMonth);
        this.announceMonthChange();
    }
    updateDisplayedDates(year, month) {
        const daysInWeek = 7;
        const weeksInGrid = 5;
        const totalDays = daysInWeek * weeksInGrid;
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);
        const firstDayOfWeek = firstDayOfMonth.getDay() || 7; // Adjusting for Monday as first day
        const daysInMonth = lastDayOfMonth.getDate();
        this.previousMonthsDates = [];
        this.dates = [];
        this.nextMonthsDates = [];
        // Fill previous month days
        for (let i = firstDayOfWeek - 1; i > 0; i--) {
            const day = new Date(year, month, 1 - i).getDate();
            this.previousMonthsDates.push(day);
        }
        // Fill current month days
        for (let i = 1; i <= daysInMonth; i++) {
            this.dates.push(i);
        }
        // Fill next month days
        let nextMonthDay = 1;
        while (this.dates.length + this.previousMonthsDates.length + this.nextMonthsDates.length < totalDays) {
            this.nextMonthsDates.push(nextMonthDay++);
        }
    }
    handleMonthKeyDown(event, monthIndex) {
        const monthsPerRow = 4;
        const totalMonths = 12;
        const currentCol = monthIndex % monthsPerRow;
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToMonth(this.calculateNextMonth(monthIndex, totalMonths));
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToMonth(this.calculatePreviousMonth(monthIndex, totalMonths));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToMonth(this.calculatePreviousRowMonth(monthIndex, monthsPerRow, currentCol));
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToMonth(this.calculateNextRowMonth(monthIndex, monthsPerRow, totalMonths, currentCol));
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.monthSelected(monthIndex);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    handleYearKeyDown(event, year) {
        const yearIndex = this.displayedYears.indexOf(year);
        const yearsPerRow = 4;
        const totalYears = this.displayedYears.length;
        const currentCol = yearIndex % yearsPerRow;
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToNextYear(yearIndex, totalYears);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToPreviousYear(yearIndex);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToYearAbove(yearIndex, yearsPerRow, currentCol);
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToYearBelow(yearIndex, yearsPerRow, totalYears, currentCol);
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.yearSelected(year);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    handleDayKeyDown(event, day) {
        const currentDate = new Date(this.selectedYear, this.selectedMonth, day);
        switch (event.key) {
            case 'ArrowRight':
                event.preventDefault();
                this.navigateToDate(this.calculateNextDay(currentDate));
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.navigateToDate(this.calculatePreviousDay(currentDate));
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.navigateToDate(this.calculatePreviousWeekDate(currentDate));
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.navigateToDate(this.calculateNextWeekDate(currentDate));
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.dateSelected(day);
                break;
            case 'Escape':
                event.preventDefault();
                this.closeCalendar();
                break;
        }
    }
    get containerStyles() {
        return componentStylesByTheme(this.theme, calendarContainerSAG, calendarContainerSHS)();
    }
    get pickerButtonStyles() {
        return componentStylesByTheme(this.theme, calendarButtonSAG, calendarButtonSHS)();
    }
    get topTextStyles() {
        return componentStylesByTheme(this.theme, calendarTopTextSAG, calendarTopTextSHS)();
    }
    get dayTextStyles() {
        return componentStylesByTheme(this.theme, calendarDayTextSAG, calendarDayTextSHS)();
    }
    trackByIndex(index) {
        return index;
    }
    get dayBoxDisabledStyles() {
        return componentStylesByTheme(this.theme, calendarDisabledDayBoxSAG, calendarDisabledDayBoxSHS)();
    }
    get dayBoxStyles() {
        return componentStylesByTheme(this.theme, calendarDayBoxSAG, calendarDayBoxSHS)();
    }
    get monthBoxStyles() {
        return componentStylesByTheme(this.theme, calendarMonthBoxSAG, calendarMonthBoxSHS)();
    }
    get dividerStyles() {
        return componentStylesByTheme(this.theme, calendarDividerSAG, calendarDividerSHS)();
    }
    // Helper methods for date calculations
    calculateNextDay(date) {
        const nextDate = new Date(date);
        nextDate.setDate(date.getDate() + 1);
        return nextDate;
    }
    calculatePreviousDay(date) {
        const prevDate = new Date(date);
        prevDate.setDate(date.getDate() - 1);
        return prevDate;
    }
    calculateNextWeekDate(date) {
        const nextWeekDate = new Date(date);
        nextWeekDate.setDate(date.getDate() + 7);
        return nextWeekDate;
    }
    calculatePreviousWeekDate(date) {
        const prevWeekDate = new Date(date);
        prevWeekDate.setDate(date.getDate() - 7);
        return prevWeekDate;
    }
    // Helper methods for month calculations
    calculateNextMonth(monthIndex, totalMonths) {
        return monthIndex < totalMonths - 1 ? monthIndex + 1 : 0;
    }
    calculatePreviousMonth(monthIndex, totalMonths) {
        return monthIndex > 0 ? monthIndex - 1 : totalMonths - 1;
    }
    calculatePreviousRowMonth(monthIndex, monthsPerRow, currentCol) {
        if (monthIndex - monthsPerRow >= 0) {
            return monthIndex - monthsPerRow;
        }
        else {
            // Wrap from top row to bottom row, same column
            return 2 * monthsPerRow + currentCol; // Last row, same column
        }
    }
    calculateNextRowMonth(monthIndex, monthsPerRow, totalMonths, currentCol) {
        if (monthIndex + monthsPerRow < totalMonths) {
            return monthIndex + monthsPerRow;
        }
        else {
            // Wrap from bottom row to top row, same column
            return currentCol; // First row, same column
        }
    }
    navigateToMonth(monthIndex) {
        this.focusElement(`month-${monthIndex}`);
    }
    // Helper methods for year calculations
    navigateToNextYear(yearIndex, totalYears) {
        if (yearIndex < totalYears - 1) {
            // Move to next year in current view
            this.focusElement(`year-${this.displayedYears[yearIndex + 1]}`);
        }
        else {
            // Move to next set of years
            this.nextYears();
            setTimeout(() => {
                // Focus first year in new view
                if (this.displayedYears.length > 0) {
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            });
        }
    }
    navigateToPreviousYear(yearIndex) {
        if (yearIndex > 0) {
            // Move to previous year in current view
            this.focusElement(`year-${this.displayedYears[yearIndex - 1]}`);
        }
        else {
            // Move to previous set of years
            this.previousYears();
            setTimeout(() => {
                // Focus last year in new view
                if (this.displayedYears.length > 0) {
                    this.focusElement(`year-${this.displayedYears[this.displayedYears.length - 1]}`);
                }
            });
        }
    }
    navigateToYearAbove(yearIndex, yearsPerRow, currentCol) {
        if (yearIndex - yearsPerRow >= 0) {
            // Move up within current view
            this.focusElement(`year-${this.displayedYears[yearIndex - yearsPerRow]}`);
        }
        else {
            // Move to previous set of years
            this.previousYears();
            setTimeout(() => {
                // Try to focus same position in previous view
                const targetIndex = this.displayedYears.length - yearsPerRow + currentCol;
                if (targetIndex >= 0 && targetIndex < this.displayedYears.length) {
                    this.focusElement(`year-${this.displayedYears[targetIndex]}`);
                }
                else {
                    // Fallback to last year in view
                    this.focusElement(`year-${this.displayedYears[this.displayedYears.length - 1]}`);
                }
            });
        }
    }
    navigateToYearBelow(yearIndex, yearsPerRow, totalYears, currentCol) {
        if (yearIndex + yearsPerRow < totalYears) {
            // Move down within current view
            this.focusElement(`year-${this.displayedYears[yearIndex + yearsPerRow]}`);
        }
        else {
            // Move to next set of years
            this.nextYears();
            setTimeout(() => {
                // Try to focus same position in next view
                const targetIndex = currentCol;
                if (targetIndex < this.displayedYears.length) {
                    this.focusElement(`year-${this.displayedYears[targetIndex]}`);
                }
                else {
                    // Fallback to first year in view
                    this.focusElement(`year-${this.displayedYears[0]}`);
                }
            });
        }
    }
    // Add this helper method to navigate to a specific date
    navigateToDate(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        // Check if we need to change the month/year
        if (month !== this.selectedMonth || year !== this.selectedYear) {
            this.selectedMonth = month;
            this.selectedYear = year;
            this.updateDisplayedDates(year, month);
            // Wait for the view to update
            setTimeout(() => {
                // Find and focus the target day
                this.focusElement(`day-${day}`);
            });
        }
        else {
            // Same month, just focus the day
            this.focusElement(`day-${day}`);
        }
    }
    // Helper method to focus elements
    focusElement(id) {
        setTimeout(() => {
            const element = this.elementRef.nativeElement.querySelector(`#${id}`);
            if (element) {
                element.focus();
            }
        });
    }
    announceMonthChange() {
        // Update the live region content to trigger an announcement
        const announcement = `${this.localizedMonths[this.selectedMonth]} ${this.selectedYear}`;
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#range-announcement');
            if (liveRegion) {
                // Force a DOM change to trigger screen reader announcement
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    announceDateSelection(date, type) {
        // Format the date in a screen reader friendly way
        const formattedDate = `${date.getDate()} ${this.localizedMonths[date.getMonth()]} ${date.getFullYear()}`;
        const announcement = type === 'start'
            ? `Start date selected: ${formattedDate}. Now select end date.`
            : `End date selected: ${formattedDate}`;
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#range-announcement');
            if (liveRegion) {
                // Force a DOM change to trigger screen reader announcement
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    announceDateRange() {
        if (!this.rangeStart || !this.rangeEnd)
            return;
        // Format the date range in a screen reader friendly way
        const startDate = `${this.rangeStart.getDate()} ${this.localizedMonths[this.rangeStart.getMonth()]} ${this.rangeStart.getFullYear()}`;
        const endDate = `${this.rangeEnd.getDate()} ${this.localizedMonths[this.rangeEnd.getMonth()]} ${this.rangeEnd.getFullYear()}`;
        const announcement = `Date range selected: from ${startDate} to ${endDate}`;
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#range-announcement');
            if (liveRegion) {
                // Force a DOM change to trigger screen reader announcement
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    announceYearRange() {
        if (this.displayedYears.length > 0) {
            const firstYear = this.displayedYears[0];
            const lastYear = this.displayedYears[this.displayedYears.length - 1];
            const announcement = `Years ${firstYear} to ${lastYear}`;
            setTimeout(() => {
                const liveRegion = this.elementRef.nativeElement.querySelector('#range-announcement');
                if (liveRegion) {
                    liveRegion.textContent = '';
                    liveRegion.textContent = announcement;
                }
            });
        }
    }
    announceYearSelection(year) {
        const announcement = `Year ${year} selected. Now select a month.`;
        setTimeout(() => {
            const liveRegion = this.elementRef.nativeElement.querySelector('#range-announcement');
            if (liveRegion) {
                liveRegion.textContent = '';
                liveRegion.textContent = announcement;
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CalendarRangeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: CalendarRangeComponent, isStandalone: true, selector: "sag-ui-calendar-range", inputs: { lang: { classPropertyName: "lang", publicName: "lang", isSignal: true, isRequired: false, transformFunction: null }, startDate: { classPropertyName: "startDate", publicName: "startDate", isSignal: true, isRequired: false, transformFunction: null }, endDate: { classPropertyName: "endDate", publicName: "endDate", isSignal: true, isRequired: false, transformFunction: null }, availableRanges: { classPropertyName: "availableRanges", publicName: "availableRanges", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { dateRangeChanged: "dateRangeChanged" }, usesOnChanges: true, ngImport: i0, template: "<div [class]=\"containerStyles\"\n     role=\"dialog\"\n     aria-label=\"Date range picker\"\n     (keydown.escape)=\"closeCalendar()\">\n  <div class=\"flex items-center justify-between p-6\">\n    <div class=\"sr-only\" aria-live=\"polite\" [attr.aria-atomic]=\"true\" id=\"range-announcement\">\n      {{ localizedMonths[selectedMonth] }} {{ selectedYear }}\n    </div>\n    <span\n      [class]=\"\n        pickerButtonStyles +\n        ' icon-chevron-left rounded-full cursor-pointer p-2'\n      \"\n      tabindex=\"0\"\n      role=\"button\"\n      aria-label=\"Previous month\"\n      (keydown.enter)=\"$event.preventDefault(); previousMonth()\"\n      (keydown.space)=\"$event.preventDefault(); previousMonth()\"\n      (click)=\"previousMonth()\"\n    ></span>\n    <span [class]=\"topTextStyles\">\n      <span (click)=\"changeView('months')\" class=\"mr-1 cursor-pointer\" tabindex=\"0\"\n            role=\"button\"\n            aria-label=\"Select month\"\n            (keydown.enter)=\"$event.preventDefault(); changeView('months')\"\n            (keydown.space)=\"$event.preventDefault(); changeView('months')\">{{\n          localizedMonths[selectedMonth]\n        }}</span>\n      <span (click)=\"changeView('years')\" class=\"cursor-pointer\" tabindex=\"0\" role=\"button\"\n            aria-label=\"Select year\"\n            (keydown.enter)=\"$event.preventDefault(); changeView('years')\"\n            (keydown.space)=\"$event.preventDefault(); changeView('years')\">{{\n          selectedYear\n        }}</span></span\n    >\n    <span\n      [class]=\"\n        pickerButtonStyles +\n        ' icon-chevron-right rounded-full cursor-pointer p-2'\n      \"\n      role=\"button\"\n      aria-label=\"Next month\"\n      tabindex=\"0\"\n      (keydown.enter)=\"$event.preventDefault(); nextMonth()\"\n      (keydown.space)=\"$event.preventDefault(); nextMonth()\"\n      (click)=\"nextMonth()\"\n    ></span>\n  </div>\n  <hr [class]=\"dividerStyles\" />\n  @switch (view) {\n    @case ('days') {\n      <div\n        class=\"grid grid-cols-7 grid-rows-6 items-center justify-center gap-y-2 p-6\"\n        role=\"grid\"\n        aria-rowcount=\"6\"\n        aria-colcount=\"7\"\n        aria-label=\"Calendar days\"\n      >\n        @for (day of localizedWeekdays; track day) {\n          <div [class]=\"dayTextStyles\" role=\"columnheader\">\n            {{ day }}\n          </div>\n        }\n        @for (day of previousMonthsDates; track day) {\n          <div\n            [class]=\"dayBoxDisabledStyles\"\n            aria-disabled=\"true\"\n          >\n            {{ day }}\n          </div>\n        }\n        @for (day of dates; track trackByIndex(i); let i = $index) {\n          @if (isInAvailableRange(day)) {\n            <div\n              [class]=\"dayBoxStyles\"\n              [attr.data-range]=\"isDayInRange(day) && !isStartOrEndDay(day)\"\n              [attr.data-selected]=\"isStartOrEndDay(day)\"\n              [id]=\"'day-' + day\"\n              tabindex=\"0\"\n              role=\"gridcell\"\n              [attr.aria-selected]=\"isDayInRange(day)\"\n              [attr.aria-label]=\"getAriaLabelForDay(day)\"\n              (keydown)=\"handleDayKeyDown($event, day)\"\n              (click)=\"dateSelected(day)\"\n            >\n              {{ day }}\n            </div>\n          }\n          @if (!isInAvailableRange(day)) {\n            <div\n              [class]=\"dayBoxDisabledStyles\"\n              aria-disabled=\"true\"\n              [attr.aria-label]=\"localizedMonths[selectedMonth] + ' ' + day + ', ' + selectedYear + ', Unavailable date'\"\n            >\n              {{ day }}\n            </div>\n          }\n        }\n        @for (day of nextMonthsDates; track day) {\n          <div [class]=\"dayBoxDisabledStyles\" aria-disabled=\"true\">\n            {{ day }}\n          </div>\n        }\n      </div>\n    }\n    @case ('months') {\n      <div\n        class=\"grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 p-6\"\n        role=\"region\"\n        aria-label=\"Month selection table with 3 rows and 4 columns\"\n      >\n        @for (month of localizedMonths; track month; let i = $index) {\n          <div\n            [class]=\"monthBoxStyles\"\n            [attr.data-selected]=\"selectedMonth === i\"\n            [id]=\"'month-' + i\"\n            tabindex=\"0\"\n            role=\"button\"\n            [attr.aria-selected]=\"selectedMonth === i\"\n            [attr.aria-label]=\"month\"\n            (keydown)=\"handleMonthKeyDown($event, i)\"\n            (click)=\"monthSelected(i)\"\n          >\n            {{ month }}\n          </div>\n        }\n      </div>\n    }\n    @case ('years') {\n      <div class=\"p-6 py-2\">\n          <span\n            [class]=\"pickerButtonStyles + ' icon-chevron-up rounded-md flex items-center justify-center w-full cursor-pointer p-2'\"\n            tabindex=\"0\"\n            role=\"button\"\n            [attr.aria-label]=\"'Show earlier years: ' + (displayedYears[0] - 12) + ' to ' + (displayedYears[0] - 1)\"\n            (keydown.enter)=\"$event.preventDefault(); previousYears()\"\n            (keydown.space)=\"$event.preventDefault(); previousYears()\"\n            (click)=\"previousYears()\"\n          ></span>\n        <div\n          class=\"grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 py-2\"\n          role=\"region\"\n          aria-label=\"Year selection table with 3 rows and 4 columns\"\n        >\n          @for (year of displayedYears; track year) {\n            <div\n              [class]=\"monthBoxStyles\"\n              [attr.data-selected]=\"selectedYear === year\"\n              [id]=\"'year-' + year\"\n              tabindex=\"0\"\n              role=\"button\"\n              [attr.aria-selected]=\"selectedYear === year\"\n              [attr.aria-label]=\"year.toString()\"\n              (keydown)=\"handleYearKeyDown($event, year)\"\n              (click)=\"yearSelected(year)\"\n            >\n              {{ year }}\n            </div>\n          }\n        </div>\n        <span\n          [class]=\"pickerButtonStyles + ' icon-chevron-down rounded-md flex items-center justify-center w-full cursor-pointer p-2'\"\n          tabindex=\"0\"\n          role=\"button\"\n          [attr.aria-label]=\"'Show later years: ' + (displayedYears[displayedYears.length - 1] + 1) + ' to ' + (displayedYears[displayedYears.length - 1] + 12)\"\n          (keydown.enter)=\"$event.preventDefault(); nextYears()\"\n          (keydown.space)=\"$event.preventDefault(); nextYears()\"\n          (click)=\"nextYears()\"\n        ></span>\n      </div>\n    }\n  }\n</div>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: CalendarRangeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-calendar-range', standalone: true, imports: [], template: "<div [class]=\"containerStyles\"\n     role=\"dialog\"\n     aria-label=\"Date range picker\"\n     (keydown.escape)=\"closeCalendar()\">\n  <div class=\"flex items-center justify-between p-6\">\n    <div class=\"sr-only\" aria-live=\"polite\" [attr.aria-atomic]=\"true\" id=\"range-announcement\">\n      {{ localizedMonths[selectedMonth] }} {{ selectedYear }}\n    </div>\n    <span\n      [class]=\"\n        pickerButtonStyles +\n        ' icon-chevron-left rounded-full cursor-pointer p-2'\n      \"\n      tabindex=\"0\"\n      role=\"button\"\n      aria-label=\"Previous month\"\n      (keydown.enter)=\"$event.preventDefault(); previousMonth()\"\n      (keydown.space)=\"$event.preventDefault(); previousMonth()\"\n      (click)=\"previousMonth()\"\n    ></span>\n    <span [class]=\"topTextStyles\">\n      <span (click)=\"changeView('months')\" class=\"mr-1 cursor-pointer\" tabindex=\"0\"\n            role=\"button\"\n            aria-label=\"Select month\"\n            (keydown.enter)=\"$event.preventDefault(); changeView('months')\"\n            (keydown.space)=\"$event.preventDefault(); changeView('months')\">{{\n          localizedMonths[selectedMonth]\n        }}</span>\n      <span (click)=\"changeView('years')\" class=\"cursor-pointer\" tabindex=\"0\" role=\"button\"\n            aria-label=\"Select year\"\n            (keydown.enter)=\"$event.preventDefault(); changeView('years')\"\n            (keydown.space)=\"$event.preventDefault(); changeView('years')\">{{\n          selectedYear\n        }}</span></span\n    >\n    <span\n      [class]=\"\n        pickerButtonStyles +\n        ' icon-chevron-right rounded-full cursor-pointer p-2'\n      \"\n      role=\"button\"\n      aria-label=\"Next month\"\n      tabindex=\"0\"\n      (keydown.enter)=\"$event.preventDefault(); nextMonth()\"\n      (keydown.space)=\"$event.preventDefault(); nextMonth()\"\n      (click)=\"nextMonth()\"\n    ></span>\n  </div>\n  <hr [class]=\"dividerStyles\" />\n  @switch (view) {\n    @case ('days') {\n      <div\n        class=\"grid grid-cols-7 grid-rows-6 items-center justify-center gap-y-2 p-6\"\n        role=\"grid\"\n        aria-rowcount=\"6\"\n        aria-colcount=\"7\"\n        aria-label=\"Calendar days\"\n      >\n        @for (day of localizedWeekdays; track day) {\n          <div [class]=\"dayTextStyles\" role=\"columnheader\">\n            {{ day }}\n          </div>\n        }\n        @for (day of previousMonthsDates; track day) {\n          <div\n            [class]=\"dayBoxDisabledStyles\"\n            aria-disabled=\"true\"\n          >\n            {{ day }}\n          </div>\n        }\n        @for (day of dates; track trackByIndex(i); let i = $index) {\n          @if (isInAvailableRange(day)) {\n            <div\n              [class]=\"dayBoxStyles\"\n              [attr.data-range]=\"isDayInRange(day) && !isStartOrEndDay(day)\"\n              [attr.data-selected]=\"isStartOrEndDay(day)\"\n              [id]=\"'day-' + day\"\n              tabindex=\"0\"\n              role=\"gridcell\"\n              [attr.aria-selected]=\"isDayInRange(day)\"\n              [attr.aria-label]=\"getAriaLabelForDay(day)\"\n              (keydown)=\"handleDayKeyDown($event, day)\"\n              (click)=\"dateSelected(day)\"\n            >\n              {{ day }}\n            </div>\n          }\n          @if (!isInAvailableRange(day)) {\n            <div\n              [class]=\"dayBoxDisabledStyles\"\n              aria-disabled=\"true\"\n              [attr.aria-label]=\"localizedMonths[selectedMonth] + ' ' + day + ', ' + selectedYear + ', Unavailable date'\"\n            >\n              {{ day }}\n            </div>\n          }\n        }\n        @for (day of nextMonthsDates; track day) {\n          <div [class]=\"dayBoxDisabledStyles\" aria-disabled=\"true\">\n            {{ day }}\n          </div>\n        }\n      </div>\n    }\n    @case ('months') {\n      <div\n        class=\"grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 p-6\"\n        role=\"region\"\n        aria-label=\"Month selection table with 3 rows and 4 columns\"\n      >\n        @for (month of localizedMonths; track month; let i = $index) {\n          <div\n            [class]=\"monthBoxStyles\"\n            [attr.data-selected]=\"selectedMonth === i\"\n            [id]=\"'month-' + i\"\n            tabindex=\"0\"\n            role=\"button\"\n            [attr.aria-selected]=\"selectedMonth === i\"\n            [attr.aria-label]=\"month\"\n            (keydown)=\"handleMonthKeyDown($event, i)\"\n            (click)=\"monthSelected(i)\"\n          >\n            {{ month }}\n          </div>\n        }\n      </div>\n    }\n    @case ('years') {\n      <div class=\"p-6 py-2\">\n          <span\n            [class]=\"pickerButtonStyles + ' icon-chevron-up rounded-md flex items-center justify-center w-full cursor-pointer p-2'\"\n            tabindex=\"0\"\n            role=\"button\"\n            [attr.aria-label]=\"'Show earlier years: ' + (displayedYears[0] - 12) + ' to ' + (displayedYears[0] - 1)\"\n            (keydown.enter)=\"$event.preventDefault(); previousYears()\"\n            (keydown.space)=\"$event.preventDefault(); previousYears()\"\n            (click)=\"previousYears()\"\n          ></span>\n        <div\n          class=\"grid grid-cols-4 grid-rows-3 items-center justify-center gap-2 py-2\"\n          role=\"region\"\n          aria-label=\"Year selection table with 3 rows and 4 columns\"\n        >\n          @for (year of displayedYears; track year) {\n            <div\n              [class]=\"monthBoxStyles\"\n              [attr.data-selected]=\"selectedYear === year\"\n              [id]=\"'year-' + year\"\n              tabindex=\"0\"\n              role=\"button\"\n              [attr.aria-selected]=\"selectedYear === year\"\n              [attr.aria-label]=\"year.toString()\"\n              (keydown)=\"handleYearKeyDown($event, year)\"\n              (click)=\"yearSelected(year)\"\n            >\n              {{ year }}\n            </div>\n          }\n        </div>\n        <span\n          [class]=\"pickerButtonStyles + ' icon-chevron-down rounded-md flex items-center justify-center w-full cursor-pointer p-2'\"\n          tabindex=\"0\"\n          role=\"button\"\n          [attr.aria-label]=\"'Show later years: ' + (displayedYears[displayedYears.length - 1] + 1) + ' to ' + (displayedYears[displayedYears.length - 1] + 12)\"\n          (keydown.enter)=\"$event.preventDefault(); nextYears()\"\n          (keydown.space)=\"$event.preventDefault(); nextYears()\"\n          (click)=\"nextYears()\"\n        ></span>\n      </div>\n    }\n  }\n</div>\n" }]
        }] });

/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
class DateRangePickerComponent {
    el = inject(ElementRef);
    id = input(this.randomId());
    disabled = input(false);
    required = input(false);
    invalid = input(null);
    readOnly = input(null);
    error = input('');
    placeholder = input('');
    label = input('');
    lang = input();
    availableRanges = input([]);
    hint = input(null);
    floatLabel = input(false);
    formGroup = input(null);
    formControlNameStart = input(null);
    formControlNameEnd = input(null);
    // Accessibility inputs
    ariaLabel = input(null);
    ariaLabelledby = input(null);
    ariaDescribedby = input(null);
    focus = output();
    blur = output();
    click = output();
    // IDs for accessibility
    inputId = '';
    labelId = '';
    errorId = '';
    rangeAnnouncement = '';
    onMouseDown(event) {
        const clickedInside = this.el.nativeElement.contains(event.target);
        if (!clickedInside) {
            this.showDatePicker = false;
        }
    }
    onFocusOut(event) {
        if (!event.relatedTarget)
            return;
        // Check if the new focus target is outside our component
        if (!this.el.nativeElement.contains(event.relatedTarget)) {
            this.showDatePicker = false;
        }
    }
    displayValueStart = '';
    displayValueEnd = '';
    destroy$ = new Subject();
    showDatePicker = false;
    focused = false;
    touched = false;
    constructor() {
        // Generate consistent IDs for accessibility
        this.inputId = `date-range-input-${this.id()}`;
        this.labelId = `date-range-label-${this.id()}`;
        this.errorId = `date-range-error-${this.id()}`;
    }
    ngOnInit() {
        if (!this.formGroup() || !this.formControlNameStart() || !this.formControlNameEnd()) {
            throw new Error('Please provide a formGroup, formControlNameStart and formControlNameEnd to use the date range picker.');
        }
        this.displayInitialValue();
        this.initFormControlsListeners();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    get displayValue() {
        if (this.displayValueStart && this.displayValueEnd) {
            return `${this.displayValueStart} - ${this.displayValueEnd}`;
        }
        return this.displayValueStart || this.displayValueEnd;
    }
    onClick(event) {
        event.stopPropagation();
        this.click.emit(event);
        this.toggleDatePicker();
    }
    onFocus(event) {
        event.stopPropagation();
        this.focus.emit(event);
        this.focused = true;
    }
    onBlur(event) {
        event.stopPropagation();
        this.focused = false;
        this.touched = true;
        this.blur.emit(event);
    }
    onKeyDown(event) {
        switch (event.key) {
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.toggleDatePicker();
                break;
            case 'Escape':
                if (this.showDatePicker) {
                    event.preventDefault();
                    this.showDatePicker = false;
                }
                break;
            case 'Tab':
                // Allow normal tab behavior
                if (this.showDatePicker) {
                    // Don't close the picker on tab, let the browser handle focus movement
                    // The focusout handler will close it if focus moves outside the component
                }
                break;
            case 'ArrowDown':
                if (!this.showDatePicker) {
                    event.preventDefault();
                    this.showDatePicker = true;
                    // Focus will be moved to the calendar in the next tick
                    setTimeout(() => {
                        this.focusFirstCalendarElement();
                    });
                }
                break;
        }
    }
    toggleDatePicker() {
        this.showDatePicker = !this.showDatePicker;
        if (this.showDatePicker) {
            // Announce that the calendar is open
            this.announceCalendarOpen();
            // Focus the first interactive element in the calendar
            setTimeout(() => {
                this.focusFirstCalendarElement();
            });
        }
    }
    focusFirstCalendarElement() {
        // Find the first focusable element in the calendar
        const calendar = this.el.nativeElement.querySelector('sag-ui-calendar-range');
        if (calendar) {
            const firstFocusable = calendar.querySelector('button, [tabindex="0"]');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        }
    }
    blockChange(event) {
        if (event.target && event.target['value']) {
            event.target['value'] = this.displayValue;
        }
    }
    isArray(value) {
        return Array.isArray(value);
    }
    handleDateRangeChange(range) {
        const formGroup = this.formGroup();
        const formControlNameStart = this.formControlNameStart();
        if (range.startDate && formGroup && formControlNameStart) {
            this.displayValueStart = range.startDate.toLocaleDateString();
            formGroup.get(formControlNameStart).setValue(range.startDate);
        }
        else {
            this.displayValueStart = '';
            formGroup.get(formControlNameStart).setValue(null);
        }
        const formControlNameEnd = this.formControlNameEnd();
        if (range.endDate && formGroup && formControlNameEnd) {
            this.displayValueEnd = range.endDate.toLocaleDateString();
            formGroup.get(formControlNameEnd).setValue(range.endDate);
        }
        else {
            this.displayValueEnd = '';
            formGroup.get(formControlNameEnd).setValue(null);
        }
        this.updateRangeAnnouncement();
        this.touched = true;
        // Only close the calendar when both dates are selected
        if (range.startDate && range.endDate) {
            setTimeout(() => {
                this.showDatePicker = false;
                // Focus back on the input
                const input = this.el.nativeElement.querySelector(`#${this.inputId}`);
                if (input) {
                    input.focus();
                }
            }, 500);
        }
        else if (range.startDate && !range.endDate) {
            // If only start date is selected, make an announcement for screen readers
            this.announceStartDateSelected(range.startDate);
            // Make sure the calendar stays open for end date selection
            this.showDatePicker = true;
        }
    }
    getAriaDescribedby() {
        // Combine user-provided describedby with error ID when there's an error
        const parts = [];
        if (this.ariaDescribedby()) {
            parts.push(this.ariaDescribedby());
        }
        if (this.error()) {
            parts.push(this.errorId);
        }
        return parts.length > 0 ? parts.join(' ') : null;
    }
    updateRangeAnnouncement() {
        if (this.displayValueStart && this.displayValueEnd) {
            this.rangeAnnouncement = `Date range selected: from ${this.displayValueStart} to ${this.displayValueEnd}`;
        }
        else if (this.displayValueStart) {
            this.rangeAnnouncement = `Start date selected: ${this.displayValueStart}. Please select an end date.`;
        }
        else if (this.displayValueEnd) {
            this.rangeAnnouncement = `End date selected: ${this.displayValueEnd}. Please select a start date.`;
        }
        else {
            this.rangeAnnouncement = 'No date range selected.';
        }
    }
    announceCalendarOpen() {
        this.rangeAnnouncement = 'Date range picker opened. Use arrow keys to navigate dates, Enter to select a date.';
        // Force screen readers to announce the change
        setTimeout(() => {
            const liveRegion = this.el.nativeElement.querySelector(`#date-range-announcement-${this.id()}`);
            if (liveRegion) {
                liveRegion.textContent = '';
                liveRegion.textContent = this.rangeAnnouncement;
            }
        });
    }
    initFormControlsListeners() {
        this.formGroup()
            .get(this.formControlNameStart())
            .valueChanges.pipe(takeUntil(this.destroy$), tap(value => {
            this.displayValueStart = value ? value.toLocaleDateString() : '';
            this.updateRangeAnnouncement();
        }))
            .subscribe();
        this.formGroup()
            .get(this.formControlNameEnd())
            .valueChanges.pipe(takeUntil(this.destroy$), tap(value => {
            this.displayValueEnd = value ? value.toLocaleDateString() : '';
            this.updateRangeAnnouncement();
        }))
            .subscribe();
    }
    displayInitialValue() {
        if (this.formGroup() && this.formControlNameStart()) {
            this.displayValueStart = this.formGroup().get(this.formControlNameStart()).value
                ? this.formGroup().get(this.formControlNameStart()).value.toLocaleDateString()
                : '';
        }
        if (this.formGroup() && this.formControlNameEnd()) {
            this.displayValueEnd = this.formGroup().get(this.formControlNameEnd()).value
                ? this.formGroup().get(this.formControlNameEnd()).value.toLocaleDateString()
                : '';
        }
        this.updateRangeAnnouncement();
    }
    get startDate() {
        return this.formGroup().get(this.formControlNameStart()).value;
    }
    get endDate() {
        return this.formGroup().get(this.formControlNameEnd()).value;
    }
    randomId() {
        return Math.random().toString(36).substring(2);
    }
    announceStartDateSelected(date) {
        const formattedDate = date.toLocaleDateString();
        this.rangeAnnouncement = `Start date selected: ${formattedDate}. Please select an end date.`;
        // Force screen readers to announce the change
        setTimeout(() => {
            const liveRegion = this.el.nativeElement.querySelector(`#date-range-announcement-${this.id()}`);
            if (liveRegion) {
                liveRegion.textContent = '';
                liveRegion.textContent = this.rangeAnnouncement;
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DateRangePickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: DateRangePickerComponent, isStandalone: true, selector: "sag-ui-date-range-picker", inputs: { id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, required: { classPropertyName: "required", publicName: "required", isSignal: true, isRequired: false, transformFunction: null }, invalid: { classPropertyName: "invalid", publicName: "invalid", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, error: { classPropertyName: "error", publicName: "error", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, lang: { classPropertyName: "lang", publicName: "lang", isSignal: true, isRequired: false, transformFunction: null }, availableRanges: { classPropertyName: "availableRanges", publicName: "availableRanges", isSignal: true, isRequired: false, transformFunction: null }, hint: { classPropertyName: "hint", publicName: "hint", isSignal: true, isRequired: false, transformFunction: null }, floatLabel: { classPropertyName: "floatLabel", publicName: "floatLabel", isSignal: true, isRequired: false, transformFunction: null }, formGroup: { classPropertyName: "formGroup", publicName: "formGroup", isSignal: true, isRequired: false, transformFunction: null }, formControlNameStart: { classPropertyName: "formControlNameStart", publicName: "formControlNameStart", isSignal: true, isRequired: false, transformFunction: null }, formControlNameEnd: { classPropertyName: "formControlNameEnd", publicName: "formControlNameEnd", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaLabelledby: { classPropertyName: "ariaLabelledby", publicName: "ariaLabelledby", isSignal: true, isRequired: false, transformFunction: null }, ariaDescribedby: { classPropertyName: "ariaDescribedby", publicName: "ariaDescribedby", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { focus: "focus", blur: "blur", click: "click" }, host: { listeners: { "document:mousedown": "onMouseDown($event)", "focusout": "onFocusOut($event)" } }, ngImport: i0, template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="inputId"
                    type="text"
                    [disabled]="disabled()"
                    [placeholder]="floatLabel() ? '' : placeholder()"
                    [readOnly]="readOnly()"
                    [required]="invalid() || (required() && touched)"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [attr.aria-label]="ariaLabel() || label() + (required() ? ' required' : '')"
                    [attr.aria-labelledby]="ariaLabelledby() || labelId"
                    [attr.aria-describedby]="getAriaDescribedby()"
                    [attr.aria-required]="required()"
                    [attr.aria-readonly]="readOnly()"
                    [attr.aria-invalid]="invalid()"
                    [attr.aria-expanded]="showDatePicker"
                    [attr.aria-owns]="showDatePicker ? 'calendar-dropdown-' + id : null"
                    [attr.aria-haspopup]="true"
                    [attr.data-float-label]="floatLabel()"
                    [value]="displayValue"
                    (input)="blockChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    (keydown)="onKeyDown($event)"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="inputId"
                    [attr.data-float]="floatLabel() ? focused || !!displayValue : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute right-5 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"
                    aria-hidden="true"
                ></span>
                <div class="absolute top-full right-0 flex items-center z-20">
                    @if (showDatePicker) {
                        <sag-ui-calendar-range
                            [id]="'calendar-dropdown-' + id()"
                            [startDate]="startDate"
                            [endDate]="endDate"
                            [lang]="lang()"
                            [availableRanges]="availableRanges()"
                            (dateRangeChanged)="handleDateRangeChange($event)"
                        ></sag-ui-calendar-range>
                    }
                </div>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
            <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="date-range-announcement-{{ id() }}">
                {{ rangeAnnouncement }}
            </div>
        </div>
    `, isInline: true, dependencies: [{ kind: "directive", type: InputDirective, selector: "input[sag-ui-input],textarea[sag-ui-input]" }, { kind: "directive", type: InputLabelDirective, selector: "label[sag-ui-input-label]" }, { kind: "component", type: CalendarRangeComponent, selector: "sag-ui-calendar-range", inputs: ["lang", "startDate", "endDate", "availableRanges"], outputs: ["dateRangeChanged"] }, { kind: "directive", type: InputErrorDirective, selector: "span[sag-ui-input-error]" }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DateRangePickerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sag-ui-date-range-picker',
                    standalone: true,
                    template: `
        <div class="flex flex-col">
            <div class="flex flex-col-reverse w-full relative">
                <input
                    sag-ui-input
                    [id]="inputId"
                    type="text"
                    [disabled]="disabled()"
                    [placeholder]="floatLabel() ? '' : placeholder()"
                    [readOnly]="readOnly()"
                    [required]="invalid() || (required() && touched)"
                    [attr.pattern]="invalid() ? 'SAG_INVALID_STATE' : null"
                    [attr.aria-label]="ariaLabel() || label() + (required() ? ' required' : '')"
                    [attr.aria-labelledby]="ariaLabelledby() || labelId"
                    [attr.aria-describedby]="getAriaDescribedby()"
                    [attr.aria-required]="required()"
                    [attr.aria-readonly]="readOnly()"
                    [attr.aria-invalid]="invalid()"
                    [attr.aria-expanded]="showDatePicker"
                    [attr.aria-owns]="showDatePicker ? 'calendar-dropdown-' + id : null"
                    [attr.aria-haspopup]="true"
                    [attr.data-float-label]="floatLabel()"
                    [value]="displayValue"
                    (input)="blockChange($event)"
                    (click)="onClick($event)"
                    (focus)="onFocus($event)"
                    (blur)="onBlur($event)"
                    (keydown)="onKeyDown($event)"
                    class="[appearance:textfield] [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
                <label
                    sag-ui-input-label
                    [id]="labelId"
                    [htmlFor]="inputId"
                    [attr.data-float]="floatLabel() ? focused || !!displayValue : undefined"
                    [attr.data-float-label]="floatLabel()"
                >
                    @if (required()) {
                        <span aria-hidden="true">*</span>
                    }
                    {{ label() }}
                    @if (hint()) {
                        <span
                            class="text-[#999999] dark:text-[#666666] icon-circle-information pl-2"
                            sag-ui-tooltip
                            [tooltip]="hint()"
                            [attr.aria-label]="hint()"
                        ></span>
                    }
                </label>
                <span
                    class="absolute right-5 bottom-[0.8rem] z-10 icon-calendar text-black dark:text-white"
                    aria-hidden="true"
                ></span>
                <div class="absolute top-full right-0 flex items-center z-20">
                    @if (showDatePicker) {
                        <sag-ui-calendar-range
                            [id]="'calendar-dropdown-' + id()"
                            [startDate]="startDate"
                            [endDate]="endDate"
                            [lang]="lang()"
                            [availableRanges]="availableRanges()"
                            (dateRangeChanged)="handleDateRangeChange($event)"
                        ></sag-ui-calendar-range>
                    }
                </div>
            </div>
            @if (error() && isArray(error())) {
                <div class="flex flex-col gap-1">
                    @for (err of error(); track err) {
                        <span sag-ui-input-error [id]="errorId" role="alert">{{ err }}</span>
                    }
                </div>
            }
            @if (error() && !isArray(error())) {
                <span sag-ui-input-error [id]="errorId" role="alert">{{ error() }}</span>
            }
            <div class="sr-only" aria-live="polite" [attr.aria-atomic]="true" id="date-range-announcement-{{ id() }}">
                {{ rangeAnnouncement }}
            </div>
        </div>
    `,
                    imports: [InputDirective, InputLabelDirective, CalendarRangeComponent, InputErrorDirective, TooltipDirective]
                }]
        }], ctorParameters: () => [], propDecorators: { onMouseDown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }], onFocusOut: [{
                type: HostListener,
                args: ['focusout', ['$event']]
            }] } });

class DateRangePickerDemoComponent {
    formGroup = new FormGroup({
        startDate: new FormControl(null),
        endDate: new FormControl(null)
    });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DateRangePickerDemoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.2.2", type: DateRangePickerDemoComponent, isStandalone: true, selector: "sag-ui-date-range-picker-demo", ngImport: i0, template: ` <div style="height: 500px;">
        <sag-ui-date-range-picker
            label="When are you on vacation?"
            placeholder="MM/DD/YYYY - MM/DD/YYYY"
            [formGroup]="formGroup"
            formControlNameStart="startDate"
            formControlNameEnd="endDate"
        ></sag-ui-date-range-picker>
    </div>`, isInline: true, dependencies: [{ kind: "component", type: DateRangePickerComponent, selector: "sag-ui-date-range-picker", inputs: ["id", "disabled", "required", "invalid", "readOnly", "error", "placeholder", "label", "lang", "availableRanges", "hint", "floatLabel", "formGroup", "formControlNameStart", "formControlNameEnd", "ariaLabel", "ariaLabelledby", "ariaDescribedby"], outputs: ["focus", "blur", "click"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: DateRangePickerDemoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-date-range-picker-demo', standalone: true, template: ` <div style="height: 500px;">
        <sag-ui-date-range-picker
            label="When are you on vacation?"
            placeholder="MM/DD/YYYY - MM/DD/YYYY"
            [formGroup]="formGroup"
            formControlNameStart="startDate"
            formControlNameEnd="endDate"
        ></sag-ui-date-range-picker>
    </div>`, imports: [DateRangePickerComponent] }]
        }] });

// Header Styles
const headerStylesSAG = cva([
    'w-full',
    'h-[80px]',
    'flex',
    'items-center',
    'justify-between',
    'pl-[56px]',
    'z-10',
    'fixed',
    'top-0',
    'left-0',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);
const headerStylesSHS = cva([
    'w-full',
    'h-[80px]',
    'flex',
    'items-center',
    'justify-between',
    'pl-[56px]',
    'z-10',
    'fixed',
    'top-0',
    'left-0',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);
// Sidebar Styles
const sidebarStylesSAG = cva([
    'h-[calc(100vh-80px)]',
    'flex',
    'flex-col',
    'justify-between',
    'items-center',
    'fixed',
    'top-[80px]',
    'left-0',
    'z-10',
    'transition-all',
    'duration-300',
    'ease-in-out',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#08082E]',
    'dark:text-[#E5E5E9]'
]);
const sidebarStylesSHS = cva([
    'h-[calc(100vh-80px)]',
    'flex',
    'flex-col',
    'justify-between',
    'items-center',
    'fixed',
    'top-[80px]',
    'left-0',
    'z-10',
    'transition-all',
    'duration-300',
    'ease-in-out',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#08082E]',
    'dark:text-[#E5E5E9]'
]);
const sidebarItemStylesSAG = cva([
    'w-[56px]',
    'h-[56px]',
    'border-l-4',
    'border-l-transparent',
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'focus:outline-none',
    'text-left',
    // Apply 1px borders on all sides when focused
    'focus:border-[1px]',
    'focus:border-[#1491EB]',
    'hover:bg-[#FFFFFF]',
    'dark:hover:bg-[#262648]'
]);
const sidebarToggleClassesSAG = cva([
    'h-[56px]',
    'flex',
    'items-center',
    'cursor-pointer',
    'group',
    'focus:outline-none',
    'focus:border-[1px]',
    'focus:border-[#1491EB]'
]);
const sidebarToggleClassesSHS = cva([
    'h-[56px]',
    'flex',
    'items-center',
    'cursor-pointer',
    'group',
    'focus:outline-none',
    'focus:border-[1px]',
    'focus:border-[#1491EB]'
]);
const sidebarItemStylesSHS = cva([
    'border-l-[3px]',
    'border-l-transparent',
    'w-[56px]',
    'h-[56px]',
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'focus:outline-none',
    // Apply 1px borders on all sides when focused
    'focus:border-[1px]',
    'focus:border-[#1491EB]',
    'hover:bg-[#FFFFFF]',
    'dark:hover:bg-[#262648]'
]);
// Content Styles
const contentStylesSAG = cva(['flex-1', 'overflow-auto', 'pt-[80px]', 'min-h-[calc(100vh-80px-56px)]']);
const contentStylesSHS = cva(['flex-1', 'overflow-auto', 'pt-[80px]', 'min-h-[calc(100vh-80px-56px)]']);
// Footer Styles
const footerStylesSAG = cva([
    'w-full',
    'min-h-[56px]',
    'z-9',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);
const footerStylesSHS = cva([
    'w-full',
    'min-h-[56px]',
    'z-9',
    // replace with light mode
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);
const mobileNavigationStylesSAG = cva([
    'absolute',
    'right-0',
    'top-[80px]',
    'w-64',
    'p-4',
    'shadow-lg',
    'z-50',
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'overflow-hidden',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);
const mobileNavigationStylesSHS = cva([
    'absolute',
    'right-0',
    'top-[80px]',
    'w-64',
    'p-4',
    'shadow-lg',
    'z-50',
    'bg-[#F3F3F0]',
    'text-[#000028]',
    'overflow-hidden',
    'dark:bg-[#000028]',
    'dark:text-[#FFFFFF]'
]);

class HeaderComponent {
    applicationName = input('Application');
    logoLink = input('/');
    logoUrl = input('');
    logoUrlDark = input();
    logoAlt = input('Siemens Logo');
    skipToContentTarget = input('#main-content');
    skipToContentTargetLinkText = input('Skip to main content');
    theme = inject(THEME_TOKEN);
    isMobileMenuOpen = signal(false);
    toggleMobileMenu() {
        this.isMobileMenuOpen.update(value => !value);
    }
    headerClasses = componentStylesByTheme(this.theme, headerStylesSAG, headerStylesSHS)();
    mobileHeaderClasses = componentStylesByTheme(this.theme, mobileNavigationStylesSAG, mobileNavigationStylesSHS)();
    skipToContent(event) {
        // Prevent default only if we need custom handling
        const targetElement = document.querySelector(this.skipToContentTarget());
        if (targetElement) {
            event.preventDefault();
            // Focus the main content element
            targetElement.setAttribute('tabindex', '0');
            targetElement.focus();
            // Remove tabindex after focus (optional)
            setTimeout(() => {
                targetElement.removeAttribute('tabindex');
            }, 100);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: HeaderComponent, isStandalone: true, selector: "sag-ui-header", inputs: { applicationName: { classPropertyName: "applicationName", publicName: "applicationName", isSignal: true, isRequired: false, transformFunction: null }, logoLink: { classPropertyName: "logoLink", publicName: "logoLink", isSignal: true, isRequired: false, transformFunction: null }, logoUrl: { classPropertyName: "logoUrl", publicName: "logoUrl", isSignal: true, isRequired: false, transformFunction: null }, logoUrlDark: { classPropertyName: "logoUrlDark", publicName: "logoUrlDark", isSignal: true, isRequired: false, transformFunction: null }, logoAlt: { classPropertyName: "logoAlt", publicName: "logoAlt", isSignal: true, isRequired: false, transformFunction: null }, skipToContentTarget: { classPropertyName: "skipToContentTarget", publicName: "skipToContentTarget", isSignal: true, isRequired: false, transformFunction: null }, skipToContentTargetLinkText: { classPropertyName: "skipToContentTargetLinkText", publicName: "skipToContentTargetLinkText", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<header [class]=\"headerClasses\" [attr.aria-label]=\"'Application header for ' + applicationName()\">\n    <div class=\"flex items-center pl-4\">\n        <img\n            [src]=\"logoUrl()\"\n            [alt]=\"logoAlt()\"\n            class=\"h-5 mr-6 cursor-pointer block dark:hidden\"\n            [routerLink]=\"logoLink()\"\n        />\n        <img\n            [src]=\"logoUrlDark() ?? logoUrl()\"\n            [alt]=\"logoAlt()\"\n            class=\"h-5 mr-6 cursor-pointer hidden dark:block\"\n            [routerLink]=\"logoLink()\"\n        />\n        <h1 class=\"text-lg sm:inline-block hidden\">{{ applicationName() }}</h1>\n        <a\n            [href]=\"skipToContentTarget\"\n            class=\"sr-only focus:not-sr-only focus:z-50 focus:px-6 focus:py-3 focus:text-white focus:ml-6 focus:font-semibold focus:text-sm focus:focus-visible:outline-1 focus:focus-visible:outline-[#1491EB] focus:bg-[#007993] focus:rounded-sm focus:border-[#007993] dark:bg-[#00CCCC] dark:text-[#000028] dark:border-[#00CCCC]\"\n            (click)=\"skipToContent($event)\"\n        >\n            {{ skipToContentTargetLinkText() }}\n        </a>\n    </div>\n    <div class=\"flex items-center pr-4\">\n        <!-- Desktop Content -->\n        <div class=\"hidden md:block\">\n            <ng-content select=\"desktop-content\"></ng-content>\n        </div>\n        <!-- Mobile Menu Button -->\n        <button\n            class=\"md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500\"\n            (click)=\"toggleMobileMenu()\"\n            [attr.aria-expanded]=\"isMobileMenuOpen()\"\n            [attr.aria-label]=\"isMobileMenuOpen() ? 'Close navigation menu' : 'Open navigation menu'\"\n        >\n            <span class=\"icon-menu text-2xl\"></span>\n        </button>\n    </div>\n    <!-- Mobile Menu -->\n    @if (isMobileMenuOpen()) {\n        <div\n            @slideInOut\n            [class]=\"mobileHeaderClasses\"\n            role=\"dialog\"\n            aria-modal=\"true\"\n            aria-label=\"Mobile navigation menu\"\n        >\n            <ng-content select=\"mobile-content\"></ng-content>\n        </div>\n    }\n</header>\n", dependencies: [{ kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }], animations: [
            trigger('slideInOut', [
                transition(':enter', [
                    style$1({ transform: 'translateX(100%)', opacity: 0 }),
                    animate('300ms ease-out', style$1({ transform: 'translateX(0)', opacity: 1 }))
                ]),
                transition(':leave', [
                    style$1({ transform: 'translateX(0)', opacity: 1 }),
                    animate('300ms ease-in', style$1({ transform: 'translateX(100%)', opacity: 0 }))
                ])
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-header', standalone: true, imports: [RouterModule], animations: [
                        trigger('slideInOut', [
                            transition(':enter', [
                                style$1({ transform: 'translateX(100%)', opacity: 0 }),
                                animate('300ms ease-out', style$1({ transform: 'translateX(0)', opacity: 1 }))
                            ]),
                            transition(':leave', [
                                style$1({ transform: 'translateX(0)', opacity: 1 }),
                                animate('300ms ease-in', style$1({ transform: 'translateX(100%)', opacity: 0 }))
                            ])
                        ])
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<header [class]=\"headerClasses\" [attr.aria-label]=\"'Application header for ' + applicationName()\">\n    <div class=\"flex items-center pl-4\">\n        <img\n            [src]=\"logoUrl()\"\n            [alt]=\"logoAlt()\"\n            class=\"h-5 mr-6 cursor-pointer block dark:hidden\"\n            [routerLink]=\"logoLink()\"\n        />\n        <img\n            [src]=\"logoUrlDark() ?? logoUrl()\"\n            [alt]=\"logoAlt()\"\n            class=\"h-5 mr-6 cursor-pointer hidden dark:block\"\n            [routerLink]=\"logoLink()\"\n        />\n        <h1 class=\"text-lg sm:inline-block hidden\">{{ applicationName() }}</h1>\n        <a\n            [href]=\"skipToContentTarget\"\n            class=\"sr-only focus:not-sr-only focus:z-50 focus:px-6 focus:py-3 focus:text-white focus:ml-6 focus:font-semibold focus:text-sm focus:focus-visible:outline-1 focus:focus-visible:outline-[#1491EB] focus:bg-[#007993] focus:rounded-sm focus:border-[#007993] dark:bg-[#00CCCC] dark:text-[#000028] dark:border-[#00CCCC]\"\n            (click)=\"skipToContent($event)\"\n        >\n            {{ skipToContentTargetLinkText() }}\n        </a>\n    </div>\n    <div class=\"flex items-center pr-4\">\n        <!-- Desktop Content -->\n        <div class=\"hidden md:block\">\n            <ng-content select=\"desktop-content\"></ng-content>\n        </div>\n        <!-- Mobile Menu Button -->\n        <button\n            class=\"md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500\"\n            (click)=\"toggleMobileMenu()\"\n            [attr.aria-expanded]=\"isMobileMenuOpen()\"\n            [attr.aria-label]=\"isMobileMenuOpen() ? 'Close navigation menu' : 'Open navigation menu'\"\n        >\n            <span class=\"icon-menu text-2xl\"></span>\n        </button>\n    </div>\n    <!-- Mobile Menu -->\n    @if (isMobileMenuOpen()) {\n        <div\n            @slideInOut\n            [class]=\"mobileHeaderClasses\"\n            role=\"dialog\"\n            aria-modal=\"true\"\n            aria-label=\"Mobile navigation menu\"\n        >\n            <ng-content select=\"mobile-content\"></ng-content>\n        </div>\n    }\n</header>\n" }]
        }] });

var SidebarItemType;
(function (SidebarItemType) {
    SidebarItemType["LINK"] = "link";
    SidebarItemType["BUTTON"] = "button";
})(SidebarItemType || (SidebarItemType = {}));

class SidebarItemComponent {
    item = input.required();
    isExternalLink = input(false);
    isExpanded = input(false);
    sidebarItemClicked = output();
    isSidebarItemButton = computed(() => this.item().type === SidebarItemType.BUTTON);
    isSidebarItemLink = computed(() => this.item().type === SidebarItemType.LINK);
    theme = inject(THEME_TOKEN);
    emitSidebarItemClicked() {
        this.sidebarItemClicked.emit(this.item().name);
    }
    itemClasses = computed(() => {
        const sidebarItemStyles = componentStylesByTheme(this.theme, sidebarItemStylesSAG, sidebarItemStylesSHS);
        return `${sidebarItemStyles()} ${this.isExpanded() ? 'w-full' : ''}`;
    });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SidebarItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: SidebarItemComponent, isStandalone: true, selector: "sag-ui-sidebar-item", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: true, transformFunction: null }, isExternalLink: { classPropertyName: "isExternalLink", publicName: "isExternalLink", isSignal: true, isRequired: false, transformFunction: null }, isExpanded: { classPropertyName: "isExpanded", publicName: "isExpanded", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { sidebarItemClicked: "sidebarItemClicked" }, ngImport: i0, template: "@if (this.isSidebarItemButton()) {\n  <button\n    [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n    [class]=\"itemClasses()\"\n    [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n    sag-ui-tooltip\n    [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n    tooltipPosition=\"right\"\n    tabindex=\"0\"\n    (click)=\"this.emitSidebarItemClicked()\"\n  >\n    <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n    @if (isExpanded() && item().name) {\n      <span\n        class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n      >{{ item().name }}</span\n      >\n    }\n  </button>\n\n} @else if (isSidebarItemLink()) {\n  @if (!isExternalLink()) {\n    <a\n      [routerLink]=\"item().routerLink\"\n      routerLinkActive=\"bg-[#ffffff] border-l-[#005159] dark:bg-[#262648] dark:border-l-[#00FFB9]  !border-l-4\"\n      ariaCurrentWhenActive=\"page\"\n      [class]=\"itemClasses()\"\n      [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n      [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n      sag-ui-tooltip\n      [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n      tooltipPosition=\"right\"\n      tabindex=\"0\"\n    >\n      <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n      @if (isExpanded() && item().name) {\n        <span\n          class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n        >{{ item().name }}</span\n        >\n      }\n    </a>\n  } @else {\n    <a\n      [href]=\"item().routerLink\"\n      [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n      target=\"_blank\"\n      rel=\"noopener noreferrer\"\n      [class]=\"itemClasses()\"\n      [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n      sag-ui-tooltip\n      [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n      tooltipPosition=\"right\"\n      tabindex=\"0\"\n    >\n      <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n      @if (isExpanded() && item().name) {\n        <span\n          class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n        >{{ item().name }}</span\n        >\n      }\n    </a>\n  }\n}\n", dependencies: [{ kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }, { kind: "directive", type: TooltipDirective, selector: "[sag-ui-tooltip]", inputs: ["tooltip", "tooltipPosition", "showDelay", "hideDelay"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SidebarItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-sidebar-item', standalone: true, imports: [RouterLink, RouterLinkActive, TooltipDirective], changeDetection: ChangeDetectionStrategy.OnPush, template: "@if (this.isSidebarItemButton()) {\n  <button\n    [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n    [class]=\"itemClasses()\"\n    [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n    sag-ui-tooltip\n    [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n    tooltipPosition=\"right\"\n    tabindex=\"0\"\n    (click)=\"this.emitSidebarItemClicked()\"\n  >\n    <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n    @if (isExpanded() && item().name) {\n      <span\n        class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n      >{{ item().name }}</span\n      >\n    }\n  </button>\n\n} @else if (isSidebarItemLink()) {\n  @if (!isExternalLink()) {\n    <a\n      [routerLink]=\"item().routerLink\"\n      routerLinkActive=\"bg-[#ffffff] border-l-[#005159] dark:bg-[#262648] dark:border-l-[#00FFB9]  !border-l-4\"\n      ariaCurrentWhenActive=\"page\"\n      [class]=\"itemClasses()\"\n      [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n      [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n      sag-ui-tooltip\n      [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n      tooltipPosition=\"right\"\n      tabindex=\"0\"\n    >\n      <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n      @if (isExpanded() && item().name) {\n        <span\n          class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n        >{{ item().name }}</span\n        >\n      }\n    </a>\n  } @else {\n    <a\n      [href]=\"item().routerLink\"\n      [attr.aria-label]=\"item().ariaLabel || item().tooltip\"\n      target=\"_blank\"\n      rel=\"noopener noreferrer\"\n      [class]=\"itemClasses()\"\n      [attr.title]=\"isExpanded() ? '' : item().tooltip\"\n      sag-ui-tooltip\n      [tooltip]=\"isExpanded() ? '' : item().tooltip\"\n      tooltipPosition=\"right\"\n      tabindex=\"0\"\n    >\n      <span class=\"icon-{{ item().icon }} text-current text-2xl absolute left-4\" aria-hidden=\"true\"></span>\n      @if (isExpanded() && item().name) {\n        <span\n          class=\"ml-12 text-sm whitespace-nowrap overflow-hidden text-ellipsis flex-1 text-[#000028] dark:text-white\"\n        >{{ item().name }}</span\n        >\n      }\n    </a>\n  }\n}\n" }]
        }] });

class SidebarComponent {
    items = input([]);
    bottomItems = input([]);
    userRoles = input([]);
    sidebarItemClicked = output();
    theme = inject(THEME_TOKEN);
    emitSidebarItemClicked(itemName) {
        this.sidebarItemClicked.emit(itemName);
    }
    isExpanded = signal(false);
    sidebarToggleClasses = componentStylesByTheme(this.theme, sidebarToggleClassesSAG, sidebarToggleClassesSHS)();
    sidebarClasses = computed(() => {
        const sidebarStyles = componentStylesByTheme(this.theme, sidebarStylesSAG, sidebarStylesSHS);
        return sidebarStyles() + (this.isExpanded() ? ' w-[296px]' : ' w-[56px]');
    });
    toggleExpanded() {
        this.isExpanded.update(isExpanded => !isExpanded);
    }
    showItem(item) {
        if (!item.roles) {
            return true;
        }
        return this.userRoles().some(role => item.roles?.includes(role));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SidebarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: SidebarComponent, isStandalone: true, selector: "sag-ui-sidebar", inputs: { items: { classPropertyName: "items", publicName: "items", isSignal: true, isRequired: false, transformFunction: null }, bottomItems: { classPropertyName: "bottomItems", publicName: "bottomItems", isSignal: true, isRequired: false, transformFunction: null }, userRoles: { classPropertyName: "userRoles", publicName: "userRoles", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { sidebarItemClicked: "sidebarItemClicked" }, ngImport: i0, template: "<nav [class]=\"sidebarClasses()\" aria-label=\"Main navigation\">\n  <div class=\"flex flex-col w-full\">\n    <!-- Toggle button as the first item -->\n    <div\n      [class]=\"sidebarToggleClasses\"\n      [class.justify-end]=\"isExpanded()\"\n      [class.justify-center]=\"!isExpanded()\"\n      (click)=\"toggleExpanded()\"\n      (keydown.enter)=\"toggleExpanded()\"\n      tabindex=\"0\"\n      role=\"button\"\n      aria-label=\"Toggle sidebar\"\n    >\n      <span class=\"icon-chevron-right text-current text-2xl dark:hover:text-[#00FFB9] dark:group-focus:text-[#00FFB9] hover:text-[#005159] group-focus:text-[#005159]\"  [class.mr-4]=\"isExpanded()\" [ngClass]=\"{'icon-chevron-right': !isExpanded(), 'icon-chevron-left': isExpanded()}\"></span>\n    </div>\n\n    @for (item of items(); track $index) {\n      @if (showItem(item)) {\n        <sag-ui-sidebar-item\n          [item]=\"item\"\n          [isExpanded]=\"isExpanded()\"\n          (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n        ></sag-ui-sidebar-item>\n      }\n    }\n  </div>\n\n  @if (bottomItems().length > 0) {\n    <div class=\"flex flex-col w-full border-t border-[#262648]\">\n      @for (item of bottomItems(); track $index) {\n        @if (showItem(item)) {\n          <sag-ui-sidebar-item\n            [item]=\"item\"\n            [isExpanded]=\"isExpanded()\"\n            [isExternalLink]=\"item.routerLink.startsWith('http')\"\n            (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n          ></sag-ui-sidebar-item>\n        }\n      }\n    </div>\n  }\n</nav>\n", dependencies: [{ kind: "component", type: SidebarItemComponent, selector: "sag-ui-sidebar-item", inputs: ["item", "isExternalLink", "isExpanded"], outputs: ["sidebarItemClicked"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: SidebarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-sidebar', standalone: true, imports: [SidebarItemComponent, NgClass], changeDetection: ChangeDetectionStrategy.OnPush, template: "<nav [class]=\"sidebarClasses()\" aria-label=\"Main navigation\">\n  <div class=\"flex flex-col w-full\">\n    <!-- Toggle button as the first item -->\n    <div\n      [class]=\"sidebarToggleClasses\"\n      [class.justify-end]=\"isExpanded()\"\n      [class.justify-center]=\"!isExpanded()\"\n      (click)=\"toggleExpanded()\"\n      (keydown.enter)=\"toggleExpanded()\"\n      tabindex=\"0\"\n      role=\"button\"\n      aria-label=\"Toggle sidebar\"\n    >\n      <span class=\"icon-chevron-right text-current text-2xl dark:hover:text-[#00FFB9] dark:group-focus:text-[#00FFB9] hover:text-[#005159] group-focus:text-[#005159]\"  [class.mr-4]=\"isExpanded()\" [ngClass]=\"{'icon-chevron-right': !isExpanded(), 'icon-chevron-left': isExpanded()}\"></span>\n    </div>\n\n    @for (item of items(); track $index) {\n      @if (showItem(item)) {\n        <sag-ui-sidebar-item\n          [item]=\"item\"\n          [isExpanded]=\"isExpanded()\"\n          (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n        ></sag-ui-sidebar-item>\n      }\n    }\n  </div>\n\n  @if (bottomItems().length > 0) {\n    <div class=\"flex flex-col w-full border-t border-[#262648]\">\n      @for (item of bottomItems(); track $index) {\n        @if (showItem(item)) {\n          <sag-ui-sidebar-item\n            [item]=\"item\"\n            [isExpanded]=\"isExpanded()\"\n            [isExternalLink]=\"item.routerLink.startsWith('http')\"\n            (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n          ></sag-ui-sidebar-item>\n        }\n      }\n    </div>\n  }\n</nav>\n" }]
        }] });

class FooterComponent {
    theme = inject(THEME_TOKEN);
    footerLinks = input.required();
    footerClasses = componentStylesByTheme(this.theme, footerStylesSAG, footerStylesSHS)();
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: FooterComponent, isStandalone: true, selector: "sag-ui-footer", inputs: { footerLinks: { classPropertyName: "footerLinks", publicName: "footerLinks", isSignal: true, isRequired: true, transformFunction: null } }, ngImport: i0, template: "<footer [class]=\"footerClasses\" role=\"contentinfo\" aria-label=\"Footer with legal information\">\n  <div class=\"min-h-[56px] w-full flex flex-col sm:flex-row justify-between items-center text-sm px-4 py-3 gap-3\">\n    <div class=\"flex flex-wrap justify-center sm:justify-start gap-1 sm:gap-0 sm:divide-x divide-[#000028] dark:divide-white\">\n      @for (footerLink of footerLinks(); track footerLink.key) {\n        <a\n          class=\"px-2 py-1 cursor-pointer hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500\"\n          [href]=\"footerLink.url\"\n          target=\"_blank\"\n          rel=\"noopener noreferrer\"\n          [attr.aria-label]=\"footerLink.text\"\n        >\n          {{ footerLink.text }}\n        </a>\n      }\n    </div>\n    <div class=\"mt-2 sm:mt-0\">\n      <p class=\"px-1.5 sm:px-0 whitespace-nowrap\">powered by <span class=\"font-black\">/GMS/</span></p>\n    </div>\n  </div>\n</footer>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-footer', standalone: true, imports: [], changeDetection: ChangeDetectionStrategy.OnPush, template: "<footer [class]=\"footerClasses\" role=\"contentinfo\" aria-label=\"Footer with legal information\">\n  <div class=\"min-h-[56px] w-full flex flex-col sm:flex-row justify-between items-center text-sm px-4 py-3 gap-3\">\n    <div class=\"flex flex-wrap justify-center sm:justify-start gap-1 sm:gap-0 sm:divide-x divide-[#000028] dark:divide-white\">\n      @for (footerLink of footerLinks(); track footerLink.key) {\n        <a\n          class=\"px-2 py-1 cursor-pointer hover:underline focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500\"\n          [href]=\"footerLink.url\"\n          target=\"_blank\"\n          rel=\"noopener noreferrer\"\n          [attr.aria-label]=\"footerLink.text\"\n        >\n          {{ footerLink.text }}\n        </a>\n      }\n    </div>\n    <div class=\"mt-2 sm:mt-0\">\n      <p class=\"px-1.5 sm:px-0 whitespace-nowrap\">powered by <span class=\"font-black\">/GMS/</span></p>\n    </div>\n  </div>\n</footer>\n" }]
        }] });

class ApplicationFrameComponent {
    applicationName = input('Application');
    logoUrl = input('assets/siemens-logo.svg');
    logoUrlDark = input();
    logoLink = input('/');
    logoAlt = input('Siemens Logo');
    skipToContentTarget = input('#main-content');
    skipToContentTargetLinkText = input('Skip to main content');
    sidebarItems = input([]);
    bottomSidebarItems = input([]);
    footerLinks = input([]);
    userRoles = input([]);
    sidebarItemClicked = output();
    desktopHeaderContent = contentChild.required('desktopHeaderContent');
    mobileHeaderContent = contentChild.required('mobileHeaderContent');
    theme = inject(THEME_TOKEN);
    contentClasses = computed(() => {
        const contentStyles = componentStylesByTheme(this.theme, contentStylesSAG, contentStylesSHS);
        return contentStyles() + (this.sidebarItems().length > 0 ? ' pl-[72px]' : '');
    });
    footerClasses = computed(() => {
        const footerStyles = componentStylesByTheme(this.theme, footerStylesSAG, footerStylesSHS);
        return footerStyles() + (this.sidebarItems().length > 0 ? ' pl-[56px]' : '');
    });
    emitSidebarItemClicked(itemName) {
        this.sidebarItemClicked.emit(itemName);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ApplicationFrameComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: ApplicationFrameComponent, isStandalone: true, selector: "sag-ui-application-frame", inputs: { applicationName: { classPropertyName: "applicationName", publicName: "applicationName", isSignal: true, isRequired: false, transformFunction: null }, logoUrl: { classPropertyName: "logoUrl", publicName: "logoUrl", isSignal: true, isRequired: false, transformFunction: null }, logoUrlDark: { classPropertyName: "logoUrlDark", publicName: "logoUrlDark", isSignal: true, isRequired: false, transformFunction: null }, logoLink: { classPropertyName: "logoLink", publicName: "logoLink", isSignal: true, isRequired: false, transformFunction: null }, logoAlt: { classPropertyName: "logoAlt", publicName: "logoAlt", isSignal: true, isRequired: false, transformFunction: null }, skipToContentTarget: { classPropertyName: "skipToContentTarget", publicName: "skipToContentTarget", isSignal: true, isRequired: false, transformFunction: null }, skipToContentTargetLinkText: { classPropertyName: "skipToContentTargetLinkText", publicName: "skipToContentTargetLinkText", isSignal: true, isRequired: false, transformFunction: null }, sidebarItems: { classPropertyName: "sidebarItems", publicName: "sidebarItems", isSignal: true, isRequired: false, transformFunction: null }, bottomSidebarItems: { classPropertyName: "bottomSidebarItems", publicName: "bottomSidebarItems", isSignal: true, isRequired: false, transformFunction: null }, footerLinks: { classPropertyName: "footerLinks", publicName: "footerLinks", isSignal: true, isRequired: false, transformFunction: null }, userRoles: { classPropertyName: "userRoles", publicName: "userRoles", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { sidebarItemClicked: "sidebarItemClicked" }, queries: [{ propertyName: "desktopHeaderContent", first: true, predicate: ["desktopHeaderContent"], descendants: true, isSignal: true }, { propertyName: "mobileHeaderContent", first: true, predicate: ["mobileHeaderContent"], descendants: true, isSignal: true }], ngImport: i0, template: "<div class=\"flex flex-col min-h-screen\">\n  <sag-ui-header\n    [applicationName]=\"applicationName()\"\n    [logoUrl]=\"logoUrl()\"\n    [logoUrlDark]=\"logoUrlDark()\"\n    [logoAlt]=\"logoAlt()\"\n    [logoLink]=\"logoLink()\"\n    [skipToContentTarget]=\"skipToContentTarget()\"\n    [skipToContentTargetLinkText]=\"skipToContentTargetLinkText()\"\n  >\n    <!-- Project desktop content -->\n    @if (desktopHeaderContent()) {\n      <ng-container ngProjectAs=\"desktop-content\">\n        <ng-container *ngTemplateOutlet=\"desktopHeaderContent()\"></ng-container>\n      </ng-container>\n    }\n\n    <!-- Project mobile content -->\n    @if (mobileHeaderContent()) {\n      <ng-container ngProjectAs=\"mobile-content\">\n        <ng-container *ngTemplateOutlet=\"mobileHeaderContent()\"></ng-container>\n      </ng-container>\n    }\n  </sag-ui-header>\n\n  @if (sidebarItems().length > 0) {\n    <sag-ui-sidebar\n      [items]=\"sidebarItems()\"\n      [bottomItems]=\"bottomSidebarItems()\"\n      [userRoles]=\"userRoles()\"\n      (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n    ></sag-ui-sidebar>\n  }\n\n  <main [class]=\"contentClasses()\" role=\"main\" id=\"main-content\">\n    <ng-content></ng-content>\n  </main>\n\n  <sag-ui-footer [class]=\"footerClasses()\" [footerLinks]=\"footerLinks()\"></sag-ui-footer>\n</div>\n", dependencies: [{ kind: "component", type: HeaderComponent, selector: "sag-ui-header", inputs: ["applicationName", "logoLink", "logoUrl", "logoUrlDark", "logoAlt", "skipToContentTarget", "skipToContentTargetLinkText"] }, { kind: "component", type: SidebarComponent, selector: "sag-ui-sidebar", inputs: ["items", "bottomItems", "userRoles"], outputs: ["sidebarItemClicked"] }, { kind: "component", type: FooterComponent, selector: "sag-ui-footer", inputs: ["footerLinks"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: ApplicationFrameComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-application-frame', standalone: true, imports: [HeaderComponent, SidebarComponent, FooterComponent, NgTemplateOutlet], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"flex flex-col min-h-screen\">\n  <sag-ui-header\n    [applicationName]=\"applicationName()\"\n    [logoUrl]=\"logoUrl()\"\n    [logoUrlDark]=\"logoUrlDark()\"\n    [logoAlt]=\"logoAlt()\"\n    [logoLink]=\"logoLink()\"\n    [skipToContentTarget]=\"skipToContentTarget()\"\n    [skipToContentTargetLinkText]=\"skipToContentTargetLinkText()\"\n  >\n    <!-- Project desktop content -->\n    @if (desktopHeaderContent()) {\n      <ng-container ngProjectAs=\"desktop-content\">\n        <ng-container *ngTemplateOutlet=\"desktopHeaderContent()\"></ng-container>\n      </ng-container>\n    }\n\n    <!-- Project mobile content -->\n    @if (mobileHeaderContent()) {\n      <ng-container ngProjectAs=\"mobile-content\">\n        <ng-container *ngTemplateOutlet=\"mobileHeaderContent()\"></ng-container>\n      </ng-container>\n    }\n  </sag-ui-header>\n\n  @if (sidebarItems().length > 0) {\n    <sag-ui-sidebar\n      [items]=\"sidebarItems()\"\n      [bottomItems]=\"bottomSidebarItems()\"\n      [userRoles]=\"userRoles()\"\n      (sidebarItemClicked)=\"emitSidebarItemClicked($event)\"\n    ></sag-ui-sidebar>\n  }\n\n  <main [class]=\"contentClasses()\" role=\"main\" id=\"main-content\">\n    <ng-content></ng-content>\n  </main>\n\n  <sag-ui-footer [class]=\"footerClasses()\" [footerLinks]=\"footerLinks()\"></sag-ui-footer>\n</div>\n" }]
        }] });

/*
  Breadcrumb styles for Siemens AG (SAG): using Siemens dark blue accents.
*/
const breadcrumbStylesSAG = cva(['bg-transparent', 'text-[#000028]', 'py-3']);
/*
  Breadcrumb styles for Siemens Healthineers (SHS): using an alternative Siemens palette.
*/
const breadcrumbStylesSHS = cva([
    'bg-transparent',
    'text-[#005159]',
    'py-3',
    'rounded',
    'border-bottom',
    'border-teal-100'
]);

const breadcrumbItemBaseStyles = [
    'flex',
    'items-center',
    'text-sm',
    'select-none',
    'transition-colors',
    'duration-200',
    'outline-none',
    'focus-visible:outline-1',
    'focus-visible:outline-offset-1',
    'rounded-sm'
];
const breadcrumbItemWrapperBaseStyles = ['flex', 'items-center', 'gap-1'];
const breadcrumbSeparatorBaseStyles = ['mx-1', 'select-none'];
const breadcrumbItemStylesSAG = cva([
    ...breadcrumbItemBaseStyles,
    'text-[#000028]',
    'px-2',
    'py-1',
    // Default state
    'bg-transparent',
    // Interactive state (not current, not disabled)
    'data-[interactive=true]:text-[#007993]',
    'data-[interactive=true]:cursor-pointer',
    'data-[interactive=true]:hover:bg-[#d1fff2]',
    'data-[interactive=true]:hover:text-[#005159]',
    'data-[interactive=true]:active:bg-[#d1fff2]',
    'data-[interactive=true]:active:text-[#005159]',
    'data-[interactive=true]:focus-visible:outline-[#1491EB]',
    // Current page state
    'data-[current=true]:text-[#000028]',
    'data-[current=true]:cursor-default',
    // Disabled state
    'data-[disabled=true]:text-[#66667e]',
    'data-[disabled=true]:cursor-not-allowed',
    'data-[disabled=true]:hover:bg-transparent',
    // Dark mode
    'dark:text-white',
    'dark:data-[interactive=true]:text-[#00CCCC]',
    'dark:data-[interactive=true]:hover:bg-[#001F39]',
    'dark:data-[interactive=true]:hover:text-[#00FFB9]',
    'dark:data-[interactive=true]:active:bg-[#001F39]',
    'dark:data-[current=true]:text-[#ffffff]',
    'dark:data-[disabled=true]:text-[#9999A9]'
]);
const breadcrumbItemStylesSHS = cva([
    ...breadcrumbItemBaseStyles,
    'text-[#000028]',
    'px-2',
    'py-1',
    // Default state
    'bg-transparent',
    // Interactive state (not current, not disabled)
    'data-[interactive=true]:text-[#007993]',
    'data-[interactive=true]:cursor-pointer',
    'data-[interactive=true]:hover:bg-[#d1fff2]',
    'data-[interactive=true]:hover:text-[#005159]',
    'data-[interactive=true]:active:bg-[#d1fff2]',
    'data-[interactive=true]:active:text-[#005159]',
    'data-[interactive=true]:focus-visible:outline-[#1491EB]',
    // Current page state
    'data-[current=true]:text-[#000028]',
    'data-[current=true]:cursor-default',
    // Disabled state
    'data-[disabled=true]:text-[#66667e]',
    'data-[disabled=true]:cursor-not-allowed',
    'data-[disabled=true]:hover:bg-transparent',
    // Dark mode
    'dark:text-white',
    'dark:data-[interactive=true]:text-[#00CCCC]',
    'dark:data-[interactive=true]:hover:bg-[#001F39]',
    'dark:data-[interactive=true]:hover:text-[#00FFB9]',
    'dark:data-[interactive=true]:active:bg-[#001F39]',
    'dark:data-[current=true]:text-[#ffffff]',
    'dark:data-[disabled=true]:text-[#9999A9]'
]);
const breadcrumbItemWrapperStylesSAG = cva([...breadcrumbItemWrapperBaseStyles]);
const breadcrumbItemWrapperStylesSHS = cva([...breadcrumbItemWrapperBaseStyles]);
const breadcrumbSeparatorStylesSAG = cva([
    ...breadcrumbSeparatorBaseStyles,
    'text-[#4C4C68]',
    'dark:text-[#B3B3BE]'
]);
const breadcrumbSeparatorStylesSHS = cva([
    ...breadcrumbSeparatorBaseStyles,
    'text-[#666666]',
    'dark:text-[#999999]'
]);

class BreadcrumbItemComponent {
    templateRef;
    key = input.required();
    label = input.required();
    href = input(null);
    isCurrent = input(false);
    isDisabled = input(false);
    hideSeparator = input(false);
    separator = input('>');
    isLast = false;
    ariaLabel = input(null);
    itemClicked = output();
    theme = inject(THEME_TOKEN);
    itemStyles = componentStylesByTheme(this.theme, breadcrumbItemStylesSAG, breadcrumbItemStylesSHS)();
    wrapperStyles = componentStylesByTheme(this.theme, breadcrumbItemWrapperStylesSAG, breadcrumbItemWrapperStylesSHS)();
    separatorStyles = componentStylesByTheme(this.theme, breadcrumbSeparatorStylesSAG, breadcrumbSeparatorStylesSHS)();
    get isInteractive() {
        return this.isCurrent() !== true && this.isDisabled() !== true;
    }
    onClick(event) {
        if (this.isDisabled() === true) {
            event.preventDefault();
            return;
        }
        if (this.isCurrent() !== true) {
            this.itemClicked.emit(this.key());
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: BreadcrumbItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.2", type: BreadcrumbItemComponent, isStandalone: true, selector: "li[sag-ui-breadcrumb-item]", inputs: { key: { classPropertyName: "key", publicName: "key", isSignal: true, isRequired: true, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: true, transformFunction: null }, href: { classPropertyName: "href", publicName: "href", isSignal: true, isRequired: false, transformFunction: null }, isCurrent: { classPropertyName: "isCurrent", publicName: "isCurrent", isSignal: true, isRequired: false, transformFunction: null }, isDisabled: { classPropertyName: "isDisabled", publicName: "isDisabled", isSignal: true, isRequired: false, transformFunction: null }, hideSeparator: { classPropertyName: "hideSeparator", publicName: "hideSeparator", isSignal: true, isRequired: false, transformFunction: null }, separator: { classPropertyName: "separator", publicName: "separator", isSignal: true, isRequired: false, transformFunction: null }, isLast: { classPropertyName: "isLast", publicName: "isLast", isSignal: false, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { itemClicked: "itemClicked" }, host: { classAttribute: "flex items-center gap-1" }, viewQueries: [{ propertyName: "templateRef", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0, template: "  @if (href() && isInteractive) {\n    <a [href]=\"href()\"\n       [class]=\"itemStyles\"\n       (click)=\"onClick($event)\"\n       [attr.aria-label]=\"ariaLabel()\"\n       [attr.data-current]=\"!!isCurrent()\"\n       [attr.data-disabled]=\"!!isDisabled()\"\n       [attr.data-interactive]=\"isInteractive\"\n       [attr.aria-current]=\"!!isCurrent() ? 'page' : null\"\n       [attr.aria-disabled]=\"!!isDisabled()\"\n       [attr.tabindex]=\"!!isDisabled() || !!isCurrent() ? -1 : 0\">\n      {{ label() }}\n    </a>\n  } @else {\n    <span [class]=\"itemStyles\"\n          (click)=\"onClick($event)\"\n          [attr.aria-label]=\"ariaLabel()\"\n          [attr.data-current]=\"!!isCurrent()\"\n          [attr.data-disabled]=\"!!isDisabled()\"\n          [attr.data-interactive]=\"isInteractive\"\n          [attr.aria-current]=\"!!isCurrent() ? 'page' : null\"\n          [attr.aria-disabled]=\"!!isDisabled()\"\n          [attr.tabindex]=\"!!!isDisabled() && !href() && !!!isCurrent() ? 0 : null\"\n          (keydown.enter)=\"!!!isDisabled() && onClick($event)\"\n          (keydown.space)=\"!!!isDisabled() && onClick($event)\">\n      {{ label() }}\n    </span>\n  }\n\n  @if (!isLast && !hideSeparator()) {\n    <span [class]=\"separatorStyles\" role=\"presentation\" aria-hidden=\"true\">\n      {{ separator() }}\n    </span>\n  }\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: BreadcrumbItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'li[sag-ui-breadcrumb-item]', changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'flex items-center gap-1'
                    }, template: "  @if (href() && isInteractive) {\n    <a [href]=\"href()\"\n       [class]=\"itemStyles\"\n       (click)=\"onClick($event)\"\n       [attr.aria-label]=\"ariaLabel()\"\n       [attr.data-current]=\"!!isCurrent()\"\n       [attr.data-disabled]=\"!!isDisabled()\"\n       [attr.data-interactive]=\"isInteractive\"\n       [attr.aria-current]=\"!!isCurrent() ? 'page' : null\"\n       [attr.aria-disabled]=\"!!isDisabled()\"\n       [attr.tabindex]=\"!!isDisabled() || !!isCurrent() ? -1 : 0\">\n      {{ label() }}\n    </a>\n  } @else {\n    <span [class]=\"itemStyles\"\n          (click)=\"onClick($event)\"\n          [attr.aria-label]=\"ariaLabel()\"\n          [attr.data-current]=\"!!isCurrent()\"\n          [attr.data-disabled]=\"!!isDisabled()\"\n          [attr.data-interactive]=\"isInteractive\"\n          [attr.aria-current]=\"!!isCurrent() ? 'page' : null\"\n          [attr.aria-disabled]=\"!!isDisabled()\"\n          [attr.tabindex]=\"!!!isDisabled() && !href() && !!!isCurrent() ? 0 : null\"\n          (keydown.enter)=\"!!!isDisabled() && onClick($event)\"\n          (keydown.space)=\"!!!isDisabled() && onClick($event)\">\n      {{ label() }}\n    </span>\n  }\n\n  @if (!isLast && !hideSeparator()) {\n    <span [class]=\"separatorStyles\" role=\"presentation\" aria-hidden=\"true\">\n      {{ separator() }}\n    </span>\n  }\n" }]
        }], propDecorators: { templateRef: [{
                type: ViewChild,
                args: [TemplateRef]
            }], isLast: [{
                type: Input
            }] } });

class BreadcrumbComponent {
    breadcrumbItems = contentChildren(BreadcrumbItemComponent);
    ariaLabel = input('Breadcrumb Navigation');
    itemClicked = output();
    theme = inject(THEME_TOKEN);
    breadcrumbsClasses = componentStylesByTheme(this.theme, breadcrumbStylesSAG, breadcrumbStylesSHS)();
    ngAfterContentInit() {
        this.processItems();
    }
    processItems() {
        const items = this.breadcrumbItems();
        // Set up event listeners for each item
        items.forEach((item, index) => {
            // Set isLast property
            item.isLast = index === items.length - 1;
            // Subscribe to itemClicked events
            item.itemClicked.subscribe(key => {
                this.itemClicked.emit(key);
            });
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: BreadcrumbComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "19.2.2", type: BreadcrumbComponent, isStandalone: true, selector: "sag-ui-breadcrumb", inputs: { ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { itemClicked: "itemClicked" }, queries: [{ propertyName: "breadcrumbItems", predicate: BreadcrumbItemComponent, isSignal: true }], ngImport: i0, template: "<nav [class]=\"breadcrumbsClasses\" [attr.aria-label]=\"ariaLabel()\">\n  <ol class=\"flex flex-wrap items-center\">\n    <ng-content select=\"li[sag-ui-breadcrumb-item]\"></ng-content>\n  </ol>\n</nav>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.2", ngImport: i0, type: BreadcrumbComponent, decorators: [{
            type: Component,
            args: [{ selector: 'sag-ui-breadcrumb', changeDetection: ChangeDetectionStrategy.OnPush, template: "<nav [class]=\"breadcrumbsClasses\" [attr.aria-label]=\"ariaLabel()\">\n  <ol class=\"flex flex-wrap items-center\">\n    <ng-content select=\"li[sag-ui-breadcrumb-item]\"></ng-content>\n  </ol>\n</nav>\n" }]
        }] });

// triggering deployment 3

/**
 * Generated bundle index. Do not edit.
 */

export { ApplicationFrameComponent, AutocompleteComponent, AutocompleteOptionComponent, AvatarBaseDirective, AvatarDirective, AvatarNameDirective, AvatarOrgDirective, BreadcrumbComponent, BreadcrumbItemComponent, ButtonComponent, ButtonDirective, CalendarRangeComponent, CheckboxComponent, CheckboxDirective, CheckboxLabelDirective, ChipComponent, ChipDirective, ChipInputComponent, DatePickerComponent, DateRangePickerComponent, DateRangePickerDemoComponent, DialogComponent, DialogConfig, DialogInjector, DialogRef, DialogService, FileDropComponent, FileDropDirective, FileDropZoneDirective, FocusTrapComponent, FooterComponent, HeaderComponent, InputComponent, InputDirective, InputErrorDirective, InputLabelDirective, InsertionDirective, MenuComponent, MenuDividerComponent, MenuItemComponent, MenuTriggerDirective, MultiAutocompleteComponent, MultiAutocompleteOptionComponent, OptionComponent, RadioComponent, RadioDirective, RadioGroupComponent, RadioGroupLabelDirective, RadioLabelDirective, RangeBaseDirective, RangeBaseMultiDirective, RangeComponent, RangeDirective, RangeMultiComponent, RangeThumbDirective, RangeTrackDirective, RemoteAutocompleteComponent, RemoteAutocompleteOptionComponent, ScdDemoComponent, ScdExtendedDemoComponent, ScdPickerComponent, ScdPickerDialogComponent, ScdPickerExtendedComponent, ScdPickerOptionComponent, SelectComponent, SidebarComponent, SidebarItemComponent, SidebarItemType, SnackbarComponent, SnackbarConfig, SnackbarInjector, SnackbarRef, SnackbarService, SpinnerComponent, StepperComponent, StepperKnobDirective, StepperKnobIndicatorDirective, StepperKnobTextDirective, StepperLineBaseDirective, StepperLineDirective, SwitchComponent, SwitchDirective, SwitchLabelDirective, TabComponent, TabContentComponent, TabViewComponent, TableComponent, TabsBaseDirective, TabsListDirective, TabsTabDirective, TextareaComponent, TooltipDirective, provideTheme };
